import {
  getGroundedMinutesAndLocation,
  buildNoFlightsFaInterp,
  STALE_AIRBORNE_MAX_MS,
  groundedFaEndpointLabel,
  fr24PreferredGroundedMinutes,
  deriveMovementAt,
} from "./status-logic-client.mjs";
import {
  faLookupKeysForAircraft,
  lookupFaCacheEntry,
  normalizeRegistrationDisplay,
  rebuildFaCacheByIdentFromEntries,
} from "./fleet-display-client.mjs";
import {
  matchesNoFlightsFiveDays,
  NO_FLIGHTS_FIVE_DAYS_LEGEND as NO_FLIGHTS_FIVE_DAYS_LEGEND_SHARED,
  buildNoFlightsFiveDaysByOperatorEml,
  buildNoFlightsFmFollowUpEml,
  buildOperatorStatusCheckEml,
  groupNoFlightsRowsByFm,
  resolveNoFlightsFollowUpLocation,
  sortNoFlightsRowsByOperator,
  compareNoFlightsRowsByOperator,
  buildNoFlightsByOperatorExcelXml,
  loadPreviousNoFlightsMsns,
  savePreviousNoFlightsMsns,
  formatTogDaysAmount,
} from "./no-flights-five-days-client.mjs";
import { SURVEILLANCE_NAV, isSurveillanceRoute, renderSurveillanceRoute, renderSurveillanceSettingsPanel } from "./surveillances-v2.jsx";
import { FIGARO_NAV, isFigaroRoute, renderFigaroRoute, renderFigaroSettingsPanels } from "./figaro-v2.jsx";
import { TRANSITIONS_NAV, isTransitionsRoute, renderTransitionsRoute } from "./transitions-v2.jsx";
import { aircraftEngineFamily, aircraftModelFamily, ENGINE_FAMILY_OPTIONS, fleetIdentForAircraft, MODEL_FAMILY_OPTIONS } from "./aircraft-taxonomy.mjs";
import { openXrmImportWizard, renderXrmImportSettingsPanel } from "./xrm-import-v2.jsx";
import {
  OPERATOR_PROGRESS_NAV,
  disposeOperatorProgressRoute,
  isOperatorProgressRoute,
  renderOperatorProgressPage,
} from "./operator-progress-v2.jsx";

const config = window.__CONFIG__ || {
  API_BASE: window.location.origin,
  MODE: "development",
};

function cleanText(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function resolveApiBase(rawBase) {
  const base = cleanText(rawBase || window.location.origin);
  return base.replace(/\/+$/, "");
}

const apiBase = resolveApiBase(config.API_BASE || config.RAW_API_BASE || config.FALLBACK_API_BASE);

function apiUrl(path) {
  const p = cleanText(path);
  if (!p) return apiBase;
  if (/^https?:\/\//i.test(p)) return p;
  return `${apiBase}${p.startsWith("/") ? p : `/${p}`}`;
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    if (response.status === 401) {
      // Session was lost (e.g., API restart). Lightweight login page (no legacy v1 shell).
      try {
        localStorage.setItem("actracker.v2.returnTo", `${window.location.pathname}${window.location.hash}`);
      } catch {
        // ignore
      }
      window.location.href = "/login.html";
    }
    let payload = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    const message =
      cleanText(payload?.error) ||
      cleanText(payload?.message) ||
      (await response.text().catch(() => "")) ||
      `HTTP ${response.status}`;
    const err = new Error(message);
    err.status = response.status;
    err.code = payload?.code;
    throw err;
  }
  // Some endpoints may respond with empty body; be defensive.
  try {
    return await response.json();
  } catch {
    return { ok: true };
  }
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function formatTimestampForFile(date = new Date()) {
  const d = date;
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}_${pad2(
    d.getUTCHours()
  )}${pad2(d.getUTCMinutes())}Z`;
}

function htmlEscape(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function cellHtmlForXls(value) {
  return htmlEscape(value).replace(/\r\n|\r|\n/g, "<br/>");
}

function encodeBase64(text) {
  try {
    return btoa(unescape(encodeURIComponent(text)));
  } catch {
    return null;
  }
}

function downloadTextFile({ filename, content, contentType }) {
  const blob = new Blob([content], { type: contentType || "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Escape a value for a SpreadsheetML <Data> node; newlines become &#10; so multi-line text stays inside ONE cell. */
function xmlCellText(value) {
  const s = value == null ? "" : String(value);
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\r\n|\r|\n/g, "&#10;");
}

/** Column widths (points) by header so wide free-text columns (Comments) get room and the rest stay tidy. */
function xlsColumnWidthForHeader(header) {
  const h = String(header || "").toLowerCase();
  if (h.includes("comment")) return 380;
  if (h === "operator" || h === "location") return 130;
  if (h === "fm" || h === "on watch") return 55;
  return 90;
}

/**
 * Build an Excel 2003 SpreadsheetML workbook (served as .xls).
 * - Every cell is a real String cell with WrapText, so multi-line comments stay in a single cell
 *   (no spilling into / merging neighbouring columns) and remain fully visible.
 * - AutoFilter + frozen header row so filtering and sorting work out of the box.
 * - Optional `extraSheets: [{ name, headers, rows }]` for additional worksheets.
 */
function buildXlsXml({ title, headers, rows, extraSheets = [] }) {
  const buildSheet = (sheetTitle, sheetHeaders, sheetRows) => {
    const cols = sheetHeaders || [];
    const colCount = Math.max(1, cols.length);
    const columnDefs = cols
      .map((h) => `<Column ss:AutoFitWidth="0" ss:Width="${xlsColumnWidthForHeader(h)}"/>`)
      .join("");
    const headerRow = `<Row ss:AutoFitHeight="0" ss:Height="22">${cols
      .map((h) => `<Cell ss:StyleID="hdr"><Data ss:Type="String">${xmlCellText(h)}</Data></Cell>`)
      .join("")}</Row>`;
    const bodyRows = (sheetRows || [])
      .map(
        (r) =>
          `<Row>${(r || [])
            .map((c) => `<Cell ss:StyleID="cell"><Data ss:Type="String">${xmlCellText(c)}</Data></Cell>`)
            .join("")}</Row>`
      )
      .join("");
    const totalRows = (sheetRows || []).length + 1;
    const sheetName =
      String(sheetTitle || "AC Tracker")
        .replace(/[\\\/\?\*\[\]:]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 31) || "Report";
    return ` <Worksheet ss:Name="${xmlCellText(sheetName)}">
  <Table>
   ${columnDefs}
   ${headerRow}
   ${bodyRows}
  </Table>
  <AutoFilter x:Range="R1C1:R${totalRows}C${colCount}" xmlns="urn:schemas-microsoft-com:office:excel"/>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>1</SplitHorizontal>
   <TopRowBottomPane>1</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>`;
  };

  const primary = buildSheet(title, headers, rows);
  const extras = (extraSheets || [])
    .map((s) => buildSheet(s?.name || "Sheet", s?.headers || headers, s?.rows || []))
    .join("\n");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Top"/></Style>
  <Style ss:ID="hdr">
   <Font ss:Bold="1" ss:Color="#FFFFFF"/>
   <Interior ss:Color="#C00000" ss:Pattern="Solid"/>
   <Alignment ss:Vertical="Top" ss:WrapText="1"/>
  </Style>
  <Style ss:ID="cell"><Alignment ss:Vertical="Top" ss:WrapText="1"/></Style>
 </Styles>
${primary}
${extras}
</Workbook>`;
}

function getSavedRecipientPool() {
  try {
    const raw = localStorage.getItem("actracker.v2.recipients") || "";
    const list = raw
      .split(/[,;\s]/)
      .map((x) => cleanText(x))
      .filter(Boolean);
    return Array.from(new Set(list));
  } catch {
    return [];
  }
}

function saveRecipientPool(list) {
  try {
    const unique = Array.from(new Set((list || []).map((x) => cleanText(x)).filter(Boolean)));
    localStorage.setItem("actracker.v2.recipients", unique.join(","));
  } catch {
    // ignore
  }
}

async function promptRecipients({ defaultSelected = [] } = {}) {
  const pool = getSavedRecipientPool();
  const sessionEmail = cleanText(state.session?.email);
  const initial = Array.from(new Set([sessionEmail, ...defaultSelected, ...pool].filter(Boolean)));

  const picked = window.prompt(
    "Recipients (comma-separated). Pre-filled from your last selections.",
    initial.join(", ")
  );
  if (picked === null) return null;
  const recipients = picked
    .split(/[,;\s]/)
    .map((x) => cleanText(x))
    .filter(Boolean);
  if (!recipients.length) return null;
  saveRecipientPool([...pool, ...recipients]);
  return recipients;
}

function formatAgo(msAgo) {
  if (!Number.isFinite(msAgo) || msAgo < 0) return "—";
  const minutes = Math.floor(msAgo / 60000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDurationMinutes(minutes) {
  if (!Number.isFinite(minutes) || minutes < 0) return "—";
  if (minutes === 0) return "AIRBORNE";
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes - h * 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(minutes / (24 * 60));
  const h = Math.floor((minutes - d * 24 * 60) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
}

function minutesSince(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return null;
  return Math.max(0, Math.floor((Date.now() - ts) / 60000));
}

/** Latest departure time in the FA snapshot (root + flights[]). Used to drop manual TOG anchor once real movement appears. */
function faLatestDepartureMs(statusObj) {
  if (!statusObj || typeof statusObj !== "object") return null;
  let max = null;
  const consider = (iso) => {
    const t = Date.parse(iso || "");
    if (Number.isFinite(t) && (max === null || t > max)) max = t;
  };
  consider(statusObj.off);
  consider(statusObj.actual_off);
  if (Array.isArray(statusObj.flights)) {
    for (const f of statusObj.flights) {
      if (!f || typeof f !== "object") continue;
      consider(f.off || f.actual_off || f.actual_departure || f.actual_out);
    }
  }
  return max;
}

function toEstLabel(date) {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

/** Display line for Excel / reports — wall time in America/New_York with EST suffix */
function formatMaterialsExportTimeEst(date = new Date()) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const main = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d);
    return `${main} EST`;
  } catch {
    return `${toEstLabel(date)} EST`;
  }
}

/** Filename fragment: date/time in America/New_York */
function formatMaterialsFilenameEst(date = new Date()) {
  try {
    const d = date instanceof Date ? date : new Date(date);
    const datePart = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
    const hm = new Intl.DateTimeFormat("en-GB", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d);
    const timePart = hm.replace(/:/g, "");
    return `${datePart}_${timePart}-EST`;
  } catch {
    return formatTimestampForFile(date);
  }
}

function buildMaterialsXlsXml({ title, exportedAtEst, headers, rows }) {
  const safeTitle = htmlEscape(title || "Materials");
  const meta = htmlEscape(exportedAtEst || "");
  const th = (headers || [])
    .map(
      (h) =>
        `<th style="background:#c00000;color:#ffffff;font-weight:600;padding:8px 10px;border:1px solid #a00000;text-transform:uppercase;font-size:11px;letter-spacing:0.04em;text-align:left;">${htmlEscape(h)}</th>`
    )
    .join("");
  const body = (rows || [])
    .map(
      (r) =>
        `<tr style="background:#ffffff;">${(r || [])
          .map(
            (c) =>
              `<td style="border:1px solid #e5e7eb;padding:6px 10px;font-size:12px;vertical-align:top;color:#111827;">${cellHtmlForXls(c)}</td>`
          )
          .join("")}</tr>`
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>
body{font-family:Inter,system-ui,Segoe UI,Roboto,sans-serif;color:#111827;margin:20px 24px;line-height:1.45;}
h1{font-size:20px;font-weight:700;margin:0 0 6px;color:#111827;}
.meta{color:#4b5563;font-size:12px;margin:0 0 18px;}
table{border-collapse:collapse;width:100%;max-width:100%;}
</style></head><body>
<h1>${safeTitle}</h1>
<p class="meta"><strong>Export time (EST):</strong> ${meta}</p>
<table cellspacing="0" cellpadding="0">
<thead><tr>${th}</tr></thead>
<tbody>${body}</tbody>
</table>
</body></html>`;
}

function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  Object.entries(attrs || {}).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (k === "class") node.className = v;
    else if (k === "text") node.textContent = v;
    else if (k === "for") node.htmlFor = String(v);
    else if (k === "value") node.value = String(v);
    else if (k === "checked") node.checked = Boolean(v);
    else if (k === "disabled") node.disabled = Boolean(v);
    else if (k === "selected") node.selected = Boolean(v);
    else if (k.startsWith("on") && typeof v === "function") node.addEventListener(k.slice(2).toLowerCase(), v);
    else node.setAttribute(k, String(v));
  });
  for (const child of Array.isArray(children) ? children : [children]) {
    if (child === undefined || child === null) continue;
    if (Array.isArray(child)) {
      for (const nested of child) {
        if (nested === undefined || nested === null) continue;
        node.appendChild(typeof nested === "string" ? document.createTextNode(nested) : nested);
      }
      continue;
    }
    node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return node;
}

/** Modal backdrops are appended to `document.body`; SPA navigation must remove them or the dim layer blocks the app. */
function removeStaleActrackerModalBackdrops() {
  try {
    document.querySelectorAll('[data-actracker-modal-backdrop="1"]').forEach((n) => n.remove());
  } catch {
    /* ignore */
  }
}

const state = {
  session: null,
  aircraft: [],
  lastUpdatedAt: null,
  /** @type {ReturnType<typeof setInterval> | null} */
  avheraldIngestPoll: null,
  /** @type {ReturnType<typeof setInterval> | null} */
  avheraldRefreshPoll: null,
  /** @type {ReturnType<typeof setTimeout> | null} */
  newsIncidentsSearchDebounce: null,
  /** 1-based page for `#/news-incidents` table (synced to `?page=` when not 1). */
  newsIncidentsPage: 1,
  sidebarOpen: true,
  faCacheByIdent: new Map(),
  bulkCommentsByAircraftId: new Map(),
  bulkCommentsKey: "",
  adminAircraft: null,
  operators: null,
  apiCostsPrefetched: null,
};

const NAV = [
  { separator: true },
  { key: "dashboard", label: "Dashboard" },
  SURVEILLANCE_NAV,
  FIGARO_NAV,
  TRANSITIONS_NAV,
  OPERATOR_PROGRESS_NAV,
  { key: "news-incidents", label: "Incidents & accidents" },
  { key: "heatmap", label: "Heatmap" },
  // Spirit tracker UI hidden; hourly activity cron + emails unchanged (see Settings → Crons).
  // { key: "spirit-tracker", label: "Spirit tracker" },
  { separator: true },
  { key: "all", label: "All aircraft" },
  {
    group: "non-service",
    label: "Non-service",
    defaultOpen: true,
    children: [
      { key: "non-service-all", label: "All" },
      { key: "aog", label: "AOG" },
      { key: "maintenance", label: "Maintenance" },
      { key: "transition", label: "Transition" },
      { key: "incident", label: "Incident" },
      { key: "storage", label: "Storage" },
      { key: "russia", label: "Russia" },
    ],
  },
  { key: "no-flights-24h", label: "No flights in 5 days" },
  { key: "ground-over-21d", label: "More than 21 days on ground" },
  { key: "awaiting-operator", label: "Awaiting operator feedback" },
  { key: "middle-east", label: "Middle East" },
  { separator: true },
  // Materials module temporarily hidden from the side menu (not in use for now).
  // Routes/pages remain intact; restore this group to re-enable navigation.
  // {
  //   group: "materials-program",
  //   label: "Materials",
  //   defaultOpen: true,
  //   children: [
  //     { key: "materials-dashboard", label: "Dashboard" },
  //     { key: "materials", label: "Parts" },
  //     { key: "materials/projects", label: "Projects" },
  //     { key: "materials/cores", label: "Cores (OFF)" },
  //   ],
  // },
  { key: "status-recent", label: "Status changes" },
  { separator: true },
  { key: "settings", label: "Settings" },
];

/** Sidebar / hash route for the aviation news table (`#/news-incidents`). Legacy `#/avherald-incidents` redirects here. */
const NEWS_INCIDENTS_ROUTE = "news-incidents";

const V2_UI_RELEASE_NOTES = [
  "Settings → Crons: Incident daily check (08:00 Dubai) imports new incidents and emails a digest table. When at least one DAE fleet match is included, Enda Swan and Antonio Lopes are CC’d. Install scripts/install-cron-avherald-incidents.sh on the server.",
  "Expected release (fleet table): any signed-in user can save for visible aircraft via PUT /api/aircraft/:id/profile (no admin API). Manual override POST checks ownership.",
  "Settings → Crons: Spirit tracker activity (hourly :45) — refreshes FA and updates snapshots every run; emails Albert + Claus only on operational changes (TAM, location, next-flight watch, or airborne), not TOG-only ticks. Install scripts/install-cron-spirit-tracker-activity-hourly.sh on the server.",
  "Sidebar: “More than 21 days on ground” lists IN SERVICE aircraft whose table time-on-ground (FA + manual anchor) exceeds 21 days; sorts by TOG by default.",
  'Settings → Crons: ME 12h / manual attention / Americas jobs show next-run from OS cron.d even if Inactive was set; amber badge means “Linux cron still fires”. Defaults list production To/CC.',
  'Heatmap page (under Dashboard): same region/operator tile view as the dashboard with Search, FM, Operator, Region, TAM, Source, and watch filters tied to the URL.',
  "Fleet aircraft tables (~80vw): fixed column widths, compact cells, no horizontal scroll; Comments uses shared typography and lists the full thread in-row.",
  "Dashboard → Aircraft by region: clicking a cell keeps the region filter together with TAM; footer row shows totals for each column.",
  'No flights (5 days) view hides the Expected release column (still editable elsewhere via other lists).',
  "Excel export (fleet lists): waits for bulk comments then writes every comment line; Comments modal delete is available to FMs and admins.",
  "Classic (v1) table: Comments (+) matches link-pill styling; Comment history removal uses trash icon.",
  'Activity (#/activity) and Settings → Activity open the Status changes table (#/status-recent). Crons opens from Settings → Crons.',
  "About (this tab) shows deploy branch, last deploy time from ./deploy.sh, GitHub commit link, and commit timestamps when available.",
];

/**
 * Spirit tracker roster — **matches** scripts/lib/spirit-tracker-fleet.mjs:
 * Spirit Airlines (`spirit-airlines`) program lines (below) then Spirit-NonDAE tails (`spirit-nondae`),
 * owner **AL**, region **Americas**, from the fleet API. No allocation / engine-desk matrices.
 *
 * MSN **4592** and **4996** often share hull **N606NK**; only one MSN row exists in DB — peer lookup below.
 */
const SPIRIT_AIRLINES_SLUG = "spirit-airlines";
const SPIRIT_NONDAE_SLUG = "spirit-nondae";
const SPIRIT_TRACKER_OWNER = "AL";
const SPIRIT_TRACKER_REGION = "Americas";

/** Display order for DAE-program lines — keep synced with SPIRIT_DA_DISPLAY_MSNS in spirit-tracker-fleet.mjs. */
const SPIRIT_DA_DISPLAY_MSNS = ["4996", "4592", "5387", "4591", "4548", "9395"];

const SPIRIT_DA_HULL_PEER = {
  "4592": "4996",
  "4996": "4592",
};

const SPIRIT_TRACKER_EXCLUDED_MSNS = new Set(["8614"]);
const SPIRIT_NONDAE_PHYSICAL_MSNS = new Set(["7045", "8047", "7058", "8400", "7135"]);

function normaliseSpiritFleetMsn(v) {
  const raw = cleanText(v);
  if (!raw) return "";
  const digits = raw.match(/(\d+)/);
  return digits ? digits[1] : raw.toUpperCase();
}

function spiritTrackerSlug(a) {
  return cleanText(a?.operator_slug).toLowerCase();
}

function spiritTrackerOwnerRegionMatch(a) {
  const owner = cleanText(a?.owner).toUpperCase();
  const region = cleanText(a?.region).toLowerCase();
  return owner === SPIRIT_TRACKER_OWNER && region === SPIRIT_TRACKER_REGION.toLowerCase();
}

function spiritNondaeOperatorMatch(a) {
  const slug = spiritTrackerSlug(a);
  if (slug === SPIRIT_NONDAE_SLUG) return true;
  const k = normaliseSpiritFleetMsn(a?.msn);
  if (SPIRIT_NONDAE_PHYSICAL_MSNS.has(k)) return spiritTrackerOwnerRegionMatch(a);
  const op = cleanText(a?.operator)
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!op.includes("spirit")) return false;
  return op.includes("nondae") || op.includes("non-dae") || op.includes("non dae");
}

function spiritProgramOperatorMatch(a) {
  const k = normaliseSpiritFleetMsn(a?.msn);
  if (SPIRIT_NONDAE_PHYSICAL_MSNS.has(k)) return false;
  const slug = spiritTrackerSlug(a);
  if (slug === SPIRIT_AIRLINES_SLUG) return true;
  if (slug === SPIRIT_NONDAE_SLUG) return false;
  const op = cleanText(a?.operator)
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (!op.includes("spirit")) return false;
  if (op.includes("nondae") || op.includes("non-dae") || op.includes("non dae")) return false;
  return true;
}

function isSpiritTrackerFleetRow(a) {
  const k = normaliseSpiritFleetMsn(a?.msn);
  if (SPIRIT_TRACKER_EXCLUDED_MSNS.has(k)) return false;
  if (!spiritTrackerOwnerRegionMatch(a)) return false;
  return spiritProgramOperatorMatch(a) || spiritNondaeOperatorMatch(a);
}

function findSpiritAirframeRecord(list, msnKey) {
  const want = normaliseSpiritFleetMsn(msnKey);
  if (!want) return null;
  for (const a of list || []) {
    if (normaliseSpiritFleetMsn(a?.msn) === want) return a;
  }
  return null;
}

function spiritMsnSortKey(msnRaw) {
  const k = normaliseSpiritFleetMsn(msnRaw);
  const n = parseInt(k, 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
}

function findSpiritProgramAirframeByMsn(list, msnKey) {
  const want = normaliseSpiritFleetMsn(msnKey);
  if (!want) return null;
  for (const a of list || []) {
    if (!isSpiritTrackerFleetRow(a) || !spiritProgramOperatorMatch(a)) continue;
    if (normaliseSpiritFleetMsn(a?.msn) === want) return a;
  }
  return null;
}

function resolveSpiritDaAircraftFromList(list, programMsn) {
  const want = normaliseSpiritFleetMsn(programMsn);
  if (!want) return null;
  let ac = findSpiritProgramAirframeByMsn(list, want);
  if (ac) return ac;
  const peer = SPIRIT_DA_HULL_PEER[want];
  if (peer) ac = findSpiritProgramAirframeByMsn(list, peer);
  return ac || null;
}

function listSpiritNondaeAircraft(list) {
  const rows = (list || []).filter((a) => isSpiritTrackerFleetRow(a) && spiritNondaeOperatorMatch(a));
  rows.sort((a, b) => spiritMsnSortKey(a?.msn) - spiritMsnSortKey(b?.msn));
  const seen = new Set();
  return rows.filter((a) => {
    const id = Number(a?.id);
    if (!Number.isFinite(id)) return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

/**
 * Ordered `{ ac, displayMsn }` for Spirit tracker page + FA refresh (dedupe by aircraft id).
 */
function listSpiritTrackerDisplayPairs(list) {
  const out = [];
  const idsShownInFixedOrder = new Set();
  for (const m of SPIRIT_DA_DISPLAY_MSNS) {
    const ac = resolveSpiritDaAircraftFromList(list, m);
    if (!ac) continue;
    const id = Number(ac?.id);
    if (Number.isFinite(id) && id > 0) idsShownInFixedOrder.add(id);
    out.push({ ac, displayMsn: normaliseSpiritFleetMsn(m) });
  }
  const extraProgram = (list || []).filter((a) => isSpiritTrackerFleetRow(a) && spiritProgramOperatorMatch(a));
  extraProgram.sort((a, b) => spiritMsnSortKey(a?.msn) - spiritMsnSortKey(b?.msn));
  const seenExtraProgram = new Set();
  for (const ac of extraProgram) {
    const id = Number(ac?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (idsShownInFixedOrder.has(id)) continue;
    if (seenExtraProgram.has(id)) continue;
    seenExtraProgram.add(id);
    out.push({ ac, displayMsn: normaliseSpiritFleetMsn(ac?.msn) });
  }
  for (const ac of listSpiritNondaeAircraft(list)) {
    out.push({ ac, displayMsn: normaliseSpiritFleetMsn(ac?.msn) });
  }
  return out;
}

function spiritTrackerRefreshAircraftList(list) {
  const seen = new Set();
  const out = [];
  for (const { ac } of listSpiritTrackerDisplayPairs(list)) {
    const id = Number(ac?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(ac);
  }
  return out;
}

function buildSpiritTrackerFaTargets(list) {
  const map = new Map();
  const add = (ident, registration = "") => {
    const id = cleanText(ident).toUpperCase();
    if (!id) return;
    if (!map.has(id)) map.set(id, { ident: id, registration: cleanText(registration) });
  };
  for (const a of spiritTrackerRefreshAircraftList(list)) {
    const ik = inferFaKey(a);
    if (ik) add(ik, cleanText(a?.registration) || cleanText(a?.tail));
  }
  return [...map.values()];
}

function currentRouteKey() {
  const hash = cleanText(window.location.hash).replace(/^#\/?/, "");
  const [path] = hash.split("?");
  return path || "dashboard";
}

function currentRouteParams() {
  const hash = cleanText(window.location.hash).replace(/^#\/?/, "");
  const [, query = ""] = hash.split("?");
  try {
    return new URLSearchParams(query);
  } catch {
    return new URLSearchParams();
  }
}

function navLinkMatchesRoute(routeKey, linkKey) {
  if (routeKey === linkKey) return true;
  if (linkKey === "materials" && (routeKey === "materials/part/new" || /^materials\/part\/\d+$/.test(routeKey))) return true;
  if (
    linkKey === "materials/projects" &&
    (routeKey === "materials/projects" || routeKey === "materials/project/new" || /^materials\/project\/\d+$/.test(routeKey))
  )
    return true;
  if (linkKey === "materials/cores" && routeKey === "materials/cores") return true;
  if (linkKey === "figaro/workscopes" && (routeKey === "figaro/workscopes" || routeKey.startsWith("figaro/workscopes/"))) {
    // Keep "Workscopes" highlighted for eval detail/new, but not for MPD library (separate nav item)
    return !routeKey.startsWith("figaro/workscopes/mpds");
  }
  if (linkKey === "figaro/workscopes/mpds" && (routeKey === "figaro/workscopes/mpds" || routeKey.startsWith("figaro/workscopes/mpds/"))) {
    return true;
  }
  if (linkKey === "figaro/lease-returns" && (routeKey === "figaro/lease-returns" || routeKey.startsWith("figaro/lease-returns/"))) {
    return true;
  }
  if (linkKey === "figaro/invoice-audit" && (routeKey === "figaro/invoice-audit" || routeKey.startsWith("figaro/invoice-audit/"))) {
    return true;
  }
  if (linkKey === "transitions" && (routeKey === "transitions" || routeKey.startsWith("transitions/"))) {
    return true;
  }
  return false;
}

function setActiveNav() {
  const active = currentRouteKey();
  document.querySelectorAll("#v2-nav a[data-route]").forEach((a) => {
    const lk = a.getAttribute("data-route");
    const on = navLinkMatchesRoute(active, lk);
    a.classList.toggle("active", on);
    if (on) {
      a.classList.add("bg-red-50", "border-l-4", "border-dae");
    } else {
      a.classList.remove("bg-red-50", "border-l-4", "border-dae");
    }
  });
}

function navGroupStorageKey(groupId) {
  return `actracker.v2.nav.group.${groupId}`;
}

function readNavGroupOpen(groupId, defaultOpen = true) {
  try {
    const legacy =
      groupId === "non-service" ? localStorage.getItem("actracker.v2.nav.nonServiceOpen") : null;
    if (legacy !== null && localStorage.getItem(navGroupStorageKey(groupId)) === null) {
      return legacy !== "false";
    }
    const v = localStorage.getItem(navGroupStorageKey(groupId));
    if (v === null) return defaultOpen;
    return v !== "false";
  } catch {
    return defaultOpen;
  }
}

function writeNavGroupOpen(groupId, open) {
  try {
    localStorage.setItem(navGroupStorageKey(groupId), open ? "true" : "false");
  } catch {
    // ignore
  }
  renderNav();
}

function renderNav() {
  const nav = document.getElementById("v2-nav");
  const inspector = isInspectorSession();
  const singaporeFm = isSingaporeFmSession();
  const singaporeAllowed = new Set([
    "dashboard",
    "news-incidents",
    "heatmap",
    "all",
    "status-recent",
    "settings",
  ]);
  nav.replaceChildren(
    ...NAV.filter((item) => {
      if (inspector) {
        if (item?.separator) return false;
        const key = cleanText(item?.key);
        return key === "surveillances" || key === "all";
      }
      if (singaporeFm) {
        if (item?.separator) return false;
        // Hide Figaro / Surveillances / Transitions / Operator progress / Non-service groups entirely.
        if (item?.children) return false;
        if (item?.group === "figaro" || cleanText(item?.label).toLowerCase() === "figaro") return false;
        const key = cleanText(item?.key);
        return singaporeAllowed.has(key);
      }
      if (item?.adminOnly) return isAdminSession();
      if (item?.fleetScope) return hasFleetScopeAccess();
      return true;
    }).map((item) => {
      if (item?.separator) {
        return el("li", { class: "my-2" }, [
          el("div", { class: "h-px bg-gray-200 w-full" }),
        ]);
      }
      if (item?.children && Array.isArray(item.children)) {
        const gid = item.group || "nav-group";
        const open = readNavGroupOpen(gid, item.defaultOpen !== false);
        const headerBtn = el(
          "button",
          {
            type: "button",
            class:
              "w-full flex items-center justify-between p-2 text-gray-900 rounded-lg hover:bg-gray-100 group border-l-4 border-transparent",
            onclick: () => writeNavGroupOpen(gid, !open),
          },
          [
            el("span", { class: "ms-2 font-semibold text-gray-800", text: item.label || "Group" }),
            el("span", { class: "text-gray-500 text-xs", text: open ? "▾" : "▸" }),
          ]
        );
        const children = open
          ? el(
              "ul",
              { class: "mt-1 space-y-1" },
              item.children.map((child) =>
                el("li", { class: "ml-3" }, [
                  el(
                    "a",
                    {
                      href: `#/${child.key}`,
                      "data-route": child.key,
                      class:
                        "flex items-center p-2 text-gray-900 rounded-lg hover:bg-gray-100 group border-l-4 border-transparent",
                    },
                    [el("span", { class: "ms-2", text: child.label })]
                  ),
                ])
              )
            )
          : null;
        return el("li", {}, [headerBtn, children].filter(Boolean));
      }
      return el("li", {}, [
        el(
          "a",
          {
            href: `#/${item.key}`,
            "data-route": item.key,
            class:
              "flex items-center p-2 text-gray-900 rounded-lg hover:bg-gray-100 group border-l-4 border-transparent",
          },
          [el("span", { class: "ms-2", text: inspector && item.key === "all" ? "Assigned aircraft" : item.label })]
        ),
      ]);
    })
  );
  setActiveNav();
}

function updateTopbarLastUpdate() {
  const elLast = document.getElementById("v2-last-update");
  if (!elLast) return;
  if (!state.lastUpdatedAt) {
    elLast.textContent = "Last update: —";
    return;
  }
  const msAgo = Date.now() - state.lastUpdatedAt.getTime();
  elLast.textContent = `Last update (EST): ${toEstLabel(state.lastUpdatedAt)} (${formatAgo(msAgo)})`;
}

function computeLastUpdatedAtFromAircraft(list) {
  let latest = 0;
  for (const a of list || []) {
    const candidates = [
      a?.tam_status_updated_at,
      a?.manual_status_updated_at,
      a?.updated_at,
      a?.last_comment?.created_at,
    ]
      .map((v) => Date.parse(v))
      .filter((v) => Number.isFinite(v));
    for (const ts of candidates) {
      if (ts > latest) latest = ts;
    }
  }
  // FlightAware refresh updates `faCacheByIdent[].fetched_at` without bumping `aircraft.updated_at`;
  // merge those so the top bar reflects FA sync, not only manual/TAM/comment timestamps.
  const cache = state.faCacheByIdent;
  if (cache instanceof Map && cache.size) {
    const keysFromList = new Set();
    for (const a of list || []) {
      const k = cleanText(inferFaKey(a)).toUpperCase();
      if (k) keysFromList.add(k);
    }
    const restrict = keysFromList.size > 0;
    for (const [cacheKey, entry] of cache.entries()) {
      const ck = String(cacheKey || "").toUpperCase();
      if (restrict && !keysFromList.has(ck)) continue;
      const fs = Date.parse(entry?.fetched_at);
      if (Number.isFinite(fs) && fs > latest) latest = fs;
    }
  }
  return latest ? new Date(latest) : null;
}

async function loadSession() {
  const data = await fetchJson(apiUrl("/api/session"), { method: "GET" });
  state.session = data?.user || data || null;
  const logoutBtn = document.getElementById("v2-logout");
  if (logoutBtn) logoutBtn.classList.toggle("hidden", !state.session);
}

function isAdminSession() {
  const u = state.session || {};
  return Boolean(u?.is_admin || u?.isAdmin || u?.role === "admin");
}

/**
 * Limited fleet UI (no Figaro / surveillances / ops modules):
 * - Singapore FMs (lw/yy/ks/jq): own aircraft only via OWNER map
 * - Enda Swan (es): whole fleet via es:* — same screens as FMs
 */
const LIMITED_FLEET_UI_USERNAMES = new Set(["lw", "yy", "ks", "jq", "es"]);

function isLimitedFleetUiSession() {
  if (!state.session) return false;
  // Username allowlist wins even if a stale session wrongly set isAdmin (full-fleet ≠ admin).
  const u = cleanText(state.session?.username).toLowerCase();
  return LIMITED_FLEET_UI_USERNAMES.has(u);
}

/** @deprecated use isLimitedFleetUiSession */
function isSingaporeFmSession() {
  return isLimitedFleetUiSession();
}

function isLimitedFleetUiAllowedRoute(key) {
  const k = cleanText(key);
  if (!k) return false;
  // No Figaro / AI / Perkins — ever — for limited fleet accounts (incl. es).
  if (k === "figaro" || k.startsWith("figaro/") || k === "ai" || k.startsWith("ai/") || k === "perkins" || k.startsWith("perkins/")) {
    return false;
  }
  const allowed = new Set([
    "dashboard",
    "news-incidents",
    "heatmap",
    "all",
    "status-recent",
    "settings",
  ]);
  return allowed.has(k);
}

function isSingaporeFmAllowedRoute(key) {
  return isLimitedFleetUiAllowedRoute(key);
}

function limitedFleetUiHomeHash() {
  return "#/dashboard";
}

function singaporeFmHomeHash() {
  return limitedFleetUiHomeHash();
}

/** null = all operators (admin / datamanagement); [] = none; else FM owner codes. */
function getAllowedOwners() {
  const u = state.session || {};
  if (!u || isAdminSession()) return null;
  if (u.allowedOwners === null || u.allowedOwners === undefined) return null;
  return Array.isArray(u.allowedOwners) ? u.allowedOwners : [];
}

function hasFleetScopeAccess() {
  if (!state.session) return false;
  if (isAdminSession()) return true;
  const allowed = getAllowedOwners();
  return Array.isArray(allowed) && allowed.some((code) => cleanText(code) !== "__INSPECTOR__");
}

/** Surveillance inspectors: only assigned MSNs + Surveillances UI. */
function isInspectorSession() {
  if (!state.session || isAdminSession()) return false;
  const allowed = getAllowedOwners();
  return Array.isArray(allowed) && allowed.length > 0 && allowed.every((code) => cleanText(code) === "__INSPECTOR__");
}

function inspectorHomeHash() {
  return "#/surveillances";
}

function isInspectorAllowedRoute(key) {
  const k = cleanText(key);
  if (!k) return false;
  if (k === "all") return true;
  if (k === "surveillances" || k.startsWith("surveillances/") || k.startsWith("surveillances")) return true;
  return false;
}

function aircraftOwnedBySession(a) {
  const allowed = getAllowedOwners();
  if (allowed === null) return true;
  if (!allowed.length) return false;
  const owner = cleanText(a?.owner).toUpperCase();
  return allowed.some((o) => owner === cleanText(o).toUpperCase());
}

function operatorsForSession(operators, aircraft) {
  const allowed = getAllowedOwners();
  if (allowed === null) return Array.isArray(operators) ? operators : [];
  const opIds = new Set();
  const opNames = new Set();
  for (const a of aircraft || []) {
    if (!aircraftOwnedBySession(a)) continue;
    const id = Number(a?.operator_id ?? a?.operatorId);
    if (Number.isFinite(id) && id > 0) opIds.add(id);
    const name = cleanText(a?.operator).toLowerCase();
    if (name) opNames.add(name);
  }
  return (Array.isArray(operators) ? operators : []).filter((o) => {
    const id = Number(o?.id);
    const name = cleanText(o?.name).toLowerCase();
    return (Number.isFinite(id) && opIds.has(id)) || (name && opNames.has(name));
  });
}

function operatorNamesForSession(operators, aircraft) {
  return Array.from(new Set(operatorsForSession(operators, aircraft).map((o) => cleanText(o?.name)).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
}

async function logout() {
  await fetchJson(apiUrl("/api/session"), { method: "DELETE" });
  window.location.reload();
}

async function loadAircraft() {
  const data = await fetchJson(apiUrl("/api/aircraft"));
  const list = Array.isArray(data) ? data : Array.isArray(data?.aircraft) ? data.aircraft : [];
  state.aircraft = list;
  state.lastUpdatedAt = computeLastUpdatedAtFromAircraft(list);
  updateTopbarLastUpdate();
}

async function loadAdminAircraft() {
  // Admins manage the full fleet; non-admins edit only the aircraft they own
  // (the owner-scoped /api/aircraft list).
  const endpoint = isAdminSession() ? "/api/admin/aircraft" : "/api/aircraft";
  const resp = await fetchJson(apiUrl(endpoint));
  const list = Array.isArray(resp?.aircraft) ? resp.aircraft : Array.isArray(resp) ? resp : [];
  state.adminAircraft = list;
  return list;
}

async function loadOperators() {
  const resp = await fetchJson(apiUrl("/api/operators"));
  const list = Array.isArray(resp?.operators) ? resp.operators : [];
  state.operators = list;
  return list;
}

async function loadAdminOwners() {
  const resp = await fetchJson(apiUrl("/api/admin/owners"));
  const list = Array.isArray(resp?.owners) ? resp.owners : [];
  state.adminOwners = list;
  return list;
}

async function saveOperatorContacts(operatorId, contacts) {
  return await fetchJson(apiUrl(`/api/admin/operators/${Number(operatorId)}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ contacts }),
  });
}

async function createAircraftAdmin(payload) {
  return await fetchJson(apiUrl("/api/admin/aircraft"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
}

async function saveAircraftAdmin(aircraftId, payload) {
  return await fetchJson(apiUrl(`/api/admin/aircraft/${Number(aircraftId)}`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
}

/** Session user profile updates (expected release, etc.) — not admin-only. */
async function saveAircraftProfile(aircraftId, payload) {
  const u = state.session || {};
  const updatedBy = cleanText(u.displayName) || cleanText(u.username) || "";
  const body = { ...(payload || {}) };
  if (updatedBy) body.updatedBy = updatedBy;
  return await fetchJson(apiUrl(`/api/aircraft/${Number(aircraftId)}/profile`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

async function deleteAircraftAdmin(aircraftId) {
  return await fetchJson(apiUrl(`/api/admin/aircraft/${Number(aircraftId)}`), { method: "DELETE" });
}

function isPlaceholderRegistration(value) {
  const v = cleanText(value).toUpperCase();
  if (!v || /^XRM-/i.test(v)) return true;
  if (/^\d+::[A-Z0-9]+$/i.test(v)) return true;
  return false;
}

function displayRegistration(aircraft) {
  const reg = (cleanText(aircraft?.registration) || cleanText(aircraft?.tail) || "").replace(/\.+$/g, "").trim();
  if (!reg || isPlaceholderRegistration(reg)) return "—";
  return reg;
}

function inferFaKey(aircraft) {
  const reg =
    normalizeRegistrationDisplay(aircraft?.registration) || normalizeRegistrationDisplay(aircraft?.tail) || "";
  if (reg && !isPlaceholderRegistration(reg)) return reg.toUpperCase();
  const ident = cleanText(aircraft?.ident) || "";
  if (ident && !isPlaceholderRegistration(ident)) return ident.toUpperCase();
  return "";
}

/** Cancel/divert wording may live only on `status`, `flight_status`, or rows in `flights[]` (matches api/status-logic.mjs). */
function aggregateFaStatusPhaseLower(statusObj) {
  if (!statusObj || typeof statusObj !== "object") return "";
  const parts = [
    cleanText(statusObj.phase),
    cleanText(statusObj.status_text),
    cleanText(statusObj.status),
    cleanText(statusObj.flight_status),
  ];
  if (Array.isArray(statusObj.flights)) {
    for (const f of statusObj.flights) {
      if (!f || typeof f !== "object") continue;
      if (f.cancelled === true || f.canceled === true) parts.push("cancelled");
      parts.push(
        cleanText(f.phase),
        cleanText(f.status),
        cleanText(f.flight_status),
        cleanText(f.status_text),
      );
    }
  }
  return parts.filter(Boolean).join(" ").toLowerCase();
}

function faStatusLooksCancelled(statusObj) {
  const t = aggregateFaStatusPhaseLower(statusObj);
  if (t.includes("cancel") || t.includes("cancelled") || t.includes("canceled")) return true;
  if (Array.isArray(statusObj?.flights)) {
    for (const f of statusObj.flights) {
      if (f && (f.cancelled === true || f.canceled === true)) return true;
    }
  }
  return false;
}

/**
 * Interpret a FlightAware cache `status` object for UI (TOF / buckets / airborne).
 * Uses shared `getGroundedMinutesAndLocation` so stale root `on` + newer `off` matches API/cron/email.
 */
function interpretFaStatus(statusObj, aircraft = null, nowMs = Date.now()) {
  if (!statusObj || typeof statusObj !== "object") {
    return { phase: null, onIso: null, offIso: null, timeOnGroundMinutes: null, airborne: false };
  }
  const ac =
    aircraft && typeof aircraft === "object"
      ? aircraft
      : {
          last_known_location: null,
          manual_status_last_flight: null,
          manual_status_text: null,
          manual_status_location: null,
          tam_status: null,
          owner: null,
          base: null,
        };

  const phase = cleanText(
    statusObj.phase || statusObj.status_text || statusObj.flight_status || statusObj.status || "",
  );
  const looksCancelled = faStatusLooksCancelled(statusObj);

  const { groundedMinutes } = getGroundedMinutesAndLocation(ac, { status: statusObj }, nowMs);

  const onIso = statusObj.on || statusObj.actual_on || null;
  const offIso = statusObj.off || statusObj.actual_off || null;

  if (groundedMinutes === 0 && looksCancelled) {
    return { phase: phase || null, onIso, offIso, timeOnGroundMinutes: null, airborne: false };
  }

  if (groundedMinutes === 0) {
    const pairEqual =
      onIso &&
      offIso &&
      Date.parse(onIso) === Date.parse(offIso) &&
      Number.isFinite(Date.parse(onIso));
    if (pairEqual && nowMs - Date.parse(onIso) > STALE_AIRBORNE_MAX_MS) {
      const lastFlightTs = Date.parse(String(ac.last_flight_at || ac.manual_status_last_flight || onIso));
      const groundTs = Number.isFinite(lastFlightTs) && lastFlightTs <= nowMs ? lastFlightTs : Date.parse(onIso);
      return {
        phase: phase || null,
        onIso,
        offIso,
        timeOnGroundMinutes: Math.max(0, Math.round((nowMs - groundTs) / 60000)),
        airborne: false,
      };
    }
    return { phase: phase || null, onIso, offIso, timeOnGroundMinutes: null, airborne: true };
  }
  if (groundedMinutes > 0) {
    return {
      phase: phase || null,
      onIso,
      offIso,
      timeOnGroundMinutes: groundedMinutes,
      airborne: false,
    };
  }

  const phaseLower = phase.toLowerCase();
  const airborneFallback =
    !looksCancelled &&
    (phaseLower.includes("enroute") ||
      phaseLower.includes("airborne") ||
      phaseLower.includes("depart") ||
      phaseLower.includes("takeoff"));

  let timeOnGroundMinutes = null;
  if (!airborneFallback) {
    const onTs = onIso ? Date.parse(onIso) : NaN;
    const offTs = offIso ? Date.parse(offIso) : NaN;
    if (Number.isFinite(onTs) && onTs <= nowMs && !(Number.isFinite(offTs) && offTs === onTs)) {
      timeOnGroundMinutes = Math.max(0, Math.round((nowMs - onTs) / 60000));
    }
  }

  return {
    phase: phase || null,
    onIso,
    offIso,
    timeOnGroundMinutes,
    airborne: airborneFallback,
  };
}

function formatStatusBucket(groundedMinutes) {
  if (!Number.isFinite(groundedMinutes) || groundedMinutes < 0) return "UNKNOWN";
  const days = groundedMinutes / (60 * 24);
  if (days < 1) return "IN SERVICE";
  if (days < 2) return "1 DAY";
  if (days < 3) return "2 DAYS";
  if (days < 5) return "3 DAYS";
  if (days < 7) return "5 DAYS";
  if (days < 14) return "1W";
  if (days < 21) return "2W";
  if (days < 28) return "3W";
  if (days < 60) return "1MO";
  if (days < 90) return "2MO";
  if (days < 120) return "3MO";
  if (days < 150) return "4MO";
  if (days < 180) return "5MO";
  if (days < 365) return "6MO";
  return "1YR";
}

/** Match api/status-logic.mjs airport extraction (IATA → ICAO → code). */
function faEndpointCode(ep) {
  if (!ep || typeof ep !== "object") return "";
  return cleanText(ep.code_iata) || cleanText(ep.code_icao) || cleanText(ep.code) || "";
}

function faPositionAirportCode(raw) {
  const p = raw && typeof raw === "object" ? raw.position : null;
  if (!p || typeof p !== "object") return "";
  return cleanText(p.airport_code) || cleanText(p.airport_iata) || cleanText(p.airport_icao) || "";
}

function faLocationLabel({ status, aircraft, isAirborne }) {
  const raw = status && typeof status === "object" ? status : {};
  const cancelled = faStatusLooksCancelled(raw);
  const airborne = Boolean(isAirborne) && !cancelled;
  const origin = faEndpointCode(raw.origin);
  const dest = faEndpointCode(raw.destination);
  const posAirport = faPositionAirportCode(raw);
  const baseLocation = cleanText(aircraft?.last_known_location) || cleanText(aircraft?.base) || "";
  const persistedSource = cleanText(aircraft?.last_location_source).toUpperCase();
  const phaseBlob = aggregateFaStatusPhaseLower(raw);

  if (airborne) {
    // Prefer FA snapshot route first so labels match FlightAware; FR24 string only when FA has no endpoints.
    if (origin && dest) return `${origin} → ${dest}`;
    if (origin || dest) return origin || dest;
    if (posAirport) return posAirport;
    if (persistedSource.startsWith("FR24") && baseLocation) {
      return baseLocation;
    }
    return baseLocation || "—";
  }

  // On ground: ADS-B/parking airport first. Without it, FA often leaves destination = *next* sector (ORD)
  // while origin = current station (MCO) — do not show destination before origin unless arrival-like phase
  // or FlightAware "result unknown" / on===off (then destination is the landing airport).
  // A newer FR24 landing beats the FA snapshot — same rule as the email (getGroundedMinutesAndLocation).
  if (
    persistedSource.startsWith("FR24") &&
    baseLocation &&
    fr24PreferredGroundedMinutes(aircraft, deriveMovementAt({ status: raw, aircraft }), false) != null
  ) {
    return baseLocation;
  }
  if (posAirport) return posAirport;
  const guess = groundedFaEndpointLabel(origin, dest, phaseBlob, raw);
  if (guess) return guess;
  return baseLocation || "—";
}

async function loadFaCache() {
  const idents = Array.from(
    new Set((state.aircraft || []).flatMap((a) => faLookupKeysForAircraft(a)).filter(Boolean))
  );
  await loadFaCacheForIdents(idents, { replace: true });
}

function chunkArray(values, size) {
  const out = [];
  const s = Math.max(1, Number(size) || 50);
  for (let i = 0; i < (values || []).length; i += s) out.push(values.slice(i, i + s));
  return out;
}

function ensureTopbarProgressUi() {
  let wrap = document.getElementById("v2-refresh-progress");
  if (wrap) return;

  const refreshBtn = document.getElementById("v2-refresh-all");
  if (!refreshBtn) return;
  const parent = refreshBtn.parentElement;
  if (!parent) return;

  wrap = el("div", { id: "v2-refresh-progress", class: "hidden w-64" }, [
    el("div", { class: "flex items-center justify-between text-[11px] text-gray-500 mb-1" }, [
      el("div", { id: "v2-refresh-progress-label", text: "Checked 0 / 0" }),
      el("div", { id: "v2-refresh-progress-pct", text: "0%" }),
    ]),
    el("div", { class: "w-full bg-gray-200 rounded-full h-2 overflow-hidden" }, [
      el("div", { id: "v2-refresh-progress-bar", class: "bg-dae h-2 rounded-full", style: "width:0%" }),
    ]),
  ]);

  // Insert right after the refresh button (before logout).
  if (refreshBtn.nextSibling) parent.insertBefore(wrap, refreshBtn.nextSibling);
  else parent.appendChild(wrap);
}

function setTopbarProgress({ visible, done = 0, total = 0 } = {}) {
  ensureTopbarProgressUi();
  const wrap = document.getElementById("v2-refresh-progress");
  const label = document.getElementById("v2-refresh-progress-label");
  const pctEl = document.getElementById("v2-refresh-progress-pct");
  const bar = document.getElementById("v2-refresh-progress-bar");
  if (!wrap || !label || !pctEl || !bar) return;
  if (!visible) {
    wrap.classList.add("hidden");
    return;
  }
  wrap.classList.remove("hidden");
  const safeTotal = Math.max(0, Number(total) || 0);
  const safeDone = Math.max(0, Math.min(safeTotal, Number(done) || 0));
  const pct = safeTotal ? Math.round((safeDone / safeTotal) * 100) : 0;
  label.textContent = `Checked ${safeDone} / ${safeTotal}`;
  pctEl.textContent = `${pct}%`;
  bar.style.width = `${pct}%`;
}

async function persistFaSnapshots(entries) {
  const list = Array.isArray(entries) ? entries.filter(Boolean) : [];
  if (!list.length) return { ok: true, saved: 0, failed: 0 };
  return await fetchJson(apiUrl("/api/flightaware/status/cache/save"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entries: list }),
  });
}

async function fetchLiveFaStatus({ ident, registration }) {
  const id = cleanText(ident);
  if (!id) throw new Error("Missing ident");
  const reg = cleanText(registration);
  const qs = new URLSearchParams();
  qs.set("ident", id);
  if (reg) qs.set("registration", reg);
  return await fetchJson(apiUrl(`/api/flightaware/status?${qs.toString()}`), { method: "GET" });
}

function applyLiveFaToCache({ ident, status, fetchedAtIso, responseError = null } = {}) {
  const key = cleanText(ident).toUpperCase();
  if (!key) return;
  const prevEntries = Array.from((state.faCacheByIdent || new Map()).values());
  const merged = new Map();
  for (const entry of prevEntries) {
    const k = cleanText(entry?.ident).toUpperCase();
    if (k) merged.set(k, entry);
  }
  merged.set(key, {
    ident: key,
    status: status && typeof status === "object" ? status : null,
    fetched_at: fetchedAtIso || new Date().toISOString(),
    response_error: responseError || null,
    source: "ui-v2-refresh",
  });
  state.faCacheByIdent = rebuildFaCacheByIdentFromEntries(Array.from(merged.values()));
}

async function refreshVisibleAircraftLive({ aircraftList, faTargets, onProgress } = {}) {
  let targets;
  if (Array.isArray(faTargets) && faTargets.length) {
    targets = faTargets
      .map((t) => {
        const ident = cleanText(t?.ident).toUpperCase();
        if (!ident) return null;
        const registration = cleanText(t?.registration) || "";
        return { ident, registration };
      })
      .filter(Boolean);
  } else {
    const list = Array.isArray(aircraftList) ? aircraftList : [];
    targets = list
      .map((a) => {
        const ident = inferFaKey(a);
        if (!ident) return null;
        const registration = cleanText(a?.registration) || cleanText(a?.tail) || cleanText(a?.ident) || "";
        return { ident, registration };
      })
      .filter(Boolean);
  }

  const total = targets.length;
  let done = 0;

  for (const batch of chunkArray(targets, 8)) {
    const entries = [];
    const batchIdents = [];
    for (const t of batch) {
      const fetchedAt = new Date().toISOString();
      try {
        const status = await fetchLiveFaStatus(t);
        batchIdents.push(t.ident);
        entries.push({
          ident: t.ident,
          lookup: t.ident,
          status,
          fetchedAt,
          responseStatus: 200,
          responseError: null,
          source: "ui-v2-refresh",
        });
        applyLiveFaToCache({ ident: t.ident, status, fetchedAtIso: fetchedAt, responseError: null });
      } catch (e) {
        const msg = cleanText(e?.message) || String(e);
        batchIdents.push(t.ident);
        entries.push({
          ident: t.ident,
          lookup: t.ident,
          status: null,
          fetchedAt,
          responseStatus: Number(e?.status) || null,
          responseError: msg,
          source: "ui-v2-refresh",
        });
        applyLiveFaToCache({ ident: t.ident, status: null, fetchedAtIso: fetchedAt, responseError: msg });
      } finally {
        done += 1;
        if (typeof onProgress === "function") onProgress({ done, total });
      }
    }
    try {
      await persistFaSnapshots(entries);
      // Re-load the cache for this batch from DB so we pick up the canonical snapshot
      // and any FR24 fallback enrichment that wrote to aircraft rows.
      await loadFaCacheForIdents(batchIdents, { replace: false }).catch(() => {});
      // Also reload aircraft rows so last_known_location / last_location_source updates
      // (e.g. FR24 enrichment) show up immediately in the table.
      await loadAircraft().catch(() => {});
    } catch (e) {
      // Non-fatal: UI still shows the latest; persistence can fail if DB is unavailable.
      console.error("snapshot persistence failed", e);
    }
  }

  return { done, total };
}

async function loadFaCacheForIdents(idents, { replace = false, onProgress = null } = {}) {
  const unique = Array.from(new Set((idents || []).map((v) => cleanText(v).toUpperCase()).filter(Boolean)));
  if (!unique.length) {
    if (replace) state.faCacheByIdent = new Map();
    return { attempted: 0, loaded: 0 };
  }

  const prevEntries = replace ? [] : Array.from((state.faCacheByIdent || new Map()).values());
  const merged = new Map();
  for (const entry of prevEntries) {
    const k = cleanText(entry?.ident).toUpperCase();
    if (k) merged.set(k, entry);
  }

  const batches = chunkArray(unique, 50);
  let attempted = 0;
  let loaded = 0;

  for (const batch of batches) {
    const resp = await fetchJson(apiUrl("/api/flightaware/status/cache"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idents: batch }),
    });
    const statuses = Array.isArray(resp?.statuses) ? resp.statuses : [];
    for (const entry of statuses) {
      const key = cleanText(entry?.ident).toUpperCase();
      if (!key) continue;
      merged.set(key, entry);
      loaded += 1;
    }
    attempted += batch.length;
    if (typeof onProgress === "function") onProgress({ attempted, total: unique.length, loaded });
  }

  state.faCacheByIdent = rebuildFaCacheByIdentFromEntries(Array.from(merged.values()));
  if (Array.isArray(state.aircraft) && state.aircraft.length) {
    state.lastUpdatedAt = computeLastUpdatedAtFromAircraft(state.aircraft);
    updateTopbarLastUpdate();
  }
  return { attempted, loaded };
}

function currentVisibleAircraft() {
  const key = currentRouteKey();
  const params = currentRouteParams();
  const list = state.aircraft || [];

  if (key === "non-service-all") {
    const base = list.filter((a) => effectiveTamForFilters(a) !== "IN SERVICE");
    return applyAircraftFilters(base, params);
  }
  if (key === "aog") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "AOG");
    return applyAircraftFilters(base, params);
  }
  if (key === "transition") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "TRANSITION");
    return applyAircraftFilters(base, params);
  }
  if (key === "maintenance") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "MAINTENANCE");
    return applyAircraftFilters(base, params);
  }
  if (key === "incident") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "INCIDENT");
    return applyAircraftFilters(base, params);
  }
  if (key === "storage") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "STORAGE");
    return applyAircraftFilters(base, params);
  }
  if (key === "russia") {
    const base = list.filter((a) => effectiveTamForFilters(a) === "RUSSIA");
    return applyAircraftFilters(base, params);
  }
  if (key === "watch") {
    const base = list.filter((a) => Boolean(a?.watch_on));
    return applyAircraftFilters(base, params);
  }
  if (key === "awaiting-operator") {
    const base = list.filter((a) => /await/i.test(cleanText(a?.tam_status)) || /await/i.test(cleanText(a?.status_text)));
    return applyAircraftFilters(base, params);
  }
  if (key === "no-flights-24h") {
    const base = list.filter((a) => matchesNoFlightsFiveDaysAircraft(a));
    return applyAircraftFilters(base, params);
  }
  if (key === "ground-over-21d") {
    const base = list.filter((a) => matchesMoreThan21DaysOnGround(a));
    return applyAircraftFilters(base, params);
  }
  if (key === "middle-east") {
    const base = list.filter((a) => Boolean(a?.is_me_fleet));
    return applyAircraftFilters(base, params);
  }
  if (key === "spirit-tracker") {
    return spiritTrackerRefreshAircraftList(list);
  }
  if (key === "all") {
    return applyAircraftFilters(list, params);
  }

  // Dashboard/activity/settings: treat as "all visible"
  return list;
}

async function loadCronStatus() {
  return await fetchJson(apiUrl("/api/cron/status"));
}

async function loadCronLogs() {
  return await fetchJson(apiUrl("/api/cron/logs"));
}

async function runCronNow() {
  return await fetchJson(apiUrl("/api/admin/cron/run"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sendEmail: true }),
  });
}

async function loadCosts({ skipCache = false } = {}) {
  const bust = skipCache ? "&skipCache=1" : "";
  const [all, mtd, today, fr24] = await Promise.all([
    fetchJson(apiUrl(`/api/admin/flightaware/usage?range=all${bust}`)),
    fetchJson(apiUrl(`/api/admin/flightaware/usage?range=mtd${bust}`)),
    fetchJson(apiUrl(`/api/admin/flightaware/usage?range=today${bust}`)),
    fetchJson(apiUrl("/api/admin/fr24/usage")),
  ]);
  return { flightaware: { all, mtd, today }, fr24 };
}

/** Pull calls/cost from AeroAPI /account/usage JSON (shape varies slightly by window). */
function extractFlightAwareCallsCost(resp) {
  if (!resp || resp.ok === false) {
    return { calls: "—", cost: "—", error: cleanText(resp?.error) || null };
  }
  const raw = resp.raw && typeof resp.raw === "object" ? resp.raw : null;
  let calls = null;
  let cost = null;
  if (raw) {
    if (typeof raw.total_calls === "number") calls = raw.total_calls;
    else if (typeof raw.totalSuccessfulCalls === "number") calls = raw.totalSuccessfulCalls;
    if (typeof raw.total_cost === "number") cost = raw.total_cost;
    else if (typeof raw.totalCost === "number") cost = raw.totalCost;
  }
  const sum = resp.summary && typeof resp.summary === "object" ? resp.summary : null;
  if (calls == null && sum?.total?.calls?.value != null && typeof sum.total.calls.value === "number") {
    calls = sum.total.calls.value;
  }
  if (cost == null && sum?.total?.cost?.value != null && typeof sum.total.cost.value === "number") {
    cost = sum.total.cost.value;
  }
  const fmtCost = (v) =>
    v == null ? "—" : typeof v === "number" ? (Number.isFinite(v) ? v.toFixed(2) : "—") : String(v);
  return {
    calls: calls != null ? String(calls) : "—",
    cost: fmtCost(cost),
    cached: Boolean(resp.cached),
    fetchedAt: resp.fetchedAt || null,
  };
}

function formatFr24Cost(fr24, calls) {
  const n = Number(calls);
  const rate = fr24?.costPerCallUsd;
  if (!Number.isFinite(n) || n < 0 || rate == null || !Number.isFinite(Number(rate))) return "—";
  return (n * Number(rate)).toFixed(2);
}

function renderApiCostsInto(hostEl, data) {
  if (!hostEl) return;
  const faToday = extractFlightAwareCallsCost(data.flightaware?.today);
  const faMtd = extractFlightAwareCallsCost(data.flightaware?.mtd);
  const faAll = extractFlightAwareCallsCost(data.flightaware?.all);
  const fr24 = data.fr24 || {};
  const periods = fr24.periods && typeof fr24.periods === "object" ? fr24.periods : {};

  const rows = [
    ["FlightAware · Today (UTC)", faToday.calls, faToday.cost],
    ["FlightAware · Month-to-date (UTC)", faMtd.calls, faMtd.cost],
    ["FlightAware · All-time", faAll.calls, faAll.cost],
    ["FR24 · Last 24 hours (this API process)", periods.day?.calls ?? "—", formatFr24Cost(fr24, periods.day?.calls)],
    ["FR24 · Last 7 days (rolling)", periods.week?.calls ?? "—", formatFr24Cost(fr24, periods.week?.calls)],
    ["FR24 · Last 30 days (rolling)", periods.month?.calls ?? "—", formatFr24Cost(fr24, periods.month?.calls)],
    [
      "FR24 · Since API process started",
      fr24.totalCallsSinceStart ?? fr24.totalCalls ?? "—",
      formatFr24Cost(fr24, fr24.totalCallsSinceStart ?? fr24.totalCalls),
    ],
  ];

  const faNote =
    faToday.cached || faMtd.cached || faAll.cached
      ? "FlightAware numbers may be served from a short server cache unless you click Refresh (skip cache)."
      : null;

  const table = el("table", { class: "mt-2 w-full text-sm text-left text-gray-600" }, [
    el("thead", { class: "text-xs text-gray-500 uppercase" }, [
      el("tr", {}, [
        el("th", { class: "py-2 pr-3", text: "Metric" }),
        el("th", { class: "py-2 pr-3", text: "Calls" }),
        el("th", { class: "py-2", text: "Cost (USD)" }),
      ]),
    ]),
    el(
      "tbody",
      {},
      rows.map((r) =>
        el("tr", { class: "border-t" }, [
          el("td", { class: "py-2 pr-3 text-gray-700 font-medium", text: r[0] }),
          el("td", { class: "py-2 pr-3 font-mono text-gray-900", text: String(r[1]) }),
          el("td", { class: "py-2 font-mono text-gray-900", text: String(r[2]) }),
        ])
      )
    ),
  ]);

  const bits = [
    table,
    el("p", { class: "mt-3 text-xs text-gray-500" }, [
      "FR24 call counts are tracked inside this API server process only (not FR24’s billing portal). ",
      "Set ",
      el("span", { class: "font-mono", text: "FR24_COST_PER_CALL_USD" }),
      " on the server to estimate spend. ",
      cleanText(fr24.note || ""),
    ]),
  ];
  if (fr24.costPerCallUsd != null && Number.isFinite(Number(fr24.costPerCallUsd))) {
    bits.push(
      el("p", { class: "mt-1 text-xs text-gray-500" }, [
        `Estimated rate: $${Number(fr24.costPerCallUsd).toFixed(4)} per FR24 call.`,
      ])
    );
  }
  if (faNote) bits.push(el("p", { class: "mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" }, [faNote]));

  hostEl.replaceChildren(el("div", { class: "space-y-1" }, bits));
}

async function refreshApiCostsInto(hostEl) {
  if (!hostEl) return;
  hostEl.replaceChildren(el("div", { class: "text-sm text-gray-600", text: "Loading…" }));
  try {
    const data = await loadCosts({ skipCache: true });
    state.apiCostsPrefetched = data;
    renderApiCostsInto(hostEl, data);
  } catch (e) {
    hostEl.replaceChildren(
      el("div", { class: "text-sm text-red-700" }, [`Error: ${cleanText(e?.message) || String(e)}`])
    );
  }
}

const COST_ANALYSIS_WINDOWS = [
  { key: "mtd", label: "Month to date" },
  { key: "d30", label: "30 days" },
  { key: "d15", label: "15 days" },
  { key: "d5", label: "5 days" },
  { key: "today", label: "Today" },
];

const COST_ANALYSIS_MODULES = [
  { key: "chat", label: "Chat" },
  { key: "workscopes", label: "Workscopes" },
  { key: "lease_return", label: "Lease return" },
  { key: "invoice_audit", label: "Invoice audit" },
  { key: "others", label: "Others" },
];

function toIsoNoMs(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

function costAnalysisWindowBounds(now = new Date()) {
  const end = now instanceof Date ? now : new Date(now);
  const dayMs = 24 * 60 * 60 * 1000;
  const utcMidnight = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate(), 0, 0, 0));
  const mtdStart = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1, 0, 0, 0));
  return {
    mtd: { start: toIsoNoMs(mtdStart), end: toIsoNoMs(end) },
    d30: { start: toIsoNoMs(new Date(end.getTime() - 30 * dayMs)), end: toIsoNoMs(end) },
    d15: { start: toIsoNoMs(new Date(end.getTime() - 15 * dayMs)), end: toIsoNoMs(end) },
    d5: { start: toIsoNoMs(new Date(end.getTime() - 5 * dayMs)), end: toIsoNoMs(end) },
    today: { start: toIsoNoMs(utcMidnight), end: toIsoNoMs(end) },
  };
}

function formatCostUsdCell(value, { digits = 2 } = {}) {
  if (value == null || value === "" || value === "—") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(digits)}`;
}

function extractFlightAwareCostNum(resp) {
  const parsed = extractFlightAwareCallsCost(resp);
  if (!resp || resp.ok === false) return { calls: null, cost: null, error: parsed.error || null };
  const raw = resp.raw && typeof resp.raw === "object" ? resp.raw : null;
  let calls = null;
  let cost = null;
  if (raw) {
    if (typeof raw.total_calls === "number") calls = raw.total_calls;
    else if (typeof raw.totalSuccessfulCalls === "number") calls = raw.totalSuccessfulCalls;
    if (typeof raw.total_cost === "number") cost = raw.total_cost;
    else if (typeof raw.totalCost === "number") cost = raw.totalCost;
  }
  const sum = resp.summary && typeof resp.summary === "object" ? resp.summary : null;
  if (calls == null && sum?.total?.calls?.value != null && typeof sum.total.calls.value === "number") {
    calls = sum.total.calls.value;
  }
  if (cost == null && sum?.total?.cost?.value != null && typeof sum.total.cost.value === "number") {
    cost = sum.total.cost.value;
  }
  return { calls, cost, cached: Boolean(resp.cached), fetchedAt: resp.fetchedAt || null };
}

async function loadCostAnalysis({ skipCache = false } = {}) {
  const bust = skipCache ? "&skipCache=1" : "";
  const bounds = costAnalysisWindowBounds();
  const faFetches = COST_ANALYSIS_WINDOWS.map(({ key }) => {
    const b = bounds[key];
    const qs = `start=${encodeURIComponent(b.start)}&end=${encodeURIComponent(b.end)}${bust}`;
    return fetchJson(apiUrl(`/api/admin/flightaware/usage?${qs}`))
      .then((resp) => [key, extractFlightAwareCostNum(resp)])
      .catch((e) => [key, { calls: null, cost: null, error: cleanText(e?.message) || String(e) }]);
  });

  const [faPairs, fr24, ai] = await Promise.all([
    Promise.all(faFetches),
    fetchJson(apiUrl("/api/admin/fr24/usage")).catch((e) => ({
      ok: false,
      error: cleanText(e?.message) || String(e),
    })),
    fetchJson(apiUrl("/api/ai/usage/costs")).catch((e) => ({
      ok: false,
      error: cleanText(e?.message) || String(e),
    })),
  ]);

  const flightaware = Object.fromEntries(faPairs);
  const periods = fr24?.periods && typeof fr24.periods === "object" ? fr24.periods : {};
  const flightradar = {};
  for (const { key } of COST_ANALYSIS_WINDOWS) {
    const p = periods[key] || null;
    const calls = p && Number.isFinite(Number(p.calls)) ? Number(p.calls) : null;
    let cost = p && p.costUsd != null && Number.isFinite(Number(p.costUsd)) ? Number(p.costUsd) : null;
    if (cost == null && calls != null && fr24?.costPerCallUsd != null && Number.isFinite(Number(fr24.costPerCallUsd))) {
      cost = Math.round(calls * Number(fr24.costPerCallUsd) * 100) / 100;
    }
    flightradar[key] = { calls, cost };
  }

  return {
    asOf: new Date().toISOString(),
    flightaware,
    flightradar,
    fr24Meta: {
      ok: fr24?.ok !== false,
      error: fr24?.error || null,
      note: fr24?.note || null,
      costPerCallUsd: fr24?.costPerCallUsd ?? null,
    },
    ai,
  };
}

function costAnalysisTable(title, rows, { costDigits = 2, footnote = null, firstCol = "Metric" } = {}) {
  const head = el("tr", { class: "text-left text-xs uppercase text-gray-500 border-b" }, [
    el("th", { class: "py-2 pr-3 font-medium", text: firstCol }),
    ...COST_ANALYSIS_WINDOWS.map((w) => el("th", { class: "py-2 pr-3 font-medium whitespace-nowrap", text: w.label })),
  ]);
  const body = rows.map((row) =>
    el("tr", { class: `border-b border-gray-100 ${row.emphasis ? "bg-gray-50" : ""}` }, [
      el("td", {
        class: `py-2 pr-3 ${row.emphasis ? "font-semibold text-gray-900" : "text-gray-700"}`,
        text: row.label,
      }),
      ...COST_ANALYSIS_WINDOWS.map((w) =>
        el("td", {
          class: `py-2 pr-3 font-mono tabular-nums ${row.emphasis ? "font-semibold text-gray-900" : "text-gray-900"}`,
          text: formatCostUsdCell(row.values?.[w.key], { digits: costDigits }),
        })
      ),
    ])
  );
  const bits = [
    el("div", { class: "overflow-x-auto" }, [
      el("table", { class: "min-w-full text-sm" }, [el("thead", {}, [head]), el("tbody", {}, body)]),
    ]),
  ];
  if (footnote) {
    bits.push(el("p", { class: "mt-2 text-xs text-gray-500" }, [footnote]));
  }
  return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-2" }, [
    el("h3", { class: "text-base font-semibold text-gray-900", text: title }),
    ...bits,
  ]);
}

function windowValuesFromMap(map, field) {
  const out = {};
  for (const { key } of COST_ANALYSIS_WINDOWS) {
    out[key] = map?.[key]?.[field] ?? null;
  }
  return out;
}

function renderCostAnalysisInto(hostEl, data) {
  if (!hostEl) return;
  const faCost = windowValuesFromMap(data.flightaware, "cost");
  const frCost = windowValuesFromMap(data.flightradar, "cost");
  const aiOk = data.ai && data.ai.ok !== false;
  const ai1 = data.ai?.ai1 || {};
  const ai2 = data.ai?.ai2 || {};

  const aiModuleRows = (lane) => [
    { label: "Total", values: lane.total || {}, emphasis: true },
    ...COST_ANALYSIS_MODULES.map((m) => ({
      label: m.label,
      values: lane.byModule?.[m.key] || {},
    })),
  ];

  const panels = [
    costAnalysisTable("FlightAware costs", [{ label: "Cost (USD)", values: faCost, emphasis: true }], {
      footnote: "From AeroAPI account usage (UTC windows).",
    }),
    costAnalysisTable("FlightRadar costs", [{ label: "Cost (USD)", values: frCost, emphasis: true }], {
      footnote:
        data.fr24Meta?.note ||
        "Estimated from this API process’s call counts × FR24_COST_PER_CALL_USD (UTC windows; resets on API restart).",
    }),
    costAnalysisTable("AI 1 costs", aiOk ? aiModuleRows(ai1) : [{ label: "Total", values: {}, emphasis: true }], {
      costDigits: 4,
      firstCol: "Module",
      footnote: aiOk
        ? "Metered from application call logs. Invoice audit = extract/match AI. Document OCR may appear under Others."
        : `Unable to load AI costs: ${cleanText(data.ai?.error) || "unknown error"}`,
    }),
    costAnalysisTable("AI 2 costs", aiOk ? aiModuleRows(ai2) : [{ label: "Total", values: {}, emphasis: true }], {
      costDigits: 4,
      firstCol: "Module",
      footnote: aiOk
        ? cleanText(data.ai?.note) || "Metered from application call logs when secondary-model calls are recorded."
        : `Unable to load AI costs: ${cleanText(data.ai?.error) || "unknown error"}`,
    }),
  ];

  const asOfLabel = (() => {
    try {
      return typeof formatDisplayTimestamp === "function"
        ? formatDisplayTimestamp(data.asOf)
        : String(data.asOf || "").replace("T", " ").slice(0, 19);
    } catch {
      return String(data.asOf || "").replace("T", " ").slice(0, 19);
    }
  })();

  hostEl.replaceChildren(
    el("div", { class: "space-y-4" }, [
      el("p", {
        class: "text-xs text-gray-500",
        text: `As of ${asOfLabel} (UTC windows).`,
      }),
      ...panels,
    ])
  );
}

async function refreshCostAnalysisInto(hostEl) {
  if (!hostEl) return;
  hostEl.replaceChildren(el("div", { class: "text-sm text-gray-600", text: "Loading…" }));
  try {
    const data = await loadCostAnalysis({ skipCache: true });
    state.costAnalysisPrefetched = data;
    renderCostAnalysisInto(hostEl, data);
  } catch (e) {
    hostEl.replaceChildren(
      el("div", { class: "text-sm text-red-700" }, [`Error: ${cleanText(e?.message) || String(e)}`])
    );
  }
}

async function fetchMaterialsDashboardStats() {
  const r = await fetchJson(apiUrl("/api/materials/dashboard-stats")).catch(() => null);
  return r && r.ok ? r : null;
}

async function fetchMaterialsProjects() {
  const r = await fetchJson(apiUrl("/api/materials/projects")).catch(() => null);
  return r && Array.isArray(r.projects) ? r.projects : [];
}

async function fetchMaterialsParts(qsObj) {
  const qs = new URLSearchParams(qsObj || {});
  const r = await fetchJson(apiUrl(`/api/materials/parts?${qs}`)).catch(() => null);
  return r && Array.isArray(r.parts) ? r.parts : [];
}

async function ipcSearchMaterialsPn(q) {
  const qq = cleanText(q);
  if (qq.length < 2) return [];
  const r = await fetchJson(apiUrl(`/api/materials/ipc-search?q=${encodeURIComponent(qq)}`)).catch(() => null);
  return r && Array.isArray(r.hits) ? r.hits : [];
}

async function patchMaterialPart(id, body) {
  return fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(id)}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function postMaterialPart(body) {
  return fetchJson(apiUrl("/api/materials/parts"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function fetchMaterialComments(partId) {
  const r = await fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(partId)}/comments`)).catch(() => null);
  return r && Array.isArray(r.comments) ? r.comments : [];
}

async function postMaterialComment(partId, body) {
  return fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(partId)}/comments`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ body }),
  });
}

async function postMaterialProject(body) {
  return fetchJson(apiUrl("/api/materials/projects"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function patchMaterialProject(id, body) {
  return fetchJson(apiUrl(`/api/materials/projects/${encodeURIComponent(id)}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function postMaterialProjectAircraft(projectId, payload) {
  return fetchJson(apiUrl(`/api/materials/projects/${encodeURIComponent(projectId)}/aircraft`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });
}

async function deleteMaterialProjectAircraft(projectId, acid) {
  return fetchJson(apiUrl(`/api/materials/projects/${encodeURIComponent(projectId)}/aircraft/${encodeURIComponent(acid)}`), {
    method: "DELETE",
  });
}

async function fetchMaterialsMsnOptions() {
  const r = await fetchJson(apiUrl("/api/materials/msn-options")).catch(() => null);
  return r && Array.isArray(r.msns) ? r.msns : [];
}

async function fetchMaterialPart(id) {
  const r = await fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(id)}`)).catch(() => null);
  return r && r.part ? r.part : null;
}

async function fetchMaterialsMeta() {
  const r = await fetchJson(apiUrl("/api/materials/meta")).catch(() => null);
  return r && r.ok ? r : null;
}

async function fetchMaterialsNotifications() {
  const r = await fetchJson(apiUrl("/api/materials/notifications")).catch(() => null);
  return r && r.ok ? r : null;
}

async function putMaterialsNotifications(body) {
  return fetchJson(apiUrl("/api/materials/notifications"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function fetchMaterialsCores(projectId) {
  const qs = projectId ? `?projectId=${encodeURIComponent(String(projectId))}` : "";
  const r = await fetchJson(apiUrl(`/api/materials/cores${qs}`)).catch(() => null);
  return r && Array.isArray(r.cores) ? r.cores : [];
}

async function postMaterialCore(body) {
  return fetchJson(apiUrl("/api/materials/cores"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function patchMaterialCore(id, body) {
  return fetchJson(apiUrl(`/api/materials/cores/${encodeURIComponent(id)}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
}

async function fetchMaterialPartAttachments(partId) {
  const r = await fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(partId)}/attachments`)).catch(() => null);
  return r && Array.isArray(r.attachments) ? r.attachments : [];
}

async function postMaterialPartAttachment(partId, { fileName, mime, base64 }) {
  return fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(partId)}/attachments`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      fileName: fileName || "file.bin",
      mime: mime || "application/octet-stream",
      base64,
    }),
  });
}

/** Matches API order: ON ORDER → … → CANX */
const MATERIAL_PART_STATUS_OPTS = [
  ["ON_ORDER", "ON ORDER"],
  ["SHIPPED", "SHIPPED"],
  ["IN_TRANSIT", "IN TRANSIT"],
  ["IN_CUSTOMS", "IN CUSTOMS"],
  ["INCOMING_INSPECTION", "INCOMING INSPECTION"],
  ["RECEIVED", "RECEIVED"],
  ["REJECTED", "REJECTED"],
  ["CANX", "CANX"],
];

const MATERIAL_PART_CLASS_OPTS = [
  ["CONSUMABLE", "Consumable"],
  ["ROTABLE", "Rotable"],
];

function materialPartClassLabel(code) {
  const c = cleanText(code).toUpperCase();
  const hit = MATERIAL_PART_CLASS_OPTS.find(([v]) => v === c);
  return hit ? hit[1] : c || "—";
}

function isoToDateInputValue(iso) {
  if (iso == null || iso === "") return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local calendar date for `<input type="date">` (browser timezone). */
function localDateInputToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Whole calendar days from request date (UTC date) to today (UTC). */
function materialsPartAgingDays(part) {
  const raw = part?.dateRequested ?? part?.date_requested;
  if (!raw) return null;
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return null;
  const start = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.floor((today - start) / 86400000));
}

/** ETA date strictly before today (UTC dates) → overdue. */
function materialsEtaDateOverdue(isoOrDate) {
  if (!isoOrDate) return false;
  const d = new Date(isoOrDate);
  if (!Number.isFinite(d.getTime())) return false;
  const eta = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return eta < today;
}

function materialStatusBadgeEl(code) {
  const c = cleanText(code).toUpperCase();
  const label = materialStatusLabel(code);
  const base = "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap leading-tight";
  const map = {
    ON_ORDER: "bg-slate-100 text-slate-800 border-slate-200",
    SHIPPED: "bg-cyan-50 text-cyan-900 border-cyan-200",
    IN_TRANSIT: "bg-blue-50 text-blue-800 border-blue-200",
    IN_CUSTOMS: "bg-amber-50 text-amber-900 border-amber-200",
    INCOMING_INSPECTION: "bg-violet-50 text-violet-800 border-violet-200",
    RECEIVED: "bg-green-50 text-green-800 border-green-200",
    REJECTED: "bg-red-50 text-red-800 border-red-200",
    CANX: "bg-gray-100 text-gray-600 border-gray-300",
  };
  const cls = map[c] || "bg-gray-50 text-gray-700 border-gray-200";
  return el("span", { class: `${base} ${cls}`, text: label });
}

function materialsPartsHash(qs) {
  const p = new URLSearchParams(qs || {});
  const s = p.toString();
  return `#/materials${s ? `?${s}` : ""}`;
}

/** Hash navigation for `#/materials/cores` with preserved query keys (project filter, edit). */
function setMaterialsCoresRouteQuery(nextParams) {
  const qp = nextParams instanceof URLSearchParams ? nextParams : new URLSearchParams(nextParams || undefined);
  const qs = qp.toString();
  window.location.hash = `#/materials/cores${qs ? `?${qs}` : ""}`;
}

/** MSNs tied to an active project's aircraft rows; `null` = use fleet-wide list */
function materialsMsnsForActiveProject(allProjects, projectId) {
  const pid = Number(projectId);
  if (!Number.isFinite(pid)) return null;
  const p = (allProjects || []).find((x) => Number(x.id) === pid);
  if (!p || !Array.isArray(p.aircraft) || !p.aircraft.length) return null;
  const ms = p.aircraft.map((a) => cleanText(a.msn)).filter(Boolean);
  return ms.length ? Array.from(new Set(ms)) : null;
}

function materialsDashForOff(m) {
  return m ? cleanText(m) : "—";
}

function materialsMatrixRowToStatusFilter(rowKey) {
  if (rowKey === "on_order") return "ON_ORDER";
  if (rowKey === "shipped") return "SHIPPED";
  if (rowKey === "in_transit") return "IN_TRANSIT";
  if (rowKey === "in_customs") return "IN_CUSTOMS";
  if (rowKey === "incoming_insp") return "INCOMING_INSPECTION";
  return "";
}

function renderMaterialsDashboardPage() {
  return el("div", { class: "space-y-4 w-full max-w-[min(100%,1280px)] mx-auto px-2 sm:px-3 box-border min-w-0" }, [
    el("div", { class: "flex flex-wrap items-end justify-between gap-4" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900 tracking-tight", text: "Materials tracker" }),
        el("p", { class: "mt-1 text-sm text-gray-500", text: "Active projects only. Click a cell to open the parts list with MSN (and status when applicable)." }),
      ]),
      el("div", { class: "flex flex-wrap gap-4 items-center text-sm font-medium" }, [
        el("a", { href: "#/materials", class: "text-dae hover:underline", text: "Parts table →" }),
        el("a", { href: "#/materials/cores", class: "text-dae hover:underline", text: "Cores (OFF)" }),
      ]),
    ]),
    el("div", { id: "v2-materials-dash", class: "text-sm text-gray-600" }, ["Loading…"]),
  ]);
}

function formatMatCostDisplay(p) {
  if (p?.cost == null || p?.cost === "") return "—";
  const n = Number(p.cost);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function materialStatusLabel(code) {
  const c = cleanText(code).toUpperCase();
  const hit = MATERIAL_PART_STATUS_OPTS.find(([v]) => v === c);
  if (hit) return hit[1];
  return c.replace(/_/g, " ");
}

function renderMaterialsPartsPage() {
  return el("div", { class: "space-y-4 w-full max-w-[min(100%,1280px)] mx-auto px-2 sm:px-3 box-border min-w-0" }, [
    el("div", { class: "flex flex-wrap items-end justify-between gap-4" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900 tracking-tight", text: "Materials — parts" }),
        el("p", { class: "mt-1 text-sm text-gray-500", text: "Open a row to edit all fields. Add a line from the full part screen. Export matches current filters." }),
      ]),
      el("div", { class: "flex flex-wrap gap-2 sm:gap-3 items-center" }, [
        el("button", {
          id: "v2-mtl-export-xls",
          type: "button",
          class:
            "inline-flex items-center px-4 py-2 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50",
          text: "Export Excel",
        }),
        el("a", { href: "#/materials/part/new", class: "inline-flex px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90", text: "Add part" }),
        el("a", { href: "#/materials/cores", class: "text-gray-700 hover:text-dae hover:underline text-sm font-medium border border-gray-200 rounded-lg px-3 py-1.5", text: "Cores" }),
        el("a", { href: "#/materials-dashboard", class: "text-dae hover:underline text-sm font-medium", text: "← Dashboard" }),
      ]),
    ]),
    el("div", { id: "v2-materials-filters", class: "flex flex-wrap gap-3 items-center" }, []),
    el("div", { id: "v2-materials-parts" }, [el("div", { class: "text-sm text-gray-600", text: "Loading…" })]),
  ]);
}

async function initMaterialsDashboard() {
  const host = document.getElementById("v2-materials-dash");
  if (!host) return;
  host.replaceChildren(el("div", { class: "text-sm text-gray-600", text: "Loading…" }));
  const thSticky =
    "px-2 sm:px-3 py-2 text-[10px] font-semibold text-white bg-dae border-b border-dae/30 whitespace-nowrap text-left sticky left-0 z-20 min-w-[10rem] shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]";
  const thMsnCls =
    "px-2 sm:px-3 py-2 text-[10px] font-semibold text-white bg-dae border-b border-dae/30 whitespace-nowrap text-right font-mono";
  const tdSticky = "px-2 sm:px-3 py-2 border-b border-gray-100 align-top text-xs text-gray-800 font-medium sticky left-0 z-10 bg-white shadow-[2px_0_6px_-2px_rgba(0,0,0,0.06)]";
  const tdCls = "px-2 sm:px-3 py-2 border-b border-gray-100 align-top text-xs";
  try {
    const data = await fetchMaterialsDashboardStats();
    if (!data || !data.ok) {
      host.replaceChildren(
        el("div", { class: "text-sm text-red-700", text: "Unable to load materials dashboard (sign in or check API connectivity)." })
      );
      return;
    }
    const { msns, matrix, projectAging } = data;
    if (!msns.length) {
      host.replaceChildren(
        el("div", { class: "text-sm text-gray-600" }, [
          "No MSN columns yet. Add aircraft to an active project under ",
          el("a", { href: "#/materials/projects", class: "text-dae hover:underline", text: "Materials → Projects" }),
          ".",
        ])
      );
      return;
    }
    const thMsns = msns.map((m) => el("th", { class: `${thMsnCls} min-w-[3.5rem]`, text: m }));
    const head = el("tr", {}, [
      el("th", { class: thSticky, text: "MEASURE" }),
      ...thMsns,
    ]);
    const bodyRows = (matrix || []).map((row) => {
      const statusQ = materialsMatrixRowToStatusFilter(row.key);
      const isMoney = Boolean(row.money);
      const rowLabel = cleanText(row.label || "").toUpperCase();
      return el("tr", { class: "hover:bg-gray-50 bg-white" }, [
        el("td", { class: `${tdSticky} max-w-[14rem]` }, [rowLabel]),
        ...msns.map((m) => {
          const raw = row.countsByMsn && row.countsByMsn[m] != null ? row.countsByMsn[m] : 0;
          let display;
          if (isMoney) {
            const n = Number.isFinite(Number(raw)) ? Number(raw) : 0;
            display = `$${n.toFixed(2)}`;
          } else {
            display = String(raw);
          }
          const qs = { msn: m };
          if (statusQ) qs.status = statusQ;
          const href = materialsPartsHash(qs);
          const isZero = !raw || raw === 0 || (isMoney && Number(raw) === 0);
          return el("td", { class: `${tdCls} text-right font-mono` }, [
            isZero
              ? el("span", { class: "text-gray-300", text: display })
              : el("a", { href, class: "text-dae hover:underline font-mono", text: display }),
          ]);
        }),
      ]);
    });
    const table = el("table", { class: "w-full text-sm text-left text-gray-700 border-collapse min-w-max" }, [
      el("thead", {}, [head]),
      el("tbody", {}, bodyRows),
    ]);

    const agingRows = Array.isArray(projectAging) ? projectAging : [];
    const agingHead = el("tr", { class: "text-xs text-white uppercase bg-dae" }, [
      el("th", { class: "px-3 py-2 text-left", text: "Project" }),
      el("th", { class: "px-3 py-2 text-right", text: "Open lines" }),
      el("th", { class: "px-3 py-2 text-right", text: "Max aging (d)" }),
      el("th", { class: "px-3 py-2 text-right", text: "Avg aging (d)" }),
    ]);
    const agingBody = agingRows.length
      ? agingRows.map((row) =>
          el("tr", { class: "border-b border-gray-100 text-xs bg-white" }, [
            el("td", { class: "px-3 py-2 font-medium text-gray-900" }, [cleanText(row.projectName) || "—"]),
            el("td", { class: "px-3 py-2 text-right font-mono tabular-nums" }, [String(row.openPartCount ?? 0)]),
            el("td", { class: "px-3 py-2 text-right font-mono tabular-nums text-amber-800" }, [String(row.maxAgingDays ?? "—")]),
            el("td", { class: "px-3 py-2 text-right font-mono tabular-nums text-gray-700" }, [String(row.avgAgingDays ?? "—")]),
          ])
        )
      : [
          el("tr", {}, [
            el("td", { class: "px-3 py-4 text-sm text-gray-500", colspan: "4", text: "No open part lines in active projects." }),
          ]),
        ];
    const agingTable = el("table", { class: "w-full text-sm border-collapse" }, [el("thead", {}, [agingHead]), el("tbody", {}, agingBody)]);

    host.replaceChildren(
      el("div", { class: "space-y-6" }, [
        el("div", {
          class:
            "overflow-x-auto w-full max-w-full border border-gray-200 rounded-xl bg-white shadow-sm box-border",
        }, [table]),
        el("div", { class: "border border-gray-200 rounded-xl bg-white shadow-sm p-4" }, [
          el("h2", { class: "text-sm font-semibold text-gray-900 mb-1", text: "Aging by project" }),
          el("p", { class: "text-xs text-gray-500 mb-3", text: "Open pipeline only (excludes RECEIVED, REJECTED, CANX). Max / average days since request date (UTC calendar days)." }),
          el("div", { class: "overflow-x-auto" }, [agingTable]),
        ]),
      ])
    );
  } catch (e) {
    host.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
  }
}

async function initMaterialsPartsPage() {
  const filters = document.getElementById("v2-materials-filters");
  const host = document.getElementById("v2-materials-parts");
  if (!filters || !host) return;

  const params = currentRouteParams();
  const initial = {
    msn: cleanText(params.get("msn")),
    status: cleanText(params.get("status")),
    projectId: cleanText(params.get("projectId")),
  };

  const projects = await fetchMaterialsProjects();
  const activeProjects = projects.filter((p) => p.isActive !== false);

  const applyFilters = () => {
    const qs = new URLSearchParams();
    const msn = cleanText(document.getElementById("v2-mtl-f-msn")?.value);
    const st = cleanText(document.getElementById("v2-mtl-f-status")?.value);
    const pid = cleanText(document.getElementById("v2-mtl-f-project")?.value);
    if (msn) qs.set("msn", msn);
    if (st) qs.set("status", st);
    if (pid) qs.set("projectId", pid);
    window.location.hash = materialsPartsHash(qs);
  };

  filters.replaceChildren(
    el("label", { class: "text-xs text-gray-600 flex flex-col gap-1" }, [
      "Project",
      el(
        "select",
        {
          id: "v2-mtl-f-project",
          class: "rounded-lg border border-gray-200 text-sm px-2 py-1.5 min-w-[10rem]",
          onchange: applyFilters,
        },
        [
          el("option", { value: "", text: "All active" }),
          ...activeProjects.map((p) => el("option", { value: String(p.id), text: cleanText(p.name) || `Project ${p.id}` })),
        ]
      ),
    ]),
    el("label", { class: "text-xs text-gray-600 flex flex-col gap-1" }, [
      "MSN",
      el("input", {
        id: "v2-mtl-f-msn",
        class: "rounded-lg border border-gray-200 text-sm px-2 py-1.5 w-28 font-mono",
        value: initial.msn,
        onchange: applyFilters,
      }),
    ]),
    el("label", { class: "text-xs text-gray-600 flex flex-col gap-1" }, [
      "Status",
      el(
        "select",
        {
          id: "v2-mtl-f-status",
          class: "rounded-lg border border-gray-200 text-sm px-2 py-1.5 min-w-[10rem]",
          onchange: applyFilters,
        },
        [el("option", { value: "", text: "Any" }), ...MATERIAL_PART_STATUS_OPTS.map(([v, lab]) => el("option", { value: v, text: lab }))]
      ),
    ]),
    el("button", {
      type: "button",
      class: "mt-5 px-3 py-1.5 text-sm bg-gray-100 rounded-lg border border-gray-200 hover:bg-gray-50",
      text: "Clear",
      onclick: () => {
        window.location.hash = "#/materials";
      },
    })
  );

  queueMicrotask(() => {
    const fp = document.getElementById("v2-mtl-f-project");
    const fs = document.getElementById("v2-mtl-f-status");
    if (fp && initial.projectId) fp.value = initial.projectId;
    if (fs && initial.status) fs.value = initial.status;
  });

  const qs = {};
  if (initial.msn) qs.msn = initial.msn;
  if (initial.status) qs.status = initial.status;
  if (initial.projectId) qs.projectId = initial.projectId;

  const metaParts = await fetchMaterialsMeta();
  const qtyUomList =
    Array.isArray(metaParts?.uoms) && metaParts.uoms.length ? metaParts.uoms : ["EA", "KG", "LB", "L", "GAL", "PCS"];

  const parts = await fetchMaterialsParts(qs);

  const reloadTable = () => initMaterialsPartsPage().catch(() => {});

  const thCls =
    "px-2 sm:px-3 py-2 text-[10px] font-semibold text-white bg-dae border-b border-dae/30 whitespace-nowrap uppercase tracking-wide";
  const tdCls = "px-2 sm:px-3 py-2 border-b border-gray-100 align-top text-xs";
  const inpCls = "w-full min-w-0 rounded border border-gray-200 px-1 py-1 text-[11px] font-mono max-w-full box-border";
  const inpTxt = "w-full min-w-0 rounded border border-gray-200 px-1 py-1 text-[11px] max-w-full box-border";

  const mtlCellViewCls =
    "block truncate max-w-full cursor-pointer rounded px-1 py-0.5 -mx-1 hover:bg-slate-50 text-gray-800 min-h-[1.35rem] text-xs leading-snug";

  const mtlHint = (s) => [cleanText(s), "Double-click to edit"].filter(Boolean).join(" · ");

  function textEditTd(patch, initial, inputClass, mono = false, titleExtra = null) {
    const td = el("td", { class: tdCls });
    const shown = initial === "" || initial == null ? "—" : String(initial);
    const disp = el("span", {
      class: `${mtlCellViewCls}${mono ? " font-mono" : ""}`,
      text: shown,
      title: mtlHint(titleExtra != null && titleExtra !== "" ? titleExtra : shown !== "—" ? shown : ""),
    });
    td.appendChild(disp);
    td.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (td.querySelector("[data-mtl-ie]")) return;
      const inp = el("input", {
        type: "text",
        "data-mtl-ie": "1",
        class: `${inputClass}${mono ? " font-mono" : ""}`,
        value: cleanText(initial),
      });
      let esc = false;
      td.replaceChildren(inp);
      queueMicrotask(() => {
        inp.focus();
        inp.select();
      });
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          esc = true;
          td.replaceChildren(disp);
        } else if (e.key === "Enter") {
          e.preventDefault();
          inp.blur();
        }
      });
      inp.addEventListener("blur", () => {
        if (esc) {
          esc = false;
          return;
        }
        void (async () => {
          try {
            await patch(cleanText(inp.value));
            reloadTable();
          } catch (err) {
            window.alert(cleanText(err?.message) || String(err));
            td.replaceChildren(disp);
          }
        })();
      });
    });
    return td;
  }

  function dateEditTd(patch, initialIso, extraTdClass = "") {
    const td = el("td", { class: `${tdCls} ${extraTdClass}`.trim() });
    const shown = formatMaterialsDateDdMmmYyyy(initialIso);
    const disp = el("span", { class: mtlCellViewCls, text: shown, title: mtlHint(initialIso) });
    td.appendChild(disp);
    td.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (td.querySelector("[data-mtl-ie]")) return;
      const inp = el("input", { type: "date", "data-mtl-ie": "1", class: inpCls, value: isoToDateInputValue(initialIso) });
      let esc = false;
      td.replaceChildren(inp);
      queueMicrotask(() => inp.focus());
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          esc = true;
          td.replaceChildren(disp);
        } else if (e.key === "Enter") {
          e.preventDefault();
          inp.blur();
        }
      });
      inp.addEventListener("blur", () => {
        if (esc) {
          esc = false;
          return;
        }
        void (async () => {
          try {
            await patch(inp.value || null);
            reloadTable();
          } catch (err) {
            window.alert(cleanText(err?.message) || String(err));
            td.replaceChildren(disp);
          }
        })();
      });
    });
    return td;
  }

  function selectEditTd(viewText, buildSelect, patch) {
    const td = el("td", { class: tdCls });
    const disp = el("span", { class: mtlCellViewCls, text: viewText || "—", title: mtlHint(viewText) });
    td.appendChild(disp);
    td.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (td.querySelector("[data-mtl-ie]")) return;
      const sel = buildSelect();
      sel.dataset.mtlIe = "1";
      td.replaceChildren(sel);
      queueMicrotask(() => sel.focus());
      const onCh = async () => {
        sel.removeEventListener("change", onCh);
        try {
          await patch(sel);
          reloadTable();
        } catch (err) {
          window.alert(cleanText(err?.message) || String(err));
          td.replaceChildren(disp);
        }
      };
      sel.addEventListener("change", onCh);
      sel.addEventListener("blur", () => {
        queueMicrotask(() => {
          if (document.activeElement === sel) return;
          if (td.contains(sel)) td.replaceChildren(disp);
        });
      });
    });
    return td;
  }

  const headerLabels = [
    "Line",
    "Project",
    "MSN",
    "PN",
    "Description",
    "Consumable / Rotable",
    "Qty",
    "UOM",
    "OFF PN",
    "OFF SN",
    "Req by",
    "Req name",
    "Requested",
    "Aging (d)",
    "ETA",
    "PO#",
    "PO date",
    "AWB",
    "Track",
    "Ship",
    "Status",
    "WO#",
    "Cost (USD)",
    "Files",
  ];
  const header = el(
    "tr",
    {},
    headerLabels.map((lab) => el("th", { class: `${thCls} text-left`, text: lab }))
  );

  const makeRow = (p) => {
    const id = Number(p.id);
    const pid = Number(p.projectId ?? p.project_id ?? NaN);
    const projLabel =
      cleanText(activeProjects.find((pr) => Number(pr.id) === pid)?.name) || (Number.isFinite(pid) ? `Project ${pid}` : "—");
    const offUnit = Boolean(p.isOffUnitRequest ?? p.is_off_unit_request);
    const offPnStr = cleanText(p.offUnitPn ?? p.off_unit_pn);
    const offSnStr = cleanText(p.offUnitSn ?? p.off_unit_sn);
    const readSnField = () => {
      const tr = document.querySelector(`tr[data-part-id="${id}"]`);
      const n = tr?.querySelector("[data-mtl-offsn-field]");
      if (!n) return "";
      return cleanText("value" in n ? n.value : n.textContent);
    };

    const projTd = selectEditTd(
      projLabel,
      () => {
        const sel = el("select", { class: `${inpTxt} max-w-[9rem]` }, [
          el("option", { value: "", text: "—" }),
          ...activeProjects.map((pr) => el("option", { value: String(pr.id), text: cleanText(pr.name) || `Project ${pr.id}` })),
        ]);
        sel.value = Number.isFinite(pid) ? String(pid) : "";
        return sel;
      },
      async (sel) => {
        const npid = Number(sel.value);
        if (!Number.isFinite(npid)) throw new Error("Project is required.");
        await patchMaterialPart(id, { projectId: npid });
      }
    );

    const msnTd = textEditTd(async (v) => patchMaterialPart(id, { msn: v }), cleanText(p.msn), `${inpCls} w-[4.5rem]`, true);

    const pnTd = textEditTd(
      async (v) =>
        patchMaterialPart(id, {
          partNumber: cleanText(v).toUpperCase(),
          pn: cleanText(v).toUpperCase(),
        }),
      cleanText(p.partNumber || p.part_number),
      `${inpCls} min-w-[5rem]`,
      true
    );

    const descTd = textEditTd(
      async (v) => patchMaterialPart(id, { description: v }),
      cleanText(p.description),
      `${inpTxt} min-w-[8rem] max-w-[14rem]`,
      false,
      cleanText(p.description)
    );

    const pcl = cleanText(p.partClass ?? p.part_class ?? "ROTABLE").toUpperCase();
    const pclSafe = MATERIAL_PART_CLASS_OPTS.some(([v]) => v === pcl) ? pcl : "ROTABLE";
    const classTd = selectEditTd(
      materialPartClassLabel(pclSafe),
      () => {
        const sel = el("select", { class: `${inpTxt} min-w-[7rem]` }, MATERIAL_PART_CLASS_OPTS.map(([v, lab]) => el("option", { value: v, text: lab })));
        sel.value = pclSafe;
        return sel;
      },
      async (sel) => patchMaterialPart(id, { partClass: sel.value || "ROTABLE" })
    );

    const qtyTd = textEditTd(
      async (v) => patchMaterialPart(id, { qty: Math.max(1, Math.floor(Number(v)) || 1) }),
      String(p.qty ?? 1),
      `${inpCls} w-12 text-right`,
      true
    );

    const uomVal = cleanText(p.qtyUom ?? p.qty_uom ?? "EA") || "EA";
    const uomTd = selectEditTd(
      uomVal,
      () => {
        const sel = el("select", { class: `${inpCls} w-[3.25rem]` }, qtyUomList.map((u) => el("option", { value: u, text: u })));
        sel.value = qtyUomList.includes(uomVal) ? uomVal : "EA";
        return sel;
      },
      async (sel) => patchMaterialPart(id, { qtyUom: sel.value || "EA" })
    );

    const offViewTxt = offUnit ? `OFF · ${offPnStr || "…"}` : "Standard";
    const offTd = el("td", { class: tdCls });
    const offDisp = el("span", { class: mtlCellViewCls, text: offViewTxt, title: mtlHint(offPnStr || offViewTxt) });
    offTd.appendChild(offDisp);
    offTd.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (offTd.querySelector("[data-mtl-ie]")) return;
      const chk = el("input", { type: "checkbox", "data-mtl-ie": "1", class: "rounded border-gray-300 shrink-0", checked: offUnit });
      const pnE = el("input", { type: "text", "data-mtl-ie": "1", class: inpCls, value: offPnStr });
      const wrap = el("div", { class: "flex items-start gap-1 min-w-[6rem]" }, [chk, pnE]);
      offTd.replaceChildren(wrap);
      queueMicrotask(() => pnE.focus());
      let fin = false;
      const finish = async () => {
        if (fin) return;
        fin = true;
        const o = Boolean(chk.checked);
        const opn = cleanText(pnE.value).toUpperCase();
        if (o && !opn) {
          window.alert("OFF unit PN is required when OFF is checked.");
          fin = false;
          offTd.replaceChildren(offDisp);
          return;
        }
        try {
          await patchMaterialPart(id, {
            isOffUnitRequest: o,
            offUnitPn: o ? opn : "",
            offUnitSn: o ? readSnField() : "",
          });
          reloadTable();
        } catch (err) {
          window.alert(cleanText(err?.message) || String(err));
          fin = false;
          offTd.replaceChildren(offDisp);
        }
      };
      chk.addEventListener("change", () => void finish());
      pnE.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          fin = true;
          offTd.replaceChildren(offDisp);
        } else if (e.key === "Enter") {
          e.preventDefault();
          pnE.blur();
        }
      });
      pnE.addEventListener("blur", () => {
        if (fin) {
          fin = false;
          return;
        }
        void finish();
      });
    });

    const snTd = el("td", { class: tdCls });
    const snDisp = el("span", {
      class: `${mtlCellViewCls} font-mono`,
      text: offSnStr || "—",
      title: mtlHint(offSnStr),
      "data-mtl-offsn-field": "1",
    });
    snTd.appendChild(snDisp);
    snTd.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (snTd.querySelector("[data-mtl-ie]")) return;
      const inp = el("input", {
        type: "text",
        "data-mtl-ie": "1",
        "data-mtl-offsn-field": "1",
        class: `${inpCls} font-mono`,
        value: offSnStr,
      });
      let esc = false;
      snTd.replaceChildren(inp);
      queueMicrotask(() => {
        inp.focus();
        inp.select();
      });
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          esc = true;
          snTd.replaceChildren(snDisp);
        } else if (e.key === "Enter") {
          e.preventDefault();
          inp.blur();
        }
      });
      inp.addEventListener("blur", () => {
        if (esc) {
          esc = false;
          return;
        }
        void (async () => {
          try {
            await patchMaterialPart(id, { offUnitSn: cleanText(inp.value) });
            reloadTable();
          } catch (err) {
            window.alert(cleanText(err?.message) || String(err));
            snTd.replaceChildren(snDisp);
          }
        })();
      });
    });

    const reqByTd = dateEditTd(async (v) => patchMaterialPart(id, { requiredBy: v }), p.requiredBy || p.required_by);

    const reqNameTd = textEditTd(
      async (v) => patchMaterialPart(id, { requestedBy: v || null }),
      cleanText(p.requestedBy ?? p.requested_by),
      inpTxt
    );

    const dateReqTd = dateEditTd(async (v) => patchMaterialPart(id, { dateRequested: v }), p.dateRequested || p.date_requested);

    const aging = materialsPartAgingDays(p);
    const agingEl = el(
      "span",
      { class: aging != null && aging > 30 ? "text-amber-700 font-semibold tabular-nums" : "text-gray-800 tabular-nums" },
      [aging != null ? String(aging) : "—"]
    );

    const etaRaw = p.expectedArrival || p.expected_arrival;
    const etaOver = materialsEtaDateOverdue(etaRaw);
    const etaTd = dateEditTd(
      async (v) => patchMaterialPart(id, { expectedArrival: v }),
      etaRaw,
      etaOver ? "border-l-2 border-red-400 bg-red-50/50" : ""
    );

    const poTd = textEditTd(async (v) => patchMaterialPart(id, { poNumber: v || null }), cleanText(p.poNumber || p.po_number), inpCls, true);

    const poDateTd = dateEditTd(async (v) => patchMaterialPart(id, { poDate: v }), p.poDate || p.po_date);

    const awbTd = textEditTd(async (v) => patchMaterialPart(id, { awb: v || null }), cleanText(p.awb), inpCls, true);

    const trackTd = textEditTd(
      async (v) => patchMaterialPart(id, { awbTrackingUrl: v || null }),
      cleanText(p.awbTrackingUrl || p.awb_tracking_url),
      inpTxt
    );

    const shipTd = dateEditTd(async (v) => patchMaterialPart(id, { shipDate: v }), p.shipDate || p.ship_date);

    const st = cleanText(p.status).toUpperCase();
    const stSafe = MATERIAL_PART_STATUS_OPTS.some(([v]) => v === st) ? st : "ON_ORDER";
    const stDispWrap = el("div", { class: "space-y-0.5 min-w-[6.5rem]" }, [
      materialStatusBadgeEl(stSafe),
      el("span", { class: mtlCellViewCls, text: materialStatusLabel(p.status), title: mtlHint("Status") }),
    ]);
    const stTd = el("td", { class: tdCls });
    stTd.appendChild(stDispWrap);
    stTd.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (stTd.querySelector("[data-mtl-ie]")) return;
      const sel = el(
        "select",
        { "data-mtl-ie": "1", class: `${inpTxt} min-w-[7.5rem]` },
        MATERIAL_PART_STATUS_OPTS.map(([v, lab]) => el("option", { value: v, text: lab }))
      );
      sel.value = stSafe;
      stTd.replaceChildren(sel);
      queueMicrotask(() => sel.focus());
      const onCh = async () => {
        sel.removeEventListener("change", onCh);
        try {
          await patchMaterialPart(id, { status: sel.value });
          reloadTable();
        } catch (err) {
          window.alert(cleanText(err?.message) || String(err));
          stTd.replaceChildren(stDispWrap);
        }
      };
      sel.addEventListener("change", onCh);
      sel.addEventListener("blur", () => {
        queueMicrotask(() => {
          if (document.activeElement === sel) return;
          if (stTd.contains(sel)) stTd.replaceChildren(stDispWrap);
        });
      });
    });

    const woTd = textEditTd(async (v) => patchMaterialPart(id, { woNumber: v || null }), cleanText(p.woNumber || p.wo_number), inpCls, true);

    const costNum = p.cost != null && p.cost !== "" && Number.isFinite(Number(p.cost)) ? Number(p.cost) : null;
    const costShown = costNum != null ? `$${costNum.toFixed(2)}` : "—";
    const costTd = el("td", { class: tdCls });
    const costDisp = el("span", {
      class: `${mtlCellViewCls} font-mono text-right`,
      text: costShown,
      title: mtlHint(costNum != null ? `${costShown} USD` : "USD"),
    });
    costTd.appendChild(costDisp);
    costTd.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      if (costTd.querySelector("[data-mtl-ie]")) return;
      const inp = el("input", {
        type: "text",
        inputmode: "decimal",
        "data-mtl-ie": "1",
        class: `${inpCls} w-16 text-right`,
        value: costNum != null ? String(costNum) : "",
      });
      let esc = false;
      costTd.replaceChildren(inp);
      queueMicrotask(() => {
        inp.focus();
        inp.select();
      });
      inp.addEventListener("keydown", (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          esc = true;
          costTd.replaceChildren(costDisp);
        } else if (e.key === "Enter") {
          e.preventDefault();
          inp.blur();
        }
      });
      inp.addEventListener("blur", () => {
        if (esc) {
          esc = false;
          return;
        }
        void (async () => {
          try {
            const raw = cleanText(inp.value);
            await patchMaterialPart(id, { cost: raw === "" ? null : Number(raw) });
            reloadTable();
          } catch (err) {
            window.alert(cleanText(err?.message) || String(err));
            costTd.replaceChildren(costDisp);
          }
        })();
      });
    });

    const nAttach = Number(p.attachmentCount ?? 0);
    const fileIn = el("input", { type: "file", class: "hidden", accept: ".pdf,.doc,.docx,.xls,.xlsx,application/pdf" });
    fileIn.addEventListener("change", async () => {
      const file = fileIn.files?.[0];
      if (!file || file.size > 12 * 1024 * 1024) {
        window.alert("Choose a file up to ~12 MB.");
        fileIn.value = "";
        return;
      }
      try {
        const buf = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result || ""));
          r.onerror = reject;
          r.readAsDataURL(file);
        });
        await postMaterialPartAttachment(id, {
          fileName: file.name,
          mime: file.type || "application/octet-stream",
          base64: buf.replace(/^[^,]+,/, ""),
        });
        fileIn.value = "";
        reloadTable();
      } catch (e) {
        window.alert(cleanText(e?.message) || String(e));
      }
    });

    const filesCell = el("div", { class: "flex flex-col gap-1 min-w-[5.5rem]" }, [
      el("div", { class: "flex items-center gap-1 flex-wrap" }, [
        el("a", { href: `#/materials/part/${id}`, class: "text-dae text-[10px] hover:underline font-medium", text: "Full detail" }),
        el("span", { class: "text-[10px] text-gray-500", text: `${nAttach} file(s)` }),
      ]),
      el(
        "button",
        {
          type: "button",
          class: "text-[10px] px-2 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50 text-left",
          text: "+ Attach file",
          onclick: () => fileIn.click(),
        },
        []
      ),
      fileIn,
    ]);

    const lineNo = p.lineNumber != null ? String(p.lineNumber) : "—";
    return el("tr", { class: "bg-white border-b border-gray-100 align-top text-gray-800", "data-part-id": String(id) }, [
      el("td", { class: `${tdCls} text-right font-mono tabular-nums text-gray-600 shrink-0 w-10` }, [
        el("span", { title: "Correlative line number for this list (DB row_number, 1-based).", text: lineNo }),
      ]),
      projTd,
      msnTd,
      pnTd,
      descTd,
      classTd,
      qtyTd,
      uomTd,
      offTd,
      snTd,
      reqByTd,
      reqNameTd,
      dateReqTd,
      el("td", { class: `${tdCls} text-right` }, [agingEl]),
      etaTd,
      poTd,
      poDateTd,
      awbTd,
      trackTd,
      shipTd,
      stTd,
      woTd,
      costTd,
      el("td", { class: tdCls }, [filesCell]),
    ]);
  };
  const tbody = el(
    "tbody",
    {},
    parts.length
      ? parts.map(makeRow)
      : [
          el("tr", {}, [
            el("td", {
              colspan: "24",
              class: "px-4 py-8 text-center text-gray-500",
              text: "No parts yet. Use Add part to create one.",
            }),
          ]),
        ]
  );

  const table = el("table", { class: "w-full text-sm text-left text-gray-700 border-collapse min-w-[1680px]" }, [
    el("thead", {}, [header]),
    tbody,
  ]);

  host.replaceChildren(
    el("div", { class: "text-xs text-gray-600 mb-2 px-1" }, [
      "Double-click a cell to edit. Press ",
      el("kbd", { class: "px-1 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono text-[10px]", text: "Enter" }),
      " or click outside to save. ",
      el("kbd", { class: "px-1 py-0.5 rounded border border-gray-300 bg-gray-50 font-mono text-[10px]", text: "Esc" }),
      " cancels. ",
      el("strong", {}, ["Line"]),
      " numbers this list 1, 2, 3… (SQL row number). ETA overdue is red. Aging is UTC days since request. Attach files here or on detail.",
    ]),
    el("div", {
      class: "overflow-x-auto w-full border border-gray-200 rounded-xl bg-white shadow-sm box-border",
    }, [table])
  );

  const exportBtn = document.getElementById("v2-mtl-export-xls");
  if (exportBtn) {
    exportBtn.onclick = () => {
      const now = new Date();
      const exportedAt = formatMaterialsExportTimeEst(now);
      const title = "Materials — parts";
      const exportHeaders = [
        "Line",
        "Project",
        "MSN",
        "PN",
        "Description",
        "Consumable / Rotable",
        "Qty",
        "UOM",
        "OFF unit PN",
        "OFF unit SN",
        "Required by date",
        "Requested by name",
        "Requested",
        "Aging (d)",
        "ETA",
        "PO#",
        "PO date",
        "AWB",
        "Tracking URL",
        "Ship date",
        "Status",
        "WO#",
        "Cost (USD)",
      ];
      const rows = parts.map((p) => {
        const costRaw = p.cost;
        let costCell = "";
        if (costRaw != null && costRaw !== "") {
          const n = Number(costRaw);
          if (Number.isFinite(n)) costCell = `$${n.toFixed(2)}`;
        }
        const agingD = materialsPartAgingDays(p);
        return [
          p.lineNumber != null ? String(p.lineNumber) : "",
          cleanText(p.projectName || ""),
          cleanText(p.msn) || "",
          cleanText(p.partNumber || p.part_number) || "",
          cleanText(p.description) || "",
          materialPartClassLabel(p.partClass ?? p.part_class),
          String(p.qty ?? 1),
          cleanText(p.qtyUom ?? p.qty_uom ?? "EA") || "EA",
          cleanText(p.offUnitPn ?? p.off_unit_pn ?? ""),
          cleanText(p.offUnitSn ?? p.off_unit_sn ?? ""),
          formatMaterialsDateDdMmmYyyy(p.required_by),
          cleanText(p.requestedBy || ""),
          formatMaterialsDateDdMmmYyyy(p.dateRequested || p.date_requested),
          agingD != null ? String(agingD) : "",
          formatMaterialsDateDdMmmYyyy(p.expectedArrival || p.expected_arrival),
          cleanText(p.poNumber || p.po_number) || "",
          formatMaterialsDateDdMmmYyyy(p.poDate || p.po_date),
          cleanText(p.awb) || "",
          cleanText(p.awbTrackingUrl || p.awb_tracking_url) || "",
          formatMaterialsDateDdMmmYyyy(p.shipDate || p.ship_date),
          materialStatusLabel(p.status),
          cleanText(p.woNumber || p.wo_number) || "",
          costCell,
        ];
      });
      const xml = buildMaterialsXlsXml({
        title,
        exportedAtEst: exportedAt,
        headers: exportHeaders,
        rows,
      });
      downloadTextFile({
        filename: `ACTracker-Materials-Parts-${formatMaterialsFilenameEst(now)}.xls`,
        content: xml,
        contentType: "application/vnd.ms-excel",
      });
    };
  }
}

function renderMaterialsPartEditorPage({ partId }) {
  const isNew = partId == null;
  return el("div", { class: "space-y-4 max-w-4xl" }, [
    el("div", { class: "flex flex-wrap items-center justify-between gap-3" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900", text: isNew ? "New materials part" : `Edit part #${partId}` }),
        el("p", { class: "mt-1 text-sm text-gray-500", text: "All fields save with Save. PN lookup uses IPC data when available." }),
      ]),
      el("a", { href: "#/materials", class: "text-dae hover:underline text-sm font-medium", text: "← Parts table" }),
    ]),
    el("div", { id: "v2-materials-part-editor", class: "bg-white border border-gray-200 rounded-xl shadow-sm p-6" }, [
      el("div", { class: "text-sm text-gray-600", text: "Loading…" }),
    ]),
  ]);
}

async function initMaterialsPartEditorPage(partId) {
  const host = document.getElementById("v2-materials-part-editor");
  if (!host) return;
  const isNew = partId == null || partId === "new";
  const projects = await fetchMaterialsProjects();
  const activeProjects = projects.filter((p) => p.isActive !== false);
  const msnOptions = await fetchMaterialsMsnOptions();
  const meta = await fetchMaterialsMeta();
  const qtyUomList = Array.isArray(meta?.uoms) && meta.uoms.length ? meta.uoms : ["EA", "KG", "LB", "L", "GAL", "PCS"];

  let part = null;
  if (!isNew) {
    part = await fetchMaterialPart(partId);
    if (!part) {
      host.replaceChildren(el("div", { class: "text-sm text-red-700", text: "Part not found." }));
      return;
    }
  }

  const fieldRow = (label, inputEl) =>
    el("div", { class: "grid grid-cols-1 sm:grid-cols-3 gap-2 sm:items-center py-2 border-b border-gray-100 last:border-0" }, [
      el("label", { class: "text-sm font-medium text-gray-700", text: label }),
      el("div", { class: "sm:col-span-2" }, [inputEl]),
    ]);

  const projectSel = el(
    "select",
    {
      id: "v2-mpe-project",
      class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm",
    },
    [
      el("option", { value: "", text: "Select project…" }),
      ...activeProjects.map((p) => el("option", { value: String(p.id), text: cleanText(p.name) || `Project ${p.id}` })),
    ]
  );
  if (part?.projectId || part?.project_id) projectSel.value = String(part.projectId || part.project_id);

  const msnSel = el("select", { id: "v2-mpe-msn", class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono" });
  const refillMsnChoices = () => {
    const pid = Number(projectSel.value);
    const scoped = materialsMsnsForActiveProject(projects, pid);
    const src = scoped && scoped.length ? scoped : msnOptions;
    msnSel.replaceChildren(el("option", { value: "", text: "MSN…" }), ...src.map((m) => el("option", { value: cleanText(m), text: cleanText(m) })));
    const cur = part ? cleanText(part.msn) : cleanText(msnSel.dataset.sticky || "");
    if (cur && !src.includes(cur)) {
      msnSel.appendChild(el("option", { value: cur, text: `${cur} (saved)` }));
    }
    if (cur) msnSel.value = cur;
  };
  refillMsnChoices();
  const curMsn0 = part ? cleanText(part.msn) : "";
  if (curMsn0) msnSel.dataset.sticky = curMsn0;
  projectSel.addEventListener("change", () => {
    refillMsnChoices();
  });
  msnSel.addEventListener("change", () => {
    msnSel.dataset.sticky = cleanText(msnSel.value);
  });

  const pnIn = el("input", {
    type: "text",
    id: "v2-mpe-pn",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono uppercase",
    value: cleanText(part?.partNumber || part?.part_number || ""),
  });
  const descIn = el("textarea", {
    id: "v2-mpe-desc",
    class: "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[4rem]",
    value: cleanText(part?.description || ""),
  });
  const partClassSel = el(
    "select",
    { id: "v2-mpe-partclass", class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm" },
    MATERIAL_PART_CLASS_OPTS.map(([v, lab]) => el("option", { value: v, text: lab }))
  );
  const pclEditor = cleanText(part?.partClass ?? part?.part_class ?? "ROTABLE").toUpperCase();
  partClassSel.value = MATERIAL_PART_CLASS_OPTS.some(([v]) => v === pclEditor) ? pclEditor : "ROTABLE";
  pnIn.addEventListener("blur", async () => {
    const v = cleanText(pnIn.value).toUpperCase();
    if (v.length < 3) return;
    const hits = await ipcSearchMaterialsPn(v);
    const exact = hits.find((h) => cleanText(h.pn).toUpperCase() === v);
    if (exact && !cleanText(descIn.value)) {
      descIn.value = exact.description || "";
    }
  });

  const qtyIn = el("input", {
    type: "number",
    min: "1",
    step: "1",
    id: "v2-mpe-qty",
    class: "w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm text-right",
    value: String(part?.qty ?? 1),
  });
  const qtyUomSel = el("select", { id: "v2-mpe-qtyuom", class: "rounded-lg border border-gray-300 px-3 py-2 text-sm" }, [
    ...qtyUomList.map((u) => el("option", { value: u, text: u })),
  ]);
  qtyUomSel.value = cleanText(part?.qtyUom ?? part?.qty_uom ?? "EA") || "EA";

  const qtyRowWrap = el("div", { class: "flex flex-wrap gap-2 items-center" }, [qtyIn, el("span", { class: "text-xs text-gray-500", text: "×" }), qtyUomSel]);

  const statusSel = el("select", { id: "v2-mpe-status", class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm" });
  MATERIAL_PART_STATUS_OPTS.forEach(([v, lab]) => statusSel.appendChild(el("option", { value: v, text: lab })));
  statusSel.value = part?.status && MATERIAL_PART_STATUS_OPTS.some(([x]) => x === part.status) ? part.status : "ON_ORDER";

  const dateRequestedIn = el("input", {
    type: "date",
    id: "v2-mpe-daterequested",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: part?.dateRequested || part?.date_requested ? isoToDateInputValue(part.dateRequested || part.date_requested) : localDateInputToday(),
  });
  const requestedByName = el("input", {
    type: "text",
    id: "v2-mpe-reqbyname",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm",
    value: cleanText(part?.requestedBy ?? part?.requested_by ?? ""),
    placeholder: "Name — included in notifications",
  });
  const reqBy = el("input", {
    type: "date",
    id: "v2-mpe-reqby",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: part?.requiredBy || part?.required_by ? String(part.requiredBy || part.required_by).slice(0, 10) : "",
  });
  const requestCommentIn = el("textarea", {
    id: "v2-mpe-reqcomment",
    class: "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[4rem]",
    value: cleanText(part?.requestComment ?? part?.request_comment ?? ""),
    placeholder: "Comment on this request — shown on new-part emails",
  });
  const offUnitChk = el("input", {
    type: "checkbox",
    id: "v2-mpe-offunit",
    class: "rounded border-gray-300",
    checked: Boolean(part?.isOffUnitRequest ?? part?.is_off_unit_request),
  });
  const offPnIn = el("input", {
    type: "text",
    id: "v2-mpe-offpn",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono uppercase",
    value: cleanText(part?.offUnitPn ?? part?.off_unit_pn ?? ""),
  });
  const offSnIn = el("input", {
    type: "text",
    id: "v2-mpe-offsn",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: cleanText(part?.offUnitSn ?? part?.off_unit_sn ?? ""),
  });
  const offFieldsWrap = el("div", { id: "v2-mpe-off-fields", class: "space-y-0" }, [
    fieldRow("OFF unit PN", offPnIn),
    fieldRow("OFF unit SN", offSnIn),
  ]);
  const syncOffUi = () => {
    offFieldsWrap.style.display = offUnitChk.checked ? "block" : "none";
    offPnIn.required = Boolean(offUnitChk.checked && isNew);
  };
  offUnitChk.addEventListener("change", syncOffUi);
  syncOffUi();
  const eta = el("input", {
    type: "date",
    id: "v2-mpe-eta",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: (part?.expectedArrival || part?.expected_arrival) ? String(part.expectedArrival || part.expected_arrival).slice(0, 10) : "",
  });
  const poNum = el("input", {
    type: "text",
    id: "v2-mpe-po",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm",
    value: cleanText(part?.poNumber || part?.po_number || ""),
  });
  const poDate = el("input", {
    type: "date",
    id: "v2-mpe-podate",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: (part?.poDate || part?.po_date) ? String(part.poDate || part.po_date).slice(0, 10) : "",
  });
  const awbIn = el("input", {
    type: "text",
    id: "v2-mpe-awb",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: cleanText(part?.awb || ""),
  });
  const trackIn = el("input", {
    type: "url",
    id: "v2-mpe-track",
    class: "w-full max-w-xl rounded-lg border border-gray-300 px-3 py-2 text-sm",
    value: cleanText(part?.awbTrackingUrl || part?.awb_tracking_url || ""),
    placeholder: "https://…",
  });
  const shipIn = el("input", {
    type: "date",
    id: "v2-mpe-ship",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono",
    value: (part?.shipDate || part?.ship_date) ? String(part.shipDate || part.ship_date).slice(0, 10) : "",
  });
  const woIn = el("input", {
    type: "text",
    id: "v2-mpe-wo",
    class: "w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm",
    value: cleanText(part?.woNumber || part?.wo_number || ""),
  });
  const costIn = el("input", {
    type: "text",
    inputmode: "decimal",
    id: "v2-mpe-cost",
    class: "w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm text-right",
    value: part?.cost != null && part?.cost !== "" ? String(part.cost) : "",
  });

  const attachmentsBlock = !isNew
    ? el("div", { class: "mt-6 pt-4 border-t border-gray-200" }, [
        el("h3", { class: "text-sm font-semibold text-gray-900 mb-2", text: "Attachments" }),
        el("div", {
          id: "v2-mpe-attachments",
          class: "flex flex-wrap gap-2 mb-3 min-h-[1.25rem]",
        }),
        el("input", {
          type: "file",
          id: "v2-mpe-attach-input",
          class: "block text-xs text-gray-600",
          accept:
            ".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      ])
    : el("span");

  async function redrawPartAttachments() {
    const box = document.getElementById("v2-mpe-attachments");
    if (!box || !part?.id) return;
    box.replaceChildren(el("span", { class: "text-xs text-gray-400", text: "Loading attachments…" }));
    try {
      const aa = await fetchMaterialPartAttachments(part.id);
      if (!aa.length) {
        box.replaceChildren(el("span", { class: "text-xs text-gray-400", text: "No files attached." }));
        return;
      }
      box.replaceChildren(
        ...aa.map((a) => {
          const lab = cleanText(a.originalName ?? a.original_name ?? "");
          const href = apiUrl(`/api/materials/attachments/${encodeURIComponent(a.id)}/download`);
          const ic = materialAttachmentIconLabel(lab, a.mime);
          const badge = `${materialAttachmentBadgeClass(lab, a.mime)} inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold uppercase no-underline`;
          return el("a", { href, target: "_blank", rel: "noopener", class: badge, title: lab || "Download" }, [
            `${ic}`,
            el("span", { class: "truncate max-w-[10rem]", text: lab || `File ${a.id}` }),
          ]);
        })
      );
    } catch {
      box.replaceChildren(el("span", { class: "text-xs text-red-600", text: "Unable to load attachments." }));
    }
  }

  if (!isNew) {
    queueMicrotask(() => redrawPartAttachments());
    queueMicrotask(() => {
      const fin = document.getElementById("v2-mpe-attach-input");
      if (!fin || !part?.id) return;
      fin.onchange = async () => {
        const file = fin.files?.[0];
        if (!file || file.size > 12 * 1024 * 1024) {
          window.alert("Attach a single file under ~12 MB.");
          fin.value = "";
          return;
        }
        try {
          const buf = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(String(r.result || ""));
            r.onerror = reject;
            r.readAsDataURL(file);
          });
          await postMaterialPartAttachment(part.id, {
            fileName: file.name,
            mime: file.type || "application/octet-stream",
            base64: buf.replace(/^[^,]+,/, ""),
          });
          window.alert("File attached.");
          fin.value = "";
          await redrawPartAttachments();
        } catch (e) {
          window.alert(cleanText(e?.message) || String(e));
        }
      };
    });
  }

  const commentsHost = el("div", { id: "v2-mpe-comments", class: "mt-6 pt-4 border-t border-gray-200" });
  if (!isNew) {
    const list = await fetchMaterialComments(part.id);
    const listBox = el("div", { class: "space-y-2 max-h-64 overflow-y-auto text-sm text-gray-700 mb-3" });
    if (list.length) {
      listBox.replaceChildren(
        ...list.map((c) =>
          el("div", { class: "border-b border-gray-100 pb-2" }, [
            el("div", { class: "text-[10px] text-gray-400" }, [
              `${formatMaterialsDateDdMmmYyyy(c.createdAt || c.created_at)} · ${cleanText(c.author)}${c.isSystem || c.is_system ? " · auto" : ""}`,
            ]),
            el("div", { class: "whitespace-pre-wrap" }, [cleanText(c.body)]),
          ])
        )
      );
    } else {
      listBox.appendChild(el("p", { class: "text-gray-400 text-sm", text: "No comments yet." }));
    }
    const pidStr = String(part.id);
    commentsHost.replaceChildren(
      el("h3", { class: "text-sm font-semibold text-gray-900 mb-2", text: "Comments" }),
      listBox,
      el("textarea", {
        id: "v2-mpe-cmt-new",
        class: "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[4rem]",
        placeholder: "Add a comment…",
      }),
      el("button", {
        type: "button",
        class: "mt-2 px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg",
        text: "Post comment",
        onclick: async () => {
          const body = cleanText(document.getElementById("v2-mpe-cmt-new")?.value);
          if (!body) return;
          await postMaterialComment(part.id, body);
          await initMaterialsPartEditorPage(pidStr);
        },
      })
    );
  }

  const saveBtn = el("button", {
    type: "button",
    class: "mt-4 px-5 py-2.5 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
    text: isNew ? "Create part" : "Save changes",
    onclick: async () => {
      const pid = Number(projectSel.value);
      const msn = cleanText(msnSel.value);
      if (!pid || !msn) {
        window.alert("Project and MSN are required.");
        return;
      }
      if (offUnitChk.checked && !cleanText(offPnIn.value)) {
        window.alert("OFF unit PN is required when OFF UNIT is enabled.");
        return;
      }
      const payload = {
        projectId: pid,
        msn,
        partNumber: cleanText(pnIn.value).toUpperCase(),
        pn: cleanText(pnIn.value).toUpperCase(),
        description: cleanText(descIn.value),
        partClass: partClassSel.value || "ROTABLE",
        qty: Number(qtyIn.value) || 1,
        qtyUom: qtyUomSel.value || "EA",
        status: statusSel.value,
        dateRequested: dateRequestedIn.value || null,
        requestedBy: cleanText(requestedByName.value) || null,
        requestComment: cleanText(requestCommentIn.value) || null,
        isOffUnitRequest: offUnitChk.checked,
        offUnitPn: offUnitChk.checked ? cleanText(offPnIn.value).toUpperCase() : "",
        offUnitSn: offUnitChk.checked ? cleanText(offSnIn.value) : "",
        requiredBy: reqBy.value || null,
        expectedArrival: eta.value || null,
        poNumber: cleanText(poNum.value) || null,
        poDate: poDate.value || null,
        awb: cleanText(awbIn.value) || null,
        awbTrackingUrl: cleanText(trackIn.value) || null,
        shipDate: shipIn.value || null,
        woNumber: cleanText(woIn.value) || null,
        cost: costIn.value === "" ? null : Number(costIn.value),
      };
      try {
        if (isNew) {
          const r = await postMaterialPart(payload);
          const newId = r?.part?.id;
          const msnH = cleanText(msnSel.value);
          if (newId) {
            window.location.hash = materialsPartsHash({ projectId: String(pid), msn: msnH || undefined });
          } else {
            window.location.hash = "#/materials";
          }
        } else {
          await patchMaterialPart(part.id, payload);
          const msnH = cleanText(msnSel.value);
          const projH = String(part.projectId || part.project_id || projectSel.value || "");
          window.location.hash = materialsPartsHash({ projectId: projH, msn: msnH || undefined });
        }
      } catch (e) {
        window.alert(cleanText(e?.message) || String(e));
      }
    },
  });

  const deleteBtn =
    !isNew && part
      ? el("button", {
          type: "button",
          class: "mt-4 ml-3 px-5 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700",
          text: "Delete part",
          onclick: async () => {
            if (!window.confirm("Delete this part line?")) return;
            try {
              await fetchJson(apiUrl(`/api/materials/parts/${encodeURIComponent(part.id)}`), { method: "DELETE" });
              window.location.hash = "#/materials";
            } catch (e) {
              window.alert(cleanText(e?.message) || String(e));
            }
          },
        })
      : null;

  host.replaceChildren(
    el("div", { class: "space-y-1" }, [
      fieldRow("Project *", projectSel),
      fieldRow(
        "MSN *",
        el("div", {}, [
          msnSel,
          el("p", { class: "mt-1 text-[11px] text-gray-500", text: "List is narrowed to aircraft on the selected project when possible." }),
        ])
      ),
      fieldRow("Part number", pnIn),
      fieldRow("Description", descIn),
      fieldRow("Consumable / Rotable", partClassSel),
      fieldRow("Quantity", qtyRowWrap),
      fieldRow(
        "OFF unit request?",
        el("label", { class: "flex items-start gap-2 text-sm text-gray-700 cursor-pointer max-w-xl" }, [
          offUnitChk,
          el("span", {}, [
            "When enabled you must specify the OFF PN (removal/core). Opens a cores row automatically on create when applicable.",
          ]),
        ])
      ),
      offFieldsWrap,
      fieldRow("Status", statusSel),
      fieldRow("Date requested", dateRequestedIn),
      fieldRow("Requested by (name)", requestedByName),
      fieldRow("Request comment", requestCommentIn),
      fieldRow("Required by (date)", reqBy),
      fieldRow("Expected arrival", eta),
      fieldRow("PO #", poNum),
      fieldRow("PO date", poDate),
      fieldRow("AWB", awbIn),
      fieldRow("Tracking URL", trackIn),
      fieldRow("Ship date", shipIn),
      fieldRow("WO #", woIn),
      fieldRow("Cost (USD)", costIn),
    ]),
    attachmentsBlock,
    saveBtn,
    deleteBtn || el("span"),
    isNew ? el("span") : commentsHost
  );
}

function renderMaterialsProjectsListPage() {
  return el("div", { class: "space-y-4" }, [
    el("div", { class: "flex flex-wrap items-end justify-between gap-4" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900", text: "Materials — projects" }),
        el("p", { class: "mt-1 text-sm text-gray-500", text: "MSNs and engine fields are saved per row. Use aircraft MSNs from the fleet database." }),
      ]),
      el("div", { class: "flex flex-wrap gap-2" }, [
        el("a", { href: "#/materials/project/new", class: "inline-flex px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90", text: "New project" }),
        el("a", { href: "#/materials-dashboard", class: "text-dae hover:underline text-sm font-medium", text: "← Dashboard" }),
      ]),
    ]),
    el("div", { id: "v2-materials-projects-list", class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto" }, [
      el("div", { class: "p-4 text-sm text-gray-600", text: "Loading…" }),
    ]),
  ]);
}

async function initMaterialsProjectsListPage() {
  const host = document.getElementById("v2-materials-projects-list");
  if (!host) return;
  try {
    const projects = await fetchMaterialsProjects();
    const flat = [];
    for (const p of projects || []) {
      const acList = Array.isArray(p.aircraft) ? p.aircraft : [];
      if (!acList.length) {
        flat.push({ project: p, row: null });
      } else {
        for (const a of acList) flat.push({ project: p, row: a });
      }
    }
    const th = "px-2 py-2 text-left text-[10px] font-semibold text-white bg-dae border-b border-dae/30 whitespace-nowrap uppercase tracking-wide";
    const header = el("tr", { class: "" }, [
      el("th", { class: th, text: "Project" }),
      el("th", { class: th, text: "Active" }),
      el("th", { class: `${th} max-w-[12rem]`, text: "Project notes" }),
      el("th", { class: th, text: "MSN" }),
      el("th", { class: th, text: "Location" }),
      el("th", { class: th, text: "ESN 1" }),
      el("th", { class: th, text: "TSN 1 (FH)" }),
      el("th", { class: th, text: "CSN 1 (FC)" }),
      el("th", { class: th, text: "ESN 2" }),
      el("th", { class: th, text: "TSN 2 (FH)" }),
      el("th", { class: th, text: "CSN 2 (FC)" }),
      el("th", { class: th, text: "Parts (this MSN)" }),
      el("th", { class: th, text: "" }),
    ]);
    const bodyRows =
      flat.length === 0
        ? [
            el("tr", {}, [
              el("td", {
                colspan: "13",
                class: "px-4 py-8 text-center text-gray-500",
                text: "No projects yet. Create one and attach aircraft rows.",
              }),
            ]),
          ]
        : flat.map(({ project: p, row: a }) => {
            const pname = cleanText(p.name) || `Project ${p.id}`;
            const active = p.isActive !== false;
            const pn = active
              ? el("span", { class: "inline-flex px-2 py-0.5 rounded-full text-[10px] bg-green-50 text-green-800 border border-green-100", text: "ACTIVE" })
              : el("span", { class: "inline-flex px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-600", text: "INACTIVE" });
            const notes = cleanText(p.notes || "").slice(0, 280);
            if (!a) {
              return el("tr", { class: "border-b border-gray-100 text-xs text-gray-800 hover:bg-gray-50/80 align-top" }, [
                el("td", { class: "px-2 py-2 font-medium whitespace-nowrap" }, [pname]),
                el("td", { class: "px-2 py-2" }, [pn]),
                el("td", { class: "px-2 py-2 text-gray-600 max-w-[12rem]" }, [
                  notes || el("span", { class: "text-gray-300", text: "—" }),
                ]),
                el("td", { class: "px-2 py-2 text-gray-400 font-mono" }, ["—"]),
                el("td", { class: "px-2 py-2 text-gray-400" }, ["—"]),
                ...Array.from({ length: 6 }, () => el("td", { class: "px-2 py-2 text-gray-300 font-mono" }, ["—"])),
                el("td", { class: "px-2 py-2" }, ["—"]),
                el("td", { class: "px-2 py-2 whitespace-nowrap" }, [
                  el("a", { href: `#/materials/project/${p.id}`, class: "text-dae hover:underline font-medium", text: "Edit project" }),
                ]),
              ]);
            }
            const msn = cleanText(a.msn);
            const loc = cleanText(a.location || "");
            const e1 = cleanText(a.esnPos1 ?? a.eng1Esn ?? a.eng1_esn ?? "");
            const e2 = cleanText(a.esnPos2 ?? a.eng2Esn ?? a.eng2_esn ?? "");
            const tsn1 = a.tsnPos1 != null ? String(a.tsnPos1) : "—";
            const csn1 = a.csnPos1 != null ? String(a.csnPos1) : "—";
            const tsn2 = a.tsnPos2 != null ? String(a.tsnPos2) : "—";
            const csn2 = a.csnPos2 != null ? String(a.csnPos2) : "—";
            const partsHref = materialsPartsHash({ projectId: String(p.id), msn });
            return el("tr", { class: "border-b border-gray-100 text-xs text-gray-800 hover:bg-gray-50/80 align-top" }, [
              el("td", { class: "px-2 py-2 font-medium whitespace-nowrap" }, [pname]),
              el("td", { class: "px-2 py-2" }, [pn]),
              el("td", { class: "px-2 py-2 text-gray-600 max-w-[12rem] break-words" }, [
                notes || el("span", { class: "text-gray-300", text: "—" }),
              ]),
              el("td", { class: "px-2 py-2 font-mono" }, [msn || "—"]),
              el("td", { class: "px-2 py-2" }, [loc ? loc : el("span", { class: "text-gray-300", text: "—" })]),
              el("td", { class: "px-2 py-2 font-mono" }, [e1 || "—"]),
              el("td", { class: "px-2 py-2 font-mono text-right" }, [tsn1]),
              el("td", { class: "px-2 py-2 font-mono text-right" }, [csn1]),
              el("td", { class: "px-2 py-2 font-mono" }, [e2 || "—"]),
              el("td", { class: "px-2 py-2 font-mono text-right" }, [tsn2]),
              el("td", { class: "px-2 py-2 font-mono text-right" }, [csn2]),
              el("td", { class: "px-2 py-2" }, [
                el("a", { href: partsHref, class: "text-dae hover:underline font-medium", text: "Open parts →" }),
              ]),
              el("td", { class: "px-2 py-2 whitespace-nowrap" }, [
                el("a", { href: `#/materials/project/${p.id}`, class: "text-dae hover:underline mr-3", text: "Edit row" }),
                el("a", { href: `#/materials/cores`, class: "text-gray-600 hover:underline text-[11px]", text: "Cores" }),
              ]),
            ]);
          });
    host.replaceChildren(
      el("div", { class: "text-xs text-gray-500 mb-2 px-1" }, [
        "Each line is one aircraft MSN on a project. Use ",
        el("strong", {}, ["Open parts →"]),
        " for part lines scoped to project + MSN (all lines are tied to a project in the tracker). ",
        " Aviation order UOMs default to EA; common alternates include LB/LBS/KG/L (liters), etc., on the part form.",
      ]),
      el("table", { class: "w-full text-sm border-collapse min-w-[1200px]" }, [
        el("thead", {}, [header]),
        el("tbody", {}, bodyRows),
      ])
    );
  } catch (e) {
    host.replaceChildren(el("div", { class: "p-4 text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
  }
}

function renderMaterialsCoresPage() {
  return el("div", { class: "space-y-4 w-full max-w-[min(100%,1480px)] mx-auto px-2 sm:px-3 box-border min-w-0" }, [
    el("div", { class: "flex flex-wrap items-end justify-between gap-4" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900 tracking-tight", text: "Materials — cores (OFF units)" }),
        el("p", {
          class: "mt-1 text-sm text-gray-500 max-w-3xl",
          text:
            "Track removals shipped as cores: same logistics fields as parts (AWB, status). Rows can link from a parts request or be entered standalone. Dates display as DD-MMM-YYYY.",
        }),
      ]),
      el("div", { class: "flex flex-wrap gap-2" }, [
        el("a", { href: "#/materials", class: "text-dae hover:underline text-sm font-medium", text: "Parts" }),
        el("a", { href: "#/materials/projects", class: "text-dae hover:underline text-sm font-medium", text: "Projects" }),
      ]),
    ]),
    el("div", { id: "v2-materials-cores-panel", class: "space-y-4" }, [el("div", { class: "text-sm text-gray-600", text: "Loading…" })]),
  ]);
}

async function initMaterialsCoresPage() {
  const host = document.getElementById("v2-materials-cores-panel");
  if (!host) return;
  const rtp = currentRouteParams();
  const filterPid = rtp.get("projectId");
  const editRaw = cleanText(rtp.get("edit"));
  const editingId = /^\d+$/.test(editRaw) ? Number(editRaw) : NaN;

  host.replaceChildren(el("div", { class: "text-sm text-gray-600", text: "Loading…" }));
  try {
    const projects = await fetchMaterialsProjects();
    const activeProjects = projects.filter((p) => p.isActive !== false);
    const meta = await fetchMaterialsMeta();
    const uoms = Array.isArray(meta?.uoms) && meta.uoms.length ? meta.uoms : ["EA", "KG", "LB", "L"];

    const fp = Number(filterPid);
    let cores = await fetchMaterialsCores(Number.isFinite(fp) && fp > 0 ? fp : undefined);

    const thCls =
      "px-2 py-2 text-[10px] font-semibold text-white bg-dae border-b border-dae/30 whitespace-nowrap uppercase tracking-wide text-left";
    const tdCls = "px-2 py-2 border-b border-gray-100 align-top text-xs";

    const projectFilter = el(
      "select",
      {
        id: "v2-mcores-f-project",
        class: "rounded-lg border border-gray-200 text-sm px-2 py-1.5 min-w-[10rem]",
        onchange: () => {
          const v = cleanText(document.getElementById("v2-mcores-f-project")?.value);
          const np = currentRouteParams();
          if (v) np.set("projectId", v);
          else np.delete("projectId");
          np.delete("edit");
          setMaterialsCoresRouteQuery(np);
        },
      },
      [
        el("option", { value: "", text: "All projects" }),
        ...activeProjects.map((p) => el("option", { value: String(p.id), text: cleanText(p.name) || `Project ${p.id}` })),
      ]
    );

    const formCard = () => {
      const isEdit = Number.isFinite(editingId);
      let editCore = null;
      if (isEdit) editCore = cores.find((c) => Number(c.id) === editingId) || null;
      const hdrMiss =
        isEdit && !editCore
          ? el("div", { class: "text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-3", text:
              "Core not shown in current table (filter?). Clear project filter or find the row.",
            })
          : null;
      const hdr = el("h2", { class: "text-sm font-semibold text-gray-900 mb-3", text: isEdit ? `Edit core #${editingId}` : "Register OFF unit (standalone)" });

      const projSel = el(
        "select",
        { id: "v2-mcores-form-project", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 w-full max-w-md" },
        [
          el("option", { value: "", text: "Project…" }),
          ...activeProjects.map((p) => el("option", { value: String(p.id), text: cleanText(p.name) || `Project ${p.id}` })),
        ]
      );
      const msnIn = el("input", {
        type: "text",
        id: "v2-mcores-form-msn",
        class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono w-full max-w-xs",
        placeholder: "MSN",
      });
      const offPnIn = el("input", {
        type: "text",
        id: "v2-mcores-form-offpn",
        class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono uppercase w-full max-w-md",
        placeholder: "OFF unit PN",
      });
      const offSnIn = el("input", {
        type: "text",
        id: "v2-mcores-form-offsn",
        class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono w-full max-w-md",
        placeholder: "OFF unit SN (optional)",
      });
      const qtyIn = el("input", {
        type: "number",
        min: "1",
        step: "1",
        id: "v2-mcores-form-qty",
        class: "w-24 rounded-lg border border-gray-300 text-sm px-2 py-2 text-right",
        value: "1",
      });
      const uomSel = el("select", { id: "v2-mcores-form-uom", class: "rounded-lg border border-gray-300 text-sm px-2 py-2" }, [
        ...uoms.map((u) => el("option", { value: u, text: u })),
      ]);
      const statSel = el("select", { id: "v2-mcores-form-status", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 min-w-[10rem]" });
      MATERIAL_PART_STATUS_OPTS.forEach(([v, lab]) => statSel.appendChild(el("option", { value: v, text: lab })));
      const awbIn = el("input", {
        type: "text",
        id: "v2-mcores-form-awb",
        class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono w-full max-w-md",
      });
      const trackIn = el("input", { type: "url", id: "v2-mcores-form-track", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 w-full max-w-xl" });
      const notesIn = el("textarea", { id: "v2-mcores-form-notes", class: "w-full rounded-lg border border-gray-300 px-2 py-2 text-sm min-h-[3rem]" });
      const reqBy = el("input", { type: "date", id: "v2-mcores-form-reqby", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono" });
      const eta = el("input", { type: "date", id: "v2-mcores-form-eta", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono" });
      const shipIn = el("input", { type: "date", id: "v2-mcores-form-ship", class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono" });

      if (editCore) {
        projSel.value = String(editCore.projectId ?? editCore.project_id ?? "");
        msnIn.value = cleanText(editCore.msn);
        offPnIn.value = cleanText(editCore.offUnitPn ?? editCore.off_unit_pn ?? "");
        offSnIn.value = cleanText(editCore.offUnitSn ?? editCore.off_unit_sn ?? "");
        qtyIn.value = String(editCore.qty ?? 1);
        uomSel.value = cleanText(editCore.qtyUom ?? editCore.qty_uom ?? "EA") || "EA";
        statSel.value = cleanText(editCore.status || "").toUpperCase() || "ON_ORDER";
        awbIn.value = cleanText(editCore.awb ?? "");
        trackIn.value = cleanText(editCore.awbTrackingUrl ?? editCore.awb_tracking_url ?? "");
        notesIn.value = cleanText(editCore.notes ?? "");
        reqBy.value = editCore.requiredBy ? String(editCore.requiredBy).slice(0, 10) : "";
        eta.value = editCore.expectedArrival ? String(editCore.expectedArrival).slice(0, 10) : "";
        shipIn.value = editCore.shipDate ? String(editCore.shipDate).slice(0, 10) : "";
      }

      const saveBtn = el("button", {
        type: "button",
        class: "mt-4 px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg",
        text: isEdit ? "Save core" : "Create core shipment",
        onclick: async () => {
          const pid = Number(projSel.value);
          const msn = cleanText(msnIn.value);
          const offPn = cleanText(offPnIn.value).toUpperCase();
          if (!Number.isFinite(pid) || !msn || !offPn) {
            window.alert("Project, MSN and OFF unit PN are required.");
            return;
          }
          const body = {
            projectId: pid,
            msn,
            offUnitPn: offPn,
            offUnitSn: cleanText(offSnIn.value) || null,
            qty: Number(qtyIn.value) || 1,
            qtyUom: uomSel.value || "EA",
            status: statSel.value,
            awb: cleanText(awbIn.value) || null,
            awbTrackingUrl: cleanText(trackIn.value) || null,
            notes: cleanText(notesIn.value) || null,
            requiredBy: reqBy.value || null,
            expectedArrival: eta.value || null,
            shipDate: shipIn.value || null,
          };
          try {
            if (isEdit) await patchMaterialCore(editingId, body);
            else await postMaterialCore(body);
            const np = currentRouteParams();
            np.delete("edit");
            setMaterialsCoresRouteQuery(np);
            await initMaterialsCoresPage();
          } catch (e) {
            window.alert(cleanText(e?.message) || String(e));
          }
        },
      });

      const cancelBtn = isEdit
        ? el(
            "button",
            {
              type: "button",
              class: "mt-4 ml-2 px-4 py-2 text-sm rounded-lg border border-gray-300",
              text: "Cancel edit",
              onclick: () => {
                const np = currentRouteParams();
                np.delete("edit");
                setMaterialsCoresRouteQuery(np);
                initMaterialsCoresPage().catch(() => {});
              },
            },
            []
          )
        : el("span");

      const fileIn = !isEdit
        ? el("span")
        : el("input", {
            type: "file",
            id: "v2-mcores-att",
            class: "block text-xs text-gray-600 mt-2",
          });
      const attList = !isEdit
        ? el("span")
        : el("div", { id: `v2-mcores-att-${editingId}`, class: "mt-2 flex flex-wrap gap-2" });

      if (isEdit) {
        (async () => {
          try {
            const list = await fetchJson(apiUrl(`/api/materials/cores/${encodeURIComponent(editingId)}/attachments`)).catch(() => null);
            const aa = Array.isArray(list?.attachments) ? list.attachments : [];
            attList.replaceChildren(
              ...(aa.length
                ? aa.map((a) => {
                    const lab = cleanText(a.originalName || a.original_name || "");
                    const href = apiUrl(`/api/materials/attachments/${encodeURIComponent(a.id)}/download`);
                    const ic = materialAttachmentIconLabel(lab);
                    const cl = `${materialAttachmentBadgeClass(lab, a.mime)} inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold uppercase`;
                    return el("a", { href, target: "_blank", rel: "noopener", class: `no-underline ${cl}`, title: lab }, [`${ic} · ${lab || "File"}`]);
                  })
                : [el("span", { class: "text-[11px] text-gray-400", text: "No attachments yet." })])
            );
          } catch {
            attList.replaceChildren(el("span", { class: "text-xs text-red-600", text: "Could not load attachments." }));
          }
        })();

        fileIn.onchange = async () => {
          const file = fileIn.files?.[0];
          if (!file || file.size > 12 * 1024 * 1024) {
            window.alert("Choose a file up to ~12 MB.");
            return;
          }
          try {
            const b64url = await new Promise((resolve, reject) => {
              const r = new FileReader();
              r.onload = () => resolve(String(r.result || ""));
              r.onerror = reject;
              r.readAsDataURL(file);
            });
            const mime = cleanText(file.type) || "application/octet-stream";
            await fetchJson(apiUrl(`/api/materials/cores/${encodeURIComponent(editingId)}/attachments`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ fileName: file.name, mime, base64: b64url.replace(/^[^,]+,/, "") }),
            });
            window.alert("Attachment uploaded.");
            await initMaterialsCoresPage();
          } catch (e) {
            window.alert(cleanText(e?.message) || String(e));
          }
        };
      }

      return el(
        "div",
        { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" },
        [
          ...(hdrMiss ? [hdrMiss] : []),
          hdr,
          el("div", { class: "grid gap-3 sm:grid-cols-2 lg:grid-cols-3" }, [
            el("label", { class: "block text-xs text-gray-600" }, ["Project *", projSel]),
            el("label", { class: "block text-xs text-gray-600" }, ["MSN *", msnIn]),
            el("label", { class: "block text-xs text-gray-600" }, ["OFF unit PN *", offPnIn]),
            el("label", { class: "block text-xs text-gray-600" }, ["OFF unit SN", offSnIn]),
            el("label", { class: "block text-xs text-gray-600" }, ["Qty", qtyIn]),
            el("label", { class: "block text-xs text-gray-600" }, ["UOM", uomSel]),
            el("label", { class: "block text-xs text-gray-600" }, ["Status", statSel]),
            el("label", { class: "block text-xs text-gray-600" }, ["Required by", reqBy]),
            el("label", { class: "block text-xs text-gray-600" }, ["Expected arrival", eta]),
            el("label", { class: "block text-xs text-gray-600" }, ["Ship date", shipIn]),
            el("label", { class: "block text-xs text-gray-600 sm:col-span-2 lg:col-span-3" }, ["AWB", awbIn]),
            el("label", { class: "block text-xs text-gray-600 sm:col-span-2 lg:col-span-3" }, ["Tracking URL", trackIn]),
            el("label", { class: "block text-xs text-gray-600 sm:col-span-2 lg:col-span-3" }, ["Notes", notesIn]),
          ]),
          saveBtn,
          cancelBtn,
          ...(isEdit
            ? [
                el("div", { class: "mt-4 pt-4 border-t border-gray-100" }, [
                  el("div", { class: "text-xs font-medium text-gray-700 mb-1", text: "Attachments" }),
                  fileIn,
                  attList,
                ]),
              ]
            : []),
        ].filter(Boolean)
      );
    };

    const head = el("tr", { class: "" }, [
      el("th", { class: thCls, text: "Project" }),
      el("th", { class: thCls, text: "MSN" }),
      el("th", { class: thCls, text: "OFF PN" }),
      el("th", { class: thCls, text: "OFF SN" }),
      el("th", { class: thCls, text: "Qty+UOM" }),
      el("th", { class: thCls, text: "Status" }),
      el("th", { class: thCls, text: "AWB" }),
      el("th", { class: thCls, text: "Req by / ETA / Ship" }),
      el("th", { class: thCls, text: "Linked part" }),
      el("th", { class: thCls, text: "" }),
    ]);

    const rows = cores.map((c) => {
      const linked = Number(c.linkedFromPartId ?? c.linked_from_part_id);
      const hasLinked = Number.isFinite(linked);
      const qty = String(c.qty ?? 1);
      return el(
        "tr",
        { class: "bg-white hover:bg-gray-50/80 border-b border-gray-50 text-xs" },
        [
          el("td", { class: `${tdCls} font-medium text-gray-800` }, [cleanText(c.projectName ?? c.project_name) || "—"]),
          el("td", { class: `${tdCls} font-mono` }, [materialsDashForOff(c.msn)]),
          el("td", { class: `${tdCls} font-mono` }, [materialsDashForOff(c.offUnitPn ?? c.off_unit_pn)]),
          el("td", { class: `${tdCls} font-mono` }, [materialsDashForOff(c.offUnitSn ?? c.off_unit_sn)]),
          el("td", { class: `${tdCls} font-mono` }, [`${qty} ${cleanText(c.qtyUom ?? c.qty_uom ?? "EA")}`]),
          el("td", { class: `${tdCls} font-medium` }, [materialStatusLabel(c.status)]),
          el("td", { class: `${tdCls} font-mono` }, [materialsDashForOff(c.awb)]),
          el("td", { class: `${tdCls} text-gray-600` }, [
            `${formatMaterialsDateDdMmmYyyy(c.requiredBy ?? c.required_by)} / `,
            `${formatMaterialsDateDdMmmYyyy(c.expectedArrival ?? c.expected_arrival)} / `,
            `${formatMaterialsDateDdMmmYyyy(c.shipDate ?? c.ship_date)}`,
          ]),
          el("td", { class: tdCls }, [
            hasLinked ? el("a", { href: `#/materials/part/${linked}`, class: "text-dae hover:underline", text: `#${linked}` }) : el("span", { class: "text-gray-300", text: "—" }),
          ]),
          el("td", { class: `${tdCls} whitespace-nowrap` }, [
            el(
              "button",
              {
                type: "button",
                class: "text-dae hover:underline mr-3 bg-transparent border-0 p-0 cursor-pointer text-xs font-medium",
                text: "Edit",
                onclick: () => {
                  const np = currentRouteParams();
                  np.set("edit", String(c.id));
                  setMaterialsCoresRouteQuery(np);
                },
              },
              []
            ),
          ]),
        ]
      );
    });

    host.replaceChildren(
      el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-wrap gap-4 items-end" }, [
        el("label", { class: "text-xs text-gray-600 flex flex-col gap-1" }, ["Filter project", projectFilter]),
      ]),
      formCard(),
      el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto" }, [
        el(
          "table",
          { class: "w-full border-collapse min-w-[960px]" },
          [
            el("thead", {}, [head]),
            el(
              "tbody",
              {},
              rows.length
                ? rows
                : [el("tr", {}, [el("td", { colspan: "10", class: "px-4 py-8 text-center text-gray-500 text-sm", text: "No core shipments recorded yet." })])]
            ),
          ]
        ),
      ])
    );

    queueMicrotask(() => {
      if (filterPid) projectFilter.value = String(filterPid);
    });
  } catch (e) {
    host.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
  }
}

function renderMaterialsProjectEditorPage({ projectId }) {
  const isNew = projectId == null || projectId === "new";
  return el("div", { class: "space-y-4 max-w-6xl" }, [
    el("div", { class: "flex flex-wrap items-center justify-between gap-3" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-900", text: isNew ? "New materials project" : `Edit project` }),
        el("p", { class: "mt-1 text-sm text-gray-500", text: "Enter a name and click Create project. After it exists, add fleet MSNs below and use Save row on each line. Position 1 / 2: ESN, TSN (hours, 2 decimals, FH), CSN (cycles, FC)." }),
      ]),
      el("a", { href: "#/materials/projects", class: "text-dae hover:underline text-sm font-medium", text: "← All projects" }),
    ]),
    el("div", { id: "v2-materials-project-editor", class: "space-y-4" }, [el("div", { class: "text-sm text-gray-600", text: "Loading…" })]),
  ]);
}

async function initMaterialsProjectEditorPage(rawId) {
  const host = document.getElementById("v2-materials-project-editor");
  if (!host) return;
  const isNew = rawId == null || rawId === "new";

  const msnOptions = await fetchMaterialsMsnOptions();
  let proj = null;
  if (!isNew) {
    const projects = await fetchMaterialsProjects();
    proj = projects.find((p) => Number(p.id) === Number(rawId));
    if (!proj) {
      host.replaceChildren(el("div", { class: "text-red-700 text-sm", text: "Project not found." }));
      return;
    }
  }

  const nameIn = el("input", {
    type: "text",
    id: "v2-mpr-name",
    class: "w-full max-w-lg rounded-lg border border-gray-300 px-3 py-2 text-sm",
    value: proj ? cleanText(proj.name) : "",
    placeholder: "Project name",
  });
  const notesIn = el("textarea", {
    id: "v2-mpr-notes",
    class: "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm min-h-[5rem]",
    value: proj ? cleanText(proj.notes || "") : "",
    placeholder: "Notes (optional)",
  });
  const activeIn = el("input", {
    type: "checkbox",
    id: "v2-mpr-active",
    class: "rounded border-gray-300",
    checked: proj ? proj.isActive !== false : true,
  });

  const saveMeta = async () => {
    const name = cleanText(nameIn.value);
    if (!name) {
      window.alert("Project name is required.");
      return;
    }
    try {
      if (isNew) {
        const r = await postMaterialProject({ name, notes: cleanText(notesIn.value), isActive: activeIn.checked });
        const id = r?.project?.id;
        if (id) {
          window.location.hash = `#/materials/project/${id}`;
        } else {
          window.alert(cleanText(r?.error) || "Create failed — no project id returned.");
        }
      } else {
        await patchMaterialProject(proj.id, {
          name,
          notes: cleanText(notesIn.value),
          isActive: activeIn.checked,
        });
        await initMaterialsProjectEditorPage(String(proj.id));
      }
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    }
  };

  const acList = proj && Array.isArray(proj.aircraft) ? proj.aircraft : [];

  const headerCells = [
    "MSN",
    "Location",
    "Pos 1 ESN",
    "Pos 1 TSN (FH)",
    "Pos 1 CSN (FC)",
    "Pos 2 ESN",
    "Pos 2 TSN (FH)",
    "Pos 2 CSN (FC)",
    "",
  ];

  const renderAcRow = (a) => {
    const loc = el("input", {
      type: "text",
      class: "w-full min-w-[6rem] rounded border border-gray-200 px-2 py-1 text-xs",
      value: cleanText(a.location ?? ""),
      placeholder: "Location",
    });
    const e1 = el("input", {
      type: "text",
      class: "w-full min-w-[5rem] rounded border border-gray-200 px-2 py-1 text-xs font-mono",
      value: cleanText(a.esnPos1 ?? a.eng1Esn ?? a.eng1_esn ?? ""),
    });
    const t1 = el("input", {
      type: "number",
      step: "0.01",
      class: "w-24 rounded border border-gray-200 px-2 py-1 text-xs text-right font-mono",
      value: a.tsnPos1 != null && a.tsnPos1 !== "" ? String(a.tsnPos1) : "",
    });
    const c1 = el("input", {
      type: "number",
      step: "1",
      class: "w-20 rounded border border-gray-200 px-2 py-1 text-xs text-right font-mono",
      value: a.csnPos1 != null && a.csnPos1 !== "" ? String(a.csnPos1) : "",
    });
    const e2 = el("input", {
      type: "text",
      class: "w-full min-w-[5rem] rounded border border-gray-200 px-2 py-1 text-xs font-mono",
      value: cleanText(a.esnPos2 ?? a.eng2Esn ?? a.eng2_esn ?? ""),
    });
    const t2 = el("input", {
      type: "number",
      step: "0.01",
      class: "w-24 rounded border border-gray-200 px-2 py-1 text-xs text-right font-mono",
      value: a.tsnPos2 != null && a.tsnPos2 !== "" ? String(a.tsnPos2) : "",
    });
    const c2 = el("input", {
      type: "number",
      step: "1",
      class: "w-20 rounded border border-gray-200 px-2 py-1 text-xs text-right font-mono",
      value: a.csnPos2 != null && a.csnPos2 !== "" ? String(a.csnPos2) : "",
    });
    const saveRow = async () => {
      try {
        await postMaterialProjectAircraft(proj.id, {
          msn: a.msn,
          sortOrder: a.sortOrder || 0,
          location: cleanText(loc.value),
          esnPos1: cleanText(e1.value),
          tsnPos1: t1.value === "" ? null : Number(t1.value),
          csnPos1: c1.value === "" ? null : parseInt(c1.value, 10),
          esnPos2: cleanText(e2.value),
          tsnPos2: t2.value === "" ? null : Number(t2.value),
          csnPos2: c2.value === "" ? null : parseInt(c2.value, 10),
        });
        await initMaterialsProjectEditorPage(rawId);
      } catch (e) {
        window.alert(cleanText(e?.message) || String(e));
      }
    };
    return el("tr", { class: "border-b border-gray-50 text-sm" }, [
      el("td", { class: "px-2 py-2 font-mono text-xs" }, [cleanText(a.msn)]),
      el("td", { class: "px-2 py-2" }, [loc]),
      el("td", { class: "px-2 py-2" }, [e1]),
      el("td", { class: "px-2 py-2" }, [t1]),
      el("td", { class: "px-2 py-2" }, [c1]),
      el("td", { class: "px-2 py-2" }, [e2]),
      el("td", { class: "px-2 py-2" }, [t2]),
      el("td", { class: "px-2 py-2" }, [c2]),
      el("td", { class: "px-2 py-2 whitespace-nowrap" }, [
        el(
          "button",
          {
            type: "button",
            class: "text-dae hover:underline text-xs mr-2",
            text: "Save row",
            onclick: saveRow,
          },
          []
        ),
        el(
          "button",
          {
            type: "button",
            class: "text-red-600 hover:underline text-xs",
            text: "Remove",
            onclick: async () => {
              if (!window.confirm("Remove this MSN from the project?")) return;
              await deleteMaterialProjectAircraft(proj.id, a.id);
              await initMaterialsProjectEditorPage(rawId);
            },
          },
          []
        ),
      ]),
    ]);
  };

  let addRow = el("div", { class: "flex flex-wrap gap-2 items-end mt-2" });
  if (!isNew && proj) {
    const msnPick = el(
      "select",
      {
        id: "v2-mpr-new-msn",
        class: "rounded-lg border border-gray-300 text-sm px-2 py-2 font-mono min-w-[8rem]",
      },
      [el("option", { value: "", text: "Add MSN…" }), ...msnOptions.map((m) => el("option", { value: m, text: m }))]
    );
    addRow = el("div", { class: "flex flex-wrap gap-2 items-end mt-2" }, [
      msnPick,
      el("button", {
        type: "button",
        class: "px-3 py-2 text-sm bg-dae text-white rounded-lg",
        text: "Add to project",
        onclick: async () => {
          const m = cleanText(msnPick.value);
          if (!m) return;
          try {
            await postMaterialProjectAircraft(proj.id, { msn: m });
            await initMaterialsProjectEditorPage(rawId);
          } catch (e) {
            window.alert(cleanText(e?.message) || String(e));
          }
        },
      }),
    ]);
  }

  const metaCard = el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3" }, [
    el("label", { class: "block text-sm font-medium text-gray-700" }, ["Name *", nameIn]),
    el("label", { class: "block text-sm font-medium text-gray-700 mt-3" }, ["Notes", notesIn]),
    el("label", { class: "flex items-center gap-2 mt-3 text-sm text-gray-700" }, [activeIn, " Active (included in materials dashboard)"]),
    el("button", {
      type: "button",
      class: "mt-2 px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg",
      text: isNew ? "Create project" : "Save project",
      onclick: saveMeta,
    }),
  ]);

  const tableCard =
    !isNew && proj
      ? el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto p-4" }, [
          el("h2", { class: "text-sm font-semibold text-gray-900 mb-3", text: "Aircraft / engines" }),
          el("table", { class: "w-full text-left border-collapse min-w-[900px]" }, [
            el(
              "thead",
              {},
              [
                el(
                  "tr",
                  { class: "text-xs text-white uppercase bg-dae" },
                  headerCells.map((h) => el("th", { class: "px-2 py-2 whitespace-nowrap", text: h }))
                ),
              ]
            ),
            el("tbody", {}, acList.length ? acList.map(renderAcRow) : [el("tr", {}, [el("td", { colspan: "9", class: "px-4 py-6 text-gray-500 text-sm", text: "No MSNs yet. Add one from the fleet list." })])]),
          ]),
          addRow,
        ])
      : el("div", { class: "text-sm text-gray-500" }, [
          "After you create the project, you can attach MSNs and engine data.",
        ]);

  host.replaceChildren(metaCard, tableCard);
}

async function loadStatusChanges({ sinceIso, hours, limit = 200 } = {}) {
  const qp = new URLSearchParams();
  if (Number.isFinite(hours) && hours > 0) {
    qp.set("hours", String(hours));
  } else if (sinceIso) {
    qp.set("since", sinceIso);
  }
  qp.set("limit", String(limit));
  const resp = await fetchJson(apiUrl(`/api/status-changes?${qp}`));
  return Array.isArray(resp?.changes) ? resp.changes : [];
}

async function loadTaskLogs({ limit = 200 } = {}) {
  const resp = await fetchJson(apiUrl(`/api/admin/task-logs?limit=${encodeURIComponent(String(limit))}`));
  return Array.isArray(resp?.logs) ? resp.logs : [];
}

function isoNowMinusHours(hours) {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
}

/** Map `status_change_events.created_by` to a short operator-facing label. */
function formatStatusChangeTrigger(createdBy) {
  const raw = cleanText(createdBy);
  if (!raw) return "Automatic — system";
  const lower = raw.toLowerCase();
  if (lower === "system") {
    return "Automatic — FlightAware / flight sync";
  }
  if (lower === "cron") {
    return "Automatic — cron / scheduled job";
  }
  if (/^automatic/i.test(raw)) {
    return raw;
  }
  return `User — ${raw}`;
}

function formatShortTime(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(ts));
  } catch {
    return iso;
  }
}

let absoluteDisplayFormatter;

function formatDisplayTimestamp(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  try {
    if (!absoluteDisplayFormatter) {
      absoluteDisplayFormatter = new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    }
    return absoluteDisplayFormatter.format(date);
  } catch {
    try {
      return date.toLocaleString();
    } catch {
      return date.toISOString();
    }
  }
}

const MANUAL_FLAG_KIND_LABELS = {
  landing: "a landing",
  takeoff: "a takeoff",
  airborne_now: "the aircraft airborne",
  result_unknown: "an unconfirmed flight (result unknown)",
  position: "a position report",
};

/** Amber pill: FA/FR24 shows activity AFTER the FM manual entry, but not clear enough to replace it. */
function manualFlagPill(r) {
  const f = r?.manualFlag || {};
  const what = MANUAL_FLAG_KIND_LABELS[cleanText(f.kind)] || "activity";
  const where = cleanText(f.location) ? ` at ${cleanText(f.location)}` : "";
  const when = formatDisplayTimestamp(f.at);
  const manualWhen = formatDisplayTimestamp(r?.manualUpdatedAt);
  const manualWho = cleanText(r?.manualUpdatedBy);
  const title =
    `${cleanText(f.source) || "Live data"} shows ${what}${where}${when ? ` on ${when}` : ""}, ` +
    `after the manual entry${manualWhen ? ` of ${manualWhen}` : ""}${manualWho ? ` by ${manualWho}` : ""}. ` +
    "The manual entry is kept — check with FM and update or clear it.";
  return el("span", {
    class:
      "ml-1 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1 text-[10px] font-semibold uppercase text-amber-800 whitespace-nowrap",
    title,
    text: "New activity?",
  });
}

const CRON_LOG_TZ = "America/New_York";

function formatCronLogDateGroup(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return "Other / no timestamp";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: CRON_LOG_TZ,
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(ts));
  } catch {
    return iso;
  }
}

function formatCronLogTimeOnly(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: CRON_LOG_TZ,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(new Date(ts));
  } catch {
    return iso;
  }
}

function cronLogDateKey(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: CRON_LOG_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(ts));
  } catch {
    return "";
  }
}

/** Parse server log tail into rows for a table (newest-first sort applied separately). */
function parseCronFileLogTail(raw) {
  const entries = [];
  let pending = null;
  const flushPending = () => {
    if (pending) {
      entries.push(pending);
      pending = null;
    }
  };
  for (const line of String(raw || "").split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const session = /^===\s*(.+?)\s*===$/.exec(trimmed);
    if (session) {
      flushPending();
      entries.push({ ts: "", kind: "session", message: session[1], raw: line });
      continue;
    }
    let ts = "";
    let rest = trimmed;
    let m = /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\s*(.*)$/.exec(trimmed);
    if (!m) m = /^\[(\d{4}-\d{2}-\d{2}T[\d:.]+Z?)\]\s*(.*)$/.exec(trimmed);
    if (m) {
      flushPending();
      ts = m[1];
      rest = m[2] || "";
      const tag = /^\[([^\]]+)\]\s*(.*)$/.exec(rest);
      const kind = tag ? tag[1] : "";
      const message = tag ? tag[2] : rest;
      entries.push({ ts, kind, message: message || rest, raw: line });
    } else if (pending) {
      pending.message = `${pending.message}\n${trimmed}`;
      pending.raw = `${pending.raw}\n${line}`;
    } else {
      pending = { ts: "", kind: "", message: trimmed, raw: line };
    }
  }
  flushPending();
  return entries;
}

function sortCronLogEntriesNewestFirst(entries) {
  return [...entries].sort((a, b) => {
    const ta = Date.parse(a.ts) || 0;
    const tb = Date.parse(b.ts) || 0;
    if (tb !== ta) return tb - ta;
    if (a.kind === "session" && b.kind !== "session") return -1;
    if (b.kind === "session" && a.kind !== "session") return 1;
    return 0;
  });
}

function renderCronLogTable(entries, { emptyMessage = "Log is empty." } = {}) {
  const sorted = sortCronLogEntriesNewestFirst(entries);
  if (!sorted.length) {
    return el("div", { class: "text-sm text-gray-600 py-4 text-center", text: emptyMessage });
  }
  const tbody = el("tbody", {});
  let lastDateKey = null;
  for (const row of sorted) {
    if (row.ts) {
      const dk = cronLogDateKey(row.ts);
      if (dk && dk !== lastDateKey) {
        lastDateKey = dk;
        tbody.appendChild(
          el("tr", { class: "bg-slate-100" }, [
            el("td", {
              class: "px-3 py-2 text-xs font-semibold text-slate-800",
              colspan: "3",
              text: formatCronLogDateGroup(row.ts),
            }),
          ])
        );
      }
    }
    const isSession = row.kind === "session";
    tbody.appendChild(
      el("tr", { class: isSession ? "bg-amber-50" : "hover:bg-gray-50" }, [
        el("td", {
          class: "px-3 py-1.5 text-xs font-mono text-gray-600 whitespace-nowrap align-top w-[7.5rem]",
          text: row.ts ? formatCronLogTimeOnly(row.ts) : "—",
        }),
        el("td", {
          class: "px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap align-top w-[8rem]",
          text: isSession ? "Run" : cleanText(row.kind) || "—",
        }),
        el("td", {
          class: "px-3 py-1.5 text-xs text-gray-800 whitespace-pre-wrap break-words align-top",
          text: cleanText(row.message) || "—",
        }),
      ])
    );
  }
  return el("div", { class: "overflow-x-auto" }, [
    el("table", { class: "w-full text-left border-collapse" }, [
      el("thead", { class: "sticky top-0 z-10 bg-gray-100 shadow-sm" }, [
        el("tr", {}, [
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "Time (ET)" }),
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "Type" }),
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "Message" }),
        ]),
      ]),
      tbody,
    ]),
  ]);
}

function renderCronHistoryTable(rows, { emptyMessage = "No runs recorded yet for this job." } = {}) {
  const sorted = [...rows].sort((a, b) => {
    const ta = Date.parse(a.created_at || a.createdAt || "") || 0;
    const tb = Date.parse(b.created_at || b.createdAt || "") || 0;
    return tb - ta;
  });
  if (!sorted.length) {
    return el("div", { class: "text-sm text-gray-600 py-4 text-center", text: emptyMessage });
  }
  const tbody = el("tbody", {});
  let lastDateKey = null;
  for (const x of sorted) {
    const iso = x?.created_at || x?.createdAt || "";
    const dk = cronLogDateKey(iso);
    if (dk && dk !== lastDateKey) {
      lastDateKey = dk;
      tbody.appendChild(
        el("tr", { class: "bg-slate-100" }, [
          el("td", {
            class: "px-3 py-2 text-xs font-semibold text-slate-800",
            colspan: "4",
            text: formatCronLogDateGroup(iso),
          }),
        ])
      );
    }
    const rec = x?.details?.recipients || {};
    const toList = rec?.to ? (Array.isArray(rec.to) ? rec.to : [rec.to]).join(", ") : "";
    const ccList = rec?.cc ? (Array.isArray(rec.cc) ? rec.cc : [rec.cc]).join(", ") : "";
    const bccList = rec?.bcc ? (Array.isArray(rec.bcc) ? rec.bcc : [rec.bcc]).join(", ") : "";
    const recParts = [
      toList ? `To: ${toList}` : "",
      ccList ? `CC: ${ccList}` : "",
      bccList ? `BCC: ${bccList}` : "",
    ].filter(Boolean);
    tbody.appendChild(
      el("tr", { class: "hover:bg-gray-50" }, [
        el("td", {
          class: "px-3 py-1.5 text-xs font-mono text-gray-600 whitespace-nowrap align-top",
          text: formatShortTime(iso) || "—",
        }),
        el("td", {
          class: "px-3 py-1.5 text-xs text-gray-800 align-top",
          text: cleanText(x?.triggered_by || x?.triggeredBy) || "system",
        }),
        el("td", {
          class: "px-3 py-1.5 text-xs text-gray-800 align-top",
          text: cleanText(x?.summary) || "—",
        }),
        el("td", {
          class: "px-3 py-1.5 text-xs text-gray-700 whitespace-pre-wrap break-words align-top",
          text: recParts.join("\n") || "—",
        }),
      ])
    );
  }
  return el("div", { class: "overflow-x-auto" }, [
    el("table", { class: "w-full text-left border-collapse" }, [
      el("thead", { class: "sticky top-0 z-10 bg-gray-100 shadow-sm" }, [
        el("tr", {}, [
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "When (ET)" }),
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "By" }),
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "Action" }),
          el("th", { class: "px-3 py-2 text-xs font-semibold text-gray-700", text: "Recipients" }),
        ]),
      ]),
      tbody,
    ]),
  ]);
}

function parseDateOnly(value) {
  const v = cleanText(value);
  if (!v) return null;
  // Accept YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const ts = Date.parse(`${v}T00:00:00Z`);
    return Number.isFinite(ts) ? ts : null;
  }
  const ts = Date.parse(v);
  return Number.isFinite(ts) ? ts : null;
}

function formatDateDdMmmYy(value) {
  const ts = parseDateOnly(value);
  if (!Number.isFinite(ts)) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "short",
      year: "2-digit",
    }).format(new Date(ts));
  } catch {
    return String(value);
  }
}

/** Materials tracker: display timestamps and date fields as `02-May-2026` */
function formatMaterialsDateDdMmmYyyy(value) {
  if (value == null || value === "") return "—";
  const ts = Date.parse(String(value));
  if (!Number.isFinite(ts)) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(ts));
  } catch {
    return String(value);
  }
}

function materialAttachmentBadgeClass(originalName = "", mime = "") {
  const n = `${cleanText(originalName)} ${cleanText(mime)}`.toLowerCase();
  if (/\.pdf$|pdf/.test(n)) return "bg-red-50 text-red-800 border-red-100";
  if (/\.docx?$|word|officedocument\.wordprocessing/.test(n)) return "bg-blue-50 text-blue-800 border-blue-100";
  if (/\.xlsx?$|spreadsheet/.test(n)) return "bg-emerald-50 text-emerald-800 border-emerald-100";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

function materialAttachmentIconLabel(originalName = "", mime = "") {
  const n = `${cleanText(originalName)} ${cleanText(mime)}`.toLowerCase();
  if (/\.pdf$|pdf/.test(n)) return "PDF";
  if (/\.docx?$|word/.test(n)) return "DOC";
  if (/\.xlsx?$|spreadsheet/.test(n)) return "XLS";
  return "FILE";
}

function isDateOverdue(value) {
  const ts = parseDateOnly(value);
  if (!Number.isFinite(ts)) return false;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return ts < todayUtc;
}

function formatTog(minutes) {
  const m = Number(minutes);
  if (!Number.isFinite(m) || m < 0) return "—";
  if (m === 0) return "AIRBORNE";
  if (m < 60) {
    const r = Math.round(m);
    if (r === 0 && m > 0) return "<1m";
    return `${r}m`;
  }

  const hours = Math.floor(m / 60);
  const remMins = Math.floor(m % 60);
  if (m < 24 * 60) {
    return remMins ? `${hours}h ${remMins}m` : `${hours}h`;
  }

  const days = Math.floor(m / (24 * 60));
  if (m < 7 * 24 * 60) {
    return `${days}d`;
  }

  const weeks = Math.floor(days / 7);
  const remDays = days % 7;
  if (m < 30 * 24 * 60) {
    return remDays ? `${weeks}w ${remDays}d` : `${weeks}w`;
  }

  const months = Math.floor(days / 30);
  const remDaysAfterMonths = days % 30;
  const remWeeks = Math.floor(remDaysAfterMonths / 7);
  if (m < 365 * 24 * 60) {
    return remWeeks ? `${months}mo ${remWeeks}w` : `${months}mo`;
  }

  const years = Math.floor(days / 365);
  const remMonths = Math.floor((days % 365) / 30);
  return remMonths ? `${years}yr ${remMonths}mo` : `${years}yr`;
}

function normaliseStatusText(value) {
  return cleanText(value).toUpperCase();
}

/** Format recipients from task log details (array or { to, cc, bcc }). */
function formatTaskLogRecipients(raw) {
  if (!raw) return "";
  if (Array.isArray(raw)) return raw.filter(Boolean).join(", ");
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const buckets = [];
    for (const key of ["to", "cc", "bcc"]) {
      const v = raw[key];
      if (Array.isArray(v)) buckets.push(...v.filter(Boolean));
    }
    return [...new Set(buckets.map((x) => String(x).trim()))].filter(Boolean).join(", ");
  }
  return cleanText(raw);
}

function buildActivityRows({ statusChanges = [], taskLogs = [] } = {}) {
  const events = [];

  for (const c of statusChanges || []) {
    const prev = normaliseStatusText(c.previousStatus) || "—";
    const next = normaliseStatusText(c.newStatus) || "—";
    if (prev === "—" && next === "—") continue;
    const reg = cleanText(c.registration) || "—";
    const messageParts = [`${reg}: ${prev} → ${next}`];
    if (cleanText(c.flightIdent)) messageParts.push(`Flight ${cleanText(c.flightIdent)}`);
    if (cleanText(c.createdBy)) messageParts.push(`by ${cleanText(c.createdBy)}`);
    events.push({
      ts: c.createdAt,
      msn: cleanText(c.msn) || "—",
      owner: cleanText(c.owner) || "—",
      tam: next,
      message: messageParts.join(" · "),
      kind: "status_change",
      raw: c,
    });
  }

  for (const t of taskLogs || []) {
    const type = cleanText(t.taskType || t.task_type).toLowerCase();
    const details = t.details && typeof t.details === "object" ? t.details : {};
    if (type === "xrm_import") {
      const who = cleanText(t.triggeredBy || t.triggered_by) || "admin";
      const stats = details.stats && typeof details.stats === "object" ? details.stats : {};
      const filename = cleanText(details.filename) || "";
      events.push({
        ts: t.createdAt || t.created_at,
        msn: "—",
        owner: "SYSTEM",
        tam: "XRM",
        message: [
          cleanText(t.summary) || (filename ? `XRM import: ${filename}` : "XRM fleet import"),
          stats.changeCount != null ? `${stats.changeCount} changes` : null,
          stats.newCount != null ? `${stats.newCount} new` : null,
          stats.removedCount != null ? `${stats.removedCount} removed` : null,
          details.applied != null ? `${details.applied} applied` : null,
          details.skipped != null ? `${details.skipped} skipped` : null,
          who ? `by ${who}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        kind: "xrm_import",
        details,
      });
      continue;
    }
    if (type === "db_backup") {
      const summary = cleanText(t.summary) || "";
      const status = cleanText(t.status) || "";
      const who = cleanText(t.triggeredBy || t.triggered_by) || "system";
      const fileHint =
        cleanText(details.fileName || details.filename) ||
        cleanText(details.size ? `size ${details.size}` : "") ||
        cleanText(details.mode ? `mode ${details.mode}` : "");
      events.push({
        ts: t.createdAt || t.created_at,
        msn: "—",
        owner: "SYSTEM",
        tam: "BACKUP",
        message: ["Database backup", status ? `(${status})` : "", summary, fileHint, who ? `by ${who}` : ""]
          .filter(Boolean)
          .join(" "),
        kind: "system",
      });
      continue;
    }
    if (type !== "email_report" && type !== "cron_run" && type !== "cron") continue;
    const recipients = formatTaskLogRecipients(details.recipients);
    const summary = cleanText(t.summary) || "";
    const status = cleanText(t.status) || "";
    const who = cleanText(t.triggeredBy || t.triggered_by) || "system";
    const msg = [
      type === "email_report" ? "Email report" : "Cron",
      status ? `(${status})` : "",
      summary,
      recipients ? `to ${recipients}` : "",
      who ? `by ${who}` : "",
    ]
      .filter(Boolean)
      .join(" ");
    events.push({
      ts: t.createdAt || t.created_at,
      msn: "—",
      owner: "SYSTEM",
      tam: type === "email_report" ? "EMAIL" : "CRON",
      message: msg,
      kind: "system",
    });
  }

  events.sort((a, b) => (Date.parse(b.ts || "") || 0) - (Date.parse(a.ts || "") || 0));
  return events;
}

function renderKpiCards(items) {
  return el("div", { class: "grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" }, items);
}

function card(title, value, subtitle, { hash = null } = {}) {
  const raw = value == null ? "—" : String(value);
  const valueNode =
    hash && typeof hash === "string"
      ? el("a", {
          href: hash.startsWith("#") ? hash : `#${hash}`,
          class: "mt-1 block text-2xl font-semibold text-dae hover:text-dae/90 hover:underline focus:outline-none focus:ring-2 focus:ring-dae/40 rounded",
          title: "Open list",
          text: raw,
        })
      : el("div", { class: "mt-1 text-2xl font-semibold text-gray-900", text: raw });
  return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
    el("div", { class: "text-sm font-medium text-gray-500", text: title }),
    valueNode,
    subtitle ? el("div", { class: "mt-1 text-xs text-gray-500", text: subtitle }) : null,
  ]);
}

function normaliseTam(value) {
  const v = cleanText(value).toUpperCase();
  if (v === "CHECK WITH DM") return "CHECK WITH FM";
  return v || "—";
}

const NO_FLIGHTS_FIVE_DAYS_LEGEND = NO_FLIGHTS_FIVE_DAYS_LEGEND_SHARED;

const FLEET_TAM_FILTER_OPTIONS = [
  "IN SERVICE",
  "AOG",
  "INCIDENT",
  "MAINTENANCE",
  "TRANSITION",
  "AWAITING OPERATOR FEEDBACK",
  "CHECK WITH FM",
  "STORAGE",
  "RUSSIA",
];

function parseCsvParam(params, key) {
  const raw = getParamText(params, key);
  if (!raw) return null;
  const parts = raw.split(",").map((s) => cleanText(s)).filter(Boolean);
  return parts.length ? parts : null;
}

function setCsvParam(params, key, values, allOptions) {
  const p = new URLSearchParams(params.toString());
  const list = Array.isArray(values) ? values.map((v) => cleanText(v)).filter(Boolean) : [];
  const all = Array.isArray(allOptions) ? allOptions.map((v) => cleanText(v)).filter(Boolean) : [];
  if (!list.length || (all.length && list.length >= all.length)) {
    p.delete(key);
  } else {
    p.set(key, list.join(","));
  }
  setHashParams(p);
}

function booleanFleetFlag(value) {
  if (value === true) return true;
  if (value === false || value == null) return false;
  const s = String(value).trim().toLowerCase();
  return s === "true" || s === "t" || s === "1" || s === "yes" || s === "y";
}

function isRussianFleetAircraft(aircraft) {
  return booleanFleetFlag(aircraft?.is_russian_fleet);
}

function matchesNoFlightsFiveDaysAircraft(aircraft) {
  const faEntry = lookupFaCacheEntry(aircraft, state.faCacheByIdent);
  const faInterp = buildNoFlightsFaInterp(aircraft, faEntry);
  return matchesNoFlightsFiveDays(aircraft, faInterp);
}

function fleetFlagToggle({ id, label, hint, checked }) {
  return el("div", { class: "flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2" }, [
    el("div", {}, [
      el("div", { class: "text-xs font-medium text-gray-600", text: label }),
      hint ? el("div", { class: "text-[11px] text-gray-500", text: hint }) : null,
    ]),
    el("label", { class: "inline-flex items-center cursor-pointer" }, [
      el("input", {
        id,
        type: "checkbox",
        class: "sr-only peer",
        checked: Boolean(checked),
      }),
      el("div", {
        class:
          "relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-dae/20 rounded-full peer peer-checked:bg-dae",
      }, [
        el("div", {
          class:
            "absolute top-[2px] left-[2px] bg-white border border-gray-200 rounded-full h-5 w-5 transition-all peer-checked:translate-x-full",
        }),
      ]),
    ]),
  ]);
}

function normaliseStatusForDisplay(value) {
  const v = cleanText(value).toUpperCase();
  if (!v || v === "—") return "—";
  if (v === "AIRBORNE") return "AIRBORNE";
  if (v === "IN SERVICE") return "IN SERVICE";
  // Allow our standard buckets only.
  if (/^\d+\s+DAY(S)?$/.test(v)) return v;
  if (/^\d+W$/.test(v)) return v;
  if (/^\d+MO$/.test(v)) return v;
  if (/^\d+YR$/.test(v)) return v;
  if (v === "UNKNOWN") return "UNKNOWN";
  return "—";
}

function commentTimestampMs(entry) {
  const ts = Date.parse(entry?.created_at || entry?.createdAt || "");
  return Number.isFinite(ts) ? ts : 0;
}

/** Newest comment first, then older — fleet grid, modal, and Excel export. */
function sortCommentsNewestFirst(items) {
  return (Array.isArray(items) ? items : [])
    .slice()
    .sort((a, b) => commentTimestampMs(b) - commentTimestampMs(a));
}

async function loadBulkComments(ids) {
  const list = Array.isArray(ids) ? ids.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0) : [];
  if (!list.length) {
    state.bulkCommentsByAircraftId = new Map();
    state.bulkCommentsKey = "";
    return { ok: true, loaded: 0 };
  }

  const sortedIds = list.slice().sort((a, b) => a - b);
  const key = sortedIds.join(",");
  if (state.bulkCommentsKey === key && state.bulkCommentsByAircraftId instanceof Map && state.bulkCommentsByAircraftId.size) {
    return { ok: true, loaded: state.bulkCommentsByAircraftId.size };
  }

  const resp = await fetchJson(apiUrl("/api/aircraft/comments/bulk"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids: sortedIds }),
  });

  const out = new Map();
  const raw = resp?.comments && typeof resp.comments === "object" ? resp.comments : {};
  for (const [idStr, entries] of Object.entries(raw)) {
    const id = Number(idStr);
    if (!Number.isFinite(id)) continue;
    const items = Array.isArray(entries) ? entries : [];
    out.set(
      id,
      sortCommentsNewestFirst(
        items
          .map((c) => ({
            id: cleanText(c?.id) || "",
            author: cleanText(c?.author) || "system",
            body: cleanText(c?.body) || "",
            created_at: cleanText(c?.created_at) || cleanText(c?.createdAt) || null,
          }))
          .filter((c) => c.body)
      )
    );
  }

  state.bulkCommentsByAircraftId = out;
  state.bulkCommentsKey = key;
  return { ok: true, loaded: out.size };
}

/** Full thread for fleet grid + Excel; same typography everywhere (see .actracker-v2-comments-* in v2/index.html). */
function renderFleetCommentsColumn(r) {
  const id = Number(r?.id);
  const preview = state.bulkCommentsByAircraftId instanceof Map ? state.bulkCommentsByAircraftId.get(id) : null;
  const items = sortCommentsNewestFirst(preview);
  const hasItems = items.length > 0;

  const blocks = [];
  if (hasItems) {
    for (const c of items) {
      const body = cleanText(c?.body);
      if (!body) continue;
      const author = cleanText(c?.author);
      const stamp = formatShortTime(c?.created_at) || "";
      const meta = [author, stamp].filter(Boolean).join(" · ");
      blocks.push(
        el("div", { class: "border-b border-gray-100 pb-1.5 mb-1.5 last:border-0 last:pb-0 last:mb-0" }, [
          el("div", { class: "actracker-v2-comments-body", text: body }),
          meta ? el("div", { class: "actracker-v2-comments-meta mt-0.5", text: meta }) : null,
        ])
      );
    }
  }

  if (!blocks.length) {
    const body =
      cleanText(r?.lastComment?.body) ||
      cleanText(r?.comments) ||
      (Number(r?.commentCount) > 0 ? "" : "");
    if (body) {
      const author = cleanText(r?.lastComment?.author) || "";
      const stamp = formatShortTime(r?.lastComment?.created_at) || "";
      const meta = [author, stamp].filter(Boolean).join(" · ");
      blocks.push(
        el("div", {}, [
          el("div", { class: "actracker-v2-comments-body", text: body }),
          meta ? el("div", { class: "actracker-v2-comments-meta mt-0.5", text: meta }) : null,
        ])
      );
    } else if (Number(r?.commentCount) > 0) {
      blocks.push(el("div", { class: "actracker-v2-comments-meta", text: "Loading comments…" }));
    }
  }

  const opener = el(
    "button",
    {
      type: "button",
      class: "mt-1 text-left text-dae hover:underline actracker-v2-comments-meta font-medium",
      title: "View / edit comments",
      onclick: () => openCommentsModal({ aircraftId: r.id, registration: r.reg, owner: r.owner, msn: r.msn, focusNew: true }),
      text: "Edit / add…",
    },
    []
  );

  return el(
    "div",
    { class: "actracker-v2-comments-stack w-full min-w-0 align-top" },
    blocks.length ? [...blocks, opener] : [el("div", { class: "actracker-v2-comments-body", text: "—" }), opener]
  );
}

const TAM_TABLE_CELL_CLASS = "actracker-tam-cell";
const COMMENTS_TABLE_CELL_CLASS = "actracker-v2-comments-cell";

function tamBadge(label) {
  const tam = normaliseTam(label);
  if (tam === "AWAITING OPERATOR FEEDBACK") {
    return el(
      "span",
      {
        class:
          "actracker-tam-badge actracker-tam-badge--stacked inline-flex flex-col items-center justify-center rounded-md px-1.5 py-1 text-[10px] leading-tight font-semibold border bg-yellow-50 text-yellow-800 border-yellow-200 text-center",
      },
      [el("span", { text: "AWAITING" }), el("span", { text: "OPERATOR" }), el("span", { text: "FEEDBACK" })]
    );
  }
  const base =
    "actracker-tam-badge inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium border whitespace-nowrap shrink-0 max-w-none";
  const map = {
    "IN SERVICE": "bg-green-50 text-green-700 border-green-200",
    AOG: "bg-red-50 text-red-700 border-red-200",
    MAINTENANCE: "bg-purple-50 text-purple-700 border-purple-200",
    MAINT: "bg-purple-50 text-purple-700 border-purple-200",
    TRANSITION: "bg-pink-50 text-pink-700 border-pink-200",
    INCIDENT: "bg-amber-50 text-amber-800 border-amber-200",
    STORAGE: "bg-gray-50 text-gray-700 border-gray-200",
    RUSSIA: "bg-blue-50 text-red-800 border-blue-300",
    "CHECK WITH FM": "bg-cyan-50 text-cyan-800 border-cyan-200",
    "CHECK WITH DM": "bg-cyan-50 text-cyan-800 border-cyan-200",
    AWAITING: "bg-yellow-50 text-yellow-800 border-yellow-200",
    "NON-DAE": "bg-orange-50 text-orange-900 border-orange-200",
    EMAIL: "bg-gray-50 text-gray-700 border-gray-200",
    CRON: "bg-gray-50 text-gray-700 border-gray-200",
  };
  const cls = map[tam] || "bg-gray-50 text-gray-700 border-gray-200";
  return el("span", { class: `${base} ${cls}`, text: tam });
}

/** Fleet match pill for the news incidents table (same shape as TAM badges). */
function avheraldFleetMatchBadge(classificationRaw) {
  const c = cleanText(classificationRaw).toUpperCase().replace(/-/g, "_");
  const base =
    "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium border whitespace-nowrap shrink-0 max-w-none";
  if (c === "DAE") {
    return el("span", { class: `${base} bg-red-50 text-red-800 border-red-200`, text: "DAE" });
  }
  if (c === "NON_DAE") {
    return el("span", { class: `${base} bg-slate-100 text-slate-700 border-slate-200`, text: "Non-DAE" });
  }
  if (c === "UNKNOWN") {
    return el("span", { class: `${base} bg-amber-50 text-amber-900 border-amber-200`, text: "Unknown" });
  }
  return el("span", { class: `${base} bg-gray-50 text-gray-600 border-gray-200`, text: c || "—" });
}

/** DAE-owned vs MAF-imported fleet for DAE-classified incidents (`fleet_source` from the API). */
function avheraldFleetSourceBadge(fleetSourceRaw) {
  const s = cleanText(fleetSourceRaw).toUpperCase();
  const base =
    "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-medium border whitespace-nowrap shrink-0 max-w-none";
  if (s === "DAE") {
    return el("span", { class: `${base} bg-red-50 text-red-800 border-red-200`, text: "DAE fleet" });
  }
  if (s === "MAF") {
    return el("span", { class: `${base} bg-sky-50 text-sky-800 border-sky-200`, text: "MAF fleet" });
  }
  return el("span", { class: "text-gray-400", text: "—" });
}

function formatNewsIncidentPrimaryDate(row) {
  const ev = cleanText(row?.event_date);
  const pub = cleanText(row?.publication_date);
  const head = (ev && /^\d{4}-\d{2}-\d{2}/.test(ev) ? ev : "") || (pub && /^\d{4}-\d{2}-\d{2}/.test(pub) ? pub : "");
  if (!head) return "—";
  const ts = parseDateOnly(head.slice(0, 10));
  if (ts == null) return "—";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(ts));
  } catch {
    return head.slice(0, 10);
  }
}

function formatHoursMinutesAgo(iso) {
  const ts = Date.parse(iso || "");
  if (!Number.isFinite(ts)) return "";
  const totalMin = Math.floor(Math.max(0, Date.now() - ts) / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0 && m > 0) return `${h} hour${h === 1 ? "" : "s"} ${m} minute${m === 1 ? "" : "s"} ago`;
  if (h > 0) return `${h} hour${h === 1 ? "" : "s"} ago`;
  if (m > 0) return `${m} minute${m === 1 ? "" : "s"} ago`;
  return "just now";
}

function aircraftForAvheraldIncident(row) {
  const id = Number(row?.matched_aircraft_id);
  if (!Number.isFinite(id) || id <= 0) return null;
  return (state.aircraft || []).find((a) => Number(a?.id) === id) || null;
}

function avheraldIncidentIsDae(row) {
  return cleanText(row?.dae_classification).toUpperCase() === "DAE";
}

function avheraldIncidentIdentLink(text, row, ac) {
  const label = cleanText(text) || "—";
  if (!ac || label === "—" || !avheraldIncidentIsDae(row)) {
    return el("span", { class: "font-mono text-sm text-gray-900", text: label });
  }
  return el("button", {
    type: "button",
    class: "font-mono text-sm text-dae hover:underline text-left",
    text: label,
    onclick: () =>
      openCommentsModal({
        aircraftId: ac.id,
        registration: cleanText(ac.registration) || cleanText(row.registration),
        owner: cleanText(ac.owner),
        msn: cleanText(ac.msn) || cleanText(row.msn),
      }),
  });
}

function avheraldIncidentFleetCells(row) {
  if (!avheraldIncidentIsDae(row)) {
    return {
      locationText: "—",
      location: el("span", { class: "text-sm text-gray-500", text: "—" }),
      tog: el("span", { class: "text-sm text-gray-500", text: "—" }),
      tam: el("span", { class: "text-sm text-gray-500", text: "—" }),
    };
  }
  const ac = aircraftForAvheraldIncident(row);
  if (!ac) {
    return {
      locationText: "—",
      location: el("span", { class: "text-sm text-gray-500", text: "—" }),
      tog: el("span", { class: "text-sm text-gray-500", text: "—" }),
      tam: el("span", { class: "text-sm text-gray-500", text: "—" }),
    };
  }
  const { location: loc, tog, tam } = avheraldAircraftFleetText(ac);
  return {
    locationText: loc,
    location: el("span", { class: "text-sm text-gray-800", text: loc }),
    tog: el("span", { class: "text-sm text-gray-800 whitespace-nowrap", text: tog }),
    tam: tamBadge(tam),
  };
}

async function exportAvheraldIncidentsToExcel() {
  const btn = document.getElementById("v2-avherald-export-btn");
  const status = document.getElementById("v2-avherald-export-status");
  const params = currentRouteParams();
  const fromForm = (id, key) => cleanText(document.getElementById(id)?.value ?? params.get(key));
  const { sortBy, sortDir } = getAvheraldIncidentsSortFromParams(params);
  const query = {
    classification: fromForm("v2-avherald-class", "classification").toUpperCase(),
    days: fromForm("v2-avherald-days", "days") || "365",
    operator: fromForm("v2-avherald-operator", "operator"),
    q: fromForm("v2-avherald-q", "q"),
    sortBy,
    sortDir,
  };
  const fleetByAircraftId = {};
  for (const ac of state.aircraft || []) {
    const id = Number(ac?.id);
    if (!Number.isFinite(id) || id <= 0) continue;
    try {
      fleetByAircraftId[id] = avheraldAircraftFleetText(ac);
    } catch {
      /* leave the row as "—" like the table */
    }
  }
  if (btn) btn.disabled = true;
  if (status) status.textContent = "Building Excel…";
  try {
    const response = await fetch(apiUrl("/api/avherald-incidents/export.xlsx"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, fleetByAircraftId, tzOffsetMinutes: new Date().getTimezoneOffset() }),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(cleanText(payload?.error) || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const cd = response.headers.get("Content-Disposition") || "";
    const filename = /filename="([^"]+)"/.exec(cd)?.[1] || `incidents_${formatTimestampForFile()}.xlsx`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    if (status) status.textContent = `Downloaded ${filename}`;
  } catch (e) {
    if (status) status.textContent = `Export failed: ${cleanText(e?.message) || "unknown error"}`;
  } finally {
    if (btn) btn.disabled = false;
  }
}

/** Loc / TOG / TAM text for an incident's matched aircraft (shared by the table and the Excel export). */
function avheraldAircraftFleetText(ac) {
  const { faEntry, faInterp, togMinutes } = computeTableTimeOnGroundContext(ac);
  const location =
    cleanText(ac.manual_status_location) ||
    (faEntry?.status
      ? faLocationLabel({ status: faEntry.status, aircraft: ac, isAirborne: Boolean(faInterp?.airborne) })
      : "") ||
    cleanText(ac.last_known_location) ||
    cleanText(ac.location) ||
    "—";
  const tog = togMinutes == null ? "—" : formatTog(togMinutes);
  const tamRaw = cleanText(ac.tam_status) || "";
  const tam = tamRaw ? normaliseTam(tamRaw) : effectiveTamForFilters(ac);
  return { location, tog, tam };
}

function formatAvheraldIngestOutcome(status) {
  if (!status || typeof status !== "object") return "";
  const st = cleanText(status.state).toLowerCase();
  if (st === "running") return "Running incident daily check…";
  if (st === "error") return `Refresh failed: ${cleanText(status.error) || "Unknown error"}`;
  const dae = Number(status.insertedDae) || 0;
  const nonDae = Number(status.insertedNonDae) || 0;
  const unknown = Number(status.insertedUnknown) || 0;
  const total = Number(status.insertedTotal);
  const n = Number.isFinite(total) ? total : dae + nonDae + unknown;
  if (n === 0) return "No new incidents found.";
  const parts = [];
  if (dae > 0) parts.push(`${dae} DAE`);
  if (nonDae > 0) parts.push(`${nonDae} non-DAE`);
  if (unknown > 0) parts.push(`${unknown} unknown`);
  return `Added ${n} new incident${n === 1 ? "" : "s"} (${parts.join(", ")}).`;
}

async function refreshAvheraldIngestStatusUi() {
  const lastEl = document.getElementById("v2-avherald-last-check");
  const outcomeEl = document.getElementById("v2-avherald-refresh-outcome");
  const btn = document.getElementById("v2-avherald-refresh-btn");
  try {
    const resp = await fetchJson(apiUrl("/api/avherald-incidents/ingest-status"));
    const status = resp?.status && typeof resp.status === "object" ? resp.status : null;
    const checkIso = cleanText(status?.finishedAt) || cleanText(status?.startedAt) || cleanText(status?.updatedAt);
    if (lastEl) {
      const when = checkIso ? formatShortTime(checkIso) : "—";
      const ago = checkIso ? formatHoursMinutesAgo(checkIso) : "";
      lastEl.textContent = checkIso
        ? `Last check: ${when} ET (${ago})`
        : "Last check: not recorded yet";
    }
    if (outcomeEl && status) {
      const line = formatAvheraldIngestOutcome(status);
      if (line) {
        outcomeEl.textContent = line;
        outcomeEl.classList.remove("hidden");
        const st = cleanText(status.state).toLowerCase();
        outcomeEl.className =
          st === "error"
            ? "text-sm text-red-700"
            : st === "running"
            ? "text-sm text-amber-800"
            : "text-sm text-emerald-800";
      }
    }
    if (btn) btn.disabled = cleanText(status?.state).toLowerCase() === "running";
    return status;
  } catch {
    if (lastEl) lastEl.textContent = "Last check: unavailable";
    if (btn) btn.disabled = false;
    return null;
  }
}

function stopAvheraldRefreshPoll() {
  if (state.avheraldRefreshPoll) {
    clearInterval(state.avheraldRefreshPoll);
    state.avheraldRefreshPoll = null;
  }
}

function startAvheraldRefreshPoll({ onDone } = {}) {
  stopAvheraldRefreshPoll();
  state.avheraldRefreshPoll = setInterval(async () => {
    const status = await refreshAvheraldIngestStatusUi().catch(() => null);
    const st = cleanText(status?.state).toLowerCase();
    if (st && st !== "running") {
      stopAvheraldRefreshPoll();
      if (typeof onDone === "function") onDone(status);
    }
  }, 2500);
}

const AVHERALD_INCIDENT_SORT_KEYS = new Set([
  "event_date",
  "seen",
  "classification",
  "fleet",
  "type",
  "registration",
  "msn",
  "operator",
  "category",
  "synopsis",
]);

function getAvheraldIncidentsSortFromParams(params) {
  const p = params || currentRouteParams();
  let sortBy = cleanText(p.get("sort")).toLowerCase();
  if (!AVHERALD_INCIDENT_SORT_KEYS.has(sortBy)) sortBy = "event_date";
  const sortDir = cleanText(p.get("dir")) === "asc" ? "asc" : "desc";
  return { sortBy, sortDir };
}

function avheraldIncidentsSortTh(label, sortKey, sortBy, sortDir, extraClass = "") {
  const sortable = AVHERALD_INCIDENT_SORT_KEYS.has(sortKey);
  const arrow = sortable && sortBy === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const base = `px-3 py-2 ${extraClass}`.trim();
  return el("th", {
    class: `${base}${sortable ? " cursor-pointer select-none" : ""}`,
    text: `${label}${arrow}`,
    ...(sortable ? { "data-avherald-sort": sortKey, "data-avherald-label": label } : {}),
  });
}

function updateAvheraldIncidentsSortHeaderLabels() {
  const { sortBy, sortDir } = getAvheraldIncidentsSortFromParams();
  document.querySelectorAll("[data-avherald-sort]").forEach((th) => {
    const key = th.getAttribute("data-avherald-sort");
    const label = th.getAttribute("data-avherald-label") || cleanText(th.textContent).replace(/ [▲▼]$/, "");
    const arrow = sortBy === key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
    th.textContent = `${label}${arrow}`;
  });
}

function renderAvheraldClassificationCounts(stats, activeCls, onSelect) {
  const host = document.getElementById("v2-avherald-class-counts");
  if (!host) return;
  const cc = stats?.classificationCounts;
  if (!cc || typeof cc !== "object") {
    host.replaceChildren();
    return;
  }
  const active = cleanText(activeCls).toUpperCase().replace(/-/g, "_");
  const chips = [
    { key: "", label: "All", count: Number(cc.all) || 0, idle: "bg-white text-gray-800 border-gray-200", active: "bg-dae text-white border-dae" },
    { key: "DAE", label: "DAE", count: Number(cc.DAE) || 0, idle: "bg-red-50 text-red-800 border-red-200", active: "bg-red-700 text-white border-red-700" },
    { key: "NON_DAE", label: "Non-DAE", count: Number(cc.NON_DAE) || 0, idle: "bg-slate-100 text-slate-700 border-slate-200", active: "bg-slate-700 text-white border-slate-700" },
    { key: "UNKNOWN", label: "Unknown", count: Number(cc.UNKNOWN) || 0, idle: "bg-amber-50 text-amber-900 border-amber-200", active: "bg-amber-700 text-white border-amber-700" },
  ];
  host.replaceChildren(
    ...chips.map((chip) => {
      const isActive = chip.key === active;
      return el("button", {
        type: "button",
        class: `inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
          isActive ? chip.active : chip.idle
        }`,
        text: `${chip.label}: ${chip.count}`,
        onclick: () => {
          if (typeof onSelect === "function") onSelect(chip.key);
        },
      });
    })
  );
}

function formatAvheraldIncidentsStatsLine(stats, displayed) {
  if (!stats || typeof stats !== "object") return "";
  const t = Number(stats.totalTable);
  const m = Number(stats.matchedTotal);
  const d = Number(stats.days);
  const lim = Number(stats.limit);
  const trunc = !!stats.truncated;
  const page = Number(stats.page);
  const totalPages = Number(stats.totalPages);
  const pageSize = Number(stats.pageSize);
  if (!Number.isFinite(t) || !Number.isFinite(m)) return "";
  let line = `Database (all time): ${t} row(s). This view (last ${Number.isFinite(d) ? d : "?"} days + filters): ${m} match${m === 1 ? "" : "es"}.`;
  if (Number.isFinite(page) && Number.isFinite(totalPages) && Number.isFinite(pageSize) && pageSize > 0) {
    if (displayed > 0 && m > 0) {
      const from = (page - 1) * pageSize + 1;
      const to = Math.min(m, (page - 1) * pageSize + displayed);
      line += ` Showing ${from}–${to} of ${m} (page ${page} of ${totalPages}, ${pageSize} per page).`;
    } else if (displayed === 0 && m > 0) {
      line += ` Page ${page} of ${totalPages} (${pageSize} per page).`;
    }
  } else if (displayed > 0) {
    line += ` Showing ${displayed}${trunc && Number.isFinite(lim) ? ` (newest ${lim} only)` : ""}.`;
  }
  if (t > 0 && m === 0) {
    line += " Try increasing Days back to 365 or set Classification to All.";
  }
  return line;
}

function updateAvheraldIncidentsPager(stats) {
  const prev = document.getElementById("v2-avherald-prev");
  const next = document.getElementById("v2-avherald-next");
  const lab = document.getElementById("v2-avherald-pager-label");
  if (!prev || !next || !lab) return;
  const page = Number(stats?.page);
  const totalPages = Number(stats?.totalPages);
  if (!Number.isFinite(page) || !Number.isFinite(totalPages)) {
    lab.textContent = "";
    prev.disabled = true;
    next.disabled = true;
    return;
  }
  lab.textContent = `Page ${page} of ${totalPages}`;
  prev.disabled = page <= 1;
  next.disabled = page >= totalPages;
}

function tamTileClass(label) {
  const tam = normaliseTam(label);
  const map = {
    "IN SERVICE": "bg-green-600",
    AOG: "bg-red-800",
    MAINTENANCE: "bg-purple-700",
    MAINT: "bg-purple-700",
    TRANSITION: "bg-pink-600",
    INCIDENT: "bg-amber-700",
    STORAGE: "bg-gray-700",
    RUSSIA: "bg-blue-800",
    "CHECK WITH FM": "bg-cyan-700",
    "CHECK WITH DM": "bg-cyan-700",
    AWAITING: "bg-yellow-600",
    "AWAITING OPERATOR FEEDBACK": "bg-yellow-600",
    "NON-DAE": "bg-orange-700",
    UNKNOWN: "bg-gray-900",
  };
  return map[tam] || "bg-gray-800";
}

/** TAM statuses shown on fleet heatmap tiles (order = legend left-to-right). */
const HEATMAP_LEGEND_ITEMS = [
  { tam: "IN SERVICE", label: "In service" },
  { tam: "AOG", label: "AOG" },
  { tam: "MAINTENANCE", label: "Maintenance" },
  { tam: "TRANSITION", label: "Transition" },
  { tam: "INCIDENT", label: "Incident" },
  { tam: "STORAGE", label: "Storage" },
  { tam: "RUSSIA", label: "Russia" },
  { tam: "AWAITING OPERATOR FEEDBACK", label: "Awaiting feedback" },
  { tam: "CHECK WITH FM", label: "Check with FM" },
  { tam: "NON-DAE", label: "Non-DAE" },
  { tam: "UNKNOWN", label: "Unknown" },
];

function renderHeatmapStatusLegend() {
  const items = HEATMAP_LEGEND_ITEMS.map(({ tam, label }) =>
    el("span", { class: "inline-flex items-center gap-1.5", role: "listitem" }, [
      el("span", {
        class: `inline-block w-3 h-3 rounded-sm shrink-0 ring-1 ring-black/10 ${tamTileClass(tam)}`,
        "aria-hidden": "true",
      }),
      el("span", { class: "text-xs text-gray-600", text: label }),
    ])
  );
  return el(
    "div",
    {
      class: "flex flex-wrap items-center gap-x-4 gap-y-2 pb-3 mb-4 border-b border-gray-100",
      role: "list",
      "aria-label": "Heatmap status colors",
    },
    [el("span", { class: "text-xs font-medium text-gray-500 w-full sm:w-auto", text: "Status" }), ...items]
  );
}

function uniqSorted(values) {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) => String(a).localeCompare(String(b)));
}

function getParamText(params, key) {
  const v = cleanText(params.get(key));
  return v || "";
}

function setHashParams(nextParams) {
  const key = currentRouteKey();
  const qs = nextParams.toString();
  const nextHash = `#/${key}${qs ? `?${qs}` : ""}`;
  // For filter tweaks on the same route, avoid triggering a full hashchange event
  // (which rebuilds the filter inputs and can steal focus while typing).
  try {
    const currentHash = cleanText(window.location.hash);
    if (currentHash && currentHash.split("?")[0] === nextHash.split("?")[0]) {
      const focus = captureFocusState();
      history.replaceState(null, "", nextHash);
      // Render immediately so results update without losing typing focus.
      renderRoute();
      // When we bypass hashchange, we must also re-init any route-specific panels
      // (settings tables, dashboard widgets, etc.) so they don't sit in "Loading…".
      if (key === "dashboard") initDashboardPanels().catch(() => {});
      else if (key === "status-recent") initRecentStatusChangesPanels().catch(() => {});
      else if (key === NEWS_INCIDENTS_ROUTE) initAvheraldIncidentsPage().catch(() => {});
      else if (key === "settings")
        initSettingsPanels()
          .catch(() => {})
          .finally(() => restoreFocusState(focus));
      else if (key === "materials/cores") initMaterialsCoresPage().catch(() => {});
      return;
    }
  } catch {
    // ignore
  }
  window.location.hash = nextHash;
}

function captureFocusState() {
  const elActive = document.activeElement;
  if (!elActive || typeof elActive !== "object") return null;
  const id = cleanText(elActive.id);
  if (!id) return null;
  const tag = String(elActive.tagName || "").toLowerCase();
  if (!["input", "select", "textarea"].includes(tag)) return null;
  const value = "value" in elActive ? String(elActive.value ?? "") : "";
  const start = Number.isFinite(elActive.selectionStart) ? elActive.selectionStart : null;
  const end = Number.isFinite(elActive.selectionEnd) ? elActive.selectionEnd : null;
  return { id, value, start, end };
}

function restoreFocusState(state) {
  if (!state?.id) return;
  const elNext = document.getElementById(state.id);
  if (!elNext) return;
  try {
    elNext.focus();
    if ("value" in elNext && state.value != null && cleanText(elNext.value) !== cleanText(state.value)) {
      elNext.value = state.value;
    }
    if (state.start != null && state.end != null && typeof elNext.setSelectionRange === "function") {
      elNext.setSelectionRange(state.start, state.end);
    }
  } catch {
    // ignore
  }
}

function normaliseSourceLabel(value, { hasFaCache = false } = {}) {
  const v = cleanText(value).toUpperCase().replace(/_/g, " ");
  const lastFlight = v.includes("LAST");
  if (v.includes("FR24")) return lastFlight ? "FR24 Last Flight" : "FR24";
  if (v.includes("MANUAL")) return "MANUAL";
  if (v.includes("FA") || v.includes("FLIGHTAWARE") || v.includes("AUTO-FLIGHTAWARE")) {
    return lastFlight ? "FA Last Flight" : "FA";
  }
  return hasFaCache ? "FA" : "—";
}

function effectiveTamForFilters(aircraft) {
  const explicit = cleanText(aircraft?.tam_status);
  if (explicit) return normaliseTam(explicit);
  if (isRussianFleetAircraft(aircraft)) return "RUSSIA";
  const faEntry = lookupFaCacheEntry(aircraft, state.faCacheByIdent);
  const faInterp = faEntry?.status ? interpretFaStatus(faEntry.status, aircraft) : null;
  const togMinutes = faInterp?.airborne
    ? 0
    : faInterp?.timeOnGroundMinutes != null
    ? faInterp.timeOnGroundMinutes
    : minutesSince(aircraft?.manual_status_last_flight)
    ? Math.max(0, minutesSince(aircraft?.manual_status_last_flight))
    : null;
  // Evidence-based "IN SERVICE" fallback:
  // Only treat as IN SERVICE when we have a real tracking activity timestamp within last 7 days.
  const sevenDaysMinutes = 7 * 24 * 60;
  const lastFlightAtMs = Number.isFinite(Date.parse(aircraft?.last_flight_at))
    ? Date.parse(aircraft.last_flight_at)
    : NaN;
  const manualLastFlightMs = Number.isFinite(Date.parse(aircraft?.manual_status_last_flight))
    ? Date.parse(aircraft.manual_status_last_flight)
    : NaN;
  const faOnMs = Number.isFinite(Date.parse(faEntry?.status?.on)) ? Date.parse(faEntry.status.on) : NaN;
  const faOffMs = Number.isFinite(Date.parse(faEntry?.status?.off)) ? Date.parse(faEntry.status.off) : NaN;
  const newestMoveMs = Math.max(
    Number.isFinite(lastFlightAtMs) ? lastFlightAtMs : 0,
    Number.isFinite(manualLastFlightMs) ? manualLastFlightMs : 0,
    Number.isFinite(faOnMs) ? faOnMs : 0,
    Number.isFinite(faOffMs) ? faOffMs : 0
  );
  const recentlyActive =
    faInterp?.airborne ||
    (Number.isFinite(togMinutes) && togMinutes <= sevenDaysMinutes) ||
    (newestMoveMs > 0 && Date.now() - newestMoveMs <= 7 * 24 * 60 * 60 * 1000);
  return recentlyActive ? "IN SERVICE" : "—";
}

function applyAircraftFilters(list, params, { ignoreKey = null } = {}) {
  const q = ignoreKey === "q" ? "" : getParamText(params, "q").toLowerCase();
  const ownerFilter = ignoreKey === "owner" ? "" : getParamText(params, "owner");
  const operatorFilter = ignoreKey === "operator" ? "" : getParamText(params, "operator");
  const regionFilter = ignoreKey === "region" ? "" : getParamText(params, "region");
  const tamFilter = ignoreKey === "tam" ? "" : getParamText(params, "tam");
  const tamList = ignoreKey === "tam" ? null : parseCsvParam(params, "tam");
  const modelList = ignoreKey === "model" ? null : parseCsvParam(params, "model");
  const sourceFilter = ignoreKey === "source" ? "" : getParamText(params, "source");
  const watchFilter = ignoreKey === "watch" ? "" : getParamText(params, "watch");

  return (list || []).filter((a) => {
    if (tamList && tamList.length) {
      if (!tamList.includes(normaliseTam(effectiveTamForFilters(a)))) return false;
    } else if (tamFilter && normaliseTam(effectiveTamForFilters(a)) !== normaliseTam(tamFilter)) {
      return false;
    }
    if (modelList && modelList.length) {
      const model = cleanText(a?.aircraft_type) || "—";
      if (!modelList.includes(model)) return false;
    }
    const modelFamilyList = ignoreKey === "model_family" ? null : parseCsvParam(params, "model_family");
    if (modelFamilyList && modelFamilyList.length) {
      if (!modelFamilyList.includes(aircraftModelFamily(a))) return false;
    }
    const engineFamilyList = ignoreKey === "engine_family" ? null : parseCsvParam(params, "engine_family");
    if (engineFamilyList && engineFamilyList.length) {
      if (!engineFamilyList.includes(aircraftEngineFamily(a))) return false;
    }
    const engineList = ignoreKey === "engine" ? null : parseCsvParam(params, "engine");
    if (engineList && engineList.length) {
      const eng = cleanText(a?.engine_model) || "—";
      if (!engineList.includes(eng)) return false;
    }
    if (ownerFilter && cleanText(a?.owner).toUpperCase() !== ownerFilter.toUpperCase()) return false;
    if (operatorFilter && cleanText(a?.operator).toUpperCase() !== operatorFilter.toUpperCase()) return false;
    if (regionFilter && cleanText(a?.region) !== regionFilter) return false;
    if (watchFilter === "1" && !a?.watch_on) return false;
    if (sourceFilter) {
      const hasFaCache = Boolean(lookupFaCacheEntry(a, state.faCacheByIdent));
      const src = normaliseSourceLabel(
        cleanText(a?.last_location_source) || cleanText(a?.watch_source) || cleanText(a?.tam_status_source) || "",
        { hasFaCache }
      );
      if (src !== sourceFilter) return false;
    }
    if (!q) return true;
    const hay = [
      a?.msn,
      a?.registration,
      a?.tail,
      a?.aircraft_type,
      a?.engine_model,
      a?.operator,
      a?.owner,
      a?.region,
      a?.last_known_location,
      a?.notes,
      a?.tam_status,
      a?.last_location_source,
    ]
      .map((v) => cleanText(v).toLowerCase())
      .filter(Boolean)
      .join(" · ");
    return hay.includes(q);
  });
}

function makeMultiCheckboxFilter({ paramKey, label, params, allOptions, buttonClass = "" }) {
  const options = uniqSorted((allOptions || []).map((o) => cleanText(o)).filter(Boolean));
  const selected = parseCsvParam(params, paramKey);
  const activeSet = selected ? new Set(selected) : new Set(options);
  const btnId = `v2-filter-${paramKey}-btn`;
  const panelId = `v2-filter-${paramKey}-panel`;

  const summaryText = () => {
    if (!selected || activeSet.size >= options.length) return `All (${options.length})`;
    if (!activeSet.size) return "None selected";
    return `${activeSet.size} selected`;
  };

  const renderPanel = (host) => {
    host.replaceChildren(
      el("div", { class: "max-h-56 overflow-y-auto p-2 space-y-1" }, [
        el(
          "button",
          {
            type: "button",
            class: "mb-1 text-xs text-dae hover:underline",
            text: activeSet.size >= options.length ? "Clear all" : "Select all",
            onclick: () => {
              const next = activeSet.size >= options.length ? [] : [...options];
              setCsvParam(params, paramKey, next, options);
            },
          },
          []
        ),
        ...options.map((opt) => {
          const checked = activeSet.has(opt);
          return el("label", { class: "flex items-center gap-2 text-sm text-gray-800 cursor-pointer py-0.5" }, [
            el("input", {
              type: "checkbox",
              checked,
              onchange: (e) => {
                const next = new Set(activeSet);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                setCsvParam(params, paramKey, [...next], options);
              },
            }),
            el("span", { text: opt }),
          ]);
        }),
      ])
    );
  };

  const wrap = el("div", { class: "relative min-w-[190px]" }, [
    el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: btnId, text: label }),
    el(
      "button",
      {
        id: btnId,
        type: "button",
        class:
          `w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-left text-gray-800 hover:bg-gray-50 focus:ring-4 focus:ring-dae/20 focus:border-dae ${buttonClass}`.trim(),
        text: summaryText(),
        onclick: (e) => {
          e.stopPropagation();
          const panel = document.getElementById(panelId);
          if (!panel) return;
          const open = !panel.classList.contains("hidden");
          document.querySelectorAll("[data-v2-filter-panel]").forEach((n) => n.classList.add("hidden"));
          if (!open) {
            panel.classList.remove("hidden");
            renderPanel(panel);
          }
        },
      },
      []
    ),
    el("div", {
      id: panelId,
      "data-v2-filter-panel": "1",
      class:
        "hidden absolute z-30 mt-1 w-full min-w-[14rem] rounded-lg border border-gray-200 bg-white shadow-lg",
    }),
  ]);

  if (!window.__v2FilterPanelCloseBound) {
    window.__v2FilterPanelCloseBound = true;
    document.addEventListener("click", () => {
      document.querySelectorAll("[data-v2-filter-panel]").forEach((n) => n.classList.add("hidden"));
    });
  }

  return wrap;
}

function filterBar({
  baseList = [],
  params,
  show = { search: true, owner: true, operator: true, region: true, tam: true, model: true, engine_family: true, source: true, watch: true },
} = {}) {
  // Cascading options: each dropdown derives options from list filtered by the other selections.
  const owners = uniqSorted(applyAircraftFilters(baseList, params, { ignoreKey: "owner" }).map((a) => cleanText(a?.owner) || ""));
  const operators = uniqSorted(applyAircraftFilters(baseList, params, { ignoreKey: "operator" }).map((a) => cleanText(a?.operator) || ""));
  const regions = uniqSorted(applyAircraftFilters(baseList, params, { ignoreKey: "region" }).map((a) => cleanText(a?.region) || ""));
  const tams = uniqSorted(
    applyAircraftFilters(baseList, params, { ignoreKey: "tam" }).map((a) => normaliseTam(effectiveTamForFilters(a)))
  );
  const models = uniqSorted(
    applyAircraftFilters(baseList, params, { ignoreKey: "model" }).map((a) => cleanText(a?.aircraft_type) || "—")
  );
  const sources = uniqSorted(
    applyAircraftFilters(baseList, params, { ignoreKey: "source" }).map((a) =>
      normaliseSourceLabel(
        cleanText(a?.last_location_source) || cleanText(a?.tam_status_source) || cleanText(a?.watch_source) || "",
        { hasFaCache: true }
      )
    )
  ).filter((s) => s !== "—");

  const searchValue = getParamText(params, "q");
  const ownerValue = getParamText(params, "owner");
  const operatorValue = getParamText(params, "operator");
  const regionValue = getParamText(params, "region");
  const tamValue = getParamText(params, "tam");
  const sourceValue = getParamText(params, "source");
  const watchValue = getParamText(params, "watch"); // "1" or ""

  const CHECKED_TAM_STATUSES = [
    "IN SERVICE",
    "AOG",
    "INCIDENT",
    "MAINTENANCE",
    "TRANSITION",
    "AWAITING OPERATOR FEEDBACK",
    "CHECK WITH FM",
    "STORAGE",
    "RUSSIA",
  ];

  const makeSelect = (paramKey, label, value, options, allLabel) => {
    const domId = `v2-filter-${paramKey}`;
    const safeValue = cleanText(value);
    const optionList = uniqSorted([...(options || []), ...(safeValue ? [safeValue] : [])]);
    const isTam = paramKey === "tam";
    const checked = isTam ? optionList.filter((o) => CHECKED_TAM_STATUSES.includes(o)) : [];
    const other = isTam ? optionList.filter((o) => !CHECKED_TAM_STATUSES.includes(o)) : optionList;
    const selectEl = el(
      "select",
      {
        id: domId,
        "data-param": paramKey,
        value: safeValue,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        onchange: (e) => {
          const p = new URLSearchParams(params.toString());
          const next = cleanText(e.target.value);
          if (next) p.set(paramKey, next);
          else p.delete(paramKey);
          setHashParams(p);
        },
      },
      [
        el("option", { value: "", text: allLabel, selected: !safeValue }),
        ...(isTam
          ? [
              el("option", { value: "", text: "──────── Checked statuses ────────", disabled: true }),
              ...checked.map((opt) => el("option", { value: opt, text: opt, selected: opt === safeValue })),
              other.length
                ? el("option", { value: "", text: "──────── Other statuses ────────", disabled: true })
                : null,
              ...other.map((opt) => el("option", { value: opt, text: opt, selected: opt === safeValue })),
            ].filter(Boolean)
          : optionList.map((opt) => el("option", { value: opt, text: opt, selected: opt === safeValue }))),
      ]
    );
    // Ensure controlled value (some browsers ignore option.selected when building DOM imperatively)
    try {
      selectEl.value = safeValue || "";
    } catch {
      // ignore
    }
    return el("div", { class: "min-w-[170px]" }, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: domId, text: label }),
      selectEl,
    ]);
  };

  const controls = [];

  if (show.search) {
    controls.push(
      el("div", { class: "flex-1 min-w-[240px]" }, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-filter-q", text: "Search" }),
        el("input", {
          id: "v2-filter-q",
          value: searchValue,
          placeholder: "MSN, registration, operator, location…",
          class:
            "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
          oninput: (e) => {
            const p = new URLSearchParams(params.toString());
            const next = cleanText(e.target.value);
            if (next) p.set("q", next);
            else p.delete("q");
            window.clearTimeout(filterBar.__t);
            filterBar.__t = window.setTimeout(() => setHashParams(p), 250);
          },
        }),
      ])
    );
  }

  if (show.owner) controls.push(makeSelect("owner", "FM", ownerValue, owners, "All FM"));
  if (show.operator) controls.push(makeSelect("operator", "Operator", operatorValue, operators, "All operators"));
  if (show.region) controls.push(makeSelect("region", "Region", regionValue, regions, "All regions"));
  if (show.tam) {
    const tamOpts = uniqSorted([...FLEET_TAM_FILTER_OPTIONS, ...tams]);
    controls.push(
      makeMultiCheckboxFilter({
        paramKey: "tam",
        label: "TAM status",
        params,
        allOptions: tamOpts,
      })
    );
  }
  if (show.model) {
    controls.push(
      makeGroupedFamilyCheckboxFilter({
        familyParamKey: "model_family",
        variantParamKey: "model",
        label: "Model family",
        params,
        list: applyAircraftFilters(baseList, params, { ignoreKey: "model_family" }),
        getFamily: aircraftModelFamily,
        getVariant: (a) => cleanText(a?.aircraft_type) || "—",
      })
    );
  }
  if (show.engine_family) {
    controls.push(
      makeGroupedFamilyCheckboxFilter({
        familyParamKey: "engine_family",
        variantParamKey: "engine",
        label: "Engine family",
        params,
        list: applyAircraftFilters(baseList, params, { ignoreKey: "engine_family" }),
        getFamily: aircraftEngineFamily,
        getVariant: (a) => cleanText(a?.engine_model) || "—",
      })
    );
  }
  if (show.source) controls.push(makeSelect("source", "Source", sourceValue, sources, "All sources"));
  if (show.watch) {
    controls.push(
      el("div", { class: "min-w-[170px]" }, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-filter-watch", text: "On watch" }),
        el(
          "select",
          {
            id: "v2-filter-watch",
            class:
              "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
            onchange: (e) => {
              const p = new URLSearchParams(params.toString());
              const next = cleanText(e.target.value);
              if (next) p.set("watch", next);
              else p.delete("watch");
              setHashParams(p);
            },
          },
          [
            el("option", { value: "", text: "All", selected: !watchValue }),
            el("option", { value: "1", text: "On watch only", selected: watchValue === "1" }),
          ]
        ),
      ])
    );
  }

  return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 mb-6" }, [
    el("div", { class: "flex flex-wrap items-end gap-3" }, controls),
  ]);
}

/* filterBar old body removed */

async function setOnWatch(aircraftId, enabled, toggleEl) {
  if (!Number.isFinite(Number(aircraftId))) return;
  if (toggleEl) {
    toggleEl.disabled = true;
    toggleEl.setAttribute("aria-busy", "true");
  }
  try {
    await fetchJson(apiUrl(`/api/aircraft/${Number(aircraftId)}/watch`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ onWatch: enabled, reason: "v2_toggle" }),
    });
    // keep local view consistent
    const target = state.aircraft.find((a) => Number(a?.id) === Number(aircraftId));
    if (target) {
      target.on_watch = enabled;
      target.onWatch = enabled;
    }
  } catch (e) {
    if (toggleEl) toggleEl.checked = !enabled;
  } finally {
    if (toggleEl) {
      toggleEl.disabled = false;
      toggleEl.removeAttribute("aria-busy");
    }
  }
}

function watchToggle({ aircraftId, checked }) {
  const id = Number(aircraftId);
  const input = el("input", {
    type: "checkbox",
    class: "sr-only peer",
  });
  input.checked = Boolean(checked);
  input.addEventListener("change", () => setOnWatch(id, input.checked, input));
  return el("label", { class: "inline-flex items-center cursor-pointer" }, [
    input,
    el("div", {
      class:
        "relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-100 rounded-full peer peer-checked:bg-dae after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full after:shadow-sm",
    }),
  ]);
}

function buildTable({ title, rows, columns, defaultSortKey = null, dense = false, fleetViewportFit = false, colWidthPctMap = null } = {}) {
  const header = title
    ? el("div", { class: "flex items-center justify-between gap-4 mb-3" }, [
        el("div", {}, [el("h1", { class: "text-2xl font-bold text-gray-800 tracking-tight", text: title })]),
      ])
    : null;

  const cellPad = dense ? "px-1 py-1" : "px-3 py-2";

  const cols = columns || [];
  const tableClass = fleetViewportFit
    ? "fleet-aircraft-table text-left text-gray-500"
    : "w-full min-w-max text-sm text-left text-gray-500 table-auto border-collapse";

  const table = el("table", { class: tableClass });
  const thead = el("thead", { class: fleetViewportFit ? "text-[10px] text-white uppercase bg-dae" : "text-xs text-white uppercase bg-dae" });

  if (fleetViewportFit && colWidthPctMap && cols.some((c) => c?.key && Number.isFinite(colWidthPctMap[c.key]))) {
    const colgroup = el(
      "colgroup",
      {},
      cols.map((c) => {
        const p = c?.key != null ? colWidthPctMap[c.key] : null;
        return el("col", Number.isFinite(p) ? { style: `width: ${p}%` } : {});
      })
    );
    table.appendChild(colgroup);
  }
  let sortKey = defaultSortKey;
  let sortDir = "asc";

  const getSortedRows = () => {
    const next = [...(rows || [])];
    if (!sortKey) return next;
    const col = cols.find((c) => c.key === sortKey);
    if (!col) return next;
    if (typeof col.sortCompare === "function") {
      next.sort(col.sortCompare);
    } else if (typeof col.sortValue === "function") {
      next.sort((a, b) => {
        const va = col.sortValue(a);
        const vb = col.sortValue(b);
        if (va === vb) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === "number" && typeof vb === "number") return va - vb;
        return String(va).localeCompare(String(vb));
      });
    } else {
      return next;
    }
    if (sortDir === "desc") next.reverse();
    return next;
  };

  const tbody = el("tbody");

  const renderBody = () => {
    tbody.replaceChildren();
    for (const r of getSortedRows()) {
      const tr = el("tr", { class: "bg-white border-b hover:bg-gray-50" });
      for (const c of cols) {
        const cellClass = c.nowrap
          ? `${cellPad} align-top whitespace-nowrap overflow-visible ${c.class || ""}`
          : `${cellPad} align-top min-w-0 break-words [overflow-wrap:anywhere] ${c.class || ""}`;
        tr.appendChild(el("td", { class: cellClass }, [c.get(r)]));
      }
      tbody.appendChild(tr);
    }
  };

  const renderHead = () => {
    const tr = el(
      "tr",
      {},
      cols.map((c) => {
        const sortable = Boolean(c.key && typeof c.sortValue === "function");
        const arrow = sortable && sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : "";
        const headLabel = cleanText(c.headerLabel) || cleanText(c.label);
        const headWrap = fleetViewportFit ? "whitespace-normal leading-tight max-w-0" : "whitespace-nowrap";
        return el("th", {
          class: `${cellPad} align-top ${headWrap} ${sortable ? "cursor-pointer select-none" : ""}`,
          scope: "col",
          text: `${headLabel}${arrow}`,
          onclick: sortable
            ? () => {
                if (sortKey === c.key) sortDir = sortDir === "asc" ? "desc" : "asc";
                else {
                  sortKey = c.key;
                  sortDir = "asc";
                }
                renderHead();
                renderBody();
              }
            : null,
        });
      })
    );
    thead.replaceChildren(tr);
  };

  renderHead();
  renderBody();

  table.appendChild(thead);
  table.appendChild(tbody);

  const scopeClass = "actracker-v2-scope";
  const tableWrap = fleetViewportFit
    ? el("div", { class: "fleet-aircraft-table-wrap" }, [table])
    : el("div", { class: "overflow-x-auto" }, [table]);
  const cardClass = fleetViewportFit
    ? `${scopeClass} w-full max-w-[80vw] mx-auto bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden`
    : `${scopeClass} bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden`;
  return el("div", { class: cardClass }, [
    ...(header ? [header] : []),
    tableWrap,
  ]);
}

function fr24AircraftUrl(registration) {
  const reg = cleanText(registration).toUpperCase();
  if (!reg) return "";
  return `https://www.flightradar24.com/data/aircraft/${encodeURIComponent(reg)}`;
}

function flightawareAircraftUrl(registrationOrIdent) {
  const ident = cleanText(registrationOrIdent).toUpperCase();
  if (!ident) return "";
  // Public FlightAware live flight page; works for both registrations and idents.
  return `https://flightaware.com/live/flight/${encodeURIComponent(ident)}`;
}

function pillLink(label, href, title) {
  return el(
    "a",
    {
      href,
      target: "_blank",
      rel: "noopener",
      title: title || label,
      class:
        "inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50",
      text: label,
    },
    []
  );
}

function iconButton({ label, title, onClick }) {
  return el("button", {
    type: "button",
    title: title || label,
    class:
      "inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50",
    onclick: onClick,
    text: label,
  });
}

/** FA / FR24 pills for Spirit tracker tables (same pattern as fleet Links column). */
function spiritTrackingLinksForAirframeRow(r) {
  const reg = cleanText(r?.reg);
  const links = [];
  const faUrl = cleanText(r?.flightawareUrl) || flightawareAircraftUrl(reg);
  if (faUrl) links.push(pillLink("FA", faUrl, "FlightAware"));
  const fr24 = fr24AircraftUrl(reg);
  if (fr24) links.push(pillLink("FR24", fr24, "Flightradar24"));
  if (!links.length) return el("span", { class: "text-gray-400 text-xs", text: "—" });
  return el("div", { class: "flex flex-wrap items-center gap-1.5" }, links);
}

function resolveOperatorContactEmail(operatorName) {
  const name = cleanText(operatorName).toLowerCase();
  const ops = Array.isArray(state.operators) ? state.operators : [];
  if (!name || !ops.length) return null;
  const op = ops.find((o) => cleanText(o?.name).toLowerCase() === name) || null;
  const contacts = Array.isArray(op?.contacts) ? op.contacts : [];
  const first = contacts.find((c) => cleanText(c?.email)) || null;
  return first
    ? { email: cleanText(first.email), name: cleanText(first.name) || "Operator contact" }
    : null;
}

function openMailto({ to, subject, body }) {
  const email = cleanText(to);
  const parts = [];
  if (cleanText(subject)) parts.push(`subject=${encodeURIComponent(subject)}`);
  if (cleanText(body)) parts.push(`body=${encodeURIComponent(body)}`);
  const qs = parts.length ? `?${parts.join("&")}` : "";
  // IMPORTANT: Do not use URLSearchParams here; some clients show '+' literally for spaces.
  window.location.href = `mailto:${email ? encodeURIComponent(email) : ""}${qs}`;
}

function openCommentsModal({ aircraftId, registration, owner, msn, focusNew = true } = {}) {
  const id = Number(aircraftId);
  if (!Number.isFinite(id) || id <= 0) return;

  const backdrop = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    "data-actracker-modal-backdrop": "1",
  });
  const modal = el("div", { class: "w-full max-w-3xl rounded-xl bg-white shadow-lg border border-gray-200" });
  const header = el("div", { class: "flex items-start justify-between gap-4 p-4 border-b border-gray-200" }, [
    el("div", {}, [
      el("div", { class: "text-lg font-semibold text-gray-900", text: "Comments" }),
      el("div", {
        class: "mt-1 text-sm text-gray-600",
        text: `${cleanText(registration) || "—"} · MSN ${cleanText(msn) || "—"} · FM ${cleanText(owner) || "—"}`,
      }),
    ]),
    el("button", {
      type: "button",
      class: "text-gray-500 hover:text-gray-800",
      text: "✕",
      onclick: () => backdrop.remove(),
    }),
  ]);
  const body = el("div", { class: "p-4" }, [
    el("div", { id: "v2-comments-list", class: "text-sm text-gray-700" }, ["Loading…"]),
    el("div", { class: "mt-4" }, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-comment-new", text: "Add comment" }),
      el("textarea", {
        id: "v2-comment-new",
        rows: 3,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        placeholder: "Type a new comment…",
      }),
      el("div", { class: "mt-3 flex items-center justify-end gap-2" }, [
        el("button", {
          type: "button",
          class:
            "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
          text: "Cancel",
          onclick: () => backdrop.remove(),
        }),
        el("button", {
          type: "button",
          id: "v2-comment-save",
          class:
            "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
          text: "Save",
        }),
      ]),
    ]),
  ]);
  modal.appendChild(header);
  modal.appendChild(body);
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) backdrop.remove();
  });
  document.body.appendChild(backdrop);

  const listEl = body.querySelector("#v2-comments-list");
  const saveBtn = body.querySelector("#v2-comment-save");
  const textarea = body.querySelector("#v2-comment-new");
  if (focusNew) {
    try {
      textarea?.focus?.();
    } catch {
      // ignore
    }
  }

  const renderList = (comments) => {
    const items = sortCommentsNewestFirst(comments);
    if (!items.length) {
      listEl.replaceChildren(el("div", { class: "text-gray-500", text: "No comments yet." }));
      return;
    }
    listEl.replaceChildren(
      el(
        "div",
        { class: "space-y-3" },
        items.map((c) => {
          const canDelete = Boolean(state.session) && !isInspectorSession() && cleanText(c?.id);
          const stamp = formatShortTime(c.created_at || c.createdAt);
          const headerRight = canDelete
            ? el("div", { class: "flex items-center gap-2" }, [
                el("div", { class: "text-xs font-mono text-gray-600", text: stamp }),
                el("button", {
                  type: "button",
                  class:
                    "inline-flex items-center justify-center w-8 h-8 text-red-700 bg-white border border-gray-200 rounded-md hover:bg-red-50",
                  title: "Delete comment",
                  text: "🗑",
                  onclick: async () => {
                    const ok = window.confirm("Delete this comment? This cannot be undone.");
                    if (!ok) return;
                    try {
                      await fetchJson(apiUrl(`/api/aircraft/${id}/comments/${encodeURIComponent(cleanText(c.id))}`), {
                        method: "DELETE",
                      });
                      await load();
                      await loadAircraft();
                      await loadFaCache().catch(() => {});
                      renderRoute();
                    } catch (e) {
                      window.alert(cleanText(e?.message) || String(e));
                    }
                  },
                }),
              ])
            : el("div", { class: "text-xs font-mono text-gray-600", text: stamp });
          return el("div", { class: "rounded-lg border border-gray-200 bg-gray-50 p-3" }, [
            el("div", { class: "flex items-center justify-between gap-3" }, [
              el("div", { class: "text-xs text-gray-600", text: cleanText(c.author) || "—" }),
              headerRight,
            ]),
            el("div", { class: "mt-2 text-sm text-gray-800 whitespace-pre-wrap", text: cleanText(c.body) || "" }),
          ]);
        })
      )
    );
  };

  const load = async () => {
    try {
      const comments = await fetchJson(apiUrl(`/api/aircraft/${id}/comments?limit=50`));
      renderList(comments);
    } catch (e) {
      listEl.replaceChildren(el("div", { class: "text-red-700", text: `Failed to load: ${cleanText(e?.message) || String(e)}` }));
    }
  };

  load();

  if (saveBtn) {
    saveBtn.onclick = async () => {
      const bodyText = cleanText(textarea?.value);
      if (!bodyText) return;
      saveBtn.disabled = true;
      saveBtn.textContent = "Saving…";
      try {
        await fetchJson(apiUrl(`/api/aircraft/${id}/comments`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: bodyText }),
        });
        textarea.value = "";
        await load();
        // refresh aircraft list so counts/last comment update
        await loadAircraft();
        await loadFaCache().catch(() => {});
        renderRoute();
      } catch (e) {
        window.alert(cleanText(e?.message) || String(e));
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = "Save";
      }
    };
  }
}

function openManualOverrideModal(row) {
  const id = Number(row?.id);
  if (!Number.isFinite(id) || id <= 0) return;

  const subtitle = `${cleanText(row?.reg) || "—"} · MSN ${cleanText(row?.msn) || "—"} · FM ${cleanText(row?.owner) || "—"}`;
  const statusNow = cleanText(row?.status) || "—";
  const tamNow = cleanText(row?.tam) || "—";

  function normaliseOverrideTimestampParts({ date, time }) {
    const d = cleanText(date);
    const t = cleanText(time);
    if (!d) return null;
    // Native date input yields YYYY-MM-DD.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
    const timePart = /^\d{2}:\d{2}$/.test(t) ? t : "00:00";
    const ts = Date.parse(`${d}T${timePart}:00Z`);
    return Number.isFinite(ts) ? new Date(ts).toISOString() : null;
  }

  const form = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
    inputField({
      id: "v2-override-last-flight-date",
      label: "Last flight date (UTC)",
      value: "",
      type: "date",
    }),
    inputField({
      id: "v2-override-last-flight-time",
      label: "Last flight time (UTC) (optional)",
      value: "",
      type: "time",
      placeholder: "00:00",
    }),
    inputField({
      id: "v2-override-location",
      label: "Location",
      value: "",
      placeholder: "Airport code or city (e.g. YYZ)",
    }),
    el("div", { class: "md:col-span-2" }, [
      el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-gray-600" }, [
        el("div", { text: `Current status: ${statusNow}` }),
        el("div", { text: `Current TAM: ${tamNow}` }),
      ]),
    ]),
    el("div", { class: "md:col-span-2" }, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-override-comment", text: "Comment (optional)" }),
      el("textarea", {
        id: "v2-override-comment",
        rows: 2,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        placeholder: "Add a note for the team (optional)…",
      }),
    ]),
    el("div", { class: "md:col-span-2" }, [
      el("p", {
        class: "mt-2 text-xs text-gray-500",
        text: "Use when FlightAware/FR24 is missing or wrong. Location follows this row. For time on ground, your last-flight time is blended with FA (longer wins) until FA shows a departure after that time — then tracking follows FA again. TAM is separate.",
      }),
    ]),
  ]);

  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const saveBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save override",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);

  const dialog = modalShell({ title: "Manual override", subtitle, bodyNode: form, footerNode: footer });
  cancelBtn.onclick = dialog.close;

  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const date = cleanText(document.getElementById("v2-override-last-flight-date")?.value) || null;
      const time = cleanText(document.getElementById("v2-override-last-flight-time")?.value) || null;
      const lastLocation = cleanText(document.getElementById("v2-override-location")?.value) || null;
      const comment = cleanText(document.getElementById("v2-override-comment")?.value) || null;

      const lastFlightAt = normaliseOverrideTimestampParts({ date, time });
      if (date && !lastFlightAt) {
        window.alert("Invalid date. Please pick a date (UTC). Time is optional.");
        return;
      }

      await fetchJson(apiUrl(`/api/aircraft/${id}/manual-status`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lastFlightAt, lastLocation, statusText: null, comment }),
      });

      await loadAircraft();
      await loadFaCache().catch(() => {});
      dialog.close();
      renderRoute();
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save override";
    }
  };
}

function openExpectedReleaseModal(row) {
  const id = Number(row?.id);
  if (!Number.isFinite(id) || id <= 0) return;

  const subtitle = `${cleanText(row?.reg) || "—"} · MSN ${cleanText(row?.msn) || "—"} · FM ${cleanText(row?.owner) || "—"}`;
  const form = el("div", { class: "grid grid-cols-1 gap-4" }, [
    inputField({
      id: "v2-expected-release-date",
      label: "Expected release date",
      value: cleanText(row?.expectedReleaseDate) || "",
      type: "date",
    }),
  ]);

  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const clearBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Clear",
  });
  const saveBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, clearBtn, saveBtn]);

  const dialog = modalShell({ title: "Expected release", subtitle, bodyNode: form, footerNode: footer });
  cancelBtn.onclick = dialog.close;

  clearBtn.onclick = () => {
    const elInput = document.getElementById("v2-expected-release-date");
    if (elInput) elInput.value = "";
  };

  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const expected_release_date = cleanText(document.getElementById("v2-expected-release-date")?.value) || null;
      await saveAircraftProfile(id, { expected_release_date });
      await loadAircraft();
      await loadFaCache().catch(() => {});
      dialog.close();
      renderRoute();
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  };
}

function modalShell({ title, subtitle, bodyNode, footerNode }) {
  const backdrop = el("div", {
    class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4",
    "data-actracker-modal-backdrop": "1",
  });
  const modal = el("div", { class: "w-full max-w-4xl rounded-xl bg-white shadow-lg border border-gray-200" });
  const header = el("div", { class: "flex items-start justify-between gap-4 p-4 border-b border-gray-200" }, [
    el("div", {}, [
      el("div", { class: "text-lg font-semibold text-gray-900", text: title || "Dialog" }),
      subtitle ? el("div", { class: "mt-1 text-sm text-gray-600", text: subtitle }) : null,
    ]),
    el("button", {
      type: "button",
      class: "text-gray-500 hover:text-gray-800",
      text: "✕",
      onclick: () => backdrop.remove(),
    }),
  ]);
  const body = el("div", { class: "p-4" }, [bodyNode || el("div", { text: "" })]);
  const footer = el("div", { class: "flex items-center justify-end gap-2 p-4 border-t border-gray-200" }, [
    footerNode || null,
  ]);
  modal.appendChild(header);
  modal.appendChild(body);
  if (footerNode) modal.appendChild(footer);
  backdrop.appendChild(modal);
  backdrop.addEventListener("click", (ev) => {
    if (ev.target === backdrop) backdrop.remove();
  });
  document.body.appendChild(backdrop);
  return { backdrop, modal, close: () => backdrop.remove() };
}

function inputField({ id, label, value, placeholder, type = "text" }) {
  return el("div", {}, [
    el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: id, text: label }),
    el("input", {
      id,
      type,
      value: value ?? "",
      placeholder: placeholder || "",
      class:
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
    }),
  ]);
}

function selectField({ id, label, value, options }) {
  return el("div", {}, [
    el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: id, text: label }),
    el(
      "select",
      {
        id,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
      },
      options.map((opt) => el("option", { value: opt.value, text: opt.label, selected: opt.value === value }))
    ),
  ]);
}

function parseOwnerCodes(raw) {
  return Array.from(
    new Set(
      cleanText(raw)
        .split(/[,\s]+/)
        .map((s) => cleanText(s).toUpperCase())
        .filter(Boolean)
    )
  );
}

const USER_PERMS_HINT = "Owner codes the user can access (comma-separated, e.g. AL, CS). Leave blank for none. Ignored when Administrator is on (full access).";

function openUserCreateModal(onSaved) {
  const getEl = (id) => document.getElementById(id);
  const val = (id) => getEl(id)?.value;
  const body = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
    inputField({ id: "uc-username", label: "Username", placeholder: "e.g. jsmith" }),
    inputField({ id: "uc-display", label: "Display name (first + last)", placeholder: "e.g. John Smith" }),
    inputField({ id: "uc-email", label: "Email", type: "email" }),
    inputField({ id: "uc-initials", label: "Initials (FM code)", placeholder: "e.g. AL" }),
    inputField({ id: "uc-pw1", label: "Password", type: "password" }),
    inputField({ id: "uc-pw2", label: "Confirm password", type: "password" }),
    el("div", { class: "md:col-span-2" }, [
      inputField({ id: "uc-owners", label: "Permissions — owner codes", placeholder: "e.g. AL, CS" }),
      el("p", { class: "mt-1 text-[11px] text-gray-500", text: USER_PERMS_HINT }),
    ]),
    fleetFlagToggle({ id: "uc-admin", label: "Administrator", hint: "Full access to all aircraft and settings", checked: false }),
    fleetFlagToggle({ id: "uc-active", label: "Active", hint: "Can sign in", checked: true }),
  ]);
  const status = el("p", { class: "text-sm mt-3 text-gray-600", text: "" });
  const setStatus = (msg) => {
    status.className = "text-sm mt-3 text-red-700";
    status.textContent = msg;
  };
  const saveBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Create user",
  });
  const cancelBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({ title: "New user", bodyNode: el("div", {}, [body, status]), footerNode: footer });
  cancelBtn.onclick = dialog.close;
  saveBtn.onclick = async () => {
    const username = cleanText(val("uc-username"));
    const pw1 = String(getEl("uc-pw1")?.value || "");
    const pw2 = String(getEl("uc-pw2")?.value || "");
    if (!username) return setStatus("Username is required.");
    if (!pw1) return setStatus("Password is required.");
    if (pw1 !== pw2) return setStatus("Passwords do not match.");
    saveBtn.disabled = true;
    saveBtn.textContent = "Creating…";
    try {
      await fetchJson(apiUrl("/api/admin/users"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          displayName: cleanText(val("uc-display")),
          email: cleanText(val("uc-email")),
          initials: cleanText(val("uc-initials")),
          password: pw1,
          isAdmin: Boolean(getEl("uc-admin")?.checked),
          ownerCodes: parseOwnerCodes(val("uc-owners")),
          active: Boolean(getEl("uc-active")?.checked),
        }),
      });
      dialog.close();
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      setStatus(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Create user";
    }
  };
}

function openUserEditModal(user, onSaved) {
  if (!user || !cleanText(user.username)) return;
  const getEl = (id) => document.getElementById(id);
  const val = (id) => getEl(id)?.value;
  const body = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", text: "Username" }),
      el("input", {
        value: cleanText(user.username),
        readOnly: true,
        class: "w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600 cursor-not-allowed",
      }),
    ]),
    inputField({ id: "ue-display", label: "Display name (first + last)", value: cleanText(user.displayName) }),
    inputField({ id: "ue-email", label: "Email", type: "email", value: cleanText(user.email) }),
    inputField({ id: "ue-initials", label: "Initials (FM code)", value: cleanText(user.initials) }),
    el("div", { class: "md:col-span-2" }, [
      inputField({ id: "ue-owners", label: "Permissions — owner codes", value: (user.ownerCodes || []).join(", ") }),
      el("p", { class: "mt-1 text-[11px] text-gray-500", text: USER_PERMS_HINT }),
    ]),
    fleetFlagToggle({ id: "ue-admin", label: "Administrator", hint: "Full access to all aircraft and settings", checked: Boolean(user.isAdmin) }),
    fleetFlagToggle({ id: "ue-active", label: "Active", hint: "Can sign in", checked: Boolean(user.active) }),
  ]);
  const status = el("p", { class: "text-sm mt-3 text-gray-600", text: "" });
  const setStatus = (msg) => {
    status.className = "text-sm mt-3 text-red-700";
    status.textContent = msg;
  };
  const saveBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save",
  });
  const cancelBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({ title: `Edit user — ${cleanText(user.username)}`, bodyNode: el("div", {}, [body, status]), footerNode: footer });
  cancelBtn.onclick = dialog.close;
  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(cleanText(user.username))}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          displayName: cleanText(val("ue-display")),
          email: cleanText(val("ue-email")),
          initials: cleanText(val("ue-initials")),
          isAdmin: Boolean(getEl("ue-admin")?.checked),
          ownerCodes: parseOwnerCodes(val("ue-owners")),
          active: Boolean(getEl("ue-active")?.checked),
        }),
      });
      dialog.close();
      if (typeof onSaved === "function") onSaved();
    } catch (e) {
      setStatus(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  };
}

function openUserPasswordModal(user, onSaved) {
  if (!user || !cleanText(user.username)) return;
  const getEl = (id) => document.getElementById(id);
  const body = el("div", { class: "space-y-3 max-w-md" }, [
    inputField({ id: "up-pw1", label: "New password", type: "password" }),
    inputField({ id: "up-pw2", label: "Confirm new password", type: "password" }),
  ]);
  const status = el("p", { class: "text-sm mt-3 text-gray-600", text: "" });
  const setStatus = (msg, ok) => {
    status.className = `text-sm mt-3 ${ok ? "text-green-700" : "text-red-700"}`;
    status.textContent = msg;
  };
  const saveBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Set password",
  });
  const cancelBtn = el("button", {
    type: "button",
    class: "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({
    title: `Change password — ${cleanText(user.username)}`,
    subtitle: "Enter the new password twice. The only check is that they match.",
    bodyNode: el("div", {}, [body, status]),
    footerNode: footer,
  });
  cancelBtn.onclick = dialog.close;
  saveBtn.onclick = async () => {
    const pw1 = String(getEl("up-pw1")?.value || "");
    const pw2 = String(getEl("up-pw2")?.value || "");
    if (!pw1 || !pw2) return setStatus("Enter the new password in both fields.", false);
    if (pw1 !== pw2) return setStatus("Passwords do not match.", false);
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(cleanText(user.username))}/reset-password`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw1 }),
      });
      setStatus("Password updated.", true);
      if (typeof onSaved === "function") onSaved();
      setTimeout(dialog.close, 700);
    } catch (e) {
      setStatus(cleanText(e?.message) || String(e), false);
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Set password";
    }
  };
}

async function openAircraftEditModal(row) {
  if (!row || !Number.isFinite(Number(row.id))) return;
  if (isInspectorSession()) {
    window.alert("Inspectors cannot edit aircraft.");
    return;
  }
  const a =
    (row.__raw && typeof row.__raw === "object" ? row.__raw : null) ||
    (state.aircraft || []).find((x) => Number(x?.id) === Number(row.id)) ||
    {};
  const admin = isAdminSession();

  // Prefetch combo data (owners list is available to FMs for handoff)
  try {
    if (!Array.isArray(state.operators) || !state.operators.length) await loadOperators();
  } catch {
    /* ignore */
  }
  try {
    if (!Array.isArray(state.adminOwners) || !state.adminOwners.length) await loadAdminOwners();
  } catch {
    /* fallback below */
  }

  const fleetIdent = fleetIdentForAircraft(a);
  const regForEdit = (() => {
    const r = cleanText(a.registration || a.tail);
    return r && !/^XRM-/i.test(r) ? r : "";
  })();
  const modelFamilyValue = cleanText(a.model_family) || aircraftModelFamily(a);
  const engineFamilyValue = cleanText(a.engine_family) || aircraftEngineFamily(a);
  const currentOwner = cleanText(a.owner) || cleanText(row.owner);
  const currentOperator = cleanText(a.operator);
  const currentRegion = cleanText(a.region);
  const currentType = cleanText(a.aircraft_type);
  const currentEngineModel = cleanText(a.engine_model);
  const subtitle = `${displayRegistration(a) !== "—" ? displayRegistration(a) : fleetIdent || "—"} · MSN ${row.msn || "—"} · ${currentType || "—"} · FM ${currentOwner || "—"}`;

  const fleet = state.aircraft || [];
  const ownerChoices = uniqSorted([
    ...(Array.isArray(state.adminOwners) ? state.adminOwners : []),
    ...fleet.map((x) => cleanText(x?.owner)),
    currentOwner,
  ].filter(Boolean));
  const operatorChoices = uniqSorted([
    ...(Array.isArray(state.operators) ? state.operators.map((o) => cleanText(o?.name)) : []),
    ...fleet.map((x) => cleanText(x?.operator)),
    currentOperator,
  ].filter(Boolean));
  const regionChoices = uniqSorted([...fleet.map((x) => cleanText(x?.region)), currentRegion].filter(Boolean));
  const typeChoices = uniqSorted([...fleet.map((x) => cleanText(x?.aircraft_type)), currentType].filter(Boolean));
  const engineModelChoices = uniqSorted([
    ...fleet.map((x) => cleanText(x?.engine_model)),
    currentEngineModel,
  ].filter(Boolean));
  const modelFamilyOptions = uniqSorted([...MODEL_FAMILY_OPTIONS, modelFamilyValue].filter((v) => v && v !== "—"));
  const engineFamilyOptions = uniqSorted([...ENGINE_FAMILY_OPTIONS, engineFamilyValue].filter((v) => v && v !== "—"));

  const meFleetChecked = booleanFleetFlag(a.is_me_fleet);
  const russianFleetChecked = booleanFleetFlag(a.is_russian_fleet);

  const comboSelect = (id, label, value, choices, { blankLabel = "Select…" } = {}) =>
    selectField({
      id,
      label,
      value: value || "",
      options: [
        { value: "", label: blankLabel },
        ...choices.map((v) => ({ value: v, label: v })),
      ],
    });

  const form = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
    inputField({ id: "ae-msn", label: "MSN", value: cleanText(a.msn) }),
    inputField({
      id: "ae-registration",
      label: "Registration (manual)",
      value: regForEdit,
      placeholder: "e.g. PK-GPM — type the tail as shown in ops",
    }),
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ae-ident", text: "Internal ident (unique)" }),
      el("input", {
        id: "ae-ident",
        value: fleetIdent,
        readOnly: true,
        class: "w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600 font-mono cursor-not-allowed",
        title: "Auto from MSN + aircraft type. Used for duplicate MSN rows.",
      }),
      el("p", { class: "mt-1 text-[11px] text-gray-500", text: "Not shown in the fleet table. Registration is what users see." }),
    ]),
    comboSelect("ae-owner", "FM (owner)", currentOwner, ownerChoices, {
      blankLabel: "Select FM…",
    }),
    comboSelect("ae-operator", "Operator", currentOperator, operatorChoices, {
      blankLabel: "Select operator…",
    }),
    comboSelect("ae-region", "Region", currentRegion, regionChoices, {
      blankLabel: "Select region…",
    }),
    comboSelect("ae-aircraft-type", "Aircraft type", currentType, typeChoices, {
      blankLabel: "Select type…",
    }),
    selectField({
      id: "ae-model-family",
      label: "Model family",
      value: modelFamilyValue,
      options: [
        { value: "", label: "Select…" },
        ...modelFamilyOptions.map((v) => ({ value: v, label: v })),
      ],
    }),
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ae-engine-model", text: "Engine model" }),
      el("input", {
        id: "ae-engine-model",
        value: currentEngineModel,
        list: "ae-engine-model-options",
        placeholder: "Select or type…",
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
      }),
      el(
        "datalist",
        { id: "ae-engine-model-options" },
        engineModelChoices.map((v) => el("option", { value: v, text: v }))
      ),
    ]),
    selectField({
      id: "ae-engine-family",
      label: "Engine family",
      value: engineFamilyValue,
      options: [
        { value: "", label: "Select…" },
        ...engineFamilyOptions.map((v) => ({ value: v, label: v })),
      ],
    }),
    inputField({ id: "ae-flightaware-url", label: "FlightAware URL", value: cleanText(a.flightaware_url) }),
    inputField({ id: "ae-tracker-url", label: "xRM URL", value: cleanText(a.tracker_url) }),
    inputField({ id: "ae-expected-release", label: "Expected release date", value: cleanText(a.expected_release_date), type: "date" }),
    admin
      ? selectField({
          id: "ae-active",
          label: "Active",
          value: a.active === false ? "false" : "true",
          options: [
            { value: "true", label: "Active" },
            { value: "false", label: "Inactive" },
          ],
        })
      : null,
    admin
      ? fleetFlagToggle({
          id: "ae-me-fleet",
          label: "ME fleet",
          hint: "No when null/unchecked",
          checked: meFleetChecked,
        })
      : null,
    admin
      ? fleetFlagToggle({
          id: "ae-russian-fleet",
          label: "Russian fleet",
          hint: "Excluded from the 5-day no-activity view when checked",
          checked: russianFleetChecked,
        })
      : null,
    el("div", { class: "md:col-span-2" }, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ae-notes", text: "Notes" }),
      el("textarea", {
        id: "ae-notes",
        rows: 4,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        text: cleanText(a.notes),
      }),
    ]),
  ].filter(Boolean));

  const saveBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save",
  });
  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });

  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({ title: "Edit aircraft", subtitle, bodyNode: form, footerNode: footer });
  cancelBtn.onclick = dialog.close;

  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const oldExpected = cleanText(a.expected_release_date) || null;
      const oldOwner = currentOwner || null;
      const nextOwner = cleanText(document.getElementById("ae-owner")?.value) || null;
      if (!nextOwner) {
        window.alert("Select an FM (owner) from the list.");
        return;
      }
      const nextOperator = cleanText(document.getElementById("ae-operator")?.value) || null;
      if (!nextOperator) {
        window.alert("Select an operator from the list.");
        return;
      }
      const payload = {
        msn: cleanText(document.getElementById("ae-msn")?.value) || null,
        registration: cleanText(document.getElementById("ae-registration")?.value) || null,
        ident: cleanText(document.getElementById("ae-ident")?.value) || null,
        owner: nextOwner,
        operator: nextOperator,
        region: cleanText(document.getElementById("ae-region")?.value) || null,
        aircraft_type: cleanText(document.getElementById("ae-aircraft-type")?.value) || null,
        model_family: cleanText(document.getElementById("ae-model-family")?.value) || null,
        engine_model: cleanText(document.getElementById("ae-engine-model")?.value) || null,
        engine_family: cleanText(document.getElementById("ae-engine-family")?.value) || null,
        flightaware_url: cleanText(document.getElementById("ae-flightaware-url")?.value) || null,
        tracker_url: cleanText(document.getElementById("ae-tracker-url")?.value) || null,
        expected_release_date: cleanText(document.getElementById("ae-expected-release")?.value) || null,
        notes: cleanText(document.getElementById("ae-notes")?.value) || null,
      };
      if (admin) {
        payload.active = cleanText(document.getElementById("ae-active")?.value) !== "false";
        payload.is_me_fleet = Boolean(document.getElementById("ae-me-fleet")?.checked);
        payload.is_russian_fleet = Boolean(document.getElementById("ae-russian-fleet")?.checked);
      }
      // Post activity notes before save so an FM handoff still allows commenting
      const notes = [];
      const nextExpected = payload.expected_release_date || null;
      if (oldExpected !== nextExpected) {
        const stamp = nextExpected ? formatDateDdMmmYy(nextExpected) : "—";
        notes.push(
          nextExpected ? `Expected release date set to ${stamp}.` : "Expected release date cleared."
        );
      }
      if ((oldOwner || "").toUpperCase() !== (nextOwner || "").toUpperCase()) {
        notes.push(`FM changed from ${oldOwner || "—"} to ${nextOwner}.`);
      }
      for (const note of notes) {
        await fetchJson(apiUrl(`/api/aircraft/${Number(row.id)}/comments`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: note }),
        });
      }
      await saveAircraftAdmin(row.id, payload);
      await loadAircraft();
      await loadFaCache().catch(() => {});
      state.adminAircraft = null;
      dialog.close();
      renderRoute();
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  };
}

function openAircraftCreateModal({ operators = null, owners = null, regions = null } = {}) {
  if (!isAdminSession()) {
    window.alert("Admin only.");
    return;
  }

  const ops = Array.isArray(operators) ? operators : Array.isArray(state.operators) ? state.operators : [];
  const ownerChoices = Array.isArray(owners) ? owners : Array.isArray(state.adminOwners) ? state.adminOwners : [];
  const existingRegions = Array.isArray(regions)
    ? regions
    : uniqSorted((state.aircraft || []).map((x) => cleanText(x?.region)).filter(Boolean));
  const operatorChoices = ops
    .map((op) => ({
      id: Number(op?.id),
      name: cleanText(op?.name),
      slug: cleanText(op?.slug),
    }))
    .filter((o) => Number.isFinite(o.id) && (o.name || o.slug));

  const initial = {
    msn: "",
    registration: "",
    owner: "",
    region: "",
    operator_id: "",
    operator: "",
    active: true,
    notes: "",
  };

  const form = el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
    inputField({ id: "ac-new-msn", label: "MSN", value: cleanText(initial.msn), placeholder: "e.g. 7135" }),
    inputField({ id: "ac-new-registration", label: "Registration", value: cleanText(initial.registration), placeholder: "e.g. N668NK" }),
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ac-new-owner", text: "FM (owner)" }),
      el("input", {
        id: "ac-new-owner",
        value: cleanText(initial.owner),
        list: "ac-new-owner-options",
        placeholder: "Select or type…",
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
      }),
      el("datalist", { id: "ac-new-owner-options" }, ownerChoices.map((o) => el("option", { value: o, text: o }))),
    ]),
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ac-new-region", text: "Region" }),
      el("input", {
        id: "ac-new-region",
        value: cleanText(initial.region),
        list: "ac-new-region-options",
        placeholder: "Select or type…",
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
      }),
      el(
        "datalist",
        { id: "ac-new-region-options" },
        existingRegions.map((r) => el("option", { value: r, text: r }))
      ),
    ]),
    el("div", { class: "md:col-span-2" }, [
      el("div", { class: "grid grid-cols-1 md:grid-cols-2 gap-4" }, [
        el("div", {}, [
          el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ac-new-operator", text: "Operator" }),
          el("input", {
            id: "ac-new-operator",
            value: cleanText(initial.operator),
            list: "ac-new-operator-options",
            placeholder: "Select or type…",
            class:
              "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
          }),
          el(
            "datalist",
            { id: "ac-new-operator-options" },
            operatorChoices.map((o) =>
              el("option", { value: o.name || o.slug, text: o.slug ? `${o.name || o.slug} (${o.slug})` : o.name || o.slug })
            )
          ),
        ]),
        el("div", {}, [
          el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ac-new-operator-slug", text: "Operator slug (optional)" }),
          el("input", {
            id: "ac-new-operator-slug",
            value: (() => {
              const id = Number(initial.operator_id);
              if (Number.isFinite(id)) {
                const hit = operatorChoices.find((o) => o.id === id);
                return cleanText(hit?.slug);
              }
              return "";
            })(),
            list: "ac-new-operator-slug-options",
            placeholder: "e.g. spirit-nondae",
            class:
              "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
          }),
          el(
            "datalist",
            { id: "ac-new-operator-slug-options" },
            operatorChoices.map((o) => (o.slug ? el("option", { value: o.slug, text: o.slug }) : null)).filter(Boolean)
          ),
        ]),
      ]),
      el("div", { class: "mt-1 text-[11px] text-gray-500" }, [
        "Tip: choose by name, or paste the operator slug to force an exact operator match.",
      ]),
    ]),
    selectField({
      id: "ac-new-active",
      label: "Active",
      value: initial.active === false ? "false" : "true",
      options: [
        { value: "true", label: "Active" },
        { value: "false", label: "Inactive" },
      ],
    }),
    el("div", { class: "md:col-span-2" }, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "ac-new-notes", text: "Notes (optional)" }),
      el("textarea", {
        id: "ac-new-notes",
        rows: 3,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        text: cleanText(initial.notes),
      }),
      el("div", { class: "mt-1 text-[11px] text-gray-500" }, [
        "Tip: after creating the aircraft, add comments from the tracker table (Edit / add…).",
      ]),
    ]),
  ]);

  const createBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Create aircraft",
  });
  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const footer = el("div", { class: "flex flex-wrap items-center justify-end gap-2 w-full" }, [
    cancelBtn,
    createBtn,
  ]);
  const dialog = modalShell({ title: "Create aircraft", subtitle: "Add a new fleet record", bodyNode: form, footerNode: footer });
  cancelBtn.onclick = dialog.close;

  createBtn.onclick = async () => {
    if (!isAdminSession()) {
      window.alert("Admin only.");
      return;
    }
    createBtn.disabled = true;
    createBtn.textContent = "Creating…";
    try {
      const registration = cleanText(document.getElementById("ac-new-registration")?.value) || null;
      const msn = cleanText(document.getElementById("ac-new-msn")?.value) || null;
      const identAuto = registration || msn;
      if (!identAuto) {
        window.alert("Registration or MSN is required (used to set Ident).");
        return;
      }
      const operatorText = cleanText(document.getElementById("ac-new-operator")?.value);
      const operatorSlug = cleanText(document.getElementById("ac-new-operator-slug")?.value);
      const bySlug = operatorSlug ? ops.find((op) => cleanText(op?.slug) === operatorSlug) : null;
      const byName = operatorText
        ? ops.find((op) => cleanText(op?.name).toLowerCase() === operatorText.toLowerCase())
        : null;
      const operatorId = Number(bySlug?.id || byName?.id);
      const payload = {
        msn,
        registration,
        ident: identAuto,
        owner: cleanText(document.getElementById("ac-new-owner")?.value) || null,
        region: cleanText(document.getElementById("ac-new-region")?.value) || null,
        operator_id: Number.isFinite(operatorId) && operatorId > 0 ? operatorId : undefined,
        operator: (!Number.isFinite(operatorId) || operatorId <= 0) && operatorText ? operatorText : undefined,
        active: cleanText(document.getElementById("ac-new-active")?.value) !== "false",
        notes: cleanText(document.getElementById("ac-new-notes")?.value) || null,
      };
      const resp = await createAircraftAdmin(payload);
      const created = resp?.aircraft || resp?.row || null;
      await Promise.allSettled([loadAircraft(), loadAdminAircraft()]);
      dialog.close();
      const id = Number(created?.id);
      if (Number.isFinite(id) && id > 0) {
        openCommentsModal({
          aircraftId: id,
          registration: cleanText(created?.registration) || cleanText(created?.tail) || cleanText(payload.registration) || "",
          owner: cleanText(created?.owner) || cleanText(payload.owner) || "",
          msn: cleanText(created?.msn) || cleanText(payload.msn) || "",
          focusNew: true,
        });
      } else {
        window.alert("Aircraft created.");
      }
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      createBtn.disabled = false;
      createBtn.textContent = "Create aircraft";
    }
  };
}

function openOperatorEditModal(op) {
  if (!op || !Number.isFinite(Number(op.id))) return;
  if (!isAdminSession()) {
    window.alert("Admin only.");
    return;
  }
  const contacts = Array.isArray(op.contacts) ? op.contacts : [];
  const rows = contacts.length ? contacts : [{ name: "", email: "" }];

  const list = el("div", { class: "space-y-3" }, []);
  const render = () => {
    list.replaceChildren(
      ...rows.map((c, idx) =>
        el("div", { class: "grid grid-cols-1 md:grid-cols-5 gap-3 items-end" }, [
          el("div", { class: "md:col-span-2" }, [
            inputField({ id: `oc-name-${idx}`, label: idx === 0 ? "First name" : "First name", value: cleanText(c.name) }),
          ]),
          el("div", { class: "md:col-span-2" }, [
            inputField({ id: `oc-email-${idx}`, label: idx === 0 ? "Contact email" : "Email", value: cleanText(c.email) }),
          ]),
          el("div", {}, [
            el("button", {
              type: "button",
              class:
                "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
              text: "Remove",
              onclick: () => {
                rows.splice(idx, 1);
                if (!rows.length) rows.push({ name: "", email: "" });
                render();
              },
            }),
          ]),
        ])
      )
    );
  };
  render();

  const addBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Add contact",
    onclick: () => {
      rows.push({ name: "", email: "" });
      render();
    },
  });

  const saveBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save",
  });
  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });

  const body = el("div", {}, [
    el("div", { class: "flex items-center justify-between gap-3 mb-4" }, [el("div", { class: "text-sm text-gray-600", text: "Operator contacts used for outreach emails." }), addBtn]),
    list,
  ]);
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({ title: "Edit operator contacts", subtitle: cleanText(op.name) || "Operator", bodyNode: body, footerNode: footer });
  cancelBtn.onclick = dialog.close;
  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const payload = rows
        .map((_c, idx) => ({
          name: cleanText(document.getElementById(`oc-name-${idx}`)?.value) || null,
          email: cleanText(document.getElementById(`oc-email-${idx}`)?.value) || null,
        }))
        .filter((c) => c.email);
      await saveOperatorContacts(op.id, payload);
      await loadOperators();
      dialog.close();
      if (typeof initSettingsPanels.__refreshOperators === "function") {
        await initSettingsPanels.__refreshOperators();
      } else {
        renderRoute();
      }
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  };
}

const TAM_OPTIONS = [
  "IN SERVICE",
  "AOG",
  "MAINTENANCE",
  "TRANSITION",
  "INCIDENT",
  "STORAGE",
  "RUSSIA",
  "CHECK WITH FM",
  "AWAITING OPERATOR FEEDBACK",
  "NON-DAE",
];

function openTamStatusModal(row) {
  if (!row || !Number.isFinite(Number(row.id))) return;
  const subtitle = `${row.reg || "—"} · MSN ${row.msn || "—"} · FM ${row.owner || "—"}`;

  const form = el("div", { class: "space-y-4" }, [
    el("div", { class: "grid grid-cols-1 md:grid-cols-1 gap-4" }, [
      selectField({
        id: "v2-tam-select",
        label: "TAM status",
        value: normaliseTam(row.tam),
        options: [
          { value: normaliseTam(row.tam), label: normaliseTam(row.tam) },
          ...TAM_OPTIONS.filter((t) => t !== normaliseTam(row.tam)).map((t) => ({ value: t, label: t })),
        ],
      }),
    ]),
    el("div", {}, [
      el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-tam-comment", text: "Add comment (optional)" }),
      el("textarea", {
        id: "v2-tam-comment",
        rows: 3,
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
        placeholder: "Add a note for this status change…",
      }),
    ]),
  ]);

  const saveBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
    text: "Save",
  });
  const cancelBtn = el("button", {
    type: "button",
    class:
      "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
    text: "Cancel",
  });
  const footer = el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, saveBtn]);
  const dialog = modalShell({ title: "Change TAM status", subtitle, bodyNode: form, footerNode: footer });
  cancelBtn.onclick = dialog.close;

  saveBtn.onclick = async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      const status = cleanText(document.getElementById("v2-tam-select")?.value);
      const comment = cleanText(document.getElementById("v2-tam-comment")?.value);

      await fetchJson(apiUrl(`/api/aircraft/${Number(row.id)}/tam-status`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status, auto: false }),
      });

      const forceWatchStatuses = new Set([
        "STORAGE",
        "CHECK WITH FM",
        "AOG",
        "MAINTENANCE",
        "INCIDENT",
        "AWAITING OPERATOR FEEDBACK",
      ]);
      if (forceWatchStatuses.has(normaliseTam(status))) {
        await fetchJson(apiUrl(`/api/aircraft/${Number(row.id)}/watch`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ onWatch: true, reason: "tam_status_auto_watch" }),
        });
      }

      if (comment) {
        await fetchJson(apiUrl(`/api/aircraft/${Number(row.id)}/comments`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: comment }),
        });
      }

      await loadAircraft();
      await loadFaCache().catch(() => {});
      dialog.close();
      renderRoute();
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  };
}

function buildFleetHeatmapGrid(rawAircraftList, { maxDisplay = 300 } = {}) {
  const list = Array.isArray(rawAircraftList) ? rawAircraftList : [];
  const totalMatching = list.length;
  const cap = Math.max(0, Number(maxDisplay) || 0);
  const sliced = cap ? list.slice(0, cap) : [];
  const rows = sliced.map((a) => aircraftRowForTable(a));
  const buckets = new Map();
  for (const r of rows) {
    const regionKey = cleanText(r.region) || "—";
    const opKey = cleanText(r.operator) || "—";
    if (!buckets.has(regionKey)) buckets.set(regionKey, new Map());
    const byOp = buckets.get(regionKey);
    if (!byOp.has(opKey)) byOp.set(opKey, []);
    byOp.get(opKey).push(r);
  }

  const regionKeys = Array.from(buckets.keys()).sort((a, b) => a.localeCompare(b));
  const regionBlocks = regionKeys.map((regionKey) => {
    const byOp = buckets.get(regionKey);
    const opKeys = Array.from(byOp.keys()).sort((a, b) => a.localeCompare(b));

    const opBlocks = opKeys.map((opKey) => {
      const opRows = byOp.get(opKey) || [];
      opRows.sort((a, b) => {
        const tamA = normaliseTam(a.tam);
        const tamB = normaliseTam(b.tam);
        if (tamA !== tamB) return tamA.localeCompare(tamB);
        const stA = cleanText(a.status) || "";
        const stB = cleanText(b.status) || "";
        if (stA !== stB) return stA.localeCompare(stB);
        return (cleanText(a.reg) || "").localeCompare(cleanText(b.reg) || "");
      });

      const tiles = opRows.map((r) => {
        const faUrl = r.flightawareUrl || flightawareAircraftUrl(r.reg);
        const tam = r.tam;
        const tile = el("button", {
          type: "button",
          class: `w-full text-left rounded-xl p-3 text-white shadow-sm hover:opacity-95 focus:ring-4 focus:ring-dae/20 ${tamTileClass(tam)}`,
          onclick: () => {
            if (faUrl) window.open(faUrl, "_blank", "noopener");
          },
          title: `${opKey} · ${r.owner} · ${normaliseTam(tam)} · ${r.loc} · ${r.tog || "—"}`,
        });
        tile.appendChild(
          el("div", {
            class: "text-sm font-semibold",
            text: `MSN ${r.msn} · ${r.reg} · ${r.model} · ${r.engine}`,
          })
        );
        tile.appendChild(el("div", { class: "mt-1 text-xs opacity-90", text: `${r.owner}` }));
        tile.appendChild(
          el("div", { class: "mt-2 text-xs opacity-95", text: `${normaliseTam(tam)} · ${r.loc} · ${r.tog || "—"}` })
        );
        return tile;
      });

      return el("div", { class: "rounded-xl border border-gray-200 bg-gray-50 p-3" }, [
        el("div", { class: "flex items-center justify-between gap-3 mb-3" }, [
          el("div", { class: "text-sm font-semibold text-gray-900", text: opKey }),
          el("div", { class: "text-xs text-gray-500", text: `${opRows.length} aircraft` }),
        ]),
        el("div", { class: "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" }, tiles),
      ]);
    });

    return el("div", { class: "mt-4" }, [
      el("div", { class: "mb-2 text-base font-semibold text-gray-900", text: regionKey }),
      el("div", { class: "space-y-3" }, opBlocks),
    ]);
  });

  const root =
    rows.length === 0
      ? el(
          "div",
          {
            class:
              "rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-600 font-medium",
          },
          ["No aircraft in this view."]
        )
      : el("div", { class: "space-y-2" }, regionBlocks);

  return {
    root,
    showing: rows.length,
    totalMatching,
    capped: totalMatching > rows.length,
  };
}

function heatmapRouteHasActiveFilters(params) {
  return ["q", "owner", "operator", "region", "tam", "model", "source", "watch"].some((k) =>
    Boolean(getParamText(params, k))
  );
}

function renderHeatmapPage() {
  const params = currentRouteParams();
  const baseList = state.aircraft || [];
  const filtered = applyAircraftFilters(baseList, params);
  const hasFilters = heatmapRouteHasActiveFilters(params);
  const maxDisplay = hasFilters ? 800 : 300;
  const { root, showing, totalMatching, capped } = buildFleetHeatmapGrid(filtered, { maxDisplay });

  const openAllQs = params.toString();

  return el("div", {}, [
    el("div", { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-800 tracking-tight", text: "Fleet heatmap" }),
        el("p", { class: "mt-1 text-gray-500", text: "Same layout as the dashboard heatmap; filters narrow tiles and stay in the URL." }),
      ]),
      el("div", { class: "flex flex-wrap items-center gap-3" }, [
        el("a", { class: "text-dae hover:underline text-sm whitespace-nowrap", href: "#/dashboard", text: "Dashboard" }),
        el("a", {
          class: "text-dae hover:underline text-sm whitespace-nowrap",
          href: openAllQs ? `#/all?${openAllQs}` : "#/all",
          text: "Matching aircraft table →",
        }),
      ]),
    ]),
    filterBar({
      baseList,
      params,
      show: { search: true, owner: true, operator: true, region: true, tam: true, source: true, watch: true },
    }),
    el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
      el("div", { class: "flex flex-wrap items-start justify-between gap-4 mb-4" }, [
        el("div", {}, [
          el("h2", { class: "text-lg font-semibold text-gray-900", text: "Fleet activity heatmap" }),
          el("p", { class: "text-sm text-gray-500", text: "Grouped by region and operator. Click a tile for FlightAware." }),
        ]),
        el(
          "button",
          {
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
            onclick: () => {
              try {
                history.replaceState(null, "", "#/heatmap");
              } catch {
                window.location.hash = "#/heatmap";
              }
              renderRoute();
            },
            text: "Clear filters",
          },
          []
        ),
      ]),
      renderHeatmapStatusLegend(),
      root,
      el("div", { class: "mt-3 text-xs text-gray-500" }, [
        totalMatching === 0
          ? el("span", { text: "No aircraft match these filters." })
          : el("span", {
              text: capped
                ? `Showing ${showing} of ${totalMatching} aircraft`
                : `Showing all ${showing} aircraft${hasFilters ? " matching filters" : ""}.`,
            }),
      ]),
    ]),
  ]);
}

function formatMatrixPct(count, rowTotal) {
  const n = Number(count) || 0;
  const total = Number(rowTotal) || 0;
  if (!total) return "—";
  return `${((n / total) * 100).toFixed(1)}%`;
}

function makeMatrixCountPctCell(linkCount, { count, rowTotal, href }) {
  const n = Number(count) || 0;
  const total = Number(rowTotal) || 0;
  const children = [];
  if (n > 0 && href) {
    children.push(linkCount({ label: n, href }));
  } else if (n > 0) {
    children.push(el("span", { class: "font-mono text-gray-900", text: String(n) }));
  } else {
    children.push(el("span", { class: "text-gray-400", text: "—" }));
  }
  children.push(
    el("div", {
      class: "text-[11px] font-mono text-gray-500",
      text: formatMatrixPct(n, total),
    })
  );
  return el("div", { class: "leading-tight py-0.5" }, children);
}

function buildFamilyVariantGroups(list, getFamily, getVariant) {
  const families = new Map();
  for (const a of list || []) {
    const family = getFamily(a);
    const variant = getVariant(a);
    if (!families.has(family)) families.set(family, new Map());
    const variants = families.get(family);
    if (!variants.has(variant)) variants.set(variant, []);
    variants.get(variant).push(a);
  }
  return families;
}

function countTamForList(aircraftList, tamLabels, tam) {
  return (aircraftList || []).filter((a) => effectiveTamForFilters(a) === tam).length;
}

function buildTamRowCounts(aircraftList, tamLabels) {
  const row = {};
  let total = 0;
  for (const t of tamLabels) {
    const n = countTamForList(aircraftList, tamLabels, t);
    row[t] = n;
    total += n;
  }
  row.__total = total;
  return row;
}

function renderTamMatrixTable({
  title,
  subtitle,
  rowHeader,
  tamLabels,
  dataRows,
  footerTotals,
  grandTotal,
  v2Link,
  linkCount,
  getRowLabel,
  getRowHref,
  getCellHref,
  expandable = false,
}) {
  const toggleFamilyVariants = (family) => {
    document.querySelectorAll(`tr[data-variant-parent="${CSS.escape(family)}"]`).forEach((tr) => {
      tr.classList.toggle("hidden");
    });
  };

  return el("div", { class: "mt-6" }, [
    el("div", { class: "mb-3" }, [
      el("h2", { class: "text-lg font-semibold text-gray-800", text: title }),
      subtitle ? el("p", { class: "mt-1 text-sm text-gray-500", text: subtitle }) : null,
    ].filter(Boolean)),
    el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" }, [
      el("div", { class: "overflow-x-auto" }, [
        el("table", { class: "w-full text-sm text-left text-gray-500" }, [
          el("thead", { class: "text-xs text-white uppercase bg-dae" }, [
            el("tr", {}, [
              el("th", { class: "px-4 py-3", text: rowHeader }),
              ...tamLabels.map((t) => el("th", { class: "px-4 py-3 whitespace-nowrap", text: t })),
              el("th", { class: "px-4 py-3 whitespace-nowrap", text: "Total" }),
            ]),
          ]),
          el(
            "tbody",
            {},
            dataRows.flatMap((r) => {
              const rowTotal = Number(r.__total) || 0;
              const isVariant = Boolean(r.__isVariant);
              const family = r.__family || "";
              const rowLabel = getRowLabel(r);
              const rowHref = getRowHref ? getRowHref(r) : null;
              const tr = el(
                "tr",
                {
                  class: [
                    isVariant ? "variant-row hidden bg-gray-50/60 border-b" : "bg-white border-b hover:bg-gray-50",
                    expandable && !isVariant ? "cursor-pointer" : "",
                  ]
                    .filter(Boolean)
                    .join(" "),
                  ...(isVariant ? { "data-variant-parent": family } : {}),
                  ...(expandable && !isVariant
                    ? {
                        onclick: (e) => {
                          if (e.target.closest("a")) return;
                          toggleFamilyVariants(family);
                        },
                      }
                    : {}),
                },
                [
                  el(
                    "td",
                    {
                      class: [
                        "px-4 py-3 font-medium text-gray-800",
                        isVariant ? "pl-8 text-sm text-gray-600" : "",
                      ]
                        .filter(Boolean)
                        .join(" "),
                    },
                    [
                      rowHref
                        ? el(
                            "a",
                            {
                              href: rowHref,
                              target: "_blank",
                              rel: "noopener",
                              class: "hover:text-dae hover:underline",
                              text: rowLabel,
                            },
                            []
                          )
                        : el("span", { text: rowLabel }),
                      expandable && !isVariant
                        ? el("span", { class: "ml-2 text-xs text-gray-400", text: "▸ variants" })
                        : null,
                    ].filter(Boolean)
                  ),
                  ...tamLabels.map((t) =>
                    el("td", { class: "px-4 py-3" }, [
                      makeMatrixCountPctCell(linkCount, {
                        count: r[t] || 0,
                        rowTotal,
                        href: getCellHref ? getCellHref(r, t) : null,
                      }),
                    ])
                  ),
                  el("td", { class: "px-4 py-3" }, [
                    makeMatrixCountPctCell(linkCount, {
                      count: rowTotal,
                      rowTotal,
                      href: rowHref,
                    }),
                  ]),
                ]
              );
              return [tr];
            })
          ),
          el("tr", { class: "bg-gray-50 border-t font-semibold" }, [
            el("td", { class: "px-4 py-3 text-gray-800", text: "Total" }),
            ...tamLabels.map((t) =>
              el("td", { class: "px-4 py-3" }, [
                makeMatrixCountPctCell(linkCount, {
                  count: footerTotals[t] || 0,
                  rowTotal: grandTotal,
                  href: v2Link(`#/all?tam=${encodeURIComponent(t)}`),
                }),
              ])
            ),
            el("td", { class: "px-4 py-3" }, [
              makeMatrixCountPctCell(linkCount, {
                count: grandTotal,
                rowTotal: grandTotal,
                href: v2Link("#/all"),
              }),
            ]),
          ]),
        ]),
      ]),
    ]),
  ]);
}

function makeGroupedFamilyCheckboxFilter({
  familyParamKey,
  variantParamKey,
  label,
  params,
  list,
  getFamily,
  getVariant,
}) {
  const families = buildFamilyVariantGroups(list, getFamily, getVariant);
  const familyNames = Array.from(families.keys()).sort((a, b) => a.localeCompare(b));
  const selectedFamilies = parseCsvParam(params, familyParamKey);
  const selectedVariants = parseCsvParam(params, variantParamKey);
  const btnId = `v2-filter-${familyParamKey}-btn`;
  const panelId = `v2-filter-${familyParamKey}-panel`;

  const allVariants = familyNames.flatMap((fam) => Array.from(families.get(fam).keys()).sort());
  const activeFamilies = selectedFamilies ? new Set(selectedFamilies) : new Set(familyNames);
  const activeVariants = selectedVariants ? new Set(selectedVariants) : new Set(allVariants);

  const summaryText = () => {
    if (!selectedFamilies && !selectedVariants) {
      return `All (${familyNames.length} families)`;
    }
    const famCount = activeFamilies.size;
    const varCount = activeVariants.size;
    if (famCount >= familyNames.length && varCount >= allVariants.length) {
      return `All (${familyNames.length} families)`;
    }
    return `${famCount} fam · ${varCount} var`;
  };

  const applySelection = (nextFamilies, nextVariants) => {
    const p = new URLSearchParams(params.toString());
    if (!nextFamilies || nextFamilies.length >= familyNames.length) p.delete(familyParamKey);
    else p.set(familyParamKey, nextFamilies.join(","));
    if (!nextVariants || nextVariants.length >= allVariants.length) p.delete(variantParamKey);
    else p.set(variantParamKey, nextVariants.join(","));
    setHashParams(p);
  };

  const renderPanel = (host) => {
    host.replaceChildren(
      el("div", { class: "max-h-64 overflow-y-auto p-2 space-y-2" }, [
        ...familyNames.map((fam) => {
          const variants = Array.from(families.get(fam).keys()).sort((a, b) => a.localeCompare(b));
          const familyChecked = activeFamilies.has(fam);
          return el("div", { class: "border-b border-gray-100 pb-2" }, [
            el("label", { class: "flex items-center gap-2 text-sm font-medium text-gray-800 py-1" }, [
              el("input", {
                type: "checkbox",
                checked: familyChecked,
                onchange: (e) => {
                  const nextFamilies = new Set(activeFamilies);
                  const nextVariants = new Set(activeVariants);
                  if (e.target.checked) {
                    nextFamilies.add(fam);
                    variants.forEach((v) => nextVariants.add(v));
                  } else {
                    nextFamilies.delete(fam);
                    variants.forEach((v) => nextVariants.delete(v));
                  }
                  applySelection([...nextFamilies], [...nextVariants]);
                },
              }),
              el("span", { text: fam }),
            ]),
            el(
              "div",
              { class: "pl-6 space-y-1" },
              variants.map((variant) =>
                el("label", { class: "flex items-center gap-2 text-xs text-gray-600 py-0.5" }, [
                  el("input", {
                    type: "checkbox",
                    checked: activeVariants.has(variant),
                    onchange: (e) => {
                      const nextVariants = new Set(activeVariants);
                      const nextFamilies = new Set(activeFamilies);
                      if (e.target.checked) {
                        nextVariants.add(variant);
                        if (variants.every((v) => nextVariants.has(v))) nextFamilies.add(fam);
                      } else {
                        nextVariants.delete(variant);
                        nextFamilies.delete(fam);
                      }
                      applySelection([...nextFamilies], [...nextVariants]);
                    },
                  }),
                  el("span", { text: variant }),
                ])
              )
            ),
          ]);
        }),
      ])
    );
  };

  return el("div", { class: "relative min-w-[190px]" }, [
    el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: btnId, text: label }),
    el(
      "button",
      {
        id: btnId,
        type: "button",
        class:
          "w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 text-left focus:ring-4 focus:ring-dae/20 focus:border-dae",
        text: summaryText(),
        onclick: (e) => {
          e.stopPropagation();
          const panel = document.getElementById(panelId);
          if (!panel) return;
          const open = !panel.classList.contains("hidden");
          document.querySelectorAll("[id^='v2-filter-'][id$='-panel']").forEach((n) => n.classList.add("hidden"));
          if (!open) {
            panel.classList.remove("hidden");
            renderPanel(panel);
          }
        },
      },
      []
    ),
    el("div", {
      id: panelId,
      "data-v2-filter-panel": "1",
      class:
        "hidden absolute z-30 mt-1 w-72 max-w-[90vw] rounded-lg border border-gray-200 bg-white shadow-lg",
    }),
  ]);
}

function buildFamilyMatrixRows(list, tamLabels, getFamily, getVariant) {
  const groups = buildFamilyVariantGroups(list, getFamily, getVariant);
  const rows = [];
  for (const family of Array.from(groups.keys()).sort((a, b) => a.localeCompare(b))) {
    const variants = groups.get(family);
    const allInFamily = Array.from(variants.values()).flat();
    rows.push({
      __family: family,
      __label: family,
      ...buildTamRowCounts(allInFamily, tamLabels),
    });
    for (const variant of Array.from(variants.keys()).sort((a, b) => a.localeCompare(b))) {
      rows.push({
        __family: family,
        __isVariant: true,
        __label: variant,
        __variant: variant,
        ...buildTamRowCounts(variants.get(variant), tamLabels),
      });
    }
  }
  return rows;
}

function matrixFooterTotals(dataRows, tamLabels, { familyOnly = false } = {}) {
  const sourceRows = familyOnly ? dataRows.filter((r) => !r.__isVariant) : dataRows;
  const totals = {};
  let grandTotal = 0;
  for (const t of tamLabels) {
    totals[t] = sourceRows.reduce((sum, r) => sum + Number(r[t] || 0), 0);
    grandTotal += totals[t];
  }
  return { totals, grandTotal };
}

function renderDashboard() {
  const list = state.aircraft || [];
  const byTam = new Map();
  const byRegion = new Map();
  const byOwner = new Map();
  const ownersSet = new Set();
  const tamsSet = new Set();
  for (const a of list) {
    const tam = effectiveTamForFilters(a);
    byTam.set(tam, (byTam.get(tam) || 0) + 1);
    const region = cleanText(a?.region) || "—";
    byRegion.set(region, (byRegion.get(region) || 0) + 1);
    const owner = cleanText(a?.owner) || "—";
    ownersSet.add(owner);
    tamsSet.add(tam);
    const ownerKey = owner.toUpperCase();
    const current = byOwner.get(ownerKey) || new Map();
    current.set(tam, (current.get(tam) || 0) + 1);
    byOwner.set(ownerKey, current);
  }
  const aogOnlyCount = list.filter((a) => effectiveTamForFilters(a) === "AOG").length;
  const nonServiceAllCount = list.filter((a) => effectiveTamForFilters(a) !== "IN SERVICE").length;

  const owners = Array.from(ownersSet).sort((a, b) => a.localeCompare(b));
  const tamLabels = Array.from(tamsSet).sort((a, b) => a.localeCompare(b));

  const regions = Array.from(new Set(list.map((a) => cleanText(a?.region) || "—"))).sort((a, b) =>
    a.localeCompare(b)
  );
  const byRegionTam = new Map();
  for (const region of regions) {
    byRegionTam.set(region, new Map());
  }
  for (const a of list) {
    const region = cleanText(a?.region) || "—";
    const tam = effectiveTamForFilters(a);
    const map = byRegionTam.get(region) || new Map();
    map.set(tam, (map.get(tam) || 0) + 1);
    byRegionTam.set(region, map);
  }

  function v2Link(pathWithHash) {
    // Absolute link so target=_blank works cleanly.
    const base = `${window.location.origin}/v2`;
    const hash = pathWithHash.startsWith("#") ? pathWithHash : `#/${pathWithHash.replace(/^\//, "")}`;
    return `${base}${hash}`;
  }

  function linkCount({ label, href }) {
    const n = Number(label) || 0;
    if (!n) {
      return el("span", { class: "text-gray-400", text: "—" });
    }
    return el(
      "a",
      {
        href,
        target: "_blank",
        rel: "noopener",
        class: "font-mono text-gray-900 hover:text-dae hover:underline",
        text: String(n),
      },
      []
    );
  }

  const ownerMatrixRows = owners.map((owner) => {
    const key = owner.toUpperCase();
    const map = byOwner.get(key) || new Map();
    const row = { owner };
    let total = 0;
    for (const t of tamLabels) {
      const count = Number(map.get(t) || 0);
      row[t] = count;
      total += count;
    }
    row.__total = total;
    return row;
  });
  const ownerMatrixTotals = {};
  let grandTotal = 0;
  for (const t of tamLabels) {
    ownerMatrixTotals[t] = ownerMatrixRows.reduce((sum, r) => sum + Number(r[t] || 0), 0);
    grandTotal += ownerMatrixTotals[t];
  }

  const modelFamilyRows = buildFamilyMatrixRows(
    list,
    tamLabels,
    aircraftModelFamily,
    (a) => cleanText(a?.aircraft_type) || "—"
  );
  const { totals: modelFamilyTotals, grandTotal: modelGrandTotal } = matrixFooterTotals(
    modelFamilyRows,
    tamLabels,
    { familyOnly: true }
  );

  const engineFamilyRows = buildFamilyMatrixRows(
    list,
    tamLabels,
    aircraftEngineFamily,
    (a) => cleanText(a?.engine_model) || "—"
  );
  const { totals: engineFamilyTotals, grandTotal: engineGrandTotal } = matrixFooterTotals(
    engineFamilyRows,
    tamLabels,
    { familyOnly: true }
  );

  const singaporeFm = isSingaporeFmSession();
  const content = el("div", {}, [
    el("div", { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-800 tracking-tight", text: "Dashboard" }),
        el("p", {
          class: "mt-1 text-gray-500",
          text: singaporeFm
            ? "Fleet overview (aircraft visible to your account)."
            : "Operational overview (v2 preview).",
        }),
        el("p", { id: "v2-dashboard-xrm-meta", class: "mt-1 text-xs text-gray-400", text: "" }),
      ]),
    ]),
    singaporeFm ? null : el("div", { id: "v2-dashboard-avherald-dae", class: "mt-2" }, []),
    renderKpiCards(
      singaporeFm
        ? [
            card("Aircraft", list.length, "Visible to your account"),
            card(
              "In service",
              list.filter((a) => effectiveTamForFilters(a) === "IN SERVICE").length,
              ""
            ),
            card("AOG", aogOnlyCount, ""),
          ]
        : [
            card("Aircraft", list.length, "Visible to your account"),
            card("AOG", aogOnlyCount, "", { hash: "#/aog" }),
            card("Non-service (all)", nonServiceAllCount, "", { hash: "#/non-service-all" }),
          ]
    ),
    el("div", { class: "mt-6" }, [
      el("div", { class: "mb-3" }, [
        el("h2", { class: "text-lg font-semibold text-gray-800", text: "Aircraft by TAM status" }),
      ]),
      buildTable({
        title: "",
    rows: [
      ...Array.from(byTam.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([tam, count]) => ({ tam, count, pct: list.length ? (count / list.length) * 100 : 0 })),
      { __total: true, tam: "TOTAL", count: -1, totalCount: list.length, pct: 100 },
    ],
      columns: [
        {
          key: "tam",
          label: "TAM status",
          nowrap: true,
          class: TAM_TABLE_CELL_CLASS,
          get: (r) => {
            if (r.__total) {
              return el("span", { class: "font-semibold text-gray-900", text: "Total" });
            }
            return el(
              "a",
              { href: v2Link(`#/all?tam=${encodeURIComponent(r.tam)}`), target: "_blank", rel: "noopener", class: "inline-flex" },
              [tamBadge(r.tam)]
            );
          },
          sortValue: (r) => (r.__total ? "~~~~" : r.tam),
        },
        {
          key: "count",
          label: "Count",
        get: (r) => {
          if (r.__total) {
            return el("span", { class: "font-mono font-semibold text-gray-900", text: String(r.totalCount || 0) });
          }
          return linkCount({
            label: r.count,
            href: v2Link(`#/all?tam=${encodeURIComponent(r.tam)}`),
          });
        },
        sortValue: (r) => (r.__total ? Number.NEGATIVE_INFINITY : Number(r.count) || 0),
        },
        {
          key: "pct",
          label: "% of fleet",
        get: (r) => {
          if (r.__total) {
            return el("span", { class: "font-mono font-semibold text-gray-900", text: "100.0%" });
          }
          return el("span", {
            class: "font-mono text-gray-700",
            text: Number.isFinite(r.pct) ? `${r.pct.toFixed(1)}%` : "—",
          });
        },
        sortValue: (r) => (r.__total ? Number.NEGATIVE_INFINITY : Number(r.pct) || 0),
        },
      ],
    }),
    ]),
    (() => {
      const regionMatrixRows = regions.map((region) => {
        const map = byRegionTam.get(region) || new Map();
        const row = { region };
        let total = 0;
        for (const t of tamLabels) {
          const n = Number(map.get(t) || 0);
          row[t] = n;
          total += n;
        }
        row.__total = total;
        return row;
      });
      const { totals: regionTamTotals, grandTotal: regionGrandTotal } = matrixFooterTotals(
        regionMatrixRows,
        tamLabels
      );
      return renderTamMatrixTable({
        title: "Aircraft by region",
        rowHeader: "Region",
        tamLabels,
        dataRows: regionMatrixRows,
        footerTotals: regionTamTotals,
        grandTotal: regionGrandTotal,
        v2Link,
        linkCount,
        getRowLabel: (r) => r.region,
        getRowHref: (r) => v2Link(`#/all?region=${encodeURIComponent(r.region)}`),
        getCellHref: (r, t) =>
          v2Link(`#/all?tam=${encodeURIComponent(t)}&region=${encodeURIComponent(r.region)}`),
      });
    })(),
    singaporeFm
      ? null
      : renderTamMatrixTable({
          title: "Aircraft by FM (Owner) and TAM status",
          rowHeader: "FM",
          tamLabels,
          dataRows: ownerMatrixRows,
          footerTotals: ownerMatrixTotals,
          grandTotal,
          v2Link,
          linkCount,
          getRowLabel: (r) => r.owner,
          getRowHref: (r) => v2Link(`#/all?owner=${encodeURIComponent(r.owner)}`),
          getCellHref: (r, t) =>
            v2Link(`#/all?owner=${encodeURIComponent(r.owner)}&tam=${encodeURIComponent(t)}`),
        }),
    singaporeFm
      ? null
      : renderTamMatrixTable({
          title: "Aircraft by model family and TAM status",
          subtitle: "Click a family row to expand individual aircraft types. Percentages are share of that row's total.",
          rowHeader: "Model family",
          tamLabels,
          dataRows: modelFamilyRows,
          footerTotals: modelFamilyTotals,
          grandTotal: modelGrandTotal,
          v2Link,
          linkCount,
          expandable: true,
          getRowLabel: (r) => r.__label,
          getRowHref: (r) => {
            if (r.__isVariant) {
              return v2Link(`#/all?model=${encodeURIComponent(r.__variant)}`);
            }
            return v2Link(`#/all?model_family=${encodeURIComponent(r.__family)}`);
          },
          getCellHref: (r, t) => {
            if (r.__isVariant) {
              return v2Link(
                `#/all?model=${encodeURIComponent(r.__variant)}&tam=${encodeURIComponent(t)}`
              );
            }
            return v2Link(
              `#/all?model_family=${encodeURIComponent(r.__family)}&tam=${encodeURIComponent(t)}`
            );
          },
        }),
    singaporeFm
      ? null
      : renderTamMatrixTable({
          title: "Aircraft by engine family and TAM status",
          subtitle: "Click a family row to expand individual engine models. Percentages are share of that row's total.",
          rowHeader: "Engine family",
          tamLabels,
          dataRows: engineFamilyRows,
          footerTotals: engineFamilyTotals,
          grandTotal: engineGrandTotal,
          v2Link,
          linkCount,
          expandable: true,
          getRowLabel: (r) => r.__label,
          getRowHref: (r) => {
            if (r.__isVariant) {
              return v2Link(`#/all?engine=${encodeURIComponent(r.__variant)}`);
            }
            return v2Link(`#/all?engine_family=${encodeURIComponent(r.__family)}`);
          },
          getCellHref: (r, t) => {
            if (r.__isVariant) {
              return v2Link(
                `#/all?engine=${encodeURIComponent(r.__variant)}&tam=${encodeURIComponent(t)}`
              );
            }
            return v2Link(
              `#/all?engine_family=${encodeURIComponent(r.__family)}&tam=${encodeURIComponent(t)}`
            );
          },
        }),
    el("div", { class: "mt-6" }, [
      el("div", { class: "flex items-center justify-between gap-4 mb-3" }, [
        el("div", {}, [
          el("h2", { class: "text-lg font-semibold text-gray-800", text: "Recent status changes" }),
          el("p", {
            class: "mt-1 text-sm text-gray-500",
            text: "Logged TAM and FlightAware-driven transitions in the last 24 hours.",
          }),
          el("p", { id: "v2-dashboard-status-changes-meta", class: "mt-1 text-xs text-gray-500", text: "Loading…" }),
        ]),
        el("a", { class: "text-dae hover:underline text-sm", href: "#/status-recent", text: "Open status changes →" }),
      ]),
      renderStatusChangesTableShell({
        tbodyId: "v2-dashboard-status-changes-body",
        loadingText: "Loading status changes…",
      }),
    ]),
    (() => {
      const hm = buildFleetHeatmapGrid(list, { maxDisplay: 300 });
      return el("div", { class: "mt-6 bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
        el("div", { class: "flex flex-wrap items-center justify-between gap-4 mb-4" }, [
          el("div", {}, [
            el("h2", { class: "text-lg font-semibold text-gray-900", text: "Fleet activity heatmap" }),
          ]),
          el("div", { class: "flex flex-wrap gap-3" }, [
            el("a", { class: "text-dae hover:underline text-sm whitespace-nowrap", href: "#/heatmap", text: "Filtered heatmap →" }),
            el("a", { class: "text-dae hover:underline text-sm whitespace-nowrap", href: "#/all", text: "Open all aircraft →" }),
          ]),
        ]),
        renderHeatmapStatusLegend(),
        hm.root,
        el("div", { class: "mt-3 text-xs text-gray-500" }, [
          list.length === 0
            ? el("span", { text: "No aircraft loaded." })
            : el("span", {
                text: hm.capped ? `Showing ${hm.showing} of ${hm.totalMatching} aircraft` : `Showing all ${hm.showing} aircraft`,
              }),
        ]),
      ]);
    })(),
    singaporeFm
      ? null
      : el("div", { class: "mt-6" }, [
          el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
            el("div", { class: "flex items-start justify-between gap-4" }, [
              el("div", {}, [
                el("h2", { class: "text-lg font-semibold text-gray-800", text: "Active crons" }),
                el("p", {
                  class: "mt-1 text-sm text-gray-500",
                  text: "Linux schedules on the server run ME, Spirit, Americas, and similar jobs. Details for each job live under Settings → Crons.",
                }),
              ]),
              el("div", { class: "flex items-center gap-2" }, [
                el("button", {
                  id: "v2-cron-refresh",
                  class:
                    "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                  text: "Refresh",
                }),
                el("button", {
                  id: "v2-cron-run",
                  class:
                    "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                  text: "Run now",
                }),
              ]),
            ]),
            el("div", { id: "v2-cron-status", class: "mt-3 text-sm text-gray-700" }, ["Loading…"]),
            el("p", { id: "v2-cron-log-meta", class: "mt-2 text-xs text-gray-500", text: "" }),
            el("pre", {
              id: "v2-cron-logs",
              class:
                "mt-2 max-h-56 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 whitespace-pre-wrap font-mono",
              text: "",
            }),
          ]),
        ]),
  ]);
  return content;
}

/** Minutes threshold: strictly longer than 21 full days on the ground clock used in the fleet table. */
const GROUND_OVER_21D_MINUTES = 21 * 24 * 60;

/**
 * FlightAware cache + manual last-flight blend for "Time on ground" — single source for table rows and sidebar filters.
 * @returns {{ faKey: string|null, faEntry: object|null, faInterp: object|null, togMinutes: number|null }}
 */
function computeTableTimeOnGroundContext(a) {
  const faKey = inferFaKey(a);
  const faEntry = lookupFaCacheEntry(a, state.faCacheByIdent);
  const faInterp = faEntry?.status ? interpretFaStatus(faEntry.status, a) : null;
  let togMinutes = faInterp?.airborne
    ? 0
    : faInterp?.timeOnGroundMinutes != null
    ? faInterp.timeOnGroundMinutes
    : minutesSince(a?.manual_status_last_flight)
    ? Math.max(0, minutesSince(a?.manual_status_last_flight))
    : minutesSince(a?.last_flight_at) && cleanText(a?.last_location_source).toUpperCase().startsWith("FR24")
    ? Math.max(0, minutesSince(a?.last_flight_at))
    : minutesSince(a?.last_flight_at)
    ? Math.max(0, minutesSince(a?.last_flight_at))
    : null;

  const manualLastIso = cleanText(a?.manual_status_last_flight);
  const manualTs = manualLastIso ? Date.parse(manualLastIso) : NaN;
  const manualGroundMin = Number.isFinite(manualTs) ? minutesSince(manualLastIso) : null;
  if (!faInterp?.airborne && manualGroundMin != null && faEntry?.status) {
    const latestDepMs = faLatestDepartureMs(faEntry.status);
    const flewAfterManualAnchor =
      Number.isFinite(latestDepMs) && Number.isFinite(manualTs) && latestDepMs > manualTs + 5 * 60 * 1000;
    if (!flewAfterManualAnchor) {
      const faTog = faInterp?.timeOnGroundMinutes;
      if (faTog != null) {
        togMinutes = Math.max(manualGroundMin, faTog);
      } else {
        togMinutes = manualGroundMin;
      }
    }
  }
  return { faKey, faEntry, faInterp, togMinutes };
}

function matchesMoreThan21DaysOnGround(a) {
  if (isRussianFleetAircraft(a)) return false;
  const tam = effectiveTamForFilters(a);
  if (tam === "RUSSIA") return false;
  if (tam !== "IN SERVICE") return false;
  const { faInterp, togMinutes } = computeTableTimeOnGroundContext(a);
  if (faInterp?.airborne) return false;
  return Number.isFinite(togMinutes) && togMinutes > GROUND_OVER_21D_MINUTES;
}

function aircraftRowForTable(a) {
  const id = Number(a?.id);
  const msn = cleanText(a?.msn) || "—";
  const model = cleanText(a?.aircraft_type) || cleanText(a?.aircraftType) || cleanText(a?.type) || "—";
  const engine = cleanText(a?.engine_model) || "—";
  const reg = displayRegistration(a);
  const { faKey, faEntry, faInterp, togMinutes } = computeTableTimeOnGroundContext(a);
  const tog =
    togMinutes == null
      ? ""
      : currentRouteKey() === "no-flights-24h"
        ? formatTogDaysAmount(togMinutes)
        : formatTog(togMinutes);
  const faLoc = faEntry?.status
    ? faLocationLabel({ status: faEntry.status, aircraft: a, isAirborne: Boolean(faInterp?.airborne) })
    : "";
  const loc =
    cleanText(a?.manual_status_location) ||
    (cleanText(faLoc) && cleanText(faLoc) !== "—" ? cleanText(faLoc) : "") ||
    cleanText(a?.last_known_location) ||
    cleanText(a?.location) ||
    "—";
  const tamRaw = cleanText(a?.tam_status) || "";
  const owner = cleanText(a?.owner) || "—";
  const onWatch = Boolean(a?.watch_on);
  const comments = cleanText(a?.notes) || "";
  const commentCount = Number(a?.comment_count || 0);
  const lastComment = a?.last_comment && typeof a.last_comment === "object" ? a.last_comment : null;
  const operator = cleanText(a?.operator) || "—";
  const manualStatusText = cleanText(a?.manual_status_text) || "";
  // Status is NOT a raw FlightAware phase label. We show only:
  // - IN SERVICE (airborne or effectively recent)
  // - The time-on-ground buckets (1 DAY, 2 DAYS, 1W, 1MO, ...)
  // Manual status text is treated as an optional override only if it matches our bucket vocabulary.
  const manualOverrideStatus = normaliseStatusForDisplay(manualStatusText);
  const statusLabel =
    manualOverrideStatus !== "—"
      ? manualOverrideStatus
      : faInterp?.airborne
      ? "AIRBORNE"
      : togMinutes != null
      ? formatStatusBucket(togMinutes)
      : "UNKNOWN";
  // TAM shown in tables should match the same logic used by filters/dashboards.
  const tam = tamRaw ? normaliseTam(tamRaw) : effectiveTamForFilters(a);
  const source =
    cleanText(a?.last_location_source) ||
    cleanText(a?.watch_source) ||
    cleanText(a?.tam_status_source) ||
    (faEntry ? "auto-flightaware" : "—");
  const flightawareUrl = cleanText(a?.flightaware_url) || "";
  const trackerUrl = cleanText(a?.tracker_url) || "";
  const region = cleanText(a?.region) || "";
  const isMeFleet = Boolean(a?.is_me_fleet);
  const expectedReleaseDate = cleanText(a?.expected_release_date) || "";
  const nextFlightAt =
    cleanText(a?.scheduledDepartureAt) ||
    cleanText(a?.scheduled_departure_at) ||
    cleanText(a?.watch_scheduled_departure) ||
    null;
  const nextFlight = nextFlightAt ? "YES" : "NO";
  const sourceLabel = normaliseSourceLabel(source, { hasFaCache: Boolean(faEntry) });
  // Freshness dot is about the last successful TRACKING CHECK for this aircraft (manual refresh / cron cache fetch).
  // This makes refreshed rows go green immediately even if the last landing itself is older.
  const faError = cleanText(faEntry?.response_error);
  const isUnknownLocation = !cleanText(loc) || cleanText(loc) === "—";

  const checkMs = Number.isFinite(Date.parse(faEntry?.fetched_at))
    ? Date.parse(faEntry.fetched_at)
    : Number.isFinite(Date.parse(faEntry?.status?.updated_at))
    ? Date.parse(faEntry.status.updated_at)
    : NaN;
  const ageHours = Number.isFinite(checkMs) ? (Date.now() - checkMs) / (1000 * 60 * 60) : Number.POSITIVE_INFINITY;

  const freshness =
    faError ? "black" : isUnknownLocation ? "unknown" : ageHours <= 12 ? "green" : ageHours <= 24 ? "blue" : ageHours <= 48 ? "yellow" : "red";
  return {
    id,
    __raw: a && typeof a === "object" ? a : null,
    msn,
    model,
    engine,
    reg,
    status: statusLabel,
    tog,
    loc,
    tam,
    owner,
    operator,
    source: sourceLabel,
    onWatch,
    comments,
    commentCount,
    lastComment,
    manualStatusText,
    manualFlag: a?.manual_flag && typeof a.manual_flag === "object" ? a.manual_flag : null,
    manualUpdatedAt: cleanText(a?.manual_status_updated_at) || "",
    manualUpdatedBy: cleanText(a?.manual_status_updated_by) || "",
    flightawareUrl,
    trackerUrl,
    region,
    isMeFleet,
    expectedReleaseDate,
    nextFlightAt,
    nextFlight,
    updatedAtMs: Number.isFinite(checkMs) ? checkMs : null,
    freshness,
    togMinutes: Number.isFinite(togMinutes) ? togMinutes : null,
  };
}

function spiritCommentsCell(r) {
  if (!r || !Number.isFinite(Number(r.id)) || Number(r.id) <= 0) {
    return el(
      "div",
      { class: "actracker-v2-comments-meta text-xs text-gray-500 max-w-xs" },
      "Add this MSN in Administration to enable comments."
    );
  }
  return renderFleetCommentsColumn(r);
}

function renderSpiritTrackerPage() {
  const list = state.aircraft || [];
  const pairs = listSpiritTrackerDisplayPairs(list);

  try {
    const ids = [...new Set(pairs.map(({ ac }) => Number(ac?.id)).filter((v) => Number.isFinite(v) && v > 0))];
    const beforeKey = cleanText(state.bulkCommentsKey);
    loadBulkComments(ids)
      .then(() => {
        if (cleanText(state.bulkCommentsKey) !== beforeKey) {
          renderRoute();
        }
      })
      .catch(() => {});
  } catch {
    // ignore
  }

  const wrapTable = (table) =>
    el("div", {
      class:
        "overflow-x-auto w-full max-w-full mx-auto mb-8 border border-gray-200 rounded-xl bg-white shadow-sm box-border",
    }, [table]);

  const thCls =
    "px-2 sm:px-3 py-2 text-xs font-semibold text-white bg-dae border-b border-dae/30 align-top whitespace-normal leading-tight min-w-0";
  const tdCls = "px-2 sm:px-3 py-2 border-b border-gray-100 align-top min-w-0 break-words text-xs";

  const headers = ["MSN", "Reg", "Operator", "TAM", "TOG", "Loc", "Source", "Links", "Comments"];

  const colgroup = el("colgroup", {}, [
    el("col", { style: { width: "4%" } }),
    el("col", { style: { width: "6%" } }),
    el("col", { style: { width: "8%" } }),
    el("col", { style: { width: "13%" } }),
    el("col", { style: { width: "7%" } }),
    el("col", { style: { width: "7%" } }),
    el("col", { style: { width: "5%" } }),
    el("col", { style: { width: "8%" } }),
    el("col", { style: { width: "42%" } }),
  ]);

  const bodyRows =
    pairs.length === 0
      ? [
          el("tr", {}, [
            el("td", {
              class: `${tdCls} text-gray-500 text-center`,
              colspan: String(headers.length),
              text: "No Spirit roster rows loaded. Fleet must include Spirit Airlines / Spirit-NonDAE tails with FM AL (Americas). Run scripts/upsert-spirit-nondae-fleet.mjs once if needed.",
            }),
          ]),
        ]
      : pairs.map(({ ac, displayMsn }) => {
          const raw = ac && typeof ac === "object" ? ac : null;
          const r = aircraftRowForTable(ac);
          const msnDisp = cleanText(displayMsn) || r.msn;
          return el("tr", { class: "hover:bg-gray-50" }, [
            el("td", { class: `${tdCls} font-mono font-medium`, text: msnDisp }),
            el("td", { class: `${tdCls} font-mono`, text: r.reg }),
            el("td", { class: `${tdCls} text-xs`, text: cleanText(raw?.operator) || r.operator }),
            el("td", { class: `${tdCls} ${TAM_TABLE_CELL_CLASS}` }, [
              !cleanText(r.tam) || cleanText(r.tam) === "—"
                ? el("span", { class: "text-gray-400", text: "—" })
                : tamBadge(r.tam),
            ]),
            el("td", { class: tdCls, text: r.tog || "—" }),
            el("td", { class: tdCls, text: r.loc }),
            el("td", { class: `${tdCls} font-mono text-xs`, text: r.source }),
            el("td", { class: `${tdCls} align-top whitespace-normal` }, [
              spiritTrackingLinksForAirframeRow({ ...r, reg: cleanText(raw?.registration) || r.reg }),
            ]),
            el("td", { class: `${tdCls} ${COMMENTS_TABLE_CELL_CLASS} min-w-0` }, [spiritCommentsCell(r)]),
          ]);
        });

  const table = el("table", { class: "w-full text-sm text-left text-gray-700 table-fixed" }, [
    colgroup,
    el(
      "thead",
      {},
      [el("tr", {}, headers.map((label) => el("th", { class: thCls, text: label })))]
    ),
    el("tbody", {}, bodyRows),
  ]);

  return el("div", { class: "mt-2 space-y-2 w-full max-w-[min(100%,1920px)] mx-auto px-2 sm:px-3 box-border min-w-0" }, [
    el("div", { class: "w-full max-w-full mb-4 min-w-0" }, [
      el("h1", { class: "text-2xl font-bold text-gray-900 tracking-tight", text: "Spirit tracker" }),
    ]),
    wrapTable(table),
  ]);
}

function formatUtcTooltip(ms) {
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  if (!Number.isFinite(d.getTime())) return null;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mmm = months[d.getUTCMonth()] || "???";
  const yyyy = String(d.getUTCFullYear());
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${dd}-${mmm}-${yyyy} ${hh}:${mm} UTC`;
}

function formatCountdownHm(msUntil) {
  if (!Number.isFinite(msUntil)) return "—";
  const totalMinutes = Math.max(0, Math.floor(msUntil / (1000 * 60)));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatBytesBrief(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function isSystemCommentAuthor(author) {
  const a = cleanText(author).toLowerCase();
  return !a || a === "system" || a === "auto" || a === "actracker" || a === "daemon";
}

/** Latest user-authored comment body only (no timestamp / author). System comments ignored. */
function commentsTextForAircraftExcelRow(row) {
  if (!row || typeof row !== "object") return "";
  const id = Number(row.id);
  const preview = state.bulkCommentsByAircraftId instanceof Map ? state.bulkCommentsByAircraftId.get(id) : null;
  const previewItems = sortCommentsNewestFirst(preview);

  for (const c of previewItems) {
    if (isSystemCommentAuthor(c?.author)) continue;
    const body = cleanText(c?.body);
    if (body) return body;
  }

  if (!isSystemCommentAuthor(row.lastComment?.author)) {
    const body = cleanText(row.lastComment?.body);
    if (body) return body;
  }

  return "";
}

function renderNoFlightsStatusSummary(summaryRows) {
  const counts = new Map();
  for (const r of summaryRows || []) {
    const tam = normaliseTam(r.tam);
    counts.set(tam, (counts.get(tam) || 0) + 1);
  }
  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  return el("div", { class: "mb-4" }, [
    el("p", { class: "text-sm text-gray-600 mb-3", text: NO_FLIGHTS_FIVE_DAYS_LEGEND }),
    sorted.length
      ? renderKpiCards(
          sorted.map(([tam, n]) =>
            card(tam, n, "aircraft", { hash: `#/no-flights-24h?tam=${encodeURIComponent(tam)}` })
          )
        )
      : el("p", { class: "text-sm text-gray-500", text: "No aircraft match this view." }),
  ]);
}

function renderAllAircraft({ title, subtitle = null, predicate, defaultSortKey = "owner", showNoFlightsSummary = false } = {}) {
  const params = currentRouteParams();
  const inspectorReadOnly = isInspectorSession();

  const baseList = (state.aircraft || []).filter((a) => (typeof predicate === "function" ? predicate(a) : true));

  const filteredAircraft = applyAircraftFilters(baseList, params);

  const mappedRows = filteredAircraft.map((a) => aircraftRowForTable(a));
  const rows = showNoFlightsSummary ? sortNoFlightsRowsByOperator(mappedRows) : mappedRows;
  const summaryRows = showNoFlightsSummary ? baseList.map((a) => aircraftRowForTable(a)) : [];

  // Load full comment threads for visible rows (single bulk call; API returns all rows per aircraft).
  try {
    const ids = rows.map((r) => Number(r?.id)).filter((v) => Number.isFinite(v) && v > 0);
    const beforeKey = cleanText(state.bulkCommentsKey);
    loadBulkComments(ids)
      .then(() => {
        if (cleanText(state.bulkCommentsKey) !== beforeKey) {
          renderRoute();
        }
      })
      .catch(() => {});
  } catch {
    // ignore
  }

  const reportTitle = `${title || "All aircraft"} — ${formatTimestampForFile(new Date())}`;

  const buildExportRows = () =>
    rows.map((r) => [
      r.msn,
      r.reg,
      r.operator,
      r.owner,
      r.status,
      normaliseTam(r.tam),
      r.nextFlight,
      showNoFlightsSummary && Number.isFinite(r.togMinutes)
        ? formatTogDaysAmount(r.togMinutes)
        : r.tog || "—",
      r.loc,
      r.source,
      r.onWatch ? "YES" : "NO",
      commentsTextForAircraftExcelRow(r),
    ]);

  const exportHeaders = [
    "MSN",
    "Registration",
    "Operator",
    "FM",
    "Status",
    "TAM status",
    "Next flight",
    showNoFlightsSummary ? "Time on ground (DAYS)" : "Time on ground",
    "Location",
    "Source",
    "On watch",
    "Comments",
  ];

  const buildNoFlightsEmailTemplateRows = () =>
    rows.map((r) => ({
      msn: r.msn,
      reg: r.reg,
      operator: r.operator,
      owner: r.owner,
      fm: r.owner,
      tam: normaliseTam(r.tam),
      togMinutes: Number.isFinite(r.togMinutes) ? r.togMinutes : null,
      tog: Number.isFinite(r.togMinutes) ? formatTogDaysAmount(r.togMinutes) : r.tog || "—",
      loc: r.loc,
      // commentsTextForAircraftExcelRow already sorts newest → oldest.
      comments: commentsTextForAircraftExcelRow(r),
    }));

  const downloadExcel = async () => {
    const ids = rows.map((r) => Number(r?.id)).filter((v) => Number.isFinite(v) && v > 0);
    await loadBulkComments(ids).catch(() => {});
    if (showNoFlightsSummary) {
      const templateRows = rows.map((r) => ({
        msn: r.msn,
        reg: r.reg,
        operator: r.operator,
        owner: r.owner,
        fm: r.owner,
        status: r.status,
        tam: normaliseTam(r.tam),
        togMinutes: Number.isFinite(r.togMinutes) ? r.togMinutes : null,
        tog: Number.isFinite(r.togMinutes) ? formatTogDaysAmount(r.togMinutes) : r.tog || "—",
        loc: r.loc,
        comments: commentsTextForAircraftExcelRow(r),
      }));
      const previousMsns = loadPreviousNoFlightsMsns();
      const xml = buildNoFlightsByOperatorExcelXml({
        rows: templateRows,
        title: "No flights 5d — by operator",
        previousMsns,
        generatedAt: new Date(),
      });
      downloadTextFile({
        filename: `ACTracker-No_flights_5d_by_operator-${formatTimestampForFile(new Date())}.xls`,
        content: xml,
        contentType: "application/vnd.ms-excel",
      });
      // After download, remember this set so the next export can highlight newcomers in red.
      savePreviousNoFlightsMsns(templateRows.map((r) => r.msn));
      return;
    }
    const exportRows = buildExportRows();
    const xml = buildXlsXml({
      title: reportTitle,
      headers: exportHeaders,
      rows: exportRows,
    });
    downloadTextFile({
      filename: `ACTracker-${(title || "All").replace(/\s+/g, "_")}-${formatTimestampForFile(new Date())}.xls`,
      content: xml,
      contentType: "application/vnd.ms-excel",
    });
  };

  const openEmailByOperatorTemplate = async () => {
    const ids = rows.map((r) => Number(r?.id)).filter((v) => Number.isFinite(v) && v > 0);
    await loadBulkComments(ids).catch(() => {});
    const { fileName, blob } = buildNoFlightsFiveDaysByOperatorEml({
      rows: buildNoFlightsEmailTemplateRows(),
      generatedAt: new Date(),
    });
    // Same pattern as Operator Progress: Outlook opens .eml as an unsent draft (X-Unsent: 1).
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const openEmailFmModal = async () => {
    const sourceRows = showNoFlightsSummary && summaryRows.length ? summaryRows : rows;
    const ids = sourceRows.map((r) => Number(r?.id)).filter((v) => Number.isFinite(v) && v > 0);
    await loadBulkComments(ids).catch(() => {});

    const templateRows = sourceRows.map((r) => ({
      msn: r.msn,
      reg: r.reg,
      operator: r.operator,
      owner: r.owner,
      fm: r.owner,
      tam: normaliseTam(r.tam),
      togMinutes: Number.isFinite(r.togMinutes) ? r.togMinutes : null,
      tog: Number.isFinite(r.togMinutes) ? formatTogDaysAmount(r.togMinutes) : r.tog || "—",
      // Same location the no-flights table shows (FA / manual / last_known_location).
      loc: resolveNoFlightsFollowUpLocation(r),
      __raw: r.__raw || null,
      comments: commentsTextForAircraftExcelRow(r),
    }));
    const groups = groupNoFlightsRowsByFm(templateRows);

    let contacts = [];
    try {
      const resp = await fetchJson(apiUrl("/api/fm-contacts"));
      contacts = Array.isArray(resp?.contacts) ? resp.contacts : [];
    } catch (e) {
      window.alert(
        `Unable to load FM emails from Settings → Users.\n${cleanText(e?.message) || e}\n\nAdd each FM’s email under Settings → Users (Initials must match the FM column).`
      );
      return;
    }

    const contactByCode = new Map();
    for (const c of contacts) {
      const initials = cleanText(c?.initials).toUpperCase();
      if (initials) contactByCode.set(initials, c);
      for (const code of c?.ownerCodes || []) {
        const k = cleanText(code).toUpperCase();
        if (k && !contactByCode.has(k)) contactByCode.set(k, c);
      }
    }

    const selected = new Set(
      groups
        .filter((g) => g.canEmail && contactByCode.has(cleanText(g.fm).toUpperCase()))
        .map((g) => g.fm)
    );

    const statusLine = el("p", { class: "text-sm text-gray-600 mt-3", text: "" });
    const setStatus = (msg, isErr = false) => {
      statusLine.className = `text-sm mt-3 ${isErr ? "text-red-700" : "text-gray-600"}`;
      statusLine.textContent = msg || "";
    };

    const tamKeys = Array.from(
      new Set(groups.flatMap((g) => Array.from(g.tamCounts.keys())))
    ).sort((a, b) => a.localeCompare(b));

    const tbody = el("tbody", {});
    const rebuildRows = () => {
      tbody.replaceChildren(
        ...groups.map((g) => {
          const code = cleanText(g.fm).toUpperCase();
          const contact = contactByCode.get(code);
          const canCheck = g.canEmail;
          const hasEmail = Boolean(cleanText(contact?.email));
          const cb = el("input", {
            type: "checkbox",
            disabled: !canCheck || !hasEmail,
            checked: canCheck && hasEmail && selected.has(g.fm),
            class: "h-4 w-4 rounded border-gray-300 text-dae focus:ring-dae/30",
            onchange: (ev) => {
              if (ev.target.checked) selected.add(g.fm);
              else selected.delete(g.fm);
            },
          });
          const statusBits = tamKeys.map((tam) => {
            const n = g.tamCounts.get(tam) || 0;
            return n ? `${tam}: ${n}` : null;
          }).filter(Boolean);
          return el("tr", { class: "border-b border-gray-100" }, [
            el("td", { class: "px-3 py-2 align-top" }, [cb]),
            el("td", { class: "px-3 py-2 font-mono font-semibold text-gray-900", text: g.fm }),
            el("td", { class: "px-3 py-2 text-gray-800", text: cleanText(contact?.displayName) || "—" }),
            el("td", { class: "px-3 py-2 text-gray-700 text-xs", text: cleanText(contact?.email) || "— missing email —" }),
            el("td", { class: "px-3 py-2 text-center font-semibold", text: String(g.aircraft.length) }),
            el("td", {
              class: "px-3 py-2 text-center font-semibold " + (g.followUpCount ? "text-red-700" : "text-gray-500"),
              text: String(g.followUpCount),
            }),
            el("td", { class: "px-3 py-2 text-xs text-gray-600", text: statusBits.join(" · ") || "—" }),
          ]);
        })
      );
    };
    rebuildRows();

    const downloadBtn = el("button", {
      type: "button",
      class:
        "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
      text: "Download selected .eml drafts",
    });
    const cancelBtn = el("button", {
      type: "button",
      class:
        "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
      text: "Cancel",
    });

    const bodyNode = el("div", { class: "space-y-3" }, [
      el("p", {
        class: "text-sm text-gray-600",
        text: "Only FMs with In Service or Awaiting Operator Feedback aircraft can be selected. Downloads Outlook drafts (To = FM, Cc = datamanagement). Nothing is sent automatically.",
      }),
      el("div", { class: "overflow-x-auto border border-gray-200 rounded-lg max-h-[50vh] overflow-y-auto" }, [
        el("table", { class: "w-full text-sm text-left" }, [
          el("thead", { class: "text-xs uppercase bg-dae text-white sticky top-0" }, [
            el("tr", {}, [
              el("th", { class: "px-3 py-2", text: "" }),
              el("th", { class: "px-3 py-2", text: "FM" }),
              el("th", { class: "px-3 py-2", text: "Name" }),
              el("th", { class: "px-3 py-2", text: "Email" }),
              el("th", { class: "px-3 py-2 text-center", text: "Total 5d" }),
              el("th", { class: "px-3 py-2 text-center", text: "Follow-up" }),
              el("th", { class: "px-3 py-2", text: "By status" }),
            ]),
          ]),
          tbody,
        ]),
      ]),
      statusLine,
    ]);

    const dialog = modalShell({
      title: "Email FM — no activity 5+ days",
      subtitle: "One Outlook draft per selected FM",
      bodyNode,
      footerNode: el("div", { class: "flex items-center justify-end gap-2 w-full" }, [cancelBtn, downloadBtn]),
    });
    cancelBtn.onclick = dialog.close;

    downloadBtn.onclick = async () => {
      const picks = groups.filter((g) => selected.has(g.fm) && g.canEmail);
      if (!picks.length) {
        setStatus("Select at least one FM with follow-up aircraft and an email on file.", true);
        return;
      }
      downloadBtn.disabled = true;
      downloadBtn.textContent = "Preparing…";
      let ok = 0;
      let skipped = 0;
      try {
        for (const g of picks) {
          const contact = contactByCode.get(cleanText(g.fm).toUpperCase());
          if (!cleanText(contact?.email)) {
            skipped += 1;
            continue;
          }
          const { fileName, blob } = buildNoFlightsFmFollowUpEml({
            fm: {
              initials: g.fm,
              email: contact.email,
              displayName: contact.displayName,
              firstName: contact.firstName,
            },
            rows: g.followUpAircraft,
            generatedAt: new Date(),
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          ok += 1;
          // Stagger downloads so the browser does not collapse them into one.
          await new Promise((r) => setTimeout(r, 400));
        }
        setStatus(
          `Downloaded ${ok} draft${ok === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (no email)` : ""}. Open each in Outlook to review and send.`
        );
        if (ok) setTimeout(() => dialog.close(), 1200);
      } catch (e) {
        setStatus(cleanText(e?.message) || String(e), true);
      } finally {
        downloadBtn.disabled = false;
        downloadBtn.textContent = "Download selected .eml drafts";
      }
    };
  };

  const emailExcel = async () => {
    const recipients = await promptRecipients({ defaultSelected: [] });
    if (!recipients) return;
    const ids = rows.map((r) => Number(r?.id)).filter((v) => Number.isFinite(v) && v > 0);
    await loadBulkComments(ids).catch(() => {});
    const xml = buildXlsXml({ title: reportTitle, headers: exportHeaders, rows: buildExportRows() });
    const base64 = encodeBase64(xml);
    if (!base64) {
      window.alert("Unable to prepare Excel attachment.");
      return;
    }
    await fetchJson(apiUrl("/api/reports/email"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        recipients,
        subject: reportTitle,
        summary: `${rows.length} aircraft`,
        html: `<p><strong>${htmlEscape(reportTitle)}</strong></p><p>${rows.length} aircraft</p>`,
        excel: {
          base64,
          fileName: `ACTracker-${(title || "All").replace(/\s+/g, "_")}-${formatTimestampForFile(new Date())}.xls`,
        },
      }),
    });
  };

  async function refreshOnlyThisRow(r) {
    const a = (filteredAircraft || []).find((x) => Number(x?.id) === Number(r?.id));
    if (!a) return;
    setTopbarProgress({ visible: true, done: 0, total: 1 });
    try {
      await refreshVisibleAircraftLive({
        aircraftList: [a],
        onProgress: ({ done, total }) => setTopbarProgress({ visible: true, done, total }),
      });
      await loadAircraft();
      renderRoute();
    } finally {
      setTopbarProgress({ visible: false });
    }
  }

  async function draftOperatorEmail(r) {
    try {
      if (!Array.isArray(state.operators) || !state.operators.length) {
        await loadOperators().catch(() => {});
      }
      const contact = resolveOperatorContactEmail(r.operator);

      const row = {
        msn: cleanText(r?.msn) || "—",
        reg: cleanText(r?.reg) || "—",
        togMinutes: Number.isFinite(r?.togMinutes) ? r.togMinutes : null,
        tog: Number.isFinite(r?.togMinutes) ? formatTogDaysAmount(r.togMinutes) : cleanText(r?.tog) || "—",
        loc: resolveNoFlightsFollowUpLocation(r),
        __raw: r?.__raw || null,
      };

      let to = cleanText(contact?.email);
      let contactName = cleanText(contact?.name) || "Operator contact";

      if (to && Number.isFinite(Number(r.id)) && Number(r.id) > 0) {
        try {
          const tpl = await fetchJson(apiUrl(`/api/aircraft/${Number(r.id)}/operator-outreach`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              contactEmail: to,
              contactName,
              markSent: false,
            }),
          });
          to = cleanText(tpl?.to) || to;
          contactName = cleanText(tpl?.contactName) || contactName;
        } catch {
          // Still download the HTML draft even if outreach template lookup fails.
        }
      }

      const { fileName, blob } = buildOperatorStatusCheckEml({
        to,
        contactName,
        row,
        generatedAt: new Date(),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (!to) {
        window.alert(
          "Downloaded Outlook HTML draft. No operator contact email on file — fill in To before sending."
        );
      }
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    }
  }

  const hideExpectedRelease =
    currentRouteKey() === "no-flights-24h" || currentRouteKey() === "ground-over-21d";

  // table-layout:fixed column weights (sum 100%) — keeps the fleet grid within ~80vw without horizontal scroll.
  const FLEET_COL_PCT_WITH_ER = {
    msn: 5,
    reg: 5,
    operator: 7,
    owner: 4,
    tam: 11,
    status: 7,
    nextFlight: 4,
    tog: 5,
    loc: 5,
    expectedRelease: 5,
    source: 8,
    comments: 24,
    watch: 4,
    links: 6,
  };
  const FLEET_COL_PCT_NO_ER = {
    msn: 5,
    reg: 5,
    operator: 7,
    owner: 4,
    tam: 11,
    status: 7,
    nextFlight: 4,
    tog: 5,
    loc: 5,
    source: 8,
    comments: 30,
    watch: 4,
    links: 5,
  };
  const fleetColPctMap = hideExpectedRelease ? FLEET_COL_PCT_NO_ER : FLEET_COL_PCT_WITH_ER;

  const columns = [
    {
      key: "msn",
      label: "MSN",
      class: "font-mono text-gray-900",
      get: (r) =>
        el("div", { class: "flex items-start gap-2" }, [
          el("span", {
            class:
              `mt-1 inline-block w-2.5 h-2.5 rounded-full ` +
              (r.freshness === "black"
                ? "bg-black"
                : r.freshness === "unknown"
                ? "bg-gray-400"
                : r.freshness === "green"
                ? "bg-green-500"
                : r.freshness === "blue"
                ? "bg-blue-500"
                : r.freshness === "yellow"
                ? "bg-yellow-400"
                : "bg-red-600"),
            title:
              r.updatedAtMs != null
                ? `Last time checked: ${formatUtcTooltip(r.updatedAtMs)}`
                : "Last time checked: —",
          }),
          el("div", { class: "min-w-0" }, [
            el("div", { class: "font-mono font-medium text-gray-900", text: r.msn }),
            el("div", { class: "text-xs text-gray-500", text: `${r.model} · ${r.engine}` }),
          ]),
        ]),
      sortValue: (r) => cleanText(r.msn) || "",
    },
    {
      key: "reg",
      label: "Registration",
      headerLabel: "Reg",
      class: "font-mono text-gray-800 whitespace-nowrap",
      get: (r) => r.reg,
      sortValue: (r) => r.reg,
    },
    {
      key: "operator",
      label: "Operator",
      class: "text-gray-700",
      get: (r) => r.operator,
      sortValue: (r) => r.operator,
      ...(currentRouteKey() === "no-flights-24h"
        ? { sortCompare: compareNoFlightsRowsByOperator }
        : {}),
    },
    {
      key: "owner",
      label: "FM",
      class: "text-gray-800 font-medium max-w-[6rem] truncate sm:max-w-[8rem] sm:whitespace-normal",
      get: (r) => r.owner,
      sortValue: (r) => r.owner,
    },
    {
      key: "tam",
      label: "TAM status",
      headerLabel: "TAM",
      class: `${TAM_TABLE_CELL_CLASS} text-gray-800`,
      get: (r) =>
        el(
          "button",
          {
            type: "button",
            class: "inline-flex max-w-none shrink-0",
            title: "Change TAM status",
            onclick: () => openTamStatusModal(r),
          },
          [tamBadge(r.tam)]
        ),
      sortValue: (r) => normaliseTam(r.tam),
    },
    {
      key: "status",
      label: "Status",
      nowrap: true,
      class: "text-gray-800 font-medium",
      get: (r) => cleanText(r.status) || "—",
      // Status is a time-on-ground bucket (3h, 1 DAY, 1W, 1MO...). Sort by the underlying ground
      // minutes so order is chronological, not the alphabetical "1 DAY < 1MO < 1W < 2 DAYS".
      sortValue: (r) => (Number.isFinite(r.togMinutes) ? r.togMinutes : Number.POSITIVE_INFINITY),
    },
    {
      key: "nextFlight",
      label: "Next flight",
      headerLabel: "Next flt",
      class: "text-gray-800 font-mono whitespace-nowrap",
      get: (r) => (r.nextFlight === "YES" ? "YES" : "NO"),
      sortValue: (r) => (r.nextFlight === "YES" ? 0 : 1),
    },
    {
      key: "tog",
      label: currentRouteKey() === "no-flights-24h" ? "Time on ground (DAYS)" : "Time on ground",
      headerLabel: currentRouteKey() === "no-flights-24h" ? "TOG (DAYS)" : "TOG",
      class: "text-gray-800",
      get: (r) =>
        el(
          "button",
          {
            type: "button",
            class: "text-left w-full hover:underline",
            title: "Manual override (last flight + location)",
            onclick: () => openManualOverrideModal(r),
          },
          [r.tog || "—"]
        ),
      sortValue: (r) => (Number.isFinite(r.togMinutes) ? r.togMinutes : Number.POSITIVE_INFINITY),
    },
    {
      key: "loc",
      label: "Location",
      headerLabel: "Loc",
      class: "text-gray-800",
      get: (r) =>
        el(
          "button",
          {
            type: "button",
            class: "text-left w-full hover:underline",
            title: "Manual override (last flight + location)",
            onclick: () => openManualOverrideModal(r),
          },
          [r.loc, r.manualFlag ? manualFlagPill(r) : null].filter(Boolean)
        ),
      sortValue: (r) => r.loc,
    },
    {
      key: "expectedRelease",
      label: "Expected release",
      headerLabel: "Exp release",
      class: "text-gray-800 font-mono align-top whitespace-nowrap truncate",
      get: (r) => {
        const value = cleanText(r.expectedReleaseDate);
        if (!value) {
          return el(
            "button",
            {
              type: "button",
              class: "inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 hover:underline whitespace-nowrap",
              title: "Set expected release date",
              onclick: () => openExpectedReleaseModal(r),
            },
            [el("span", { class: "text-lg leading-none", text: "＋" }), "—"]
          );
        }
        const overdue = isDateOverdue(value);
        return el(
          "button",
          {
            type: "button",
            class: (overdue ? "text-red-700 font-semibold hover:underline" : "text-gray-900 hover:underline") + " whitespace-nowrap",
            title: "Edit expected release date",
            onclick: () => openExpectedReleaseModal(r),
            text: formatDateDdMmmYy(value),
          },
          []
        );
      },
      sortValue: (r) => parseDateOnly(r.expectedReleaseDate) ?? Number.POSITIVE_INFINITY,
    },
    {
      key: "source",
      label: "Source",
      class: "text-gray-600 font-mono whitespace-normal leading-tight",
      get: (r) => r.source,
      sortValue: (r) => r.source,
    },
    {
      key: "comments",
      label: "Comments",
      class: `${COMMENTS_TABLE_CELL_CLASS} text-gray-600`,
      get: (r) => renderFleetCommentsColumn(r),
      sortValue: (r) => cleanText(r?.lastComment?.body) || r.comments || "",
    },
    {
      key: "watch",
      label: "On watch",
      headerLabel: "Watch",
      class: "text-gray-800 align-top",
      get: (r) =>
        inspectorReadOnly
          ? el("span", { class: "text-xs text-gray-500", text: r.onWatch ? "YES" : "—" })
          : watchToggle({ aircraftId: r.id, checked: r.onWatch }),
      sortValue: (r) => (r.onWatch ? 1 : 0),
    },
    {
      key: "links",
      label: "Links",
      class: "text-gray-800",
      get: (r) => {
        const links = [];
        // FMs (and admins) can edit normal fields on aircraft in their scope.
        // Inspectors stay read-only. API enforces ownership on save.
        if (!inspectorReadOnly) {
          links.push(
            iconButton({
              label: "✎",
              title: "Edit aircraft",
              onClick: () => openAircraftEditModal(r),
            })
          );
        }
        if (!inspectorReadOnly) {
          links.push(
            iconButton({
              label: "✉",
              title: "Download Outlook HTML draft (.eml) to operator",
              onClick: () => draftOperatorEmail(r),
            })
          );
          links.push(
            iconButton({
              label: "↻",
              title: "Refresh this aircraft (FlightAware live)",
              onClick: () => refreshOnlyThisRow(r),
            })
          );
        }
        if (r.trackerUrl) links.push(pillLink("xRM", r.trackerUrl, "xRM Asset datasheet"));
        const faUrl = r.flightawareUrl || flightawareAircraftUrl(r.reg);
        if (faUrl) links.push(pillLink("FA", faUrl, "FlightAware"));
        const fr24 = fr24AircraftUrl(r.reg);
        if (fr24) links.push(pillLink("FR24", fr24, "Flightradar24"));
        return el("div", { class: "flex flex-wrap items-center gap-1.5 whitespace-normal" }, links);
      },
      sortValue: (r) => `${r.flightawareUrl || ""}${r.trackerUrl || ""}`,
    },
  ];

  const tableColumns = hideExpectedRelease ? columns.filter((c) => c.key !== "expectedRelease") : columns;

  return el("div", { class: "mt-2" }, [
    el("div", { class: "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-800 tracking-tight", text: title }),
        subtitle ? el("p", { class: "mt-1 text-sm text-gray-500 max-w-3xl", text: subtitle }) : null,
        el("p", { class: "mt-1 text-gray-500", text: `${rows.length} aircraft` }),
      ]),
      el("div", { class: "flex items-center gap-2 flex-wrap" }, [
        el(
          "button",
          {
            type: "button",
            class:
              "inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
            onclick: downloadExcel,
            title: "Download Excel",
          },
          [
            el(
              "svg",
              { class: "w-4 h-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", "stroke-width": "2" },
              [
                el("path", { d: "M14 3v4a1 1 0 0 0 1 1h4" }),
                el("path", { d: "M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" }),
                el("path", { d: "M9 17l2-2-2-2" }),
                el("path", { d: "M15 13l-2 2 2 2" }),
              ]
            ),
            "Export Excel",
          ]
        ),
        ...(inspectorReadOnly
          ? []
          : showNoFlightsSummary
          ? [
              el("button", {
                type: "button",
                class:
                  "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
                onclick: () =>
                  openEmailByOperatorTemplate().catch((e) =>
                    window.alert(cleanText(e?.message) || String(e))
                  ),
                title: "Download Outlook draft (.eml) — by operator",
                text: "Email by operator",
              }),
              el("button", {
                type: "button",
                class:
                  "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                onclick: () =>
                  openEmailFmModal().catch((e) =>
                    window.alert(cleanText(e?.message) || String(e))
                  ),
                title: "Download Outlook drafts to each FM (In Service / Awaiting Operator Feedback)",
                text: "Email FM",
              }),
            ]
          : [
              el("button", {
                type: "button",
                class:
                  "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                onclick: () => emailExcel().catch((e) => window.alert(cleanText(e?.message) || String(e))),
                title: "Email Excel report",
                text: "Email",
              }),
            ]),
      ]),
    ]),
    filterBar({ baseList, params }),
    ...(showNoFlightsSummary ? [renderNoFlightsStatusSummary(summaryRows)] : []),
    buildTable({
      title: "",
      rows,
      columns: tableColumns,
      defaultSortKey,
      dense: true,
      fleetViewportFit: true,
      colWidthPctMap: fleetColPctMap,
    }),
    el("div", { class: "mt-2 text-[11px] text-gray-500" }, [
      "Tracking freshness ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-green-500" }),
        "<12h",
      ]),
      " ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-blue-500" }),
        "12–24h",
      ]),
      " ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-yellow-400" }),
        "24–48h",
      ]),
      " ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-red-600" }),
        ">48h",
      ]),
      " ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-black" }),
        "check failed",
      ]),
      " ",
      el("span", { class: "inline-flex items-center gap-1" }, [
        el("span", { class: "inline-block w-2 h-2 rounded-full bg-gray-400" }),
        "unknown location",
      ]),
    ]),
  ]);
}

function aboutSettingsPanel() {
  return el("div", { class: "space-y-6" }, [
    el("div", { id: "v2-about-version", class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700 min-h-[4rem]" }, [
      el("div", { class: "text-gray-500", text: "Loading deployed version details…" }),
    ]),
    el("div", { class: "rounded-xl border border-gray-200 bg-white p-4" }, [
      el("h2", { class: "text-lg font-semibold text-gray-900 mb-3", text: "Recent UI / behaviour updates" }),
      el(
        "ul",
        { class: "list-disc pl-5 space-y-2 text-sm text-gray-700" },
        V2_UI_RELEASE_NOTES.map((line) => el("li", { text: line }))
      ),
    ]),
  ]);
}

async function initAboutPanels() {
  const host = document.getElementById("v2-about-version");
  if (!host) return;

  host.replaceChildren(el("div", { class: "text-gray-500", text: "Loading deployed version details…" }));
  try {
    const data = await fetchJson(apiUrl("/api/about"));
    const lc = data?.lastCommit || {};
    const dep = data?.deployment || {};
    const apiMeta = data?.apiProcess || {};
    const dlRow = (label, value) => {
      const v =
        value === undefined || value === null
          ? "—"
          : String(value).trim()
          ? String(value)
          : "—";
      return el("div", { class: "flex flex-wrap gap-x-4 gap-y-1 border-b border-gray-100 py-2 last:border-b-0" }, [
        el("div", { class: "w-44 shrink-0 text-[11px] font-medium text-gray-500 uppercase tracking-wide", text: label }),
        el("div", { class: "min-w-0 flex-1 text-sm text-gray-900 whitespace-pre-wrap break-words font-mono", text: v }),
      ]);
    };

    const ghLink = dep.githubCommitUrl
      ? el("div", { class: "flex flex-wrap gap-x-4 gap-y-1 border-b border-gray-100 py-2 last:border-b-0" }, [
          el("div", {
            class: "w-44 shrink-0 text-[11px] font-medium text-gray-500 uppercase tracking-wide",
            text: "Commit on GitHub",
          }),
          el("div", { class: "min-w-0 flex-1 text-sm" }, [
            el("a", {
              href: cleanText(dep.githubCommitUrl),
              class: "font-mono text-sm text-dae underline hover:text-dae/90 break-all",
              target: "_blank",
              rel: "noopener noreferrer",
              text: cleanText(dep.githubCommitUrl),
            }),
          ]),
        ])
      : null;

    host.replaceChildren(
      el("div", { class: "mb-4" }, [
        el("h2", { class: "text-lg font-semibold text-gray-900", text: "About — deployed API / code metadata" }),
        data?.note ? el("p", { class: "mt-1 text-xs text-gray-500 leading-relaxed", text: cleanText(data.note) }) : null,
      ]),
      el("div", {
        class: "mb-2 text-[11px] font-semibold text-gray-600 uppercase tracking-wide",
        text: "Production deploy (GitHub → this server)",
      }),
      dlRow("Deploy branch", dep.branch || "—"),
      dlRow("Last deploy (when ./deploy.sh ran) — Eastern US", dep.deployedAtEst || "—"),
      dlRow("Last deploy — ISO (UTC)", dep.deployedAtIso || "—"),
      dlRow("Web bundle id (?v= cache bust)", dep.webBuildId || "—"),
      dlRow("Deploy metadata source", dep.metaSource || "—"),
      ...(ghLink ? [ghLink] : []),
      el("div", {
        class: "mt-4 mb-2 text-[11px] font-semibold text-gray-600 uppercase tracking-wide",
        text: "Commit details",
      }),
      dlRow("Last commit subject", lc.message || "—"),
      dlRow("Git SHA (abbreviated)", lc.shortSha || lc.fullSha || "—"),
      dlRow("Git SHA (full)", lc.fullSha || "—"),
      dlRow("Commit time — Eastern Time (US)", lc.committedAtEst || "—"),
      dlRow("Commit time — ISO (UTC/Z)", lc.committedAtIso || "—"),
      dlRow(
        "Commit metadata source",
        lc.source === "git"
          ? "repository .git at API"
          : lc.source === "env"
            ? "GIT_* deploy env vars"
            : lc.source === "deploy-meta"
              ? "deploy-meta.json (deploy.sh)"
              : "—",
      ),
      dlRow("API package version", apiMeta.packageVersion || "—"),
      dlRow("API process started (EST)", apiMeta.startedAtEst || "—"),
      dlRow("API process started (ISO)", apiMeta.startedAtIso || "—"),
    );
  } catch (e) {
    host.replaceChildren(
      el("div", { class: "text-red-700 text-sm whitespace-pre-wrap" }, [
        "Unable to load `/api/about`. ",
        cleanText(e?.message) || String(e),
      ])
    );
  }
}

function renderSettings() {
  const params = currentRouteParams();
  const tab = cleanText(params.get("tab")) || "aircraft";

  const tabLink = (key, label) =>
    el("a", {
      href: key === "activity" ? "#/status-recent" : `#/settings?tab=${encodeURIComponent(key)}`,
      class:
        "inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border " +
        (tab === key
          ? "bg-red-50 text-dae border-red-200"
          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"),
      text: label,
    });

  const cronsSettingsPanel = () =>
    el("div", { class: "space-y-4" }, [
      el("p", {
        class: "text-sm text-gray-600",
        text: "Cron schedules remain on the server; recipients and active flags are saved to cron-config. Use RUN TEST before full sends.",
      }),
      el("div", { id: "v2-cron-config", class: "space-y-4" }, [
        el("div", { class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700" }, ["Loading…"]),
      ]),
    ]);

  const apiUsagePanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
      el("div", { class: "flex flex-wrap items-start justify-between gap-4" }, [
        el("div", {}, [
          el("h2", { class: "text-lg font-semibold text-gray-900", text: "API usage & estimated costs" }),
          el("p", {
            class: "mt-1 text-sm text-gray-500",
            text: "FlightAware figures come from AeroAPI account usage. FR24 rows count requests issued by this API server (rolling windows; not FR24’s own billing UI).",
          }),
        ]),
        el("button", {
          id: "v2-settings-api-costs-refresh",
          type: "button",
          class:
            "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
          text: "Refresh",
        }),
      ]),
      el("div", { id: "v2-settings-api-costs", class: "mt-4 text-sm text-gray-700" }, ["Loading…"]),
    ]);
  };

  const costAnalysisPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { class: "space-y-4" }, [
      el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4" }, [
        el("div", { class: "flex flex-wrap items-start justify-between gap-4" }, [
          el("div", {}, [
            el("h2", { class: "text-lg font-semibold text-gray-900", text: "Cost analysis" }),
            el("p", {
              class: "mt-1 text-sm text-gray-500",
              text: "FlightAware, FlightRadar, AI 1, and AI 2 spend by time window. AI rows break out Chat, Workscopes, Lease return, and Others.",
            }),
          ]),
          el("button", {
            id: "v2-settings-cost-analysis-refresh",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "Refresh",
          }),
        ]),
      ]),
      el("div", { id: "v2-settings-cost-analysis", class: "space-y-4 text-sm text-gray-700" }, ["Loading…"]),
    ]);
  };

  const dbExplorerPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { id: "v2-db-explorer-root", class: "space-y-4" }, [
      el("div", {
        class: "rounded-xl border border-amber-100 bg-amber-50/80 p-4 text-sm text-amber-950 space-y-2",
      }, [
        el("p", { class: "font-semibold text-amber-950", text: "pgAdmin (full SQL console)" }),
        el("p", {
          class: "text-amber-900/90",
          text:
            "Docker exposes pgAdmin on the host at port 5050 (localhost only). Open http://localhost:5050 after SSH port-forwarding, or see PGADMIN_SETUP.md. Login uses PGADMIN_EMAIL and PGADMIN_PASSWORD from your .env (defaults admin@actracker.local / admin). Register a server with host postgres, port 5432, database and user from PGDATABASE / PGUSER, password PGPASSWORD.",
        }),
      ]),
      el("div", { class: "rounded-xl border border-gray-200 bg-white shadow-sm p-4" }, [
        el("h2", { class: "text-lg font-semibold text-gray-900", text: "Browse tables (no SQL required)" }),
        el("p", {
          class: "mt-1 text-sm text-gray-500",
          text:
            "All public tables load dynamically. Filter rows with the search box (matches any column), pick a sort column, paginate. Read-only in this view.",
        }),
        el("div", { class: "mt-4 flex flex-col lg:flex-row gap-4" }, [
          el("div", { class: "w-full lg:w-72 shrink-0 flex flex-col gap-2" }, [
            el("label", { class: "text-xs font-medium text-gray-600", text: "Filter table names" }),
            el("input", {
              id: "v2-db-explorer-table-q",
              type: "search",
              class:
                "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-dae focus:ring-dae",
              placeholder: "e.g. aircraft…",
            }),
            el("div", {
              id: "v2-db-explorer-table-list",
              class:
                "max-h-[22rem] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 text-sm space-y-1",
            }),
          ]),
          el("div", { class: "flex-1 min-w-0 flex flex-col gap-3" }, [
            el("div", {
              id: "v2-db-explorer-toolbar",
              class: "flex flex-wrap items-end gap-3 text-sm",
            }),
            el("div", { id: "v2-db-explorer-meta", class: "text-xs text-gray-600" }),
            el("div", { class: "overflow-x-auto rounded-lg border border-gray-200" }, [
              el("table", { class: "w-full text-xs text-left text-gray-700" }, [
                el("thead", { class: "text-[10px] uppercase bg-dae text-white" }, [
                  el("tr", { id: "v2-db-explorer-thead-row" }, [
                    el("th", { class: "px-2 py-2", text: "Select a table" }),
                  ]),
                ]),
                el("tbody", { id: "v2-db-explorer-tbody" }, [
                  el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-500", text: "Choose a table from the list." })]),
                ]),
              ]),
            ]),
          ]),
        ]),
      ]),
    ]);
  };

  const backupSettingsPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { id: "v2-db-backup-root", class: "space-y-4" }, [
      el("div", {
        id: "v2-db-backup-summary",
        class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700",
      }, ["Loading backup information…"]),
      el("div", { id: "v2-db-backup-quick" }),
      el("div", { class: "rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm" }, [
        el("div", { class: "px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3" }, [
          el("div", { class: "font-semibold text-gray-900", text: "Recent backups" }),
          el("button", {
            id: "v2-db-backup-refresh",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
            text: "Refresh",
          }),
        ]),
        el("div", { class: "overflow-x-auto" }, [
          el("table", { class: "w-full text-sm text-left text-gray-500" }, [
            el("thead", { class: "text-xs text-white uppercase bg-dae" }, [
              el("tr", {}, [
                el("th", { class: "px-4 py-3", text: "File" }),
                el("th", { class: "px-4 py-3", text: "Source" }),
                el("th", { class: "px-4 py-3", text: "Modified (UTC)" }),
                el("th", { class: "px-4 py-3", text: "Size" }),
                el("th", { class: "px-4 py-3", text: "Download" }),
              ]),
            ]),
            el("tbody", { id: "v2-db-backup-table" }, [el("tr", {}, [el("td", { class: "px-4 py-3", colspan: "5", text: "Loading…" })])]),
          ]),
        ]),
      ]),
    ]);
  };

  const diskSettingsPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { id: "v2-disk-usage-root", class: "space-y-4" }, [
      el("div", {
        id: "v2-disk-usage-summary",
        class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700",
      }, ["Loading disk usage…"]),
      el("div", { id: "v2-disk-usage-categories", class: "rounded-xl border border-gray-200 bg-white p-4" }, []),
      el("div", { class: "rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm" }, [
        el("div", { class: "px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3" }, [
          el("div", {}, [
            el("div", { class: "font-semibold text-gray-900", text: "Surveillance by operator / MSN" }),
            el("div", { class: "text-xs text-gray-500 mt-0.5", text: "Delete photos or records files for an MSN to free space." }),
          ]),
          el("button", {
            id: "v2-disk-usage-refresh",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
            text: "Refresh",
          }),
        ]),
        el("div", { id: "v2-disk-usage-tree", class: "p-3 space-y-2" }, ["Loading…"]),
      ]),
    ]);
  };

  const operatorsPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" }, [
      el("div", { class: "p-4 border-b border-gray-200 flex items-start justify-between gap-4" }, [
        el("div", {}, [
          el("h2", { class: "text-lg font-semibold text-gray-900", text: "Operators" }),
          el("p", { class: "mt-1 text-sm text-gray-500", text: "Edit operator outreach contacts (name + email)." }),
        ]),
        el("div", { class: "flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto" }, [
          el("input", {
            id: "v2-operators-q",
            placeholder: "Search operator…",
            class:
              "w-full sm:w-64 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
          }),
          el("button", {
            id: "v2-operators-refresh",
            type: "button",
            class:
              "inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "Refresh",
          }),
        ]),
      ]),
      el("div", { class: "overflow-x-auto" }, [
        el("table", { class: "w-full text-sm text-left text-gray-500" }, [
          el("thead", { class: "text-xs text-white uppercase bg-dae" }, [
            el("tr", {}, [
              el("th", { class: "px-4 py-3", text: "Operator" }),
              el("th", { class: "px-4 py-3", text: "Primary contact" }),
              el("th", { class: "px-4 py-3", text: "Contacts" }),
              el("th", { class: "px-4 py-3", text: "Aircraft" }),
              el("th", { class: "px-4 py-3", text: "Actions" }),
            ]),
          ]),
          el("tbody", { id: "v2-operators-body" }, [
            el("tr", {}, [el("td", { class: "px-4 py-3", text: "Loading…" })]),
          ]),
        ]),
      ]),
    ]);
  };

  const aircraftAdminPanel = () => {
    const admin = isAdminSession();
    const adminButtons = admin
      ? el("div", { class: "flex items-center gap-2" }, [
          el("button", {
            id: "v2-admin-aircraft-xrm-import",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "XRM import…",
          }),
          el("button", {
            id: "v2-admin-aircraft-new",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
            text: "New aircraft…",
          }),
          el("button", {
            id: "v2-admin-aircraft-refresh",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "Refresh",
          }),
          el("button", {
            id: "v2-admin-aircraft-delete",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-200",
            text: "Delete selected",
          }),
        ])
      : el("div", { class: "flex items-center gap-2" }, [
          el("button", {
            id: "v2-admin-aircraft-refresh",
            type: "button",
            class:
              "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "Refresh",
          }),
        ]);
    const headCells = [
      ...(admin ? [el("th", { class: "px-4 py-3", text: "" })] : []),
      el("th", { class: "px-4 py-3", text: "MSN" }),
      el("th", { class: "px-4 py-3", text: "Registration" }),
      el("th", { class: "px-4 py-3", text: "Operator" }),
      el("th", { class: "px-4 py-3", text: "FM" }),
      el("th", { class: "px-4 py-3", text: "Region" }),
      el("th", { class: "px-4 py-3", text: "Active" }),
      el("th", { class: "px-4 py-3", text: "ME fleet" }),
      el("th", { class: "px-4 py-3", text: "Russian fleet" }),
      el("th", { class: "px-4 py-3", text: "Actions" }),
    ];
    return el("div", {}, [
      el("div", { class: "flex items-start justify-between gap-4 mb-4" }, [
        el("div", {}, [
          el("h2", {
            class: "text-lg font-semibold text-gray-900",
            text: admin ? "Aircraft administration" : "Edit aircraft",
          }),
          el("p", {
            class: "mt-1 text-sm text-gray-500",
            text: admin ? "Filter, edit, and bulk delete aircraft." : "Filter and edit the aircraft you own.",
          }),
        ]),
        adminButtons,
      ]),
      el("div", { id: "v2-admin-aircraft-filters" }, []),
      el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" }, [
        el("div", { class: "overflow-x-auto" }, [
          el("table", { class: "w-full text-sm text-left text-gray-500" }, [
            el("thead", { class: "text-xs text-white uppercase bg-dae" }, [
              el("tr", {}, headCells),
            ]),
            el("tbody", { id: "v2-admin-aircraft-body" }, [
              el("tr", {}, [el("td", { class: "px-4 py-3", text: "Loading…" })]),
            ]),
          ]),
        ]),
      ]),
    ]);
  };

  const materialsNotifyPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-6 text-sm text-gray-700" }, [
        "Only admins can edit materials tracker notifications. Lists are comma-separated email addresses.",
      ]);
    }
    return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4 max-w-3xl" }, [
      el("h2", { class: "text-lg font-semibold text-gray-900", text: "Materials — notifications" }),
      el("p", {
        class: "text-sm text-gray-600",
        text:
          "New-part lines trigger “MSN xxx | PROJECT | NEW PARTS REQUEST” messages. Configure the cron job (daily digest) separately on the server; list below decides who receives the parts pipeline summary.",
      }),
      el("label", { class: "block text-sm font-medium text-gray-700" }, [
        "New part requests (comma-separated emails)",
        el("textarea", {
          id: "v2-mat-notify-new",
          class: "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono min-h-[4rem]",
          placeholder: "procurement@example.com, materiel@example.com",
        }),
      ]),
      el("label", { class: "block text-sm font-medium text-gray-700 mt-4" }, [
        "Daily parts digest (comma-separated)",
        el("textarea", {
          id: "v2-mat-notify-daily",
          class: "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono min-h-[4rem]",
          placeholder: "ops@example.com",
        }),
      ]),
      el("button", {
        id: "v2-mat-notify-save",
        type: "button",
        class:
          "inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
        text: "Save recipients",
      }),
    ]);
  };

  const accountPanel = () => {
    const u = state.session || {};
    const who = cleanText(u.displayName) || cleanText(u.username) || "your account";
    const pw1 = el("input", {
      id: "v2-account-pw1",
      type: "password",
      autocomplete: "new-password",
      class: "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm",
    });
    const pw2 = el("input", {
      id: "v2-account-pw2",
      type: "password",
      autocomplete: "new-password",
      class: "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm",
    });
    const status = el("p", { class: "text-sm mt-2 text-gray-600", text: "" });
    const saveBtn = el("button", {
      type: "button",
      class:
        "inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
      text: "Update password",
    });
    const setStatus = (msg, ok) => {
      status.className = `text-sm mt-2 ${ok ? "text-green-700" : "text-red-700"}`;
      status.textContent = msg;
    };
    saveBtn.onclick = async () => {
      const p1 = String(pw1.value || "");
      const p2 = String(pw2.value || "");
      if (!p1 || !p2) return setStatus("Enter the new password in both fields.", false);
      if (p1 !== p2) return setStatus("Passwords do not match.", false);
      saveBtn.disabled = true;
      saveBtn.textContent = "Updating…";
      try {
        await fetchJson(apiUrl("/api/account/password"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: p1, confirmPassword: p2 }),
        });
        setStatus("Password updated.", true);
        pw1.value = "";
        pw2.value = "";
      } catch (e) {
        setStatus(cleanText(e?.message) || String(e), false);
      } finally {
        saveBtn.disabled = false;
        saveBtn.textContent = "Update password";
      }
    };
    return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 max-w-md" }, [
      el("h2", { class: "text-lg font-semibold text-gray-900", text: "Change password" }),
      el("p", {
        class: "mt-1 text-sm text-gray-500",
        text: `Set a new password for ${who}. Enter it twice to confirm.`,
      }),
      el("label", { class: "block text-sm font-medium text-gray-700 mt-4" }, ["New password", pw1]),
      el("label", { class: "block text-sm font-medium text-gray-700 mt-3" }, ["Confirm new password", pw2]),
      el("div", { class: "mt-4" }, [saveBtn]),
      status,
    ]);
  };

  const usersAdminPanel = () => {
    if (!isAdminSession()) {
      return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-sm text-gray-700" }, [
        "Admin only.",
      ]);
    }
    const tbody = el("tbody", {}, [
      el("tr", {}, [el("td", { class: "px-4 py-3 text-gray-500", text: "Loading…" })]),
    ]);
    const roleText = (u) =>
      u.isAdmin ? "Administrator" : (u.ownerCodes || []).length ? (u.ownerCodes || []).join(", ") : "—";
    let refresh = () => {};
    const userRow = (u) =>
      el("tr", { class: "bg-white border-b hover:bg-gray-50" }, [
        el("td", { class: "px-4 py-3 font-medium text-gray-900", text: cleanText(u.username) }),
        el("td", { class: "px-4 py-3 font-mono text-gray-800", text: cleanText(u.initials) || "—" }),
        el("td", { class: "px-4 py-3 text-gray-700", text: cleanText(u.displayName) || "—" }),
        el("td", { class: "px-4 py-3 text-gray-700", text: cleanText(u.email) || "—" }),
        el("td", { class: "px-4 py-3 text-gray-700", text: roleText(u) }),
        el("td", { class: "px-4 py-3", text: u.active ? "YES" : "NO" }),
        el("td", { class: "px-4 py-3" }, [
          el("div", { class: "flex flex-wrap items-center gap-2" }, [
            el("button", {
              type: "button",
              class: "inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
              text: "Edit",
              onclick: () => openUserEditModal(u, refresh),
            }),
            el("button", {
              type: "button",
              class: "inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
              text: "Password",
              onclick: () => openUserPasswordModal(u, refresh),
            }),
            el("button", {
              type: "button",
              class: "inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700",
              text: "Delete",
              onclick: async () => {
                if (!window.confirm(`Delete user ${cleanText(u.username)}? This cannot be undone.`)) return;
                try {
                  await fetchJson(apiUrl(`/api/admin/users/${encodeURIComponent(cleanText(u.username))}`), {
                    method: "DELETE",
                  });
                  refresh();
                } catch (e) {
                  window.alert(cleanText(e?.message) || String(e));
                }
              },
            }),
          ]),
        ]),
      ]);
    refresh = async () => {
      tbody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3 text-gray-500", text: "Loading…" })]));
      try {
        const resp = await fetchJson(apiUrl("/api/admin/users"));
        const users = Array.isArray(resp?.users) ? resp.users : [];
        if (!users.length) {
          tbody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3 text-gray-500", text: "No users." })]));
          return;
        }
        tbody.replaceChildren(...users.map((u) => userRow(u)));
      } catch (e) {
        tbody.replaceChildren(
          el("tr", {}, [el("td", { class: "px-4 py-3 text-red-700", text: cleanText(e?.message) || String(e) })])
        );
      }
    };
    refresh();
    return el("div", {}, [
      el("div", { class: "flex items-start justify-between gap-4 mb-4" }, [
        el("div", {}, [
          el("h2", { class: "text-lg font-semibold text-gray-900", text: "User / FM administration" }),
          el("p", {
            class: "mt-1 text-sm text-gray-500",
            text: "Initials = FM code on the fleet. Email is used for Email FM drafts. Password lets each user sign in and see their aircraft.",
          }),
        ]),
        el("div", { class: "flex items-center gap-2" }, [
          el("button", {
            type: "button",
            class: "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
            text: "New user…",
            onclick: () => openUserCreateModal(refresh),
          }),
          el("button", {
            type: "button",
            class: "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
            text: "Refresh",
            onclick: () => refresh(),
          }),
        ]),
      ]),
      el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" }, [
        el("div", { class: "overflow-x-auto" }, [
          el("table", { class: "w-full text-sm text-left text-gray-500" }, [
            el("thead", { class: "text-xs text-white uppercase bg-dae" }, [
              el("tr", {}, [
                el("th", { class: "px-4 py-3", text: "Username" }),
                el("th", { class: "px-4 py-3", text: "Initials (FM)" }),
                el("th", { class: "px-4 py-3", text: "Name" }),
                el("th", { class: "px-4 py-3", text: "Email" }),
                el("th", { class: "px-4 py-3", text: "Permissions" }),
                el("th", { class: "px-4 py-3", text: "Active" }),
                el("th", { class: "px-4 py-3", text: "Actions" }),
              ]),
            ]),
            tbody,
          ]),
        ]),
      ]),
    ]);
  };

  const singaporeFm = isSingaporeFmSession();
  let body;
  if (singaporeFm) {
    body = accountPanel();
  } else if (tab === "account") body = accountPanel();
  else if (tab === "users") body = usersAdminPanel();
  else if (tab === "operators") body = operatorsPanel();
  else if (tab === "crons") body = cronsSettingsPanel();
  else if (tab === "materials-email") body = materialsNotifyPanel();
  else if (tab === "api-usage") body = apiUsagePanel();
  else if (tab === "cost-analysis") body = costAnalysisPanel();
  else if (tab === "db-explorer") body = dbExplorerPanel();
  else if (tab === "backup") body = backupSettingsPanel();
  else if (tab === "disk") body = diskSettingsPanel();
  else if (tab === "inspectors") body = renderSurveillanceSettingsPanel({ el, fetchJson, apiUrl, cleanText, state });
  else if (tab === "xrm-imports") {
    body = renderXrmImportSettingsPanel({
      el,
      fetchJson,
      apiUrl,
      cleanText,
      formatDisplayTimestamp,
      isAdminSession,
    });
  }
  else if (tab === "about") body = aboutSettingsPanel();
  else if (tab === "figaro-config" || tab === "figaro-usage" || tab === "perkins-config" || tab === "perkins-usage") {
    const figaroTab = String(tab || "").replace(/^perkins-/, "figaro-");
    body = renderFigaroSettingsPanels({ el, fetchJson, apiUrl, cleanText, tab: figaroTab });
  }
  else body = aircraftAdminPanel();

  return el("div", {}, [
    el("div", { class: "mb-6" }, [
      el("h1", { class: "text-2xl font-bold text-gray-900 tracking-tight", text: "Settings" }),
      el(
        "div",
        { class: "mt-4 flex flex-wrap items-center gap-2" },
        singaporeFm
          ? [tabLink("account", "Account")]
          : [
              tabLink("aircraft", "Aircraft"),
              tabLink("operators", "Operators"),
              tabLink("activity", "Activity"),
              tabLink("crons", "Crons"),
              tabLink("materials-email", "Materials email"),
              tabLink("api-usage", "API usage"),
              ...(isAdminSession() ? [tabLink("cost-analysis", "Cost analysis")] : []),
              ...(isAdminSession() ? [tabLink("figaro-usage", "Figaro usage")] : []),
              ...(isAdminSession() ? [tabLink("figaro-config", "Figaro")] : []),
              tabLink("db-explorer", "Database"),
              tabLink("backup", "DB backup"),
              ...(isAdminSession() ? [tabLink("disk", "Disk")] : []),
              ...(isAdminSession() ? [tabLink("xrm-imports", "XRM imports")] : []),
              tabLink("inspectors", "Inspectors & Companies"),
              ...(isAdminSession() ? [tabLink("users", "Users")] : []),
              tabLink("account", "Account"),
              tabLink("about", "About"),
            ]
      ),
    ]),
    body,
  ]);
}

const STATUS_CHANGES_TABLE_COLSPAN = 8;

function renderStatusChangesTableHeadRow() {
  return el("tr", {}, [
    el("th", { class: "px-4 py-3", text: "Time (EST)" }),
    el("th", { class: "px-4 py-3", text: "Aircraft" }),
    el("th", { class: "px-4 py-3", text: "Registration" }),
    el("th", { class: "px-4 py-3", text: "Operator" }),
    el("th", { class: "px-4 py-3", text: "FM" }),
    el("th", { class: "px-4 py-3", text: "Old → New" }),
    el("th", { class: "px-4 py-3", text: "Flight / context" }),
    el("th", { class: "px-4 py-3", text: "Trigger" }),
  ]);
}

function renderActivityEventRow(event) {
  if (event?.kind === "status_change" && event.raw) {
    return renderStatusChangeTableRow(event.raw);
  }
  const badge = cleanText(event?.tam) || "EVENT";
  const badgeClass =
    badge === "XRM"
      ? "inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-red-50 text-dae border border-red-100"
      : "inline-flex px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-700";
  return el("tr", { class: "bg-white border-b border-gray-100 hover:bg-gray-50" }, [
    el("td", {
      class: "px-4 py-3 font-mono text-gray-700 align-top whitespace-nowrap",
      text: formatShortTime(event?.ts),
    }),
    el("td", { class: "px-4 py-3 align-top text-gray-500", text: "—" }),
    el("td", { class: "px-4 py-3 align-top text-gray-500", text: "—" }),
    el("td", { class: "px-4 py-3 align-top text-gray-500", text: "—" }),
    el("td", { class: "px-4 py-3 align-top", text: event?.owner || "—" }),
    el("td", { class: "px-4 py-3 align-top" }, [el("span", { class: badgeClass, text: badge })]),
    el("td", {
      class: "px-4 py-3 align-top text-gray-800 text-sm max-w-[28rem]",
      text: cleanText(event?.message) || "—",
    }),
    el("td", { class: "px-4 py-3 align-top text-gray-500 text-xs", text: "System" }),
  ]);
}

function renderStatusChangeTableRow(c) {
  const prev = normaliseStatusText(c.previousStatus) || "—";
  const next = normaliseStatusText(c.newStatus) || "—";
  const msn = cleanText(c.msn) || "—";
  const model = cleanText(c.aircraftType);
  const engine = cleanText(c.engineModel);
  const detailLine = [model, engine].filter(Boolean).join(" · ");

  const contextParts = [];
  if (cleanText(c.flightIdent)) contextParts.push(`Flight ${cleanText(c.flightIdent)}`);
  if (c.watchRemoved) contextParts.push("Watch cleared");
  if (cleanText(c.flightDate)) contextParts.push(`Ref ${formatShortTime(c.flightDate)}`);

  return el("tr", { class: "bg-white border-b border-gray-100 hover:bg-gray-50" }, [
    el("td", { class: "px-4 py-3 font-mono text-gray-700 align-top whitespace-nowrap", text: formatShortTime(c.createdAt) }),
    el("td", { class: "px-4 py-3 align-top" }, [
      el("div", { class: "font-mono font-medium text-gray-900", text: msn }),
      detailLine ? el("div", { class: "mt-0.5 text-xs text-gray-500", text: detailLine }) : null,
    ]),
    el("td", {
      class: "px-4 py-3 align-top font-mono text-gray-900 font-medium",
      text: cleanText(c.registration) || "—",
    }),
    el("td", { class: "px-4 py-3 align-top text-gray-800", text: cleanText(c.operator) || "—" }),
    el("td", { class: "px-4 py-3 align-top text-gray-800 font-medium", text: cleanText(c.owner) || "—" }),
    el("td", { class: "px-4 py-3 align-top text-gray-800 font-mono text-xs", text: `${prev} → ${next}` }),
    el("td", {
      class: "px-4 py-3 align-top text-gray-600 text-xs max-w-[14rem]",
      text: contextParts.length ? contextParts.join(" · ") : "—",
    }),
    el("td", { class: "px-4 py-3 align-top text-gray-800 text-xs", text: formatStatusChangeTrigger(c.createdBy) }),
  ]);
}

function renderStatusChangesTableShell({ tbodyId, loadingText = "Loading…" } = {}) {
  return el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" }, [
    el("div", { class: "overflow-x-auto" }, [
      el("table", { class: "w-full text-sm text-left text-gray-500" }, [
        el("thead", { class: "text-xs text-white uppercase bg-dae" }, [renderStatusChangesTableHeadRow()]),
        el("tbody", { id: tbodyId }, [
          el("tr", {}, [
            el("td", {
              class: "px-4 py-3 text-gray-500",
              colspan: String(STATUS_CHANGES_TABLE_COLSPAN),
              text: loadingText,
            }),
          ]),
        ]),
      ]),
    ]),
  ]);
}

async function populateStatusChangesTable({
  tbody,
  meta,
  hours = 24,
  limit = 1000,
  maxRows = null,
  emptyMessage = "No status changes logged in this window.",
  loadingMessage = "Loading status changes…",
} = {}) {
  if (!tbody) return;

  const windowHours = Math.min(Math.max(Math.floor(Number(hours) || 24), 1), 720);

  if (meta) meta.textContent = `Last ${windowHours} hours · loading…`;
  tbody.replaceChildren(
    el("tr", {}, [
      el("td", {
        colspan: String(STATUS_CHANGES_TABLE_COLSPAN),
        class: "px-4 py-6 text-gray-500",
        text: loadingMessage,
      }),
    ])
  );

  let changes = [];
  let taskLogs = [];
  try {
    [changes, taskLogs] = await Promise.all([
      loadStatusChanges({ hours: windowHours, limit }),
      isAdminSession() ? loadTaskLogs({ limit: 200 }) : Promise.resolve([]),
    ]);
  } catch (e) {
    if (meta) meta.textContent = `Unable to load: ${cleanText(e?.message) || String(e)}`;
    tbody.replaceChildren(
      el("tr", {}, [
        el("td", {
          colspan: String(STATUS_CHANGES_TABLE_COLSPAN),
          class: "px-4 py-6 text-red-700",
          text: cleanText(e?.message) || String(e),
        }),
      ])
    );
    return;
  }

  const cutoff = Date.now() - windowHours * 60 * 60 * 1000;
  const events = buildActivityRows({ statusChanges: changes, taskLogs }).filter((e) => {
    const ts = Date.parse(e?.ts || "");
    return Number.isFinite(ts) && ts >= cutoff;
  });
  const shown = Number.isFinite(maxRows) && maxRows > 0 ? events.slice(0, maxRows) : events;
  const truncated = shown.length < events.length;

  if (meta) {
    meta.textContent = truncated
      ? `Showing ${shown.length} of ${events.length} event(s) in the last ${windowHours} hours · newest first.`
      : `Showing ${events.length} event(s) in the last ${windowHours} hours · status changes and system events · newest first.`;
  }

  if (!shown.length) {
    tbody.replaceChildren(
      el("tr", {}, [
        el("td", {
          colspan: String(STATUS_CHANGES_TABLE_COLSPAN),
          class: "px-4 py-6 text-gray-500",
          text: emptyMessage,
        }),
      ])
    );
    return;
  }

  tbody.replaceChildren(...shown.map((e) => renderActivityEventRow(e)));
}

async function initDashboardStatusChangesPreview() {
  await populateStatusChangesTable({
    tbody: document.getElementById("v2-dashboard-status-changes-body"),
    meta: document.getElementById("v2-dashboard-status-changes-meta"),
    hours: 24,
    limit: 1000,
    maxRows: 20,
    emptyMessage: "No status changes logged in the last 24 hours.",
    loadingMessage: "Loading status changes…",
  });
}

function renderRecentStatusChangesPage() {
  const params = currentRouteParams();
  let hourVal = Number(params.get("hours"));
  if (!Number.isFinite(hourVal) || hourVal <= 0) hourVal = 24;
  hourVal = Math.min(Math.max(Math.floor(hourVal), 1), 720);
  const hourPresets = [6, 12, 24, 48, 72, 168];
  const hourChoices = Array.from(new Set([...hourPresets, hourVal])).sort((a, b) => a - b);

  const hoursSelect = el("select", {
    id: "v2-status-recent-hours",
    class:
      "block rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-dae focus:ring-dae",
    "aria-label": "Time window (hours preset)",
    onchange: (ev) => {
      const v = cleanText(ev.target?.value);
      const next = currentRouteParams();
      next.set("hours", v);
      setHashParams(next);
    },
  });
  for (const h of hourChoices) {
    const opt = el("option", { value: String(h), text: `${h} hours` });
    if (h === hourVal) opt.selected = true;
    hoursSelect.appendChild(opt);
  }

  const applyCustomHours = () => {
    const input = document.getElementById("v2-status-recent-hours-custom");
    let n = Number(cleanText(input?.value));
    if (!Number.isFinite(n)) {
      window.alert("Enter a number of hours (1–720).");
      return;
    }
    n = Math.min(Math.max(Math.floor(n), 1), 720);
    const next = currentRouteParams();
    next.set("hours", String(n));
    setHashParams(next);
  };

  return el("div", {}, [
    el("div", { class: "mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between" }, [
      el("div", {}, [
        el("h1", { class: "text-2xl font-bold text-gray-800 tracking-tight", text: "Recent status changes" }),
        el("p", {
          class: "mt-1 text-gray-500 max-w-3xl",
          text:
            "Aircraft with a logged status transition in the selected window (TAM updates, FlightAware-driven changes, etc.). Columns match the fleet list style where possible.",
        }),
      ]),
      el("div", { class: "flex flex-wrap items-end gap-3" }, [
        el("label", { class: "flex flex-col gap-1 text-xs font-medium text-gray-600" }, [
          el("span", { text: "Preset window" }),
          hoursSelect,
        ]),
        el("div", { class: "flex flex-col gap-1" }, [
          el("label", { for: "v2-status-recent-hours-custom", class: "text-xs font-medium text-gray-600", text: "Custom hours (1–720)" }),
          el("div", { class: "flex gap-2" }, [
            el("input", {
              id: "v2-status-recent-hours-custom",
              type: "number",
              min: "1",
              max: "720",
              class:
                "w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-dae focus:ring-dae",
              value: hourVal,
              onkeydown: (e) => {
                if (e.key === "Enter") applyCustomHours();
              },
            }),
            el("button", {
              type: "button",
              class:
                "px-4 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
              text: "Apply",
              onclick: applyCustomHours,
            }),
          ]),
        ]),
      ]),
    ]),
    el("p", {
      id: "v2-status-recent-meta",
      class: "mb-3 text-sm text-gray-600",
      text: "Loading…",
    }),
    renderStatusChangesTableShell({ tbodyId: "v2-status-recent-body", loadingText: "Loading…" }),
  ]);
}

function renderRoute() {
  try {
    const rk = currentRouteKey();
    const rtp = currentRouteParams();
    if (isInspectorSession() && !isInspectorAllowedRoute(rk)) {
      try {
        history.replaceState(null, "", inspectorHomeHash());
      } catch {}
      return renderRoute();
    }
    if (isLimitedFleetUiSession() && (isFigaroRoute(rk) || !isLimitedFleetUiAllowedRoute(rk))) {
      try {
        history.replaceState(null, "", limitedFleetUiHomeHash());
      } catch {}
      return renderRoute();
    }
    if (isLimitedFleetUiSession() && rk === "settings" && cleanText(rtp.get("tab")) && cleanText(rtp.get("tab")) !== "account") {
      try {
        history.replaceState(null, "", "#/settings?tab=account");
      } catch {}
      return renderRoute();
    }
    if (rk === "settings" && cleanText(rtp.get("tab")) === "materials-projects") {
      try {
        history.replaceState(null, "", "#/materials/projects");
      } catch {}
      return renderRoute();
    }
    if (rk === "settings" && cleanText(rtp.get("tab")) === "activity") {
      try {
        history.replaceState(null, "", "#/status-recent");
      } catch {}
      return renderRoute();
    }
    if (currentRouteKey() === "crons") {
      history.replaceState(null, "", "#/settings?tab=crons");
    }
    if (currentRouteKey() === "activity") {
      history.replaceState(null, "", "#/status-recent");
      return renderRoute();
    }
    if (currentRouteKey() === "about") {
      history.replaceState(null, "", "#/settings?tab=about");
    }
  } catch {
    // ignore
  }

  const key = currentRouteKey();
  if (key === "ai" || key.startsWith("ai/") || key === "perkins" || key.startsWith("perkins/")) {
    try {
      const qs = currentRouteParams().toString();
      const next = key.replace(/^ai/, "figaro").replace(/^perkins/, "figaro");
      history.replaceState(null, "", `#/${next}${qs ? `?${qs}` : ""}`);
    } catch {
      /* ignore */
    }
    return renderRoute();
  }
  if (key === "avherald-incidents") {
    try {
      const qs = currentRouteParams().toString();
      history.replaceState(null, "", `#/${NEWS_INCIDENTS_ROUTE}${qs ? `?${qs}` : ""}`);
    } catch {
      /* ignore */
    }
    return renderRoute();
  }
  const partEdit = /^materials\/part\/(\d+)$/.exec(key);
  const projectEdit = /^materials\/project\/(\d+)$/.exec(key);
  const mount = document.getElementById("v2-content");
  if (!mount) return;
  removeStaleActrackerModalBackdrops();
  if (key !== NEWS_INCIDENTS_ROUTE) stopAvheraldRefreshPoll();

  const focusState = captureFocusState();
  if (!isOperatorProgressRoute(key)) disposeOperatorProgressRoute();

  let node;
  if (key === "dashboard") node = renderDashboard();
  else if (isOperatorProgressRoute(key)) {
    try {
      node = renderOperatorProgressPage(
        {
          el,
          fetchJson,
          apiUrl,
          cleanText,
          state,
          isAdminSession,
          hasFleetScopeAccess,
          getAllowedOwners,
          operatorNamesForSession,
          aircraftModelFamily,
          aircraftRowForTable,
          normaliseTam,
          htmlEscape,
          formatMaterialsExportTimeEst,
          renderHeatmapStatusLegend,
          loadOperators,
          tamTileClass,
          flightawareAircraftUrl,
          tamBadge,
          currentRouteParams,
          refreshRoute: renderRoute,
        },
        currentRouteParams()
      );
    } catch (err) {
      console.error("Operator progress render failed", err);
      node = el("div", { class: "p-8" }, [
        el("h1", { class: "text-xl font-semibold text-red-800", text: "Operator progress failed to load" }),
        el("p", { class: "mt-2 text-sm text-gray-700", text: cleanText(err?.message) || String(err) }),
      ]);
    }
  }
  else if (key === "heatmap") node = renderHeatmapPage();
  else if (key === "spirit-tracker") node = renderSpiritTrackerPage();
  else if (key === "non-service-all")
    node = renderAllAircraft({
      title: "Non-service (all)",
      predicate: (a) => effectiveTamForFilters(a) !== "IN SERVICE",
    });
  else if (key === "aog")
    node = renderAllAircraft({
      title: "AOG",
      predicate: (a) => effectiveTamForFilters(a) === "AOG",
    });
  else if (key === "transition")
    node = renderAllAircraft({
      title: "Transition",
      predicate: (a) => effectiveTamForFilters(a) === "TRANSITION",
    });
  else if (key === "maintenance")
    node = renderAllAircraft({
      title: "Maintenance",
      predicate: (a) => effectiveTamForFilters(a) === "MAINTENANCE",
    });
  else if (key === "incident")
    node = renderAllAircraft({
      title: "Incident",
      predicate: (a) => effectiveTamForFilters(a) === "INCIDENT",
    });
  else if (key === "storage")
    node = renderAllAircraft({
      title: "Storage",
      predicate: (a) => effectiveTamForFilters(a) === "STORAGE",
    });
  else if (key === "russia")
    node = renderAllAircraft({
      title: "Russia",
      predicate: (a) => effectiveTamForFilters(a) === "RUSSIA",
    });
  else if (key === "watch")
    node = renderAllAircraft({ title: "On watch", predicate: (a) => Boolean(a?.watch_on) });
  else if (key === "awaiting-operator")
    node = renderAllAircraft({
      title: "Awaiting operator feedback",
      predicate: (a) => /await/i.test(cleanText(a?.tam_status)) || /await/i.test(cleanText(a?.status_text)),
    });
  else if (key === "no-flights-24h")
    node = renderAllAircraft({
      title: "No flights in 5 days",
      predicate: (a) => matchesNoFlightsFiveDaysAircraft(a),
      defaultSortKey: "operator",
      showNoFlightsSummary: true,
    });
  else if (key === "ground-over-21d")
    node = renderAllAircraft({
      title: "More than 21 days on ground",
      predicate: (a) => matchesMoreThan21DaysOnGround(a),
      defaultSortKey: "tog",
    });
  else if (key === "middle-east")
    node = renderAllAircraft({
      title: "Middle East",
      predicate: (a) => Boolean(a?.is_me_fleet),
    });
  else if (key === "materials/part/new") node = renderMaterialsPartEditorPage({ partId: null });
  else if (partEdit) node = renderMaterialsPartEditorPage({ partId: partEdit[1] });
  else if (key === "materials/projects") node = renderMaterialsProjectsListPage();
  else if (key === "materials/project/new") node = renderMaterialsProjectEditorPage({ projectId: null });
  else if (projectEdit) node = renderMaterialsProjectEditorPage({ projectId: projectEdit[1] });
  else if (key === "materials-dashboard") node = renderMaterialsDashboardPage();
  else if (key === "materials") node = renderMaterialsPartsPage();
  else if (key === "materials/cores") node = renderMaterialsCoresPage();
  else if (isSurveillanceRoute(key)) node = renderSurveillanceRoute({ el, fetchJson, apiUrl, cleanText, state }, key, currentRouteParams());
  else if (isFigaroRoute(key)) {
    // Limited fleet accounts (es, Singapore FMs): never mount Figaro UI.
    if (isLimitedFleetUiSession()) node = renderDashboard();
    else node = renderFigaroRoute({ el, fetchJson, apiUrl, cleanText, routeKey: key });
  }
  else if (isTransitionsRoute(key)) node = renderTransitionsRoute({ el, routeKey: key });
  else if (key === "settings") node = renderSettings();
  else if (key === "status-recent") node = renderRecentStatusChangesPage();
  else if (key === NEWS_INCIDENTS_ROUTE) node = renderAvheraldIncidentsPage();
  else node = renderAllAircraft({
    title: isInspectorSession() ? "Assigned aircraft" : "All aircraft",
    subtitle: isInspectorSession()
      ? "Operational view of aircraft on your surveillance assignments (location, status, next flight)."
      : null,
  });

  mount.replaceChildren(node);
  setActiveNav();
  restoreFocusState(focusState);
  if (key === NEWS_INCIDENTS_ROUTE) initAvheraldIncidentsPage().catch(() => {});
  if (key === "materials-dashboard") initMaterialsDashboard().catch(() => {});
  if (key === "materials") initMaterialsPartsPage().catch(() => {});
  if (key === "materials/cores") initMaterialsCoresPage().catch(() => {});
  if (key === "materials/part/new") initMaterialsPartEditorPage("new").catch(() => {});
  if (partEdit) initMaterialsPartEditorPage(partEdit[1]).catch(() => {});
  if (key === "materials/projects") initMaterialsProjectsListPage().catch(() => {});
  if (key === "materials/project/new") initMaterialsProjectEditorPage("new").catch(() => {});
  if (projectEdit) initMaterialsProjectEditorPage(projectEdit[1]).catch(() => {});
}

function applySidebar(open, { animate = true } = {}) {
  state.sidebarOpen = open;
  const aside = document.getElementById("sidebar");
  const main = document.getElementById("main-content");
  if (!aside || !main) return;
  if (!animate) {
    aside.style.transition = "none";
    main.style.transition = "none";
  }
  if (open) {
    aside.style.transform = "translateX(0)";
    main.style.marginLeft = "16rem";
  } else {
    aside.style.transform = "translateX(-100%)";
    main.style.marginLeft = "0";
  }
  if (!animate) {
    requestAnimationFrame(() => {
      aside.style.transition = "transform .25s ease";
      main.style.transition = "margin-left .25s ease";
    });
  }
  try {
    localStorage.setItem("actracker.v2.sidebarOpen", open ? "true" : "false");
  } catch {
    // ignore
  }
}

function bindUi() {
  const toggle = document.getElementById("sb-toggle-btn");
  if (toggle) toggle.addEventListener("click", () => applySidebar(!state.sidebarOpen));

  const refresh = document.getElementById("v2-refresh-all");
  if (refresh) {
    ensureTopbarProgressUi();
    refresh.addEventListener("click", async () => {
      refresh.disabled = true;
      refresh.textContent = "Refreshing…";
      let sawError = null;
      try {
        await loadAircraft();
        const routeKey = currentRouteKey();
        const visible = currentVisibleAircraft();
        const faTargets = routeKey === "spirit-tracker" ? buildSpiritTrackerFaTargets(state.aircraft || []) : null;
        const total =
          routeKey === "spirit-tracker" && Array.isArray(faTargets) && faTargets.length
            ? faTargets.length
            : (visible || []).length;
        setTopbarProgress({ visible: true, done: 0, total });
        try {
          await refreshVisibleAircraftLive({
            aircraftList: visible,
            faTargets: routeKey === "spirit-tracker" && faTargets?.length ? faTargets : undefined,
            onProgress: ({ done, total: t }) => {
              setTopbarProgress({ visible: true, done, total: t });
              renderRoute();
            },
          });
        } catch (e) {
          sawError = e;
        }
        renderRoute();
        if (sawError) {
          console.error("v2 refresh failed", sawError);
          window.alert(`Refresh failed: ${cleanText(sawError?.message) || String(sawError)}`);
        }
      } catch (e) {
        console.error("v2 refresh failed (loadAircraft)", e);
        window.alert(`Refresh failed: ${cleanText(e?.message) || String(e)}`);
      } finally {
        setTopbarProgress({ visible: false });
        refresh.disabled = false;
        refresh.textContent = "Refresh all";
      }
    });
  }

  const logoutBtn = document.getElementById("v2-logout");
  if (logoutBtn) logoutBtn.addEventListener("click", logout);

  window.addEventListener("hashchange", () => {
    renderRoute();
    initDashboardPanels().catch(() => {});
    initRecentStatusChangesPanels().catch(() => {});
    initSettingsPanels().catch(() => {});
    initMaterialsDashboard().catch(() => {});
    initMaterialsPartsPage().catch(() => {});
    initMaterialsCoresPage().catch(() => {});
    const rk = currentRouteKey();
    const pe = /^materials\/part\/(\d+)$/.exec(rk);
    const pj = /^materials\/project\/(\d+)$/.exec(rk);
    if (rk === "materials/part/new") initMaterialsPartEditorPage("new").catch(() => {});
    if (pe) initMaterialsPartEditorPage(pe[1]).catch(() => {});
    if (rk === "materials/projects") initMaterialsProjectsListPage().catch(() => {});
    if (rk === "materials/project/new") initMaterialsProjectEditorPage("new").catch(() => {});
    if (pj) initMaterialsProjectEditorPage(pj[1]).catch(() => {});
    if (rk === "materials/cores") initMaterialsCoresPage().catch(() => {});
  });
}

async function initDashboardPanels() {
  const xrmMetaEl = document.getElementById("v2-dashboard-xrm-meta");
  if (xrmMetaEl) {
    fetchJson(apiUrl("/api/xrm-import/last"))
      .then((r) => {
        const at = r?.last?.completed_at;
        if (!at) {
          xrmMetaEl.textContent = "Last XRM import: never";
          return;
        }
        const who = cleanText(r?.last?.uploaded_by);
        xrmMetaEl.textContent = `Last XRM import: ${formatDisplayTimestamp(at)}${who ? ` by ${who}` : ""}`;
      })
      .catch(() => {
        xrmMetaEl.textContent = "";
      });
  }

  const cronStatusEl = document.getElementById("v2-cron-status");
  const cronLogsEl = document.getElementById("v2-cron-logs");
  const cronLogMetaEl = document.getElementById("v2-cron-log-meta");
  const cronRefreshBtn = document.getElementById("v2-cron-refresh");
  const cronRunBtn = document.getElementById("v2-cron-run");

  const refreshCron = async () => {
    if (!cronStatusEl) return;
    cronStatusEl.textContent = "Loading…";
    if (cronLogMetaEl) cronLogMetaEl.textContent = "";
    try {
      const status = await loadCronStatus();
      const parts = [];
      parts.push(`Cron entries detected on server: ${status?.cronFound ? "yes" : "no"}`);
      if (status?.scheduleDescription) parts.push(`Schedule: ${status.scheduleDescription}`);
      if (status?.nextRunTime) parts.push(`Next run (approx.): ${status.nextRunTime}`);
      if (status?.duplicateEntriesDetected) parts.push("Warning: duplicate cron entries detected.");
      const log = status?.logStatus || {};
      if (log?.lastRunLine) parts.push(`Latest log line: ${log.lastRunLine}`);
      cronStatusEl.textContent = parts.filter(Boolean).join(" · ");

      if (cronLogsEl) {
        const payload = await loadCronLogs();
        let lines = [];
        if (Array.isArray(payload?.logs)) lines = payload.logs;
        else if (Array.isArray(payload?.lines)) lines = payload.lines;
        else if (typeof payload?.tail === "string") lines = payload.tail.split("\n");
        else if (Array.isArray(payload)) lines = payload;

        if (lines.length) {
          cronLogsEl.textContent = lines.slice(-120).join("\n");
          if (cronLogMetaEl) {
            const tail = Math.min(lines.length, 120);
            const pathShort = cleanText(payload?.logPath || "").replace(/^.*\//, "") || "cron log";
            cronLogMetaEl.textContent = `Recent output (${tail} lines from ${pathShort}). Full job list and logs: Settings → Crons.`;
          }
        } else if (!payload?.ok && payload?.error) {
          cronLogsEl.textContent = cleanText(payload.error);
        } else {
          cronLogsEl.textContent = cleanText(payload?.message) || "No log lines returned yet.";
        }
      }
    } catch (e) {
      cronStatusEl.textContent = `Error: ${cleanText(e?.message) || String(e)}`;
      if (cronLogsEl) cronLogsEl.textContent = "";
      if (cronLogMetaEl) cronLogMetaEl.textContent = "";
    }
  };

  const runCron = async () => {
    if (!cronStatusEl) return;
    if (!isAdminSession()) {
      cronStatusEl.textContent = "Only admins can trigger the legacy cron runner from here.";
      return;
    }
    cronStatusEl.textContent = "Starting cron…";
    try {
      await runCronNow();
      cronStatusEl.textContent = "Cron triggered. Use Refresh in a few seconds to update the log snippet.";
    } catch (e) {
      cronStatusEl.textContent = `Run failed: ${cleanText(e?.message) || String(e)}`;
    }
  };

  if (cronRefreshBtn) cronRefreshBtn.onclick = refreshCron;
  if (cronRunBtn) cronRunBtn.onclick = runCron;

  await refreshCron();
  await loadAvheraldDashboardDaeStrip();
  await initDashboardStatusChangesPreview();
}

function renderAvheraldIncidentsPage() {
  const params = currentRouteParams();
  const cls = cleanText(params.get("classification")).toUpperCase();
  let daysSel = Number(params.get("days"));
  if (!Number.isFinite(daysSel) || daysSel <= 0) daysSel = 365;
  daysSel = Math.min(1825, Math.max(7, Math.floor(daysSel)));
  const q = cleanText(params.get("q"));
  const operatorSel = cleanText(params.get("operator"));
  const { sortBy, sortDir } = getAvheraldIncidentsSortFromParams(params);
  const dayChoices = Array.from(new Set([30, 60, 120, 180, 365, 730, 1095, 1460, 1820, daysSel])).sort(
    (a, b) => a - b
  );

  const classSel = el("select", {
    id: "v2-avherald-class",
    class: "rounded-lg border border-gray-200 px-3 py-2 text-sm",
  }, [
    el("option", { value: "", text: "All", selected: !cls }),
    el("option", { value: "DAE", text: "DAE", selected: cls === "DAE" }),
    el("option", { value: "NON_DAE", text: "Non-DAE", selected: cls === "NON_DAE" }),
    el("option", { value: "UNKNOWN", text: "Unknown", selected: cls === "UNKNOWN" }),
  ]);

  const daySel = el(
    "select",
    {
      id: "v2-avherald-days",
      class: "rounded-lg border border-gray-200 px-3 py-2 text-sm",
    },
    dayChoices.map((d) => el("option", { value: String(d), text: String(d), selected: d === daysSel }))
  );

  const qInput = el("input", {
    id: "v2-avherald-q",
    type: "text",
    value: q,
    class: "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm",
    placeholder: "Filter as you type (title, registration, MSN, synopsis…)",
  });

  const operatorSelect = el(
    "select",
    {
      id: "v2-avherald-operator",
      class: "rounded-lg border border-gray-200 px-3 py-2 text-sm min-w-[12rem] max-w-[20rem]",
      disabled: true,
    },
    [el("option", { value: "", text: "All operators", selected: !operatorSel })]
  );

  const tableCard = el("div", { class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden w-[95%] max-w-none" }, [
    el("div", { class: "overflow-x-auto" }, [
      el("table", { class: "w-full min-w-[88rem] text-sm text-left text-gray-600" }, [
        el("thead", { id: "v2-avherald-thead", class: "text-xs text-white uppercase bg-dae" }, [
          el("tr", { id: "v2-avherald-thead-row" }, [
            avheraldIncidentsSortTh("Event date", "event_date", sortBy, sortDir, "whitespace-nowrap"),
            avheraldIncidentsSortTh("Seen", "seen", sortBy, sortDir, "whitespace-nowrap"),
            avheraldIncidentsSortTh("Fleet match", "classification", sortBy, sortDir),
            avheraldIncidentsSortTh("DAE / MAF", "fleet", sortBy, sortDir, "whitespace-nowrap"),
            avheraldIncidentsSortTh("Type", "type", sortBy, sortDir),
            avheraldIncidentsSortTh("Reg", "registration", sortBy, sortDir, "min-w-[8rem]"),
            avheraldIncidentsSortTh("MSN", "msn", sortBy, sortDir, "whitespace-nowrap"),
            el("th", { class: "px-3 py-2 align-top whitespace-normal leading-tight", text: "Loc" }),
            el("th", { class: "px-3 py-2 align-top whitespace-normal leading-tight", text: "TOG" }),
            el("th", { class: "px-3 py-2 align-top whitespace-nowrap", text: "TAM" }),
            avheraldIncidentsSortTh("Operator", "operator", sortBy, sortDir),
            avheraldIncidentsSortTh("Category", "category", sortBy, sortDir),
            avheraldIncidentsSortTh("Synopsis", "synopsis", sortBy, sortDir),
            el("th", { class: "px-3 py-2", text: "Link" }),
          ]),
        ]),
        el("tbody", { id: "v2-avherald-body" }, [
          el("tr", {}, [el("td", { class: "px-3 py-4", colspan: "14", text: "Loading…" })]),
        ]),
      ]),
    ]),
  ]);

  return el("div", { class: "w-[95%] max-w-none mx-auto px-2 py-6" }, [
    el("div", { class: "mb-4 flex flex-wrap items-start justify-between gap-4" }, [
      el("h1", { class: "text-2xl font-bold text-gray-900", text: "Incidents & accidents" }),
      el("div", { class: "flex flex-col items-end gap-1 min-w-[16rem]" }, [
        el("span", {
          id: "v2-avherald-last-check",
          class: "text-xs text-gray-600",
          text: "Last check: loading…",
        }),
        el("button", {
          type: "button",
          id: "v2-avherald-refresh-btn",
          class: "rounded-lg bg-dae px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50",
          text: "Refresh incidents now",
        }),
        el("div", {
          id: "v2-avherald-refresh-outcome",
          class: "hidden text-sm text-gray-700 text-right max-w-md",
          text: "",
        }),
      ]),
    ]),
    el("div", { class: "flex flex-wrap gap-3 items-end mb-4" }, [
      el("div", {}, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-avherald-class", text: "Classification" }),
        classSel,
      ]),
      el("div", {}, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-avherald-days", text: "Days back" }),
        daySel,
      ]),
      el("div", { id: "v2-avherald-operator-wrap", class: "hidden" }, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-avherald-operator", text: "Operator" }),
        operatorSelect,
      ]),
      el("div", { class: "flex-1 min-w-[14rem]" }, [
        el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: "v2-avherald-q", text: "Search" }),
        qInput,
      ]),
      el("div", { class: "flex flex-col items-end gap-1" }, [
        el("button", {
          type: "button",
          id: "v2-avherald-export-btn",
          class:
            "inline-flex items-center gap-2 rounded-lg border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-wait",
          title: "Download every incident matching the current filters and sort (all pages) as an Excel workbook",
          text: "Export to Excel",
        }),
        el("span", { id: "v2-avherald-export-status", class: "text-xs text-gray-500 min-h-[1rem]", text: "" }),
      ]),
    ]),
    el("div", {
      id: "v2-avherald-class-counts",
      class: "mb-3 flex flex-wrap gap-2 min-h-[1.75rem]",
    }),
    el("div", {
      id: "v2-avherald-stats",
      class: "mb-2 text-xs text-gray-600 min-h-[2.5rem] leading-relaxed",
      text: "",
    }),
    el("div", { id: "v2-avherald-pager", class: "mb-2 flex flex-wrap items-center gap-2" }, [
      el("button", {
        type: "button",
        id: "v2-avherald-prev",
        class: "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed",
        text: "Previous",
        disabled: true,
      }),
      el("span", { id: "v2-avherald-pager-label", class: "text-sm text-gray-600 min-w-[9rem]", text: "" }),
      el("button", {
        type: "button",
        id: "v2-avherald-next",
        class: "rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed",
        text: "Next",
        disabled: true,
      }),
    ]),
    tableCard,
    el("div", {
      id: "v2-avherald-ingest",
      class: "mt-6 hidden rounded-xl border border-amber-100 bg-amber-50/70 p-4",
    }, []),
  ]);
}

async function initAvheraldIncidentsPage() {
  if (state.avheraldIngestPoll) {
    clearInterval(state.avheraldIngestPoll);
    state.avheraldIngestPoll = null;
  }
  stopAvheraldRefreshPoll();
  if (state.newsIncidentsSearchDebounce) {
    clearTimeout(state.newsIncidentsSearchDebounce);
    state.newsIncidentsSearchDebounce = null;
  }

  await Promise.allSettled([loadAircraft(), loadFaCache()]);

  const rp0 = currentRouteParams();
  const pg0 = Number(cleanText(rp0.get("page")));
  state.newsIncidentsPage = Number.isFinite(pg0) && pg0 > 0 ? Math.floor(pg0) : 1;

  function syncNewsIncidentsUrlFromFormKeepPage() {
    const p = new URLSearchParams();
    const clsEl = document.getElementById("v2-avherald-class");
    const daysEl = document.getElementById("v2-avherald-days");
    const opEl = document.getElementById("v2-avherald-operator");
    const qEl = document.getElementById("v2-avherald-q");
    const cls = cleanText(clsEl?.value).toUpperCase();
    const days = cleanText(daysEl?.value) || "365";
    const op = cleanText(opEl?.value);
    const qq = cleanText(qEl?.value);
    if (cls) p.set("classification", cls);
    if (days) p.set("days", days);
    if (op) p.set("operator", op);
    if (qq) p.set("q", qq);
    const { sortBy, sortDir } = getAvheraldIncidentsSortFromParams();
    if (sortBy && sortBy !== "event_date") p.set("sort", sortBy);
    else p.delete("sort");
    if (sortDir && sortDir !== "desc") p.set("dir", sortDir);
    else p.delete("dir");
    if (state.newsIncidentsPage > 1) p.set("page", String(state.newsIncidentsPage));
    const qs = p.toString();
    try {
      history.replaceState(null, "", `#/${NEWS_INCIDENTS_ROUTE}${qs ? `?${qs}` : ""}`);
    } catch {
      /* ignore */
    }
  }

  function syncQueryToUrlAndReload() {
    state.newsIncidentsPage = 1;
    syncNewsIncidentsUrlFromFormKeepPage();
    loadAvheraldOperatorOptions().catch(() => {});
    loadAvheraldIncidentsTable().catch(() => {});
  }

  async function loadAvheraldOperatorOptions() {
    const wrap = document.getElementById("v2-avherald-operator-wrap");
    const sel = document.getElementById("v2-avherald-operator");
    if (!wrap || !sel) return;
    const params = currentRouteParams();
    const qs = new URLSearchParams();
    qs.set("days", cleanText(params.get("days")) || "365");
    const c = cleanText(params.get("classification")).toUpperCase();
    if (c) qs.set("classification", c);
    const qq = cleanText(params.get("q"));
    if (qq) qs.set("q", qq);
    const curOp = cleanText(params.get("operator"));
    try {
      const resp = await fetchJson(apiUrl(`/api/avherald-incidents/operators?${qs.toString()}`));
      if (!resp?.enabled) {
        wrap.classList.add("hidden");
        sel.disabled = true;
        return;
      }
      const ops = Array.isArray(resp?.operators) ? resp.operators : [];
      if (!ops.length) {
        wrap.classList.add("hidden");
        sel.disabled = true;
        return;
      }
      wrap.classList.remove("hidden");
      sel.disabled = false;
      sel.title = resp?.bulkImportActive
        ? "History import in progress — operator list grows as rows are added."
        : "";
      sel.replaceChildren(
        el("option", { value: "", text: "All operators", selected: !curOp }),
        ...ops.map((row) => {
          const name = cleanText(row?.operator);
          const n = Number(row?.incidentCount) || 0;
          return el("option", {
            value: name,
            text: `${name} (${n})`,
            selected: curOp === name,
          });
        })
      );
      if (curOp && !ops.some((row) => cleanText(row?.operator) === curOp)) {
        sel.appendChild(el("option", { value: curOp, text: `${curOp} (0)`, selected: true }));
      }
    } catch {
      wrap.classList.add("hidden");
      sel.disabled = true;
    }
  }

  const clsEl = document.getElementById("v2-avherald-class");
  const daysEl = document.getElementById("v2-avherald-days");
  const opEl = document.getElementById("v2-avherald-operator");
  const qEl = document.getElementById("v2-avherald-q");
  const exportBtn = document.getElementById("v2-avherald-export-btn");
  if (exportBtn) exportBtn.onclick = () => exportAvheraldIncidentsToExcel().catch(() => {});
  if (clsEl) clsEl.onchange = () => syncQueryToUrlAndReload();
  if (daysEl) daysEl.onchange = () => syncQueryToUrlAndReload();
  if (opEl) opEl.onchange = () => syncQueryToUrlAndReload();
  if (qEl) {
    qEl.oninput = () => {
      if (state.newsIncidentsSearchDebounce) clearTimeout(state.newsIncidentsSearchDebounce);
      state.newsIncidentsSearchDebounce = setTimeout(() => {
        state.newsIncidentsSearchDebounce = null;
        syncQueryToUrlAndReload();
      }, 380);
    };
  }
  async function loadAvheraldIncidentsTable() {
    const tbody = document.getElementById("v2-avherald-body");
    const statsHost = document.getElementById("v2-avherald-stats");
    if (!tbody) return;
    if (statsHost) statsHost.textContent = "Loading…";
    updateAvheraldIncidentsPager({});
    const params = currentRouteParams();
    const qs = new URLSearchParams();
    qs.set("days", cleanText(params.get("days")) || "365");
    const c = cleanText(params.get("classification")).toUpperCase();
    if (c) qs.set("classification", c);
    const qq = cleanText(params.get("q"));
    if (qq) qs.set("q", qq);
    const op = cleanText(params.get("operator"));
    if (op) qs.set("operator", op);
    qs.set("pageSize", "50");
    qs.set("page", String(state.newsIncidentsPage));
    const { sortBy, sortDir } = getAvheraldIncidentsSortFromParams(params);
    qs.set("sortBy", sortBy);
    qs.set("sortDir", sortDir);
    tbody.replaceChildren(
      el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-500", colspan: "14", text: "Loading…" })])
    );
    try {
      const resp = await fetchJson(apiUrl(`/api/avherald-incidents?${qs.toString()}`));
      const items = Array.isArray(resp?.items) ? resp.items : [];
      const st = resp?.stats && typeof resp.stats === "object" ? resp.stats : null;
      if (statsHost) statsHost.textContent = formatAvheraldIncidentsStatsLine(st, items.length);
      renderAvheraldClassificationCounts(st, c, (nextCls) => {
        const sel = document.getElementById("v2-avherald-class");
        if (sel) sel.value = nextCls;
        syncQueryToUrlAndReload();
      });
      updateAvheraldIncidentsPager(st || {});

      const mTotal = Number(st?.matchedTotal);
      const tp = Number(st?.totalPages);
      if (
        st &&
        Number.isFinite(mTotal) &&
        Number.isFinite(tp) &&
        state.newsIncidentsPage > tp
      ) {
        state.newsIncidentsPage = Math.max(1, tp);
        syncNewsIncidentsUrlFromFormKeepPage();
        return loadAvheraldIncidentsTable();
      }

      if (!items.length) {
        const totalDb = Number(st?.totalTable);
        const hint =
          Number.isFinite(totalDb) && totalDb === 0
            ? isAdminSession()
              ? "Use Run ingest (admin tools below the table). The AI layer key lives in the repo root `.env` (Compose env_file); if the AI layer still errors, pull `stable`, rebuild the API container, and check the log."
              : "Rows appear after an admin runs ingest on the server (Settings → Crons or server scripts)."
            : "";
        const emptyKids = [
          el("p", { class: "font-medium text-gray-900", text: "No rows in this view." }),
          ...(hint ? [el("p", { class: "mt-1 text-sm text-gray-500 max-w-xl", text: hint })] : []),
        ];
        tbody.replaceChildren(
          el("tr", {}, [
            el("td", { class: "px-3 py-8 text-gray-600 align-middle", colspan: "14" }, emptyKids),
          ])
        );
        return;
      }
      const frag = document.createDocumentFragment();
      for (const row of items) {
        const ac = aircraftForAvheraldIncident(row);
        const fleet = avheraldIncidentFleetCells(row);
        frag.appendChild(
          el("tr", { class: "border-b border-gray-100 align-top" }, [
            el("td", {
              class: "px-3 py-2 whitespace-nowrap text-sm align-top text-gray-900",
              text: formatNewsIncidentPrimaryDate(row),
            }),
            el("td", {
              class: "px-3 py-2 whitespace-nowrap text-sm align-top text-gray-600",
              text: formatShortTime(row.first_seen_at) || "—",
            }),
            el("td", { class: "px-3 py-2 align-top" }, [avheraldFleetMatchBadge(row.dae_classification)]),
            el("td", { class: "px-3 py-2 align-top" }, [avheraldFleetSourceBadge(row.fleet_source)]),
            el("td", { class: "px-3 py-2 text-sm align-top text-gray-800", text: cleanText(row.aircraft_type) || "—" }),
            el("td", { class: "px-3 py-2 align-top min-w-[8rem]" }, [
              avheraldIncidentIdentLink(cleanText(row.registration) || "—", row, ac),
            ]),
            el("td", { class: "px-3 py-2 align-top whitespace-nowrap" }, [
              avheraldIncidentIdentLink(cleanText(row.msn) || "—", row, ac),
            ]),
            el("td", { class: "px-3 py-2 align-top max-w-[12rem]", title: fleet.locationText || "" }, [fleet.location]),
            el("td", { class: "px-3 py-2 align-top" }, [fleet.tog]),
            el("td", { class: `px-3 py-2 align-top ${TAM_TABLE_CELL_CLASS}` }, [fleet.tam]),
            el("td", {
              class: "px-3 py-2 text-sm align-top max-w-[10rem] text-gray-800",
              title: cleanText(row.airline_operator) || "",
              text: cleanText(row.airline_operator) || "—",
            }),
            el("td", { class: "px-3 py-3 text-sm align-top text-gray-800", text: cleanText(row.event_category) || "—" }),
            el("td", {
              class: "px-3 py-2 text-sm align-top min-w-[14rem] max-w-xl text-gray-800 break-words [overflow-wrap:anywhere]",
              text: cleanText(row.synopsis) || cleanText(row.list_title) || "—",
            }),
            el("td", { class: "px-3 py-2 align-top" }, [
              el("a", {
                href: cleanText(row.source_url) || "#",
                target: "_blank",
                rel: "noopener",
                class: "text-dae hover:underline text-sm",
                text: "Article →",
              }),
            ]),
          ])
        );
      }
      tbody.replaceChildren(frag);
    } catch (e) {
      if (statsHost) statsHost.textContent = "";
      updateAvheraldIncidentsPager({});
      tbody.replaceChildren(
        el("tr", {}, [
          el("td", { class: "px-3 py-4 text-red-700 align-middle", colspan: "14", text: cleanText(e?.message) || "Failed to load" }),
        ])
      );
    }
  }

  document.getElementById("v2-avherald-thead")?.addEventListener("click", (ev) => {
    const th = ev.target?.closest?.("[data-avherald-sort]");
    if (!th) return;
    const key = cleanText(th.getAttribute("data-avherald-sort"));
    if (!AVHERALD_INCIDENT_SORT_KEYS.has(key)) return;
    const cur = getAvheraldIncidentsSortFromParams();
    const p = new URLSearchParams(currentRouteParams().toString());
    if (cur.sortBy === key) p.set("dir", cur.sortDir === "asc" ? "desc" : "asc");
    else {
      p.set("sort", key);
      p.set("dir", key === "event_date" || key === "seen" ? "desc" : "asc");
    }
    state.newsIncidentsPage = 1;
    p.delete("page");
    const clsEl2 = document.getElementById("v2-avherald-class");
    const daysEl2 = document.getElementById("v2-avherald-days");
    const opEl2 = document.getElementById("v2-avherald-operator");
    const qEl2 = document.getElementById("v2-avherald-q");
    const cls2 = cleanText(clsEl2?.value).toUpperCase();
    const days2 = cleanText(daysEl2?.value) || "365";
    const op2 = cleanText(opEl2?.value);
    const qq2 = cleanText(qEl2?.value);
    if (cls2) p.set("classification", cls2);
    else p.delete("classification");
    if (days2) p.set("days", days2);
    if (op2) p.set("operator", op2);
    else p.delete("operator");
    if (qq2) p.set("q", qq2);
    else p.delete("q");
    try {
      history.replaceState(null, "", `#/${NEWS_INCIDENTS_ROUTE}${p.toString() ? `?${p.toString()}` : ""}`);
    } catch {
      /* ignore */
    }
    updateAvheraldIncidentsSortHeaderLabels();
    loadAvheraldIncidentsTable().catch(() => {});
  });

  document.getElementById("v2-avherald-prev")?.addEventListener("click", () => {
    const b = document.getElementById("v2-avherald-prev");
    if (b?.disabled) return;
    if (state.newsIncidentsPage <= 1) return;
    state.newsIncidentsPage -= 1;
    syncNewsIncidentsUrlFromFormKeepPage();
    loadAvheraldIncidentsTable().catch(() => {});
  });
  document.getElementById("v2-avherald-next")?.addEventListener("click", () => {
    const b = document.getElementById("v2-avherald-next");
    if (b?.disabled) return;
    state.newsIncidentsPage += 1;
    syncNewsIncidentsUrlFromFormKeepPage();
    loadAvheraldIncidentsTable().catch(() => {});
  });

  const ingestHost = document.getElementById("v2-avherald-ingest");
  if (ingestHost && isAdminSession()) {
    ingestHost.classList.remove("hidden");
    async function refreshAvheraldIngestLog() {
      const pre = document.getElementById("v2-avherald-ingest-log");
      if (!pre) return;
      try {
        const r = await fetchJson(apiUrl("/api/admin/avherald/ingest-log?lines=1200"));
        if (!r?.ok) throw new Error(cleanText(r?.error) || "Log request failed");
        pre.textContent = cleanText(r.tail) || "(empty log)";
      } catch (e) {
        pre.textContent = cleanText(e?.message) || String(e);
      }
    }
    async function triggerAvheraldIngest(mode, max) {
      const statusEl = document.getElementById("v2-avherald-ingest-status");
      const body = { mode };
      if (mode === "lists" && max != null) body.max = max;
      if (statusEl) statusEl.textContent = "Starting…";
      try {
        const r = await fetchJson(apiUrl("/api/admin/avherald/trigger-ingest"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!r?.ok) throw new Error(cleanText(r?.error) || "Trigger failed");
        if (statusEl) {
          statusEl.textContent = `Started (${cleanText(r.mode)}), pid ${r.pid != null ? r.pid : "—"}). Log below; refresh table when finished.`;
        }
        await refreshAvheraldIngestLog();
        if (state.avheraldIngestPoll) clearInterval(state.avheraldIngestPoll);
        state.avheraldIngestPoll = setInterval(() => {
          refreshAvheraldIngestLog().catch(() => {});
        }, 2500);
        setTimeout(() => {
          if (state.avheraldIngestPoll) {
            clearInterval(state.avheraldIngestPoll);
            state.avheraldIngestPoll = null;
          }
        }, 240000);
      } catch (e) {
        if (statusEl) statusEl.textContent = cleanText(e?.message) || String(e);
      }
    }

    ingestHost.replaceChildren(
      el("div", {}, [
        el("div", { class: "text-sm font-semibold text-gray-900", text: "Run ingest (admin)" }),
        el("p", {
          class: "mt-1 text-xs text-gray-700 leading-relaxed max-w-3xl",
          text: "Runs the same ingest scripts as production cron inside the API container. Log tail includes manual runs and the scheduled job log.",
        }),
        el("div", { id: "v2-avherald-ingest-status", class: "mt-2 text-xs text-gray-800 min-h-[1.25rem]" }),
        el("div", { class: "mt-3 flex flex-wrap gap-2" }, [
          el("button", {
            type: "button",
            id: "v2-avherald-ingest-homepage",
            class:
              "inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-dae rounded-lg hover:bg-dae/90",
            text: "Sweep homepage now",
          }),
          el("button", {
            type: "button",
            id: "v2-avherald-ingest-lists",
            class:
              "inline-flex items-center px-3 py-2 text-xs font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50",
            text: "Import list pages (initial, max 120)",
          }),
          el("button", {
            type: "button",
            id: "v2-avherald-ingest-refreshlog",
            class: "inline-flex items-center px-3 py-2 text-xs text-dae hover:underline",
            text: "Refresh log",
          }),
          el("button", {
            type: "button",
            id: "v2-avherald-ingest-reloadtable",
            class: "inline-flex items-center px-3 py-2 text-xs text-dae hover:underline",
            text: "Reload table",
          }),
        ]),
        el("pre", {
          id: "v2-avherald-ingest-log",
          class: "mt-3 max-h-[28rem] overflow-auto whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-2 text-[11px] leading-snug text-gray-800 font-mono",
          text: "Loading log…",
        }),
      ])
    );
    document.getElementById("v2-avherald-ingest-homepage")?.addEventListener("click", () => triggerAvheraldIngest("homepage"));
    document.getElementById("v2-avherald-ingest-lists")?.addEventListener("click", () => triggerAvheraldIngest("lists", 120));
    document.getElementById("v2-avherald-ingest-refreshlog")?.addEventListener("click", () => refreshAvheraldIngestLog().catch(() => {}));
    document.getElementById("v2-avherald-ingest-reloadtable")?.addEventListener("click", () => loadAvheraldIncidentsTable().catch(() => {}));
    refreshAvheraldIngestLog().catch(() => {});
  } else if (ingestHost) {
    ingestHost.classList.add("hidden");
    ingestHost.replaceChildren();
  }

  document.getElementById("v2-avherald-refresh-btn")?.addEventListener("click", async () => {
    const btn = document.getElementById("v2-avherald-refresh-btn");
    const outcomeEl = document.getElementById("v2-avherald-refresh-outcome");
    if (btn?.disabled) return;
    if (btn) btn.disabled = true;
    if (outcomeEl) {
      outcomeEl.classList.remove("hidden");
      outcomeEl.className = "text-sm text-amber-800 text-right max-w-md";
      outcomeEl.textContent = "Starting refresh…";
    }
    try {
      const resp = await fetchJson(apiUrl("/api/avherald-incidents/refresh"), { method: "POST" });
      if (!resp?.ok) throw new Error(cleanText(resp?.error) || "Refresh failed");
      if (outcomeEl) outcomeEl.textContent = cleanText(resp?.message) || "Running incident daily check…";
      startAvheraldRefreshPoll({
        onDone: async () => {
          await loadAvheraldIncidentsTable().catch(() => {});
          await loadAvheraldDashboardDaeStrip().catch(() => {});
        },
      });
      await refreshAvheraldIngestStatusUi().catch(() => {});
    } catch (e) {
      if (outcomeEl) {
        outcomeEl.className = "text-sm text-red-700 text-right max-w-md";
        outcomeEl.textContent = cleanText(e?.message) || "Refresh failed";
      }
      if (btn) btn.disabled = false;
    }
  });

  await refreshAvheraldIngestStatusUi().catch(() => {});
  await loadAvheraldOperatorOptions().catch(() => {});
  await loadAvheraldIncidentsTable();
}

async function dismissAvheraldDashboardAlert(incidentId) {
  const id = Number(incidentId);
  if (!Number.isFinite(id) || id <= 0) return;
  await fetchJson(apiUrl(`/api/avherald-incidents/dashboard-dae/${id}/dismiss`), { method: "POST" });
  await loadAvheraldDashboardDaeStrip();
}

async function loadAvheraldDashboardDaeStrip() {
  const host = document.getElementById("v2-dashboard-avherald-dae");
  if (!host) return;
  host.replaceChildren();
  try {
    const resp = await fetchJson(apiUrl("/api/avherald-incidents/dashboard-dae"));
    const items = Array.isArray(resp?.items) ? resp.items : [];
    if (!items.length) return;
    const cards = items.map((row) =>
      el("div", { class: "relative rounded-lg border border-emerald-100 bg-emerald-50/80 px-4 py-3 pr-10 text-sm" }, [
        el("button", {
          type: "button",
          class: "absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full text-emerald-700 hover:bg-white/80 hover:text-emerald-900",
          title: "Dismiss",
          text: "×",
          onclick: () => dismissAvheraldDashboardAlert(row.id).catch(() => {}),
        }),
        el("div", { class: "font-semibold text-emerald-900", text: cleanText(row.list_title) || "Incident" }),
        el("div", { class: "mt-1 text-emerald-800 text-xs", text: cleanText(row.synopsis) || "" }),
        row.airline_operator
          ? el("div", { class: "mt-1 text-emerald-900 text-xs font-medium", text: cleanText(row.airline_operator) })
          : null,
        el("div", { class: "mt-2 flex flex-wrap items-center gap-2 text-xs text-emerald-900" }, [
          row.fleet_source ? avheraldFleetSourceBadge(row.fleet_source) : null,
          row.registration
            ? el("span", { class: "font-mono bg-white/70 px-2 py-0.5 rounded", text: cleanText(row.registration) })
            : null,
          row.msn
            ? el("span", { class: "font-mono bg-white/70 px-2 py-0.5 rounded", text: `MSN ${cleanText(row.msn)}` })
            : null,
          row.aircraft_type ? el("span", { text: cleanText(row.aircraft_type) }) : null,
        ]),
        el("div", { class: "mt-2" }, [
          el("a", {
            href: cleanText(row.source_url) || "#",
            target: "_blank",
            rel: "noopener",
            class: "text-dae hover:underline font-medium",
            text: "Open report →",
          }),
        ]),
      ])
    );
    host.appendChild(
      el("div", { class: "rounded-xl border border-emerald-200 bg-white p-4 shadow-sm" }, [
        el("div", { class: "flex flex-wrap items-center justify-between gap-3 mb-3" }, [
          el("h2", { class: "text-lg font-semibold text-gray-900", text: "DAE fleet — recent news matches (last 5 days)" }),
          el("a", {
            href: `#/${NEWS_INCIDENTS_ROUTE}?classification=DAE&days=14`,
            class: "text-dae text-sm hover:underline",
            text: "View all →",
          }),
        ]),
        el("div", { class: "grid gap-3 md:grid-cols-2" }, cards),
      ])
    );
  } catch {
    /* optional widget */
  }
}

async function initRecentStatusChangesPanels() {
  const tbody = document.getElementById("v2-status-recent-body");
  const meta = document.getElementById("v2-status-recent-meta");
  if (!tbody || !meta) return;

  const params = currentRouteParams();
  let hours = Number(params.get("hours"));
  if (!Number.isFinite(hours) || hours <= 0) hours = 24;
  hours = Math.min(Math.max(Math.floor(hours), 1), 720);

  const customInput = document.getElementById("v2-status-recent-hours-custom");
  const presetSelect = document.getElementById("v2-status-recent-hours");
  if (customInput && String(customInput.value) !== String(hours)) customInput.value = String(hours);
  if (presetSelect) {
    const valStr = String(hours);
    const existingValues = [...presetSelect.options].map((o) => o.value);
    if (!existingValues.includes(valStr) && hours) {
      presetSelect.appendChild(el("option", { value: valStr, text: `${hours} hours` }));
    }
    presetSelect.value = valStr;
  }

  await populateStatusChangesTable({
    tbody,
    meta,
    hours,
    limit: 1000,
    emptyMessage: "No status changes logged in this window.",
    loadingMessage: "Loading status changes…",
  });
}

async function initSettingsPanels() {
  const matSave = document.getElementById("v2-mat-notify-save");
  const matNew = document.getElementById("v2-mat-notify-new");
  const matDaily = document.getElementById("v2-mat-notify-daily");
  if (matSave && matNew && matDaily && isAdminSession()) {
    if (!matSave.dataset.boundMat) {
      matSave.dataset.boundMat = "1";
      matSave.addEventListener("click", async () => {
        try {
          await putMaterialsNotifications({
            newPartEmails: cleanText(matNew.value),
            dailyReportEmails: cleanText(matDaily.value),
          });
          window.alert("Materials notification recipients saved.");
        } catch (e) {
          window.alert(cleanText(e?.message) || String(e));
        }
      });
    }
    (async () => {
      try {
        const cfg = await fetchMaterialsNotifications();
        if (cfg && matNew && matDaily) {
          matNew.value = cfg.newPartEmails ?? "";
          matDaily.value = cfg.dailyReportEmails ?? "";
        }
      } catch {
        /* ignore */
      }
    })();
  }

  const operatorsBody = document.getElementById("v2-operators-body");
  const operatorsRefresh = document.getElementById("v2-operators-refresh");
  const operatorsSearch = document.getElementById("v2-operators-q");
  const adminAircraftBody = document.getElementById("v2-admin-aircraft-body");
  const adminAircraftRefresh = document.getElementById("v2-admin-aircraft-refresh");
  const adminAircraftDelete = document.getElementById("v2-admin-aircraft-delete");
  const adminAircraftNew = document.getElementById("v2-admin-aircraft-new");
  const adminAircraftFilters = document.getElementById("v2-admin-aircraft-filters");

  const operatorQueryText = () => cleanText(operatorsSearch?.value).toLowerCase();

  const renderOperators = (list) => {
    if (!operatorsBody) return;
    const opsRaw = Array.isArray(list) ? list : [];
    const q = operatorQueryText();
    const ops = q
      ? opsRaw.filter((op) => {
          const name = cleanText(op?.name).toLowerCase();
          const contacts = Array.isArray(op?.contacts) ? op.contacts : [];
          const contactHay = contacts
            .map((c) => `${cleanText(c?.name)} ${cleanText(c?.email)}`.trim().toLowerCase())
            .filter(Boolean)
            .join(" · ");
          return `${name} ${contactHay}`.includes(q);
        })
      : opsRaw;
    if (!ops.length) {
      operatorsBody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3", text: "No operators found." })]));
      return;
    }
    operatorsBody.replaceChildren(
      ...ops.map((op) => {
        const contacts = Array.isArray(op.contacts) ? op.contacts : [];
        const usableContacts = contacts.filter((c) => cleanText(c?.email));
        const first = usableContacts[0] || null;
        const primaryName = cleanText(first?.name) || "—";
        const primaryEmail = cleanText(first?.email) || cleanText(op.contact_email) || cleanText(op.contactEmail) || "—";
        const contactCount = usableContacts.length;
        return el("tr", { class: "bg-white border-b hover:bg-gray-50" }, [
          el("td", { class: "px-4 py-3 font-medium text-gray-900", text: cleanText(op.name) || "—" }),
          el("td", { class: "px-4 py-3" }, [
            el("div", { class: "text-gray-900", text: primaryName }),
            el("div", { class: "mt-0.5 font-mono text-xs text-gray-600", text: primaryEmail }),
          ]),
          el("td", { class: "px-4 py-3 text-gray-700", text: contactCount ? `${contactCount} contact(s)` : "—" }),
          el("td", { class: "px-4 py-3 font-mono text-gray-700", text: String(op.aircraft_count ?? op.aircraftCount ?? "—") }),
          el("td", { class: "px-4 py-3" }, [
            el("button", {
              type: "button",
              class:
                "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
              text: "Edit",
              onclick: () => openOperatorEditModal(op),
            }),
          ]),
        ]);
      })
    );
  };

  const refreshOperators = async () => {
    if (!operatorsBody) return;
    operatorsBody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3", text: "Loading…" })]));
    try {
      const ops = await loadOperators();
      renderOperators(ops);
    } catch (e) {
      operatorsBody.replaceChildren(
        el("tr", {}, [el("td", { class: "px-4 py-3 text-red-700", text: `Error: ${cleanText(e?.message) || String(e)}` })])
      );
    }
  };

  // Make refresh available to modals.
  initSettingsPanels.__refreshOperators = refreshOperators;

  const refreshOwners = async () => {
    try {
      await loadAdminOwners();
    } catch {
      /* ignore */
    }
  };

  if (operatorsSearch) {
    operatorsSearch.oninput = () => renderOperators(state.operators || []);
  }

  if (adminAircraftNew && isAdminSession() && !adminAircraftNew.dataset.bound) {
    adminAircraftNew.dataset.bound = "1";
    adminAircraftNew.addEventListener("click", async () => {
      let ops = Array.isArray(state.operators) ? state.operators : [];
      let owners = Array.isArray(state.adminOwners) ? state.adminOwners : [];
      try {
        if (!ops.length) ops = await loadOperators();
      } catch {
        ops = [];
      }
      try {
        if (!owners.length) owners = await loadAdminOwners();
      } catch {
        owners = [];
      }
      const regions = uniqSorted((state.aircraft || []).map((x) => cleanText(x?.region)).filter(Boolean));
      openAircraftCreateModal({ operators: ops, owners, regions });
    });
  }

  const adminAircraftXrmImport = document.getElementById("v2-admin-aircraft-xrm-import");
  if (adminAircraftXrmImport && isAdminSession() && !adminAircraftXrmImport.dataset.bound) {
    adminAircraftXrmImport.dataset.bound = "1";
    adminAircraftXrmImport.addEventListener("click", () => {
      openXrmImportWizard({
        el,
        fetchJson,
        apiUrl,
        cleanText,
        isAdminSession,
        onComplete: async () => {
          try {
            await loadAircraft(true);
            await refreshAdminAircraft();
          } catch {
            /* ignore */
          }
        },
      });
    });
  }

  const renderAdminAircraft = (list) => {
    if (!adminAircraftBody) return;
    const selected = initSettingsPanels.__selected || new Set();
    const rows = (list || []).map((a) => ({
      id: Number(a?.id),
      msn: cleanText(a?.msn) || "—",
      reg: cleanText(a?.registration) || cleanText(a?.tail) || "—",
      operator: cleanText(a?.operator) || "—",
      owner: cleanText(a?.owner) || "—",
      region: cleanText(a?.region) || "—",
      active: a?.active === false ? false : true,
      isMeFleet: Boolean(a?.is_me_fleet),
      isRussianFleet: Boolean(a?.is_russian_fleet),
      __raw: a,
    }));

    const params = currentRouteParams();
    const q = getParamText(params, "q").toLowerCase();
    const ownerFilter = getParamText(params, "owner");
    const operatorFilter = getParamText(params, "operator");
    const regionFilter = getParamText(params, "region");

    const filtered = rows.filter((r) => {
      if (ownerFilter && r.owner.toUpperCase() !== ownerFilter.toUpperCase()) return false;
      if (operatorFilter && r.operator.toUpperCase() !== operatorFilter.toUpperCase()) return false;
      if (regionFilter && r.region !== regionFilter) return false;
      if (!q) return true;
      const hay = `${r.msn} ${r.reg} ${r.operator} ${r.owner} ${r.region}`.toLowerCase();
      return hay.includes(q);
    });

    if (adminAircraftFilters) {
      adminAircraftFilters.replaceChildren(
        filterBar({
          baseList: list || [],
          params,
          show: { search: true, owner: true, operator: true, region: true, tam: false, source: false, watch: false },
        })
      );
    }

    if (!filtered.length) {
      adminAircraftBody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3", text: "No aircraft match." })]));
      return;
    }

    const admin = isAdminSession();
    adminAircraftBody.replaceChildren(
      ...filtered.map((r) =>
        el("tr", { class: "bg-white border-b hover:bg-gray-50" }, [
          ...(admin
            ? [
                el("td", { class: "px-4 py-3" }, [
                  el("input", {
                    type: "checkbox",
                    checked: selected.has(String(r.id)),
                    onchange: (e) => {
                      if (e.target.checked) selected.add(String(r.id));
                      else selected.delete(String(r.id));
                      initSettingsPanels.__selected = selected;
                    },
                  }),
                ]),
              ]
            : []),
          el("td", { class: "px-4 py-3 font-mono text-gray-900", text: r.msn }),
          el("td", { class: "px-4 py-3 font-mono text-gray-700", text: r.reg }),
          el("td", { class: "px-4 py-3 text-gray-700", text: r.operator }),
          el("td", { class: "px-4 py-3 font-medium text-gray-900", text: r.owner }),
          el("td", { class: "px-4 py-3 text-gray-700", text: r.region }),
          el("td", { class: "px-4 py-3", text: r.active ? "YES" : "NO" }),
          el("td", { class: "px-4 py-3", text: r.isMeFleet ? "YES" : "NO" }),
          el("td", { class: "px-4 py-3", text: r.isRussianFleet ? "YES" : "NO" }),
          el("td", { class: "px-4 py-3" }, [
            el("div", { class: "flex items-center gap-2" }, [
              el("button", {
                type: "button",
                class:
                  "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                text: "Edit",
                onclick: () => openAircraftEditModal(r),
              }),
              ...(admin
                ? [
                    el("button", {
                      type: "button",
                      class:
                        "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-200",
                      text: "Delete",
                      onclick: async () => {
                        if (!window.confirm(`Delete aircraft ${r.reg} (id ${r.id})?`)) return;
                        await deleteAircraftAdmin(r.id);
                        state.adminAircraft = null;
                        await loadAircraft();
                        renderRoute();
                        initSettingsPanels().catch(() => {});
                      },
                    }),
                  ]
                : []),
            ]),
          ]),
        ])
      )
    );
  };

  const refreshAdminAircraft = async () => {
    if (!adminAircraftBody) return;
    adminAircraftBody.replaceChildren(el("tr", {}, [el("td", { class: "px-4 py-3", text: "Loading…" })]));
    try {
      const list = await loadAdminAircraft();
      renderAdminAircraft(list);
    } catch (e) {
      adminAircraftBody.replaceChildren(
        el("tr", {}, [el("td", { class: "px-4 py-3 text-red-700", text: `Error: ${cleanText(e?.message) || String(e)}` })])
      );
    }
  };

  const bulkDelete = async () => {
    const selected = initSettingsPanels.__selected || new Set();
    const ids = Array.from(selected.values()).map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0);
    if (!ids.length) {
      window.alert("Select at least one aircraft.");
      return;
    }
    if (!window.confirm(`Delete ${ids.length} aircraft? This cannot be undone.`)) return;
    if (adminAircraftDelete) {
      adminAircraftDelete.disabled = true;
      adminAircraftDelete.textContent = "Deleting…";
    }
    try {
      for (const id of ids) {
        await deleteAircraftAdmin(id);
      }
      initSettingsPanels.__selected = new Set();
      state.adminAircraft = null;
      await loadAircraft();
      renderRoute();
      await refreshAdminAircraft();
    } catch (e) {
      window.alert(cleanText(e?.message) || String(e));
    } finally {
      if (adminAircraftDelete) {
        adminAircraftDelete.disabled = false;
        adminAircraftDelete.textContent = "Delete selected";
      }
    }
  };

  if (operatorsRefresh) operatorsRefresh.onclick = refreshOperators;
  if (adminAircraftRefresh) adminAircraftRefresh.onclick = refreshAdminAircraft;
  if (adminAircraftDelete) adminAircraftDelete.onclick = bulkDelete;

  const apiCostsHost = document.getElementById("v2-settings-api-costs");
  const apiCostsRefresh = document.getElementById("v2-settings-api-costs-refresh");
  if (apiCostsRefresh && apiCostsHost) {
    apiCostsRefresh.onclick = () => refreshApiCostsInto(apiCostsHost).catch(() => {});
  }

  const costAnalysisHost = document.getElementById("v2-settings-cost-analysis");
  const costAnalysisRefresh = document.getElementById("v2-settings-cost-analysis-refresh");
  if (costAnalysisRefresh && costAnalysisHost) {
    costAnalysisRefresh.onclick = () => refreshCostAnalysisInto(costAnalysisHost).catch(() => {});
  }

  // Initial loads (only if panels exist on the page)
  if (operatorsBody) refreshOperators();
  if (adminAircraftBody) refreshAdminAircraft();

  const tabNow = cleanText(currentRouteParams().get("tab")) || "aircraft";
  if (tabNow === "api-usage" && apiCostsHost) {
    if (state.apiCostsPrefetched) renderApiCostsInto(apiCostsHost, state.apiCostsPrefetched);
    else refreshApiCostsInto(apiCostsHost).catch(() => {});
  }
  if (tabNow === "cost-analysis" && costAnalysisHost) {
    if (state.costAnalysisPrefetched) renderCostAnalysisInto(costAnalysisHost, state.costAnalysisPrefetched);
    else refreshCostAnalysisInto(costAnalysisHost).catch(() => {});
  }
  if (tabNow === "crons") initCronsPanels().catch(() => {});
  if (tabNow === "db-explorer") initDbExplorerPanels().catch(() => {});
  if (tabNow === "backup") initBackupPanels().catch(() => {});
  if (tabNow === "disk") initDiskUsagePanels().catch(() => {});
  if (tabNow === "about") initAboutPanels().catch(() => {});
}

async function initCronsPanels() {
  const container = document.getElementById("v2-cron-config");
  if (!container) return;
  container.replaceChildren(
    el("div", { class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700" }, ["Loading…"])
  );
  try {
    const [cfg, cronStatusResp] = await Promise.all([
      fetchJson(apiUrl("/api/admin/cron/config")),
      fetchJson(apiUrl("/api/cron/status")).catch(() => null),
    ]);
    if (!cfg?.ok) {
      container.replaceChildren(
        el("div", { class: "rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" }, [
          `Failed to load cron config: ${cleanText(cfg?.error) || "unknown error"}`,
        ])
      );
      return;
    }
    const jobs = Array.isArray(cfg?.config?.jobs) ? cfg.config.jobs : [];
    const configState = cfg?.config && typeof cfg.config === "object" ? cfg.config : { jobs: [] };
    const cronStatus = cronStatusResp && typeof cronStatusResp === "object" ? cronStatusResp : null;
    const header = el("div", { class: "rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700" }, [
      el("div", { class: "font-semibold text-gray-900", text: "Server config" }),
      el("div", { class: "mt-1 text-xs text-gray-500", text: `Server time (UTC): ${cleanText(cfg?.serverTimeUtc)}` }),
      el("div", {
        class: "mt-1 text-xs text-gray-500",
        text: `Source: ${cleanText(cfg?.filePath)}`,
      }),
      cronStatus
        ? el("div", {
            class: "mt-2 text-xs text-gray-600",
            text: `Detected schedule: ${cleanText(cronStatus?.scheduleDescription) || "—"} · Installed: ${cronStatus?.cronFound ? "YES" : "NO"}`,
          })
        : el("div", { class: "mt-2 text-xs text-gray-600", text: "Detected schedule: unavailable" }),
      el("div", {
        class:
          "mt-2 text-xs text-sky-950 bg-sky-50 border border-sky-100 rounded-lg px-3 py-2 leading-snug",
        text: "Panel Active (green) used to mean only “enabled” in saved JSON — not whether Linux cron runs. Cards below now compare JSON to /etc/cron.d (mounted at /host-etc-cron.d in the API container) and log freshness. ME 12h, Spirit daily report, Spirit watch alerts (15m), ME manual attention, and Americas are started by /etc/cron.d on the server; turning a job Inactive here does not disable Linux cron.",
      }),
      cfg?.defaulted
        ? el("div", {
            class: "mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2",
            text: `Using default config (file missing/unreadable): ${cleanText(cfg?.error) || "unknown"}`,
          })
        : null,
    ]);

    const SYSTEM_CROND_JOB_IDS = new Set([
      "me-12h",
      "spirit-4h-et",
      "spirit-watch-status-15m",
      "spirit-tracker-activity-45m",
      "me-manual-attention-12h",
      "americas-6am-et",
      "singapore-8am-sgt",
      "avherald-incidents-12h",
      "week-planner-thu-5am-et",
    ]);

    const saveConfig = async (nextConfig) => {
      const resp = await fetchJson(apiUrl("/api/admin/cron/config"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nextConfig),
      });
      if (!resp?.ok) throw new Error(resp?.error || "Failed to save cron config");
      await initCronsPanels();
    };

    const nextRunUtcMsForJob = (jobId) => {
      const id = cleanText(jobId);
      const now = new Date();
      const msNow = now.getTime();
      const utcY = now.getUTCFullYear();
      const utcM = now.getUTCMonth();
      const utcD = now.getUTCDate();

      const isWeekdayUtc = (d) => {
        const wd = d.getUTCDay();
        return wd >= 1 && wd <= 5;
      };

      const nextAtHourMinuteUtc = (hours, minute = 0, { weekdaysOnly = false } = {}) => {
        const sorted = (Array.isArray(hours) ? hours : [hours]).slice().sort((a, b) => a - b);
        for (let dayOffset = 0; dayOffset < 14; dayOffset++) {
          const base = new Date(Date.UTC(utcY, utcM, utcD + dayOffset, 0, 0, 0));
          if (weekdaysOnly && !isWeekdayUtc(base)) continue;
          for (const h of sorted) {
            const candidate = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate(), h, minute, 0));
            if (candidate.getTime() > msNow) return candidate.getTime();
          }
        }
        return null;
      };

      if (id === "me-12h") {
        // Weekdays 06:45 Asia/Dubai = 02:45 UTC. Weekend refresh does not email.
        return nextAtHourMinuteUtc([2], 45, { weekdaysOnly: true });
      }
      if (id === "me-manual-attention-12h") {
        // Still twice daily: 12:00 DXB and 00:00 DXB = 08:00 and 20:00 UTC
        return nextAtHourMinuteUtc([8, 20], 0);
      }
      if (id === "spirit-4h-et") {
        const TZ = "America/New_York";
        const start = Math.floor(msNow / 60000) * 60000;
        for (let m = 1; m < 8 * 24 * 60; m++) {
          const t = start + m * 60000;
          const d = new Date(t);
          if (d.getSeconds() !== 0) continue;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: TZ,
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }).formatToParts(d);
          const hour = Number(parts.find((p) => p.type === "hour")?.value);
          const minute = Number(parts.find((p) => p.type === "minute")?.value);
          if (hour === 6 && minute === 0) return t;
        }
        return null;
      }
      if (id === "spirit-watch-status-15m") {
        const intervalMs = 15 * 60 * 1000;
        return Math.ceil(msNow / intervalMs) * intervalMs;
      }
      if (id === "spirit-tracker-activity-45m") {
        const d = new Date(msNow);
        let t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), 45, 0, 0);
        if (t <= msNow) t += 60 * 60 * 1000;
        return t;
      }
      if (id === "americas-6am-et") {
        const TZ = "America/New_York";
        const start = Math.floor(msNow / 60000) * 60000;
        for (let m = 1; m < 14 * 24 * 60; m++) {
          const t = start + m * 60000;
          const d = new Date(t);
          if (d.getSeconds() !== 0) continue;
          const wd = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short" }).format(d);
          if (!["Mon", "Tue", "Wed", "Thu", "Fri"].includes(wd)) continue;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: TZ,
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }).formatToParts(d);
          const hour = Number(parts.find((p) => p.type === "hour")?.value);
          const minute = Number(parts.find((p) => p.type === "minute")?.value);
          if (hour === 6 && minute === 0) return t;
        }
        return null;
      }
      if (id === "singapore-8am-sgt") {
        const TZ = "Asia/Singapore";
        const start = Math.floor(msNow / 60000) * 60000;
        for (let m = 1; m < 14 * 24 * 60; m++) {
          const t = start + m * 60000;
          const d = new Date(t);
          if (d.getSeconds() !== 0) continue;
          const wd = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short" }).format(d);
          if (!["Mon", "Tue", "Wed", "Thu", "Fri"].includes(wd)) continue;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: TZ,
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }).formatToParts(d);
          const hour = Number(parts.find((p) => p.type === "hour")?.value);
          const minute = Number(parts.find((p) => p.type === "minute")?.value);
          if (hour === 8 && minute === 0) return t;
        }
        return null;
      }
      if (id === "avherald-incidents-12h") {
        const TZ = "Asia/Dubai";
        const start = Math.floor(msNow / 60000) * 60000;
        for (let m = 1; m < 3 * 24 * 60; m++) {
          const t = start + m * 60000;
          const d = new Date(t);
          if (d.getSeconds() !== 0) continue;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: TZ,
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }).formatToParts(d);
          const hour = Number(parts.find((p) => p.type === "hour")?.value);
          const minute = Number(parts.find((p) => p.type === "minute")?.value);
          if (hour === 8 && minute === 0) return t;
        }
        return null;
      }
      if (id === "week-planner-thu-5am-et") {
        const TZ = "America/New_York";
        const start = Math.floor(msNow / 60000) * 60000;
        for (let m = 1; m < 10 * 24 * 60; m++) {
          const t = start + m * 60000;
          const d = new Date(t);
          if (d.getSeconds() !== 0) continue;
          const wd = new Intl.DateTimeFormat("en-US", { timeZone: TZ, weekday: "short" }).format(d);
          if (wd !== "Thu") continue;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: TZ,
            hour: "numeric",
            minute: "numeric",
            hour12: false,
          }).formatToParts(d);
          const hour = Number(parts.find((p) => p.type === "hour")?.value);
          const minute = Number(parts.find((p) => p.type === "minute")?.value);
          if (hour === 5 && minute === 0) return t;
        }
        return null;
      }
      return null;
    };

    const formatCountdown = (msUntil) => {
      if (!Number.isFinite(msUntil)) return "—";
      const totalMinutes = Math.max(0, Math.floor(msUntil / (1000 * 60)));
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      if (h <= 0) return `${m}m`;
      return `${h}h ${m}m`;
    };

    const runJob = async ({ jobId, mode }) => {
      const sendEmail = mode === "send" || mode === "test";
      const dryRun = mode === "dry";
      const jid = cleanText(jobId);
      let overrides = {};
      if (mode === "test") {
        if (jid === "spirit-4h-et") {
          overrides = {
            CRON_SPIRIT_TO: "Simon.Connor@dubaiaerospace.com",
            SPIRIT_REPORT_TEST: "true",
          };
        } else if (jid === "spirit-tracker-activity-45m") {
          overrides = { CRON_SPIRIT_ACTIVITY_TO: "claus.schmetkamp@dubaiaerospace.com" };
        } else if (jid === "week-planner-thu-5am-et") {
          overrides = {
            CRON_WEEK_PLANNER_TEST: "true",
            CRON_ME_TEST_EMAIL: "claus.schmetkamp@dubaiaerospace.com",
          };
        } else {
          overrides = { CRON_ME_TEST_EMAIL: "claus.schmetkamp@dubaiaerospace.com" };
        }
      }
      const resp = await fetchJson(apiUrl("/api/admin/cron/run-job"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobId,
          sendEmail,
          dryRun,
          overrides,
        }),
      });
      if (!resp?.ok) throw new Error(resp?.error || "Failed to trigger job");
      await initCronsPanels();
    };

    const openHistoryModal = async (job) => {
      const j = job && typeof job === "object" ? job : {};
      const jobId = cleanText(j?.id);
      const backdrop = el("div", {
        class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto",
        "data-actracker-modal-backdrop": "1",
      });
      const modal = el("div", {
        class:
          "w-full max-w-5xl max-h-[90vh] my-auto flex flex-col rounded-xl bg-white shadow-lg border border-gray-200",
      });
      const headerNode = el("div", { class: "shrink-0 flex items-start justify-between gap-4 p-4 border-b border-gray-200" }, [
        el("div", {}, [
          el("div", { class: "text-lg font-semibold text-gray-900", text: `History: ${cleanText(j?.name) || jobId || "Job"}` }),
          el("div", {
            class: "mt-1 text-sm text-gray-600",
            text: "Past manual and scheduled sends — newest first. Dates are grouped for easier reading.",
          }),
        ]),
        el("button", { type: "button", class: "text-gray-500 hover:text-gray-800", text: "✕", onclick: () => backdrop.remove() }),
      ]);
      const historyMount = el("div", { id: "v2-cron-history-body", class: "text-sm text-gray-600 py-6 text-center", text: "Loading…" });
      const historyScroll = el("div", {
        class: "flex-1 min-h-0 overflow-y-auto rounded-lg border border-gray-200 bg-white",
      });
      historyScroll.appendChild(historyMount);
      let historyCopyText = "";

      const historyCopyBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Copy all",
        onclick: async () => {
          try {
            await navigator.clipboard.writeText(historyCopyText || "");
            historyCopyBtn.textContent = "Copied";
            setTimeout(() => {
              historyCopyBtn.textContent = "Copy all";
            }, 1600);
          } catch {
            window.alert("Copy failed.");
          }
        },
      });
      const historyToolbar = el("div", { class: "shrink-0 flex flex-wrap items-center gap-2 p-4 border-b border-gray-100" }, [
        historyCopyBtn,
        el("span", { class: "text-xs text-gray-500", text: "Sorted: newest first" }),
      ]);
      const bodyNode = el("div", { class: "flex-1 min-h-0 flex flex-col p-4 pt-0" }, [historyToolbar, historyScroll]);
      modal.append(headerNode, bodyNode);
      backdrop.appendChild(modal);
      backdrop.addEventListener("click", (ev) => {
        if (ev.target === backdrop) backdrop.remove();
      });
      document.body.appendChild(backdrop);

      try {
        const resp = await fetchJson(apiUrl("/api/admin/task-logs?limit=200"));
        const logs = Array.isArray(resp?.logs) ? resp.logs : [];
        const filtered = logs
          .filter((x) => cleanText(x?.task_type || x?.taskType).toUpperCase() === "CRON_RUN")
          .filter((x) => cleanText(x?.details?.jobId) === jobId)
          .slice(0, 80);
        historyCopyText = filtered
          .map((x) => {
            const iso = x?.created_at || x?.createdAt || "";
            const who = cleanText(x?.triggered_by || x?.triggeredBy) || "system";
            const summary = cleanText(x?.summary) || "";
            const rec = x?.details?.recipients || {};
            const toLine = rec?.to ? `To: ${(Array.isArray(rec.to) ? rec.to : [rec.to]).join(", ")}` : "";
            return `${formatShortTime(iso)} · ${who} · ${summary} · ${toLine}`;
          })
          .join("\n");
        historyMount.replaceChildren(
          renderCronHistoryTable(filtered, { emptyMessage: "No runs recorded yet for this job." })
        );
      } catch (e) {
        historyMount.replaceChildren(
          el("div", { class: "text-sm text-red-700 py-4", text: `Failed to load history: ${cleanText(e?.message) || String(e)}` })
        );
      }
    };

    const openCronLogModal = async (job) => {
      const j = job && typeof job === "object" ? job : {};
      const jobId = cleanText(j?.id);
      const backdrop = el("div", {
        class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto",
        "data-actracker-modal-backdrop": "1",
      });
      const modal = el("div", {
        class:
          "w-full max-w-6xl max-h-[90vh] my-auto flex flex-col rounded-xl bg-white shadow-lg border border-gray-200",
      });
      const headerNode = el("div", { class: "shrink-0 flex items-start justify-between gap-4 p-4 border-b border-gray-200" }, [
        el("div", {}, [
          el("div", { class: "text-lg font-semibold text-gray-900", text: `Cron output log: ${cleanText(j?.name) || jobId || "Job"}` }),
          el("div", {
            class: "mt-1 text-sm text-gray-600",
            text: "Server log in a table — newest entries first, grouped by date. Scroll inside the panel.",
          }),
        ]),
        el("button", { type: "button", class: "text-gray-500 hover:text-gray-800", text: "✕", onclick: () => backdrop.remove() }),
      ]);

      const logScroll = el("div", {
        id: "v2-cron-log-scroll",
        class: "flex-1 min-h-0 overflow-y-auto rounded-lg border border-gray-200 bg-white",
      });
      const logMount = el("div", { id: "v2-cron-log-body", class: "text-sm text-gray-600 py-6 text-center", text: "Loading…" });
      logScroll.appendChild(logMount);
      let logCopyText = "";

      const scrollLogToTop = () => {
        requestAnimationFrame(() => {
          logScroll.scrollTop = 0;
        });
      };

      const btnClass =
        "inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50";

      const loadCronLogTail = async () => {
        logMount.replaceChildren(el("div", { class: "text-sm text-gray-600 py-6 text-center", text: "Loading…" }));
        try {
          const resp = await fetchJson(apiUrl(`/api/admin/cron/log?jobId=${encodeURIComponent(jobId)}&lines=600`));
          const raw = resp?.tail || "";
          logCopyText = raw;
          const entries = parseCronFileLogTail(raw);
          logMount.replaceChildren(renderCronLogTable(entries, { emptyMessage: "Log is empty." }));
          scrollLogToTop();
        } catch (e) {
          logMount.replaceChildren(
            el("div", { class: "text-sm text-red-700 py-4 px-3", text: `Failed to load cron log: ${cleanText(e?.message) || String(e)}` })
          );
        }
      };

      const refreshLogBtn = el("button", { type: "button", class: btnClass, text: "Refresh", onclick: () => loadCronLogTail() });
      const copyLogBtn = el("button", {
        type: "button",
        class: btnClass,
        text: "Copy raw log",
        onclick: async () => {
          try {
            await navigator.clipboard.writeText(logCopyText || "");
            copyLogBtn.textContent = "Copied";
            setTimeout(() => {
              copyLogBtn.textContent = "Copy raw log";
            }, 1600);
          } catch {
            window.alert("Copy failed.");
          }
        },
      });
      const jumpTopBtn = el("button", {
        type: "button",
        class: btnClass,
        text: "Back to top",
        onclick: () => scrollLogToTop(),
      });

      const logToolbar = el("div", { class: "shrink-0 flex flex-wrap items-center gap-2 p-4 border-b border-gray-100" }, [
        refreshLogBtn,
        copyLogBtn,
        jumpTopBtn,
        el("span", { class: "text-xs text-gray-500", text: "Sorted: newest first" }),
      ]);
      const bodyNode = el("div", { class: "flex-1 min-h-0 flex flex-col p-4 pt-0" }, [logToolbar, logScroll]);
      modal.append(headerNode, bodyNode);
      backdrop.appendChild(modal);
      backdrop.addEventListener("click", (ev) => {
        if (ev.target === backdrop) backdrop.remove();
      });
      document.body.appendChild(backdrop);

      await loadCronLogTail();
    };

    const splitCronRecipientLines = (raw) =>
      String(raw || "")
        .split(/\n|;/)
        .map((x) => x.trim())
        .filter(Boolean);

    const openEditJob = (job) => {
      const j = job && typeof job === "object" ? job : {};
      const jobIdEdit = cleanText(j?.id);
      const isWeekPlanner = jobIdEdit === "week-planner-thu-5am-et";
      const backdrop = el("div", {
        class: "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto",
        "data-actracker-modal-backdrop": "1",
      });
      const modal = el("div", {
        class:
          "w-full max-w-3xl max-h-[90vh] my-auto flex flex-col rounded-xl bg-white shadow-lg border border-gray-200",
      });
      const title = cleanText(j?.name) || cleanText(j?.id) || "Job";
      const headerNode = el("div", { class: "shrink-0 flex items-start justify-between gap-4 p-4 border-b border-gray-200" }, [
        el("div", {}, [
          el("div", { class: "text-lg font-semibold text-gray-900", text: `Edit: ${title}` }),
          el("div", { class: "mt-1 text-sm text-gray-600", text: cleanText(j?.kind) || "cron" }),
        ]),
        el("button", { type: "button", class: "text-gray-500 hover:text-gray-800", text: "✕", onclick: () => backdrop.remove() }),
      ]);

      const enabled = el("input", { id: "cron-edit-enabled", type: "checkbox", checked: j?.enabled !== false });
      const field = (id, label, value, placeholder = "") =>
        el("div", {}, [
          el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: id, text: label }),
          el("input", {
            id,
            value: cleanText(value),
            placeholder,
            class:
              "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
          }),
        ]);
      const textarea = (id, label, value, placeholder = "", rows = 2) =>
        el("div", {}, [
          el("label", { class: "block text-xs font-medium text-gray-600 mb-1", for: id, text: label }),
          el("textarea", {
            id,
            rows,
            class:
              "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 focus:ring-4 focus:ring-dae/20 focus:border-dae",
            placeholder,
            text: cleanText(value),
          }),
        ]);

      const bodyNode = el("div", { class: "p-4 grid grid-cols-1 md:grid-cols-2 gap-4" }, [
        el("div", { class: "md:col-span-2 flex items-center gap-3" }, [
          enabled,
          el("label", { for: "cron-edit-enabled", class: "text-sm font-medium text-gray-800", text: "Active (green) / Inactive (red)" }),
        ]),
        field("cron-edit-id", "ID", j?.id),
        field("cron-edit-name", "Name", j?.name),
        field("cron-edit-kind", "Type (cron/event)", j?.kind, "cron"),
        field("cron-edit-timezone", "Timezone", j?.timezone, "America/New_York / Asia/Dubai / Immediate"),
        field("cron-edit-schedule", "When / How often", j?.schedule, "e.g. Mon–Fri 6 AM ET"),
        textarea("cron-edit-criteria", "Criteria / trigger", j?.criteria, "e.g. is_me_fleet=true"),
        textarea("cron-edit-command", "Source / command", j?.command, "e.g. run-cron.sh"),
        isWeekPlanner
          ? el("div", { class: "md:col-span-2" }, [
              textarea(
                "cron-edit-to",
                "To — one person per line",
                Array.isArray(j?.to) ? j.to.join("\n") : j?.to || "",
                '"First Last" <name@dubaiaerospace.com>',
                8
              ),
              el("div", {
                class: "mt-2 text-xs text-gray-600 leading-snug space-y-1",
              }, [
                el("p", {
                  text: 'Each line is one email. Use quotes for the full name, then the address in angle brackets — e.g. "Claus Schmetkamp" <Claus.Schmetkamp@dubaiaerospace.com>',
                }),
                el("p", {
                  text: 'The name in quotes is used in the message ("Hello Claus"). To remove someone (e.g. Massao), delete their entire line and click Save.',
                }),
              ]),
            ])
          : textarea("cron-edit-to", "To (comma-separated)", Array.isArray(j?.to) ? j.to.join(", ") : j?.to || "", "a@b.com, c@d.com"),
        isWeekPlanner
          ? field(
              "cron-edit-planner-url",
              "SharePoint planner URL",
              j?.plannerUrl,
              "https://..."
            )
          : null,
        textarea("cron-edit-cc", "CC (comma-separated)", Array.isArray(j?.cc) ? j.cc.join(", ") : j?.cc || "", ""),
        textarea("cron-edit-bcc", "BCC (comma-separated)", Array.isArray(j?.bcc) ? j.bcc.join(", ") : j?.bcc || "", ""),
      ]);

      const saveBtn = el("button", {
        type: "button",
        class: "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
        text: "Save",
      });
      const cancelBtn = el("button", {
        type: "button",
        class: "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Cancel",
        onclick: () => backdrop.remove(),
      });
      const footerNode = el("div", {
        class: "shrink-0 flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-white rounded-b-xl",
      }, [cancelBtn, saveBtn]);

      const bodyScroll = el("div", { class: "flex-1 min-h-0 overflow-y-auto" }, [bodyNode]);

      saveBtn.onclick = async () => {
        saveBtn.disabled = true;
        saveBtn.textContent = "Saving…";
        try {
          const next = JSON.parse(JSON.stringify(configState || { jobs: [] }));
          const list = Array.isArray(next.jobs) ? next.jobs : [];
          const updated = {
            ...j,
            id: cleanText(document.getElementById("cron-edit-id")?.value) || cleanText(j?.id) || `job-${Date.now()}`,
            name: cleanText(document.getElementById("cron-edit-name")?.value) || cleanText(j?.name) || "Job",
            kind: cleanText(document.getElementById("cron-edit-kind")?.value) || "cron",
            enabled: Boolean(document.getElementById("cron-edit-enabled")?.checked),
            timezone: cleanText(document.getElementById("cron-edit-timezone")?.value),
            schedule: cleanText(document.getElementById("cron-edit-schedule")?.value),
            criteria: cleanText(document.getElementById("cron-edit-criteria")?.value),
            command: cleanText(document.getElementById("cron-edit-command")?.value),
            to: isWeekPlanner
              ? splitCronRecipientLines(document.getElementById("cron-edit-to")?.value)
              : cleanText(document.getElementById("cron-edit-to")?.value)
                  .split(",")
                  .map((x) => x.trim())
                  .filter(Boolean),
            ...(isWeekPlanner
              ? { plannerUrl: cleanText(document.getElementById("cron-edit-planner-url")?.value) }
              : {}),
            cc: cleanText(document.getElementById("cron-edit-cc")?.value)
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            bcc: cleanText(document.getElementById("cron-edit-bcc")?.value)
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          };
          const idx = list.findIndex((x) => cleanText(x?.id) === cleanText(j?.id));
          if (idx >= 0) list[idx] = updated;
          else list.push(updated);
          next.jobs = list;
          await saveConfig(next);
          backdrop.remove();
        } catch (e) {
          window.alert(cleanText(e?.message) || String(e));
        } finally {
          saveBtn.disabled = false;
          saveBtn.textContent = "Save";
        }
      };

      modal.append(headerNode, bodyScroll, footerNode);
      backdrop.appendChild(modal);
      backdrop.addEventListener("click", (ev) => {
        if (ev.target === backdrop) backdrop.remove();
      });
      document.body.appendChild(backdrop);
    };

    const addBtn = el("button", {
      type: "button",
      class:
        "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
      text: "Add job/email",
      onclick: () => openEditJob({ id: `job-${Date.now()}`, enabled: false, kind: "cron", to: [], cc: [], bcc: [] }),
    });

    const visibleJobs = jobs.filter((j) => cleanText(j?.id) !== "avherald-dae-match-notify-12h");
    const cards = visibleJobs.map((j) => {
      const to = Array.isArray(j?.to) ? j.to : cleanText(j?.to) ? [cleanText(j.to)] : [];
      const cc = Array.isArray(j?.cc) ? j.cc : cleanText(j?.cc) ? [cleanText(j.cc)] : [];
      const bcc = Array.isArray(j?.bcc) ? j.bcc : cleanText(j?.bcc) ? [cleanText(j.bcc)] : [];
      const jobIdClean = cleanText(j?.id);
      const scheduledOnOsCron = SYSTEM_CROND_JOB_IDS.has(jobIdClean);
      const nextRunMs =
        scheduledOnOsCron || j?.enabled !== false ? nextRunUtcMsForJob(j?.id) : null;
      const jobTruth =
        cronStatus && typeof cronStatus === "object" && cronStatus.jobs && jobIdClean
          ? cronStatus.jobs[jobIdClean]
          : null;

      let dotColor = "bg-green-500";
      let dotTitle = "Active (enabled in JSON)";
      let badgeColor = "bg-green-100 text-green-800";
      let badgeText = "ACTIVE";

      if (scheduledOnOsCron && j?.enabled === false) {
        dotColor = "bg-amber-500";
        dotTitle = "Inactive in UI only; Linux cron may still run";
        badgeColor = "bg-amber-100 text-amber-900";
        badgeText = "Inactive (panel) · OS may still fire";
      } else if (j?.enabled === false) {
        dotColor = "bg-red-600";
        dotTitle = "Inactive";
        badgeColor = "bg-gray-100 text-gray-700";
        badgeText = "INACTIVE";
      } else if (scheduledOnOsCron && jobTruth) {
        if (jobTruth.health === "error") {
          dotColor = "bg-orange-500";
          dotTitle = cleanText(jobTruth.reason) || "No matching /etc/cron.d entry detected for this job.";
          badgeColor = "bg-orange-100 text-orange-900";
          badgeText = "JSON ON · OS MISSING";
        } else if (jobTruth.health === "warning") {
          dotColor = "bg-amber-400";
          dotTitle = cleanText(jobTruth.reason) || "Check log file.";
          badgeColor = "bg-amber-100 text-amber-900";
          badgeText = jobTruth.logStale ? "STALE LOG?" : "CHECK LOG";
        }
      }

      const runBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "Run now (dry)",
        onclick: () =>
          runJob({ jobId: cleanText(j?.id), mode: "dry" }).catch((e) =>
            window.alert(cleanText(e?.message) || String(e))
          ),
      });
      const testBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text:
          jobIdClean === "spirit-4h-et"
            ? "RUN TEST (Simon only)"
            : jobIdClean === "spirit-tracker-activity-45m"
            ? "RUN TEST (Claus only)"
            : jobIdClean === "week-planner-thu-5am-et"
            ? "RUN TEST (Claus only)"
            : "RUN TEST (to Claus)",
        onclick: () =>
          runJob({ jobId: cleanText(j?.id), mode: "test" }).catch((e) =>
            window.alert(cleanText(e?.message) || String(e))
          ),
      });
      const sendBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
        text: "Send now",
        onclick: () =>
          runJob({ jobId: cleanText(j?.id), mode: "send" }).catch((e) =>
            window.alert(cleanText(e?.message) || String(e))
          ),
      });
      const historyBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "View history",
        onclick: () => openHistoryModal(j),
      });
      const logBtn = el("button", {
        type: "button",
        class:
          "inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50",
        text: "View log",
        onclick: () => openCronLogModal(j),
      });
      const nextRunNode =
        cleanText(j?.kind) === "event"
          ? el("div", { class: "mt-2 text-xs text-gray-600", text: "Next run: Event-driven (immediate)" })
          : nextRunMs
          ? el("div", { class: "mt-2 text-xs text-gray-600" }, [
              el("span", { class: "font-medium text-gray-700", text: "Next run in: " }),
              el("span", {
                class: "font-mono",
                id: `v2-cron-next-${cleanText(j?.id)}`,
                "data-next-run-ms": String(nextRunMs),
                text: `${formatCountdown(nextRunMs - Date.now())} (${formatUtcTooltip(nextRunMs)})`,
              }),
            ])
          : el("div", { class: "mt-2 text-xs text-gray-600", text: "Next run: —" });
      return el("div", { class: "rounded-xl border border-gray-200 bg-white p-4" }, [
        el("div", { class: "flex items-start justify-between gap-3" }, [
          el("div", { class: "flex items-start gap-3" }, [
            el("span", {
              class: "mt-1 inline-block w-2.5 h-2.5 rounded-full " + dotColor,
              title: dotTitle,
            }),
            el("div", {}, [
              el("div", { class: "text-sm font-semibold text-gray-900", text: cleanText(j?.name) || cleanText(j?.id) || "Cron" }),
              cleanText(j?.description)
                ? el("div", { class: "mt-1 text-xs text-gray-600 leading-snug", text: cleanText(j?.description) })
                : null,
            ]),
          ]),
          el("div", { class: "flex items-center gap-2" }, [
            el("span", {
              class:
                "inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold max-w-[16rem] text-center leading-snug " +
                badgeColor,
              text: badgeText,
            }),
            runBtn,
            testBtn,
            sendBtn,
            historyBtn,
            logBtn,
            iconButton({ label: "✎", title: "Edit", onClick: () => openEditJob(j) }),
          ]),
        ]),
        el("div", { class: "mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" }, [
          el("div", {}, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "Schedule" }),
            el("div", { class: "mt-1 font-mono text-gray-900", text: cleanText(j?.schedule) || "—" }),
          ]),
          el("div", {}, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "Timezone" }),
            el("div", { class: "mt-1 font-mono text-gray-900 break-words", text: cleanText(j?.timezone) || "—" }),
          ]),
          el("div", { class: "md:col-span-2" }, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "Criteria / trigger" }),
            el("div", { class: "mt-1 text-gray-900 break-words", text: cleanText(j?.criteria) || "—" }),
          ]),
          el("div", { class: "md:col-span-2" }, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "Source / command" }),
            el("div", { class: "mt-1 font-mono text-gray-900 break-words", text: cleanText(j?.command) || "—" }),
          ]),
          scheduledOnOsCron
            ? el("div", { class: "md:col-span-2" }, [
                el("div", { class: "text-xs font-medium text-gray-600", text: "Server check (/etc/cron.d + log)" }),
                el("div", {
                  class: "mt-1 text-xs text-gray-800 break-words",
                  text: jobTruth
                    ? `${jobTruth.osCronInstalled ? "Cron.d: found" : "Cron.d: not found"} · ${
                        jobTruth.logExists ? `Log: ${cleanText(jobTruth.logPath || "")} (${cleanText(jobTruth.logLastModified || "")})` : "Log: none yet"
                      }`
                    : "Run refresh — could not load job status from /api/cron/status.",
                }),
              ])
            : null,
          el("div", {}, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "To" }),
            el("div", { class: "mt-1 font-mono text-gray-900 break-words", text: to.length ? to.join(", ") : "—" }),
          ]),
          el("div", {}, [
            el("div", { class: "text-xs font-medium text-gray-600", text: "CC / BCC" }),
            el("div", {
              class: "mt-1 font-mono text-gray-900 break-words",
              text:
                (cc.length ? `CC: ${cc.join(", ")}` : "") +
                (cc.length && bcc.length ? " · " : "") +
                (bcc.length ? `BCC: ${bcc.join(", ")}` : "") || "—",
            }),
          ]),
        ]),
        nextRunNode,
      ]);
    });

    container.replaceChildren(header, el("div", { class: "flex items-center justify-end" }, [addBtn]), ...cards);
    // Update countdown text periodically (no reload).
    try {
      if (state.__cronNextRunInterval) clearInterval(state.__cronNextRunInterval);
      state.__cronNextRunInterval = setInterval(() => {
        const nodes = container.querySelectorAll("[data-next-run-ms]");
        nodes.forEach((n) => {
          const target = Number(n.getAttribute("data-next-run-ms"));
          if (!Number.isFinite(target)) return;
          const diff = target - Date.now();
          n.textContent = `${formatCountdown(diff)} (${formatUtcTooltip(target)})`;
        });
      }, 30 * 1000);
    } catch {
      // ignore
    }
  } catch (e) {
    container.replaceChildren(
      el("div", { class: "rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900" }, [
        `Failed to load cron config: ${cleanText(e?.message) || String(e)}`,
      ])
    );
  }
}

async function initDbExplorerPanels() {
  const root = document.getElementById("v2-db-explorer-root");
  if (!root || !isAdminSession()) return;

  const tableList = document.getElementById("v2-db-explorer-table-list");
  const tableFilter = document.getElementById("v2-db-explorer-table-q");
  const tbody = document.getElementById("v2-db-explorer-tbody");
  const theadRow = document.getElementById("v2-db-explorer-thead-row");
  const meta = document.getElementById("v2-db-explorer-meta");
  const toolbar = document.getElementById("v2-db-explorer-toolbar");
  if (!tableList || !tbody || !theadRow || !meta || !toolbar || !tableFilter) return;

  const cellText = (val) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "bigint") return val.toString();
    if (val instanceof Date) return val.toISOString();
    if (typeof val === "object") {
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const readDex = () => {
    const p = currentRouteParams();
    const lim = Number(getParamText(p, "dex_lim"));
    const off = Number(getParamText(p, "dex_off"));
    return {
      table: getParamText(p, "dex_table"),
      sort: getParamText(p, "dex_sort"),
      dir: getParamText(p, "dex_dir") === "desc" ? "desc" : "asc",
      limit: Number.isFinite(lim) && lim > 0 ? Math.min(500, lim) : 100,
      offset: Number.isFinite(off) && off >= 0 ? off : 0,
    };
  };

  const writeDex = (patch) => {
    const cur = readDex();
    const next = { ...cur, ...patch };
    const p = currentRouteParams();
    p.set("tab", "db-explorer");
    if (next.table) p.set("dex_table", next.table);
    else p.delete("dex_table");
    if (next.sort) p.set("dex_sort", next.sort);
    else p.delete("dex_sort");
    p.set("dex_dir", next.dir === "desc" ? "desc" : "asc");
    p.set("dex_lim", String(next.limit));
    p.set("dex_off", String(next.offset));
    setHashParams(p);
  };

  let tablesCache = initDbExplorerPanels.__tablesCache || null;
  let filterText = tableFilter.value || "";

  const rowFilterText = () => String(initDbExplorerPanels.__rowQ || "").trim();

  const renderTableButtons = () => {
    const ft = (filterText || "").toLowerCase();
    const list = (tablesCache?.tables || []).filter((t) => !ft || String(t.name || "").toLowerCase().includes(ft));
    const dex = readDex();
    if (!list.length) {
      tableList.replaceChildren(
        el("div", { class: "text-gray-500 text-xs p-2" }, [
          tablesCache ? "No tables match the filter." : "Loading tables…",
        ])
      );
      return;
    }
    tableList.replaceChildren(
      ...list.map((t) => {
        const name = cleanText(t.name);
        const est = t.rowEstimate != null ? `~${t.rowEstimate}` : "—";
        const active = dex.table === name;
        return el(
          "button",
          {
            type: "button",
            class:
              "w-full text-left px-2 py-1.5 rounded border text-xs font-mono " +
              (active ? "bg-red-50 border-red-200 text-gray-900" : "bg-white border-gray-200 hover:bg-gray-50"),
            text: `${name} (${est})`,
            title: name,
            onclick: () => {
              initDbExplorerPanels.__rowQ = "";
              writeDex({ table: name, offset: 0 });
            },
          },
          []
        );
      })
    );
  };

  const loadTables = async () => {
    try {
      const resp = await fetchJson(apiUrl("/api/admin/db/explorer/tables"));
      if (!resp?.ok) throw new Error(resp?.error || "Failed to list tables");
      tablesCache = { tables: Array.isArray(resp.tables) ? resp.tables : [] };
      initDbExplorerPanels.__tablesCache = tablesCache;
      const dex = readDex();
      if (!dex.table && tablesCache.tables.length) {
        writeDex({ ...dex, table: tablesCache.tables[0].name });
        return;
      }
      renderTableButtons();
    } catch (e) {
      tablesCache = { tables: [], error: e?.message };
      tableList.replaceChildren(
        el("div", { class: "text-red-700 text-xs p-2" }, [cleanText(e?.message) || String(e)])
      );
    }
  };

  const loadRows = async () => {
    const dex = readDex();
    if (!dex.table) {
      meta.textContent = "Pick a table.";
      tbody.replaceChildren(
        el("tr", {}, [el("td", { class: "px-3 py-4 text-gray-500", colspan: "99", text: "—" })])
      );
      theadRow.replaceChildren(el("th", { class: "px-2 py-2", text: "—" }));
      toolbar.replaceChildren();
      return;
    }
    meta.textContent = "Loading…";
    toolbar.replaceChildren();
    try {
      const q = rowFilterText();
      const qs = new URLSearchParams({
        limit: String(dex.limit),
        offset: String(dex.offset),
        q,
        sort: dex.sort,
        dir: dex.dir,
      });
      const resp = await fetchJson(
        apiUrl(`/api/admin/db/explorer/table/${encodeURIComponent(dex.table)}?${qs.toString()}`)
      );
      if (!resp?.ok) throw new Error(resp?.error || "Failed to load rows");

      const cols = Array.isArray(resp.columns) ? resp.columns : [];
      const colCount = Math.max(cols.length, 1);
      theadRow.replaceChildren(
        ...cols.map((c) =>
          el("th", {
            class: "px-2 py-2 whitespace-nowrap max-w-[12rem] truncate",
            title: `${cleanText(c.name)} (${cleanText(c.dataType)})`,
            text: cleanText(c.name) || "—",
          })
        )
      );

      const rows = Array.isArray(resp.rows) ? resp.rows : [];
      if (!rows.length) {
        tbody.replaceChildren(
          el("tr", {}, [
            el("td", {
              class: "px-3 py-4 text-gray-500",
              colspan: String(colCount),
              text: "No rows (or no matches for this filter).",
            }),
          ])
        );
      } else {
        tbody.replaceChildren(
          ...rows.map((row) =>
            el(
              "tr",
              { class: "bg-white border-b border-gray-100 hover:bg-gray-50" },
              cols.map((c) =>
                el("td", {
                  class: "px-2 py-1.5 align-top max-w-[14rem] whitespace-pre-wrap break-all font-mono text-[11px]",
                  text: cellText(row[c.name]),
                })
              )
            )
          )
        );
      }

      const total = Number(resp.total) || 0;
      const from = total ? dex.offset + 1 : 0;
      const to = Math.min(dex.offset + rows.length, total);
      meta.textContent = `${dex.table} · rows ${from}-${to} of ${total} · page size ${dex.limit}`;

      const sortSelect = el(
        "select",
        {
          id: "v2-db-explorer-sort",
          class: "rounded-lg border border-gray-200 px-2 py-1.5 text-xs",
          "aria-label": "Sort column",
          onchange: (ev) => {
            const v = cleanText(ev.target?.value);
            writeDex({ sort: v, offset: 0 });
          },
        },
        cols.map((c) => {
          const opt = el("option", { value: cleanText(c.name), text: cleanText(c.name) });
          if (cleanText(c.name) === cleanText(resp.sort)) opt.selected = true;
          return opt;
        })
      );

      const dirBtn = el("button", {
        type: "button",
        class: "px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50",
        text: dex.dir === "desc" ? "Descending ↓" : "Ascending ↑",
        onclick: () => writeDex({ dir: dex.dir === "desc" ? "asc" : "desc", offset: 0 }),
      });

      const limSelect = el(
        "select",
        {
          id: "v2-db-explorer-limit",
          class: "rounded-lg border border-gray-200 px-2 py-1.5 text-xs",
          onchange: (ev) => {
            const n = Number(ev.target?.value);
            writeDex({ limit: Number.isFinite(n) ? n : 100, offset: 0 });
          },
        },
        [50, 100, 200, 500].map((n) => {
          const opt = el("option", { value: String(n), text: `${n} / page` });
          if (n === dex.limit) opt.selected = true;
          return opt;
        })
      );

      const searchInput = el("input", {
        type: "search",
        class: "min-w-[8rem] flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-xs",
        placeholder: "Filter rows (any column)…",
        value: rowFilterText(),
        id: "v2-db-explorer-row-q",
      });
      let searchTimer = null;
      searchInput.addEventListener("input", () => {
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
          initDbExplorerPanels.__rowQ = cleanText(searchInput.value);
          loadRows().catch(() => {});
        }, 400);
      });

      const prevBtn = el("button", {
        type: "button",
        class: "px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50",
        text: "Prev",
        disabled: dex.offset <= 0,
        onclick: () => writeDex({ offset: Math.max(0, dex.offset - dex.limit) }),
      });
      const nextBtn = el("button", {
        type: "button",
        class: "px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50",
        text: "Next",
        disabled: dex.offset + rows.length >= total,
        onclick: () => writeDex({ offset: dex.offset + dex.limit }),
      });

      const refreshBtn = el("button", {
        type: "button",
        class: "px-2 py-1.5 text-xs rounded-lg border border-gray-200 bg-white hover:bg-gray-50",
        text: "Refresh",
        onclick: () => loadRows().catch(() => {}),
      });

      toolbar.replaceChildren(
        el("div", { class: "flex flex-wrap items-center gap-2 w-full" }, [
          el("label", { class: "flex flex-col gap-0.5 text-[10px] font-medium text-gray-600" }, [
            el("span", { text: "Sort" }),
            sortSelect,
          ]),
          dirBtn,
          limSelect,
          prevBtn,
          nextBtn,
          refreshBtn,
          el("label", { class: "flex flex-col gap-0.5 flex-1 min-w-[10rem] text-[10px] font-medium text-gray-600" }, [
            el("span", { text: "Row filter" }),
            searchInput,
          ]),
        ])
      );
    } catch (e) {
      meta.textContent = cleanText(e?.message) || String(e);
      tbody.replaceChildren(
        el("tr", {}, [
          el("td", { class: "px-3 py-4 text-red-700", colspan: "99", text: cleanText(e?.message) || String(e) }),
        ])
      );
    }
  };

  if (!root.dataset.boundDex) {
    root.dataset.boundDex = "1";
    tableFilter.addEventListener("input", () => {
      filterText = tableFilter.value || "";
      renderTableButtons();
    });
  } else {
    filterText = tableFilter.value || "";
  }

  if (!tablesCache) {
    await loadTables();
  } else {
    renderTableButtons();
  }

  const dex = readDex();
  if (dex.table) {
    await loadRows();
  } else if (tablesCache?.tables?.length) {
    writeDex({ table: tablesCache.tables[0].name, offset: 0 });
  }
}

async function initBackupPanels() {
  const root = document.getElementById("v2-db-backup-root");
  if (!root) return;

  const render = async () => {
    const summary = document.getElementById("v2-db-backup-summary");
    const quickMount = document.getElementById("v2-db-backup-quick");
    const tbody = document.getElementById("v2-db-backup-table");
    const refreshBtn = document.getElementById("v2-db-backup-refresh");
    if (!summary || !tbody || !quickMount) return;

    if (refreshBtn) {
      refreshBtn.disabled = true;
      refreshBtn.textContent = "Loading…";
    }

    try {
      try {
        if (state.__backupNextInterval) clearInterval(state.__backupNextInterval);
      } catch {
        // ignore
      }
      state.__backupNextInterval = null;

      const resp = await fetchJson(apiUrl("/api/admin/db-backup/overview"));
      if (!resp?.ok) throw new Error(resp?.error || "Failed to load backups");

      const dirs = Array.isArray(resp?.scannedDirectories) ? resp.scannedDirectories : [];
      const backupsAll = Array.isArray(resp?.backupsAll) ? resp.backupsAll : [];
      const backupsRecent = Array.isArray(resp?.backupsRecent) ? resp.backupsRecent : backupsAll.slice(0, 3);
      const sched = resp?.scheduledCron || {};
      const nextIso = sched?.nextRunUtc || null;
      const nextMs = nextIso ? Date.parse(nextIso) : NaN;

      summary.replaceChildren(
        el("div", { class: "space-y-2" }, [
          el("div", { class: "font-semibold text-gray-900", text: "Backup schedule & storage" }),
          el("div", { class: "text-xs text-gray-600" }, [
            el("span", { class: "font-medium text-gray-700", text: "Manual / UI backups: " }),
            el("span", { class: "font-mono text-gray-800", text: cleanText(resp?.manualDirectory) || "—" }),
          ]),
          el("div", { class: "text-xs text-gray-600" }, [
            el("span", { class: "font-medium text-gray-700", text: "Postgres nightly (cron script): " }),
            el("span", { class: "font-mono text-gray-800", text: cleanText(resp?.postgresCronDirectory) || "—" }),
          ]),
          el("div", { class: "text-xs text-gray-600 mt-2" }, [
            el("span", { class: "font-medium text-gray-700", text: "Next nightly run: " }),
            el("span", {
              id: "v2-db-backup-next-run",
              class: "font-mono text-gray-900",
              "data-next-backup-ms": Number.isFinite(nextMs) ? String(nextMs) : "",
              text: Number.isFinite(nextMs)
                ? `${formatCountdownHm(nextMs - Date.now())} (${formatUtcTooltip(nextMs)})`
                : "—",
            }),
          ]),
          el("div", { class: "text-xs text-gray-500 mt-1", text: cleanText(sched?.description) || "" }),
          dirs.length
            ? el("div", { class: "text-xs text-gray-500 mt-2" }, [
                "Scanned: ",
                el("span", { class: "font-mono", text: dirs.map((d) => cleanText(d?.path)).filter(Boolean).join(" · ") }),
              ])
            : el("div", { class: "text-xs text-amber-800 mt-2", text: "No backup directories visible to the API yet (check volume mount)." }),
        ])
      );

      if (Number.isFinite(nextMs)) {
        state.__backupNextInterval = setInterval(() => {
          const n = document.getElementById("v2-db-backup-next-run");
          const raw = Number(n?.getAttribute("data-next-backup-ms"));
          if (!n || !Number.isFinite(raw)) return;
          n.textContent = `${formatCountdownHm(raw - Date.now())} (${formatUtcTooltip(raw)})`;
        }, 60 * 1000);
      }

      const quick = el("div", { class: "mb-3 rounded-lg border border-gray-100 bg-gray-50 p-3" }, [
        el("div", { class: "text-xs font-semibold text-gray-800 mb-2", text: "Quick access · last 3 backups" }),
      ]);
      if (!backupsRecent.length) {
        quick.append(el("div", { class: "text-xs text-gray-600", text: "None found yet." }));
      } else {
        quick.append(
          el(
            "div",
            { class: "flex flex-wrap gap-2" },
            backupsRecent.map((b) => {
              const name = cleanText(b?.fileName);
              const href = name ? apiUrl(`/api/admin/db-backup/download/${encodeURIComponent(name)}`) : "";
              return el(
                "a",
                {
                  href: href || "#",
                  class:
                    "inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-dae rounded-lg hover:bg-dae/90 focus:ring-4 focus:ring-dae/30",
                  download: name,
                  title: `Download ${name}`,
                  text: name || "download",
                  onclick: (e) => {
                    if (!href) e.preventDefault();
                  },
                },
                []
              );
            })
          )
        );
      }
      quickMount.replaceChildren(quick);

      const rows = backupsAll.slice(0, 50);
      if (!rows.length) {
        tbody.replaceChildren(
          el("tr", {}, [el("td", { class: "px-4 py-3", colspan: "5", text: "No backup files found." })])
        );
      } else {
        tbody.replaceChildren(
          ...rows.map((b) => {
            const name = cleanText(b?.fileName);
            const href = name ? apiUrl(`/api/admin/db-backup/download/${encodeURIComponent(name)}`) : "";
            return el("tr", { class: "bg-white border-b hover:bg-gray-50" }, [
              el("td", { class: "px-4 py-3 font-mono text-gray-900 break-all", text: name || "—" }),
              el("td", { class: "px-4 py-3 text-gray-700", text: cleanText(b?.directoryLabel) || "—" }),
              el("td", { class: "px-4 py-3 font-mono text-gray-700", text: formatUtcTooltip(Date.parse(b?.modifiedAt)) || "—" }),
              el("td", { class: "px-4 py-3 font-mono text-gray-700", text: formatBytesBrief(Number(b?.bytes)) }),
              el("td", { class: "px-4 py-3" }, [
                el(
                  "a",
                  {
                    href: href || "#",
                    class: "inline-flex px-3 py-1.5 text-xs font-medium rounded-md border border-gray-200 hover:bg-gray-50",
                    text: "Download",
                    download: name,
                    onclick: (e) => {
                      if (!href) e.preventDefault();
                    },
                  },
                  []
                ),
              ]),
            ]);
          })
        );
      }
    } catch (e) {
      summary.replaceChildren(
        el("div", { class: "text-sm text-red-800" }, [`Error: ${cleanText(e?.message) || String(e)}`])
      );
      quickMount.replaceChildren();
      tbody.replaceChildren(
        el("tr", {}, [el("td", { class: "px-4 py-3", colspan: "5", text: "—" })])
      );
    } finally {
      if (refreshBtn) {
        refreshBtn.disabled = false;
        refreshBtn.textContent = "Refresh";
      }
    }
  };

  await render();
  const refreshBtn = document.getElementById("v2-db-backup-refresh");
  if (refreshBtn)
    refreshBtn.onclick = () => {
      render().catch((e) => window.alert(cleanText(e?.message) || String(e)));
    };
}

function formatDiskBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function diskUsageBar(used, total, colorClass = "bg-blue-500") {
  const t = Math.max(1, Number(total) || 1);
  const u = Math.max(0, Number(used) || 0);
  const pct = Math.min(100, Math.round((u / t) * 1000) / 10);
  return el("div", { class: "w-full bg-gray-100 rounded-full h-2.5 overflow-hidden", title: `${pct}%` }, [
    el("div", { class: `${colorClass} h-2.5 rounded-full`, style: `width:${Math.min(100, pct)}%` }),
  ]);
}

async function initDiskUsagePanels() {
  const root = document.getElementById("v2-disk-usage-root");
  if (!root) return;
  const summaryEl = document.getElementById("v2-disk-usage-summary");
  const catsEl = document.getElementById("v2-disk-usage-categories");
  const treeEl = document.getElementById("v2-disk-usage-tree");
  const refreshBtn = document.getElementById("v2-disk-usage-refresh");

  const purge = async (saId, { photos, files }, label) => {
    const parts = [photos ? "photos" : null, files ? "records files" : null].filter(Boolean).join(" + ");
    if (!confirm(`Delete all ${parts} for ${label}?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetchJson(apiUrl(`/api/admin/disk-usage/aircraft/${saId}`), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photos: Boolean(photos), files: Boolean(files) }),
      });
      if (!res?.ok) throw new Error(res?.error || "Delete failed");
      alert(`Freed ~${formatDiskBytes(res.freedBytes)} (removed ${res.deletedPhotos || 0} photo row(s), ${res.deletedFiles || 0} file row(s)).`);
      await render(true);
    } catch (e) {
      alert(cleanText(e?.message) || String(e));
    }
  };

  const render = async (force = false) => {
    if (refreshBtn) {
      refreshBtn.disabled = true;
      refreshBtn.textContent = "Refreshing…";
    }
    try {
      const data = await fetchJson(apiUrl(`/api/admin/disk-usage${force ? "?refresh=1" : ""}`));
      if (!data?.ok) throw new Error(data?.error || "Failed to load disk usage");

      const pooled = data.pooled || {};
      const hosts = Array.isArray(data.hosts) ? data.hosts : [];
      const usedPct = Number(pooled.usedPct) || 0;
      const freePct = Number(pooled.freePct) || 0;
      const barColor = usedPct >= 90 ? "bg-red-500" : usedPct >= 80 ? "bg-amber-500" : "bg-blue-500";
      const scaleTotal = Number(data.primaryDisk?.totalBytes) || Number(pooled.totalBytes) || 1;

      summaryEl.replaceChildren(
        el("div", { class: "flex flex-wrap items-start justify-between gap-3" }, [
          el("div", {}, [
            el("div", { class: "font-semibold text-gray-900", text: "Combined disk space" }),
            el("div", {
              class: "text-xs text-gray-500 mt-0.5",
              text: `${hosts.length} server${hosts.length === 1 ? "" : "s"} · ${data.cached ? "cached" : "live"} · ${cleanText(data.generatedAt)}`,
            }),
          ]),
          el("div", { class: "text-right" }, [
            el("div", {
              class: "text-lg font-bold text-gray-900",
              text: `${formatDiskBytes(pooled.freeBytes)} free (${freePct}%)`,
            }),
            el("div", {
              class: "text-xs text-gray-500",
              text: `${formatDiskBytes(pooled.usedBytes)} used of ${formatDiskBytes(pooled.totalBytes)} total (${usedPct}%)`,
            }),
          ]),
        ]),
        el("div", { class: "mt-3" }, [diskUsageBar(pooled.usedBytes, pooled.totalBytes, barColor)]),
        el("div", { class: "mt-1 flex justify-between text-[10px] text-gray-400" }, [
          el("span", { text: "0%" }),
          el("span", { text: `${usedPct}% used across all servers` }),
          el("span", { text: "100%" }),
        ]),
        el("div", { class: "mt-3 grid grid-cols-1 md:grid-cols-2 gap-2" }, hosts.map((h) => {
          const hUsedPct = h.totalBytes ? Math.round((Number(h.usedBytes) / Number(h.totalBytes)) * 1000) / 10 : 0;
          const hFreePct = h.totalBytes ? Math.round((Number(h.freeBytes) / Number(h.totalBytes)) * 1000) / 10 : 0;
          const hColor = hUsedPct >= 90 ? "bg-red-500" : hUsedPct >= 80 ? "bg-amber-500" : "bg-blue-500";
          return el("div", { class: "rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs" }, [
            el("div", { class: "font-medium text-gray-800", text: h.label || h.id }),
            el("div", {
              class: "text-gray-600 mt-0.5",
              text: `${formatDiskBytes(h.freeBytes)} free (${hFreePct}%) · ${formatDiskBytes(h.usedBytes)} used · ${formatDiskBytes(h.totalBytes)} total`,
            }),
            el("div", { class: "mt-1.5" }, [diskUsageBar(h.usedBytes, h.totalBytes, hColor)]),
          ]);
        })),
      );

      const cats = Array.isArray(data.categories) ? data.categories : [];
      catsEl.replaceChildren(
        el("div", { class: "flex items-baseline justify-between gap-2 mb-1" }, [
          el("div", { class: "font-semibold text-gray-900", text: "On production disk" }),
          el("div", {
            class: "text-[11px] text-gray-500",
            text: `Bars = % of production disk (${formatDiskBytes(scaleTotal)})`,
          }),
        ]),
        el("div", { class: "space-y-3 mt-3" }, cats.map((c) => {
          const pct = Number(c.pctOfPrimaryDisk);
          const pctLabel = Number.isFinite(pct) ? pct : Math.round((Number(c.bytes) / scaleTotal) * 1000) / 10;
          return el("div", {}, [
            el("div", { class: "flex justify-between text-sm gap-2" }, [
              el("span", { class: "font-medium text-gray-800", text: c.label }),
              el("span", {
                class: "text-gray-600 tabular-nums shrink-0",
                text: `${formatDiskBytes(c.bytes)} · ${pctLabel}%`,
              }),
            ]),
            el("div", { class: "mt-1" }, [
              diskUsageBar(c.bytes, scaleTotal, c.key === "other" ? "bg-gray-400" : "bg-blue-500"),
            ]),
          ]);
        })),
      );

      const ops = Array.isArray(data.byOperator) ? data.byOperator : [];
      if (!ops.length) {
        treeEl.replaceChildren(el("div", { class: "text-sm text-gray-400 px-1 py-2", text: "No surveillance photos or files yet." }));
      } else {
        treeEl.replaceChildren(...ops.map((op) => {
          const body = el("div", { class: "hidden mt-2 space-y-1 pl-2 border-l border-gray-100" });
          const toggle = el("button", {
            type: "button",
            class: "w-full text-left flex items-center justify-between gap-2 px-2 py-2 rounded-lg hover:bg-gray-50",
            onclick: () => body.classList.toggle("hidden"),
          }, [
            el("div", {}, [
              el("div", { class: "text-sm font-medium text-gray-900", text: op.operator || "—" }),
              el("div", {
                class: "text-[11px] text-gray-500",
                text: `Photos ${formatDiskBytes(op.photosBytes)} (${op.photoCount}) · Files ${formatDiskBytes(op.filesBytes)} (${op.fileCount})`,
              }),
            ]),
            el("span", { class: "text-xs text-gray-400", text: "▸" }),
          ]);
          for (const a of op.aircraft || []) {
            const label = `MSN ${a.msn || a.saId}${a.registration ? ` · ${a.registration}` : ""}`;
            body.appendChild(el("div", { class: "flex flex-wrap items-center justify-between gap-2 px-2 py-1.5 rounded border border-gray-100 bg-white" }, [
              el("div", { class: "min-w-0" }, [
                el("div", { class: "text-sm text-gray-800", text: label }),
                el("div", {
                  class: "text-[11px] text-gray-500",
                  text: `Photos ${formatDiskBytes(a.photos?.bytesOnDisk)} (${a.photos?.count || 0}) · Files ${formatDiskBytes(a.files?.bytesOnDisk)} (${a.files?.count || 0})`,
                }),
              ]),
              el("div", { class: "flex gap-1.5 flex-wrap" }, [
                el("button", {
                  type: "button",
                  class: "text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50",
                  text: "Delete photos",
                  disabled: !(a.photos?.count || a.photos?.bytesOnDisk),
                  onclick: () => purge(a.saId, { photos: true, files: false }, label),
                }),
                el("button", {
                  type: "button",
                  class: "text-[11px] px-2 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50",
                  text: "Delete files",
                  disabled: !(a.files?.count || a.files?.bytesOnDisk),
                  onclick: () => purge(a.saId, { photos: false, files: true }, label),
                }),
                el("button", {
                  type: "button",
                  class: "text-[11px] px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50",
                  text: "Delete both",
                  onclick: () => purge(a.saId, { photos: true, files: true }, label),
                }),
              ]),
            ]));
          }
          return el("div", { class: "rounded-lg border border-gray-200" }, [toggle, body]);
        }));
      }
    } catch (e) {
      summaryEl.replaceChildren(el("div", { class: "text-sm text-red-700", text: cleanText(e?.message) || String(e) }));
      catsEl.replaceChildren();
      treeEl.replaceChildren();
    } finally {
      if (refreshBtn) {
        refreshBtn.disabled = false;
        refreshBtn.textContent = "Refresh";
      }
    }
  };

  await render(true);
  if (refreshBtn) refreshBtn.onclick = () => render(true).catch(() => {});
}

async function main() {
  renderNav();
  bindUi();
  const stored = (() => {
    try {
      return localStorage.getItem("actracker.v2.sidebarOpen");
    } catch {
      return null;
    }
  })();
  applySidebar(stored !== "false", { animate: false });

  try {
    await loadSession();
    renderNav();
  } catch (e) {
    // v2 doesn't have its own login UI yet; fall back to the legacy app login.
    if (e?.status === 401 || e?.code === "unauthenticated") {
      window.location.href = "/login.html";
      return;
    }
    throw e;
  }
  if (isAdminSession()) {
    loadCosts({ skipCache: true })
      .then((data) => {
        state.apiCostsPrefetched = data;
        const mount = document.getElementById("v2-settings-api-costs");
        if (mount) renderApiCostsInto(mount, data);
      })
      .catch(() => {});
  }
  if (isInspectorSession() && !isInspectorAllowedRoute(currentRouteKey())) {
    try {
      history.replaceState(null, "", inspectorHomeHash());
    } catch {
      /* ignore */
    }
  }
  await loadAircraft();
  if (isAdminSession()) {
    loadOperators().catch(() => {});
  }
  // Load FlightAware cache once per refresh so v2 "time on ground" aligns with legacy UI.
  await loadFaCache().catch(() => {
    state.faCacheByIdent = new Map();
  });
  state.lastUpdatedAt = computeLastUpdatedAtFromAircraft(state.aircraft);
  renderRoute();
  if (currentRouteKey() === "dashboard") {
    initDashboardPanels().catch(() => {});
  }
  if (currentRouteKey() === "status-recent") {
    initRecentStatusChangesPanels().catch(() => {});
  }
  initSettingsPanels().catch(() => {});
  updateTopbarLastUpdate();
  setInterval(updateTopbarLastUpdate, 30_000);
}

main().catch((err) => {
  const mount = document.getElementById("v2-content");
  if (mount) {
    mount.replaceChildren(
      el("div", { class: "bg-white border border-red-200 rounded-xl shadow-sm p-4" }, [
        el("h1", { class: "text-lg font-semibold text-gray-900", text: "Unable to load v2 UI" }),
        el("p", { class: "mt-2 text-sm text-gray-700", text: cleanText(err?.message) || String(err) }),
        el("p", { class: "mt-2 text-xs text-gray-500", text: `API base: ${apiBase}` }),
      ])
    );
  }
});

